import { useState, useRef, useEffect, useCallback, useMemo, createContext, useContext } from "react";

// ─── Inline Supabase REST client ──────────────────────────────────────────────
// Full Supabase client compatible with artifact environment (no CDN import needed)
const SUPABASE_URL  = "https://pjgqhgyttefazbkjqpvk.supabase.co";
const SUPABASE_ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBqZ3FoZ3l0dGVmYXpia2pxcHZrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgwMzYwOTYsImV4cCI6MjA5MzYxMjA5Nn0.ETv2OtvJHQqtX7nSfnXhE38nBfwZsjMUjSfCtNmki2s";

// Session stored in memory (localStorage not available in artifact env)
let _session = null;
let _authListeners = [];

const supabase = {
  // ── REST query builder ────────────────────────────────────────────────────
  from(table) {
    let _url = `${SUPABASE_URL}/rest/v1/${table}`;
    let _filters = [];
    let _order = null;
    let _single = false;
    let _select = "*";

    const headers = () => {
      const h = {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON,
        "Authorization": `Bearer ${_session?.access_token || SUPABASE_ANON}`,
        "Prefer": _single ? "return=representation" : "return=representation",
      };
      if (_single) h["X-Single"] = "true"; // marker only — handled in JS, not by PostgREST
      return h;
    };

    const buildUrl = () => {
      let url = `${_url}?select=${_select}`;
      _filters.forEach(f => { url += `&${f}`; });
      if (_order) url += `&order=${_order}`;
      return url;
    };

    const q = {
      select(cols = "*") { _select = cols; return q; },
      eq(col, val)  { _filters.push(`${col}=eq.${val}`); return q; },
      is(col, val)   { _filters.push(val === null ? `${col}=is.null` : `${col}=is.${val}`); return q; },
      not(col, op, val) { _filters.push(`${col}=not.${op}.${val === null ? "null" : val}`); return q; },
      in(col, vals) { _filters.push(`${col}=in.(${vals.join(",")})`); return q; },
      order(col, opts = {}) { _order = `${col}.${opts.ascending === false ? "desc" : "asc"}`; return q; },
      single() { _single = true; return q; },

      then(resolve) {
        return fetch(buildUrl(), { headers: headers() })
          .then(async r => {
            const d = await r.json();
            if (!r.ok) {
              // 406 = no rows found for single() — return null data, not error
              if (r.status === 406) return { data: null, error: null };
              return { data: null, error: d };
            }
            // If single() was set, unwrap array to single object
            const data = _single && Array.isArray(d) ? (d[0] || null) : d;
            return { data, error: null };
          })
          .then(resolve);
      },

      insert(data) {
        // Return a chainable object so .insert({...}).select().single() works
        let _insertSingle = false;
        const insertQ = {
          select(cols = "*") { _select = cols; return insertQ; },
          single() { _insertSingle = true; return insertQ; },
          then(resolve) {
            return fetch(_url, {
              method: "POST",
              headers: { ...headers(), "Prefer": "return=representation" },
              body: JSON.stringify(Array.isArray(data) ? data : [data]),
            })
            .then(r => r.json().then(d => {
              const result = Array.isArray(d) ? (_insertSingle ? d[0] : d) : d;
              return { data: result, error: r.ok ? null : d };
            }))
            .then(resolve);
          },
        };
        return insertQ;
      },

      update(data) {
        // Chainable update — .update(data).eq(...).select().single() all work
        const updateQ = {
          select(cols = "*") { _select = cols; return updateQ; },
          single() { _single = true; return updateQ; },
          eq(col, val) { _filters.push(`${col}=eq.${val}`); return updateQ; },
          then(resolve) {
            return fetch(buildUrl(), {
              method: "PATCH",
              headers: { ...headers(), "Prefer": "return=representation" },
              body: JSON.stringify(data),
            })
            .then(async r => {
              const d = await r.json();
              if (!r.ok) return { data: null, error: d };
              const result = _single ? (Array.isArray(d) ? d[0] || null : d) : d;
              return { data: result, error: null };
            })
            .then(resolve);
          },
        };
        return updateQ;
      },

      delete() {
        // Chainable delete — .delete().eq(...) works
        const deleteQ = {
          eq(col, val) { _filters.push(`${col}=eq.${val}`); return deleteQ; },
          then(resolve) {
            return fetch(buildUrl(), { method: "DELETE", headers: headers() })
            .then(async r => {
              return { data: null, error: r.ok ? null : await r.json() };
            })
            .then(resolve);
          },
        };
        return deleteQ;
      },

      async upsert(data, opts = {}) {
        const h = { ...headers(), "Prefer": `return=representation,resolution=${opts.onConflict ? `merge-duplicates` : "merge-duplicates"}` };
        if (opts.onConflict) h["on_conflict"] = opts.onConflict;
        const r = await fetch(_url, {
          method: "POST",
          headers: { ...h, "Prefer": "return=representation,resolution=merge-duplicates" },
          body: JSON.stringify(Array.isArray(data) ? data : [data]),
        });
        const d = await r.json();
        return { data: d, error: r.ok ? null : d };
      },
    };

    // Make select().eq().single() chainable AND awaitable
    q.select = (cols = "*") => { _select = cols; return q; };
    return q;
  },

  // ── Auth ──────────────────────────────────────────────────────────────────
  auth: {
    async signInWithPassword({ email, password }) {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) return { data: null, error: { message: d.error_description || d.msg || "Sign in failed" } };
      _session = d;
      _authListeners.forEach(fn => fn("SIGNED_IN", d));
      return { data: { user: d.user, session: d }, error: null };
    },

    async signUp({ email, password }) {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) return { data: null, error: { message: d.error_description || d.msg || "Sign up failed" } };
      _session = d;
      _authListeners.forEach(fn => fn("SIGNED_IN", d));
      return { data: { user: d.user, session: d }, error: null };
    },

    async signOut() {
      await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON, "Authorization": `Bearer ${_session?.access_token || SUPABASE_ANON}` },
      });
      _session = null;
      _authListeners.forEach(fn => fn("SIGNED_OUT", null));
      return { error: null };
    },

    async getSession() {
      // In artifact env sessions don't persist across reloads — return current in-memory session
      return { data: { session: _session } };
    },

    onAuthStateChange(callback) {
      _authListeners.push(callback);
      return { data: { subscription: { unsubscribe: () => { _authListeners = _authListeners.filter(f => f !== callback); } } } };
    },
  },

  // ── Storage — real Supabase Storage REST API ───────────────────────────────
  storage: {
    from(bucket) {
      return {
        upload: async (path, file, opts = {}) => {
          const r = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`, {
            method: "POST",
            headers: {
              "apikey": SUPABASE_ANON,
              "Authorization": `Bearer ${_session?.access_token || SUPABASE_ANON}`,
              "Content-Type": file.type || "application/octet-stream",
              ...(opts.upsert ? { "x-upsert": "true" } : {}),
            },
            body: file,
          });
          if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            return { data: null, error: { message: d.message || d.error || `Upload failed (${r.status})` } };
          }
          return { data: { path }, error: null };
        },
        getPublicUrl: (path) => ({ data: { publicUrl: `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}` } }),
        remove: async (paths) => {
          const r = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}`, {
            method: "DELETE",
            headers: {
              "apikey": SUPABASE_ANON,
              "Authorization": `Bearer ${_session?.access_token || SUPABASE_ANON}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ prefixes: paths }),
          });
          return { data: null, error: r.ok ? null : await r.json().catch(() => ({})) };
        },
      };
    },
  },
};

// ─── App-wide context — org + user shared without prop drilling ───────────────
const AppCtx = createContext(null);
const useAppCtx = () => useContext(AppCtx);

// ─── Central DB helpers — all queries in one place ────────────────────────────
let LIVE_DEMO_MODE = false;
function demoWrite(d) {
  return Promise.resolve({ data: { ...d, id: d.id ?? ("demo-" + Math.random().toString(36).slice(2, 10)) }, error: null });
}
function demoDelete() {
  return Promise.resolve({ error: null });
}

const db = {
  homeowners:  {
    list:   (oid) => supabase.from("homeowners").select("*").eq("org_id", oid).is("deleted_at", null).order("unit"),
    listAll: (oid) => supabase.from("homeowners").select("*").eq("org_id", oid).order("unit"),
    save:   (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : (d.id
      ? supabase.from("homeowners").update(d).eq("id", d.id).select().single()
      : supabase.from("homeowners").insert({ ...d, org_id: oid }).select().single()),
    delete: (id) => LIVE_DEMO_MODE ? demoDelete() : supabase.from("homeowners").delete().eq("id", id),
  },
  dues: {
    list:     (oid) => supabase.from("dues").select("*").eq("org_id", oid).order("due_date", { ascending: false }),
    markPaid: (id)  => LIVE_DEMO_MODE ? demoWrite({ id, status: "paid", paid_date: new Date().toISOString().split("T")[0] }) : supabase.from("dues").update({ status: "paid", paid_date: new Date().toISOString().split("T")[0] }).eq("id", id).select().single(),
    save:     (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : (d.id
      ? supabase.from("dues").update(d).eq("id", d.id).select().single()
      : supabase.from("dues").insert({ ...d, org_id: oid }).select().single()),
  },
  violations: {
    list:   (oid) => supabase.from("violations").select("*").eq("org_id", oid).order("created_at", { ascending: false }),
    save:   (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : (d.id
      ? supabase.from("violations").update(d).eq("id", d.id).select().single()
      : supabase.from("violations").insert({ ...d, org_id: oid }).select().single()),
    delete: (id)  => LIVE_DEMO_MODE ? demoDelete() : supabase.from("violations").delete().eq("id", id),
  },
  emailLog: {
    list:   (oid) => supabase.from("email_log").select("*").eq("org_id", oid).order("sent_at", { ascending: false }),
    add:    (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : supabase.from("email_log").insert({ ...d, org_id: oid }).select().single(),
  },
  maintenance: {
    list:   (oid) => supabase.from("maintenance_requests").select("*").eq("org_id", oid).order("created_at", { ascending: false }),
    save:   (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : (d.id
      ? supabase.from("maintenance_requests").update(d).eq("id", d.id).select().single()
      : supabase.from("maintenance_requests").insert({ ...d, org_id: oid }).select().single()),
  },
  vendors: {
    list:   (oid) => supabase.from("vendors").select("*").eq("org_id", oid).order("name"),
    save:   (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : (d.id
      ? supabase.from("vendors").update(d).eq("id", d.id).select().single()
      : supabase.from("vendors").insert({ ...d, org_id: oid }).select().single()),
  },
  workOrders: {
    list:   (oid) => supabase.from("work_orders").select("*, work_order_invoices(*)").eq("org_id", oid).order("created_at_ts", { ascending: false }),
    save:   (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : (d.id
      ? supabase.from("work_orders").update(d).eq("id", d.id).select().single()
      : supabase.from("work_orders").insert({ ...d, org_id: oid }).select().single()),
  },
  transactions: {
    list:   (oid) => supabase.from("transactions").select("*").eq("org_id", oid).order("date", { ascending: false }),
    add:    (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : supabase.from("transactions").insert({ ...d, org_id: oid }).select().single(),
    delete: (id)  => LIVE_DEMO_MODE ? demoDelete() : supabase.from("transactions").delete().eq("id", id),
  },
  announcements: {
    list:   (oid) => supabase.from("announcements").select("*").eq("org_id", oid).order("created_at", { ascending: false }),
  },
  ballots: {
    list:   (oid) => supabase.from("ballots").select("*, ballot_options(*)").eq("org_id", oid).order("created_at", { ascending: false }),
  },
  reservations: {
    list:   (oid) => supabase.from("reservations").select("*").eq("org_id", oid).order("date"),
    add:    (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : supabase.from("reservations").insert({ ...d, org_id: oid }).select().single(),
    cancel: (id)  => LIVE_DEMO_MODE ? demoDelete() : supabase.from("reservations").update({ status: "cancelled" }).eq("id", id),
  },
  sitePhotos: {
    list:   (oid, category) => supabase.from("site_photos").select("*").eq("org_id", oid).eq("category", category).order("created_at", { ascending: false }),
    add:    (oid, d) => LIVE_DEMO_MODE ? demoWrite(d) : supabase.from("site_photos").insert({ ...d, org_id: oid }).select().single(),
    delete: (id)  => LIVE_DEMO_MODE ? demoDelete() : supabase.from("site_photos").delete().eq("id", id),
  },
};

// ─── Site photo upload — real camera/library upload used by Emergency
//     Response and Pre-Loss Documentation. Uploads to Supabase Storage, then
//     records a row in site_photos so the photo is visible to every board
//     member on any device, not just the phone it was taken on. In demo
//     mode, skips the network and previews the file locally instead. ───────
async function uploadSitePhoto(orgId, category, subcategory, file, caption, takenBy) {
  if (LIVE_DEMO_MODE) {
    return {
      data: {
        id: "demo-" + Math.random().toString(36).slice(2, 10),
        org_id: orgId, category, subcategory: subcategory || "", caption: caption || "",
        photo_url: URL.createObjectURL(file), taken_by: takenBy || "",
        created_at: new Date().toISOString(),
      },
      error: null,
    };
  }
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const path = `${orgId}/${category}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error: upErr } = await supabase.storage.from("site-photos").upload(path, file, { upsert: true });
  if (upErr) return { data: null, error: upErr };
  const { data: { publicUrl } } = supabase.storage.from("site-photos").getPublicUrl(path);
  return db.sitePhotos.add(orgId, { category, subcategory: subcategory || "", caption: caption || "", storage_path: path, photo_url: publicUrl, taken_by: takenBy || "" });
}

// ─── Secure AI proxy helper — replaces direct callClaude() calls ──────────────
// Points to Netlify function which holds the real API key server-side.
// Falls back to direct call in development if function not available.
async function callClaude(prompt, maxTokens = 900) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers = { "Content-Type": "application/json" };
    if (session) headers["Authorization"] = `Bearer ${session.access_token}`;
    const res = await fetch("/.netlify/functions/ai", {
      method: "POST", headers,
      body: JSON.stringify({ prompt, maxTokens }),
    });
    if (!res.ok) throw new Error(`AI proxy error: ${res.status}`);
    const { text } = await res.json();
    return text;
  } catch {
    // Dev fallback — direct call (remove before production)
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: maxTokens, messages: [{ role: "user", content: prompt }] }),
    });
    const d = await res.json();
    return d.content?.[0]?.text ?? "Unable to generate content.";
  }
}

async function callClaudeChat(messages, systemPrompt, maxTokens = 1200) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers = { "Content-Type": "application/json" };
    if (session) headers["Authorization"] = `Bearer ${session.access_token}`;
    const res = await fetch("/.netlify/functions/ai", {
      method: "POST", headers,
      body: JSON.stringify({ messages, system: systemPrompt, maxTokens }),
    });
    if (!res.ok) throw new Error(`AI proxy error: ${res.status}`);
    const { text } = await res.json();
    return text;
  } catch {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: maxTokens, system: systemPrompt, messages }),
    });
    const d = await res.json();
    return d.content?.[0]?.text ?? "Unable to generate a response.";
  }
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  :root{
    --navy:#F3F5F9;--navy2:#FFFFFF;--navy3:#F7F8FA;--navy4:#E4E9F2;
    --slate:#5B6B85;--ice:#3D4B61;--white:#1B3C78;
    --teal:#2A63D6;--teal2:#1B3C78;--teal3:rgba(42,99,214,.12);
    --amber:#f59e0b;--amber2:rgba(245,158,11,.12);
    --red:#dc2626;--red2:rgba(220,38,38,.12);
    --purple:#a78bfa;--purple2:rgba(124,58,237,.12);
    --green:#3abf7e;--green2:rgba(22,163,74,.12);
    --border:rgba(61,75,97,.14);--border2:rgba(61,75,97,.16);
    --sb-bg:#1B1C1F;--sb-bg-hover:#2A2B2F;--sb-text:#D6D7DA;--sb-text-dim:#9AA0A8;--sb-border:rgba(255,255,255,.08);
    --serif:'Inter',system-ui,sans-serif;--sans:'Inter',system-ui,sans-serif;
    --r:10px;--r2:16px;
  }
  body{font-family:var(--sans);background:var(--navy);color:#1B3C78;min-height:100vh}
  ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:#a3a29c;border-radius:3px}

  .lw{min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--navy);background-image:radial-gradient(ellipse 55% 45% at 75% 15%,rgba(45,212,160,.09) 0%,transparent 70%),radial-gradient(ellipse 40% 35% at 5% 85%,rgba(167,139,250,.07) 0%,transparent 60%)}
  .lc{background:var(--navy2);border:.5px solid var(--border2);border-radius:var(--r2);padding:2.5rem 2rem;width:360px}
  .ll{font-family:'Inter',system-ui,sans-serif;font-size:28px;margin-bottom:4px}.ll span{color:#2A63D6}
  .ls{font-size:12px;color:var(--slate);margin-bottom:2rem;letter-spacing:.04em}
  .li{width:100%;background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:10px 12px;font-size:14px;color:#1B3C78;font-family:var(--sans);margin-bottom:1rem;transition:border-color .2s}
  .li:focus{outline:none;border-color:var(--teal)}
  .lb{width:100%;background:var(--teal);color:#fff;border:none;border-radius:var(--r);padding:11px;font-size:14px;font-weight:600;font-family:var(--sans);cursor:pointer;transition:background .2s}
  .lb:hover{background:var(--teal2)}
  .lh{font-size:11px;color:var(--slate);text-align:center;margin-top:1rem}
  .le{font-size:12px;color:var(--red);margin-bottom:.75rem}
  .ll2{font-size:11px;font-weight:600;color:var(--slate);letter-spacing:.06em;text-transform:uppercase;margin-bottom:6px;display:block}

  .app{display:grid;grid-template-columns:230px 1fr;height:100vh;overflow:hidden}

  .sb{background:var(--sb-bg);border-right:5px solid var(--teal);display:flex;flex-direction:column;padding:0 0 1.5rem;transition:left .25s ease;height:100vh;overflow-y:auto;overflow-x:hidden}
  @media(max-width:768px){.sb{position:fixed;top:0;bottom:0;left:-270px;width:260px;z-index:1000;overflow-y:auto;padding-top:3rem}.mob-ham{display:block !important}.mob-close{display:block !important}.app{grid-template-columns:1fr !important}.main{padding-top:48px;width:100vw !important}}
  .sb-logo{font-family:'Inter',system-ui,sans-serif;font-size:22px;padding:1.5rem 1.25rem;display:flex;align-items:center;gap:8px;background:linear-gradient(180deg, rgba(255,255,255,.35) 0%, rgba(255,255,255,0) 42%), linear-gradient(180deg, #4A7CE8 0%, #1F4488 58%, #122A54 100%);box-shadow:inset 0 1px 0 rgba(255,255,255,.5), 0 2px 10px rgba(8,18,40,.25);border-bottom:.5px solid var(--sb-border);color:#FFFFFF;text-shadow:0 1px 3px rgba(6,14,32,.4);margin-bottom:.25rem}.sb-logo span{color:#C9D7F5}.sb-logo svg{flex-shrink:0;min-width:38px}
  .sb-ver{font-size:10px;color:var(--sb-text-dim);margin-left:4px;font-family:var(--sans);font-weight:400}
  .sb-sec{font-size:10px;font-weight:600;color:var(--sb-text-dim);letter-spacing:.1em;text-transform:uppercase;padding:.85rem 1.25rem .5rem;border-bottom:.5px solid var(--sb-border);margin-bottom:.3rem}
  .ni{display:flex;align-items:center;gap:10px;padding:.6rem 1.25rem;font-size:13px;color:var(--sb-text);cursor:pointer;transition:all .15s;border-left:2px solid transparent;margin:1px 0;position:relative}
  .ni:hover{color:#fff;background:var(--sb-bg-hover)}
  .ni.active{color:var(--teal);border-left-color:var(--teal);background:rgba(42,99,214,.16)}
  .sb-group{margin-bottom:.5rem}
  .sb-group-label{font-size:11px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;padding:.9rem 1.25rem .5rem;display:flex;align-items:center;gap:6px;border-bottom:.5px solid var(--sb-border);margin-bottom:.3rem}
  .sb-group-label::before{content:"";display:block;width:3px;height:12px;border-radius:2px;background:currentColor;opacity:.8}
  .sb-group.community .sb-group-label{color:#2dd4bf}
  .sb-group.community .ni.active{color:#2dd4bf;border-left-color:#2dd4bf;background:rgba(45,212,191,.16)}
  .sb-group.operations .sb-group-label{color:#a78bfa}
  .sb-group.operations .ni.active{color:#a78bfa;border-left-color:#a78bfa;background:rgba(167,139,250,.16)}
  .sb-group.financial .sb-group-label{color:#4ade80}
  .sb-group.financial .ni.active{color:#4ade80;border-left-color:#4ade80;background:rgba(74,222,128,.16)}
  .sb-group.risk .sb-group-label{color:#fb923c}
  .sb-group.risk .ni.active{color:#fb923c;border-left-color:#fb923c;background:rgba(251,146,60,.18)}
  .sb-group.ai .sb-group-label{color:#fbbf24}
  .sb-group.ai .ni.active{color:#fbbf24;border-left-color:#fbbf24;background:rgba(251,191,36,.16)}
  .sb-group.account .sb-group-label{color:#9ca3af}
  .sb-group.account .ni.active{color:#9ca3af;border-left-color:#9ca3af;background:rgba(156,163,175,.16)}
  .ni-badge{position:absolute;right:12px;background:var(--red);color:#fff;font-size:10px;font-weight:600;border-radius:10px;padding:1px 6px;min-width:18px;text-align:center}
  .ni-badge.purple{background:var(--purple);color:#fff}
  .ni-badge.amber{background:var(--amber);color:#fff}
  .sb-ft{margin-top:auto;padding:1rem 1.25rem;border-top:.5px solid var(--sb-border)}
  .ua{width:32px;height:32px;border-radius:50%;background:var(--teal);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:#fff;flex-shrink:0}
  .un{font-size:13px;font-weight:500;color:#fff}.ur{font-size:11px;color:#9aa0a8;font-weight:500}
  .lo{margin-top:.75rem;width:100%;background:transparent;border:.5px solid var(--border2);border-radius:var(--r);padding:7px;font-size:12px;color:var(--sb-text-dim);cursor:pointer;font-family:var(--sans);transition:all .15s}
  .lo:hover{border-color:var(--red);color:var(--red)}

  .main{overflow-y:auto;height:100vh;scroll-behavior:smooth}
  .ph{padding:1.75rem 2rem 1rem;border-bottom:.5px solid var(--border);display:flex;align-items:flex-start;justify-content:space-between;gap:1rem}
  .pt{font-family:'Inter',system-ui,sans-serif;font-size:26px}.ps{font-size:13px;color:var(--slate);margin-top:3px}
  .pb{padding:1.5rem 2rem}

  .sr{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:1.5rem}
  .sc{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);padding:1rem 1.1rem}
  .sl{font-size:11px;color:var(--slate);font-weight:500;letter-spacing:.04em;text-transform:uppercase;margin-bottom:6px}
  .sv{font-size:22px;font-weight:600}.sv.t{color:var(--teal)}.sv.a{color:var(--amber)}.sv.r{color:var(--red)}.sv.p{color:var(--purple)}

  .tb{display:flex;align-items:center;gap:10px;margin-bottom:1.25rem;flex-wrap:wrap}
  .si{flex:1;min-width:180px;background:var(--navy3);border:1px solid rgba(255,255,255,.15);border-radius:var(--r);padding:8px 12px;font-size:13px;color:#1B3C78;font-family:var(--sans)}
  .si::placeholder{color:var(--slate)}.si:focus{outline:none;border-color:var(--teal)}
  .btn{background:var(--teal);color:#fff;border:none;border-radius:var(--r);padding:8px 16px;font-size:13px;font-weight:600;font-family:var(--sans);cursor:pointer;transition:background .15s,transform .1s;white-space:nowrap}
  .btn:hover{background:var(--teal2)}.btn:active{transform:scale(.97)}.btn:disabled{opacity:.45;cursor:not-allowed}
  .btn-g{background:transparent;color:var(--slate);border:.5px solid var(--border2);border-radius:var(--r);padding:8px 14px;font-size:13px;font-family:var(--sans);cursor:pointer;transition:all .15s;white-space:nowrap}
  .btn-g:hover{color:#1B3C78;background:rgba(61,75,97,.05)}
  .btn-sm{padding:5px 10px;font-size:12px}
  .btn-pu{background:var(--purple);color:#fff}.btn-pu:hover{background:#9171f8}

  .tw{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);overflow-x:auto;-webkit-overflow-scrolling:touch;margin-bottom:1rem}
  table{width:100%;border-collapse:collapse}
  th{font-size:11px;font-weight:600;color:var(--slate);letter-spacing:.06em;text-transform:uppercase;padding:.75rem 1rem;text-align:left;border-bottom:.5px solid var(--border)}
  td{font-size:13px;color:#1B3C78;font-weight:500;padding:.75rem 1rem;border-bottom:.5px solid var(--border);vertical-align:middle}
  tr:last-child td{border-bottom:none}
  tr:hover td{background:rgba(61,75,97,.02)}

  .bg{display:inline-block;font-size:11px;font-weight:500;padding:2px 8px;border-radius:20px}
  .bg-g{background:var(--green2);color:var(--green)}.bg-a{background:var(--amber2);color:var(--amber)}
  .bg-r{background:var(--red2);color:var(--red)}.bg-s{background:rgba(138,153,179,.12);color:var(--slate)}
  .bg-t{background:var(--teal3);color:var(--teal)}.bg-p{background:var(--purple2);color:var(--purple)}

  .mo{position:fixed;inset:0;background:rgba(10,10,10,.85);display:flex;align-items:center;justify-content:center;z-index:100;padding:1rem}
  .md{background:var(--navy2);border:.5px solid var(--border2);border-radius:var(--r2);padding:1.75rem;width:100%;max-width:560px;max-height:92vh;overflow-y:auto}
  .md-lg{max-width:680px}
  .mt2{font-family:'Inter',system-ui,sans-serif;font-size:20px;margin-bottom:.25rem}
  .ms2{font-size:13px;color:var(--slate);margin-bottom:1.5rem}
  .fr{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .fg{margin-bottom:1rem}
  .fl{font-size:11px;font-weight:600;color:var(--slate);letter-spacing:.05em;text-transform:uppercase;display:block;margin-bottom:6px}
  .fi{width:100%;background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:9px 12px;font-size:13px;color:#1B3C78;font-family:var(--sans)}
  .fi:focus{outline:none;border-color:var(--teal)}
  select.fi option{background:var(--navy3)}
  textarea.fi{resize:vertical;min-height:80px;line-height:1.5}
  .ma{display:flex;gap:10px;justify-content:flex-end;margin-top:1.25rem}

  .aip{background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:1rem;margin-top:1rem}
  .ail{font-size:11px;font-weight:600;color:var(--teal);letter-spacing:.06em;text-transform:uppercase;margin-bottom:.6rem;display:flex;align-items:center;gap:6px}
  .aio{font-size:13px;color:var(--ice);line-height:1.75;white-space:pre-wrap;max-height:260px;overflow-y:auto}
  .ais{color:var(--slate);font-size:13px;font-style:italic}
  .ai-spin{display:inline-block;width:13px;height:13px;border:2px solid var(--teal3);border-top-color:var(--teal);border-radius:50%;animation:spin .7s linear infinite}
  @keyframes spin{to{transform:rotate(360deg)}}

  .ep{background:var(--navy);border:.5px solid var(--border2);border-radius:var(--r);padding:1rem;margin-top:.75rem}
  .eh{display:flex;flex-direction:column;gap:4px;margin-bottom:12px;padding-bottom:10px;border-bottom:.5px solid var(--border2)}
  .ef{display:flex;gap:8px;font-size:12px}
  .ef-l{color:var(--slate);min-width:36px;font-weight:500}
  .ef-v{color:#1B3C78}
  .eb{font-size:13px;color:var(--ice);line-height:1.7;white-space:pre-wrap}

  .risk-bar-wrap{display:flex;align-items:center;gap:8px}
  .risk-bar{flex:1;height:5px;background:var(--navy3);border-radius:3px;overflow:hidden}
  .risk-fill{height:100%;border-radius:3px;transition:width .4s}
  .risk-low{background:var(--green)}.risk-medium{background:var(--amber)}.risk-high{background:var(--red)}

  .card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.25rem}
  .card-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:1.5rem}
  .card-title{font-size:11px;font-weight:600;color:var(--slate);letter-spacing:.06em;text-transform:uppercase;margin-bottom:1rem}
  .act-item{display:flex;align-items:flex-start;gap:10px;padding:.6rem 0;border-bottom:.5px solid var(--border)}
  .act-item:last-child{border-bottom:none}
  .act-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;margin-top:4px}
  .act-text{font-size:13px;line-height:1.4}
  .act-time{font-size:11px;color:var(--slate);margin-top:2px}

  .feat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:12px;margin-bottom:1.5rem}
  .feat-card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1rem 1.1rem;cursor:pointer;transition:border-color .2s}
  .feat-card:hover{border-color:var(--border2)}
  .feat-name{font-size:13px;font-weight:700;color:#1B3C78;margin-bottom:4px}
  .feat-desc{font-size:12px;color:#3D4B61;line-height:1.5}
  .feat-tag{font-size:10px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;padding:2px 7px;border-radius:20px;display:inline-block;margin-bottom:8px}
  .ft-ai{background:var(--purple2);color:var(--purple)}
  .ft-auto{background:var(--teal3);color:var(--teal)}

  .tabs{display:flex;gap:4px;margin-bottom:1.25rem;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);padding:4px}
  .tab{flex:1;text-align:center;padding:7px;font-size:12px;font-weight:500;color:var(--slate);border-radius:7px;cursor:pointer;transition:all .15s}
  .tab.active{background:var(--navy3);color:#1B3C78;font-weight:600;box-shadow:0 1px 3px rgba(255,255,255,.1)}

  .del-card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1rem 1.25rem;margin-bottom:10px;display:flex;align-items:center;gap:1rem}
  .del-info{flex:1}
  .del-name{font-size:14px;font-weight:500}
  .del-sub{font-size:12px;color:var(--slate);margin-top:2px}

  .seq-dot{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;flex-shrink:0}
  .sd1{background:var(--teal3);color:var(--teal)}.sd2{background:var(--amber2);color:var(--amber)}.sd3{background:var(--red2);color:var(--red)}

  .toast{position:fixed;bottom:24px;right:24px;background:var(--navy3);border:.5px solid var(--teal);border-radius:var(--r);padding:12px 16px;font-size:13px;color:var(--teal);z-index:999;max-width:320px;animation:slideUp .25s ease}
  @keyframes slideUp{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}

  /* ── AI Compliance Auditor ── */
  .ca-layout{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:1.5rem}
  .ca-audit-card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);overflow:hidden}
  .ca-audit-card.ok{border-color:rgba(58,191,126,.25)}
  .ca-audit-card.warn{border-color:rgba(245,166,35,.3)}
  .ca-audit-card.fail{border-color:rgba(224,82,82,.35)}
  .ca-audit-card.info{border-color:rgba(42,99,214,.15)}
  .ca-audit-header{padding:.85rem 1.1rem;display:flex;align-items:center;gap:10px;border-bottom:.5px solid var(--border)}
  .ca-audit-icon{font-size:20px;flex-shrink:0}
  .ca-audit-title{font-size:13px;font-weight:700;color:#1B3C78;flex:1}
  .ca-status-badge{font-size:10px;font-weight:700;padding:2px 9px;border-radius:20px;flex-shrink:0}
  .ca-ok{background:var(--green2);color:var(--green)}
  .ca-warn{background:var(--amber2);color:var(--amber)}
  .ca-fail{background:var(--red2);color:var(--red)}
  .ca-info{background:var(--teal3);color:var(--teal)}
  .ca-audit-body{padding:.85rem 1.1rem}
  .ca-fact{font-size:12px;color:#3D4B61;margin-bottom:4px;line-height:1.5;font-weight:500}
  .ca-fact strong{color:#1B3C78;font-weight:600}
  .ca-deadline-bar{height:5px;background:var(--navy3);border-radius:3px;margin:.5rem 0;overflow:hidden}
  .ca-deadline-fill{height:100%;border-radius:3px;transition:width .4s}
  .ca-action-btn{width:100%;margin-top:.65rem;background:transparent;border:.5px solid var(--border2);border-radius:var(--r);padding:7px;font-size:12px;color:var(--slate);cursor:pointer;font-family:var(--sans);transition:all .15s}
  .ca-action-btn:hover{color:#1B3C78;border-color:var(--teal)}
  .ca-action-btn.primary{background:var(--teal);color:#fff;border-color:var(--teal);font-weight:600}
  .ca-action-btn.primary:hover{background:var(--teal2)}
  .ca-timeline{position:relative;padding-left:22px;margin-top:.5rem}
  .ca-timeline::before{content:"";position:absolute;left:6px;top:8px;bottom:8px;width:1.5px;background:var(--border2)}
  .ca-tl-item{position:relative;padding-bottom:12px}
  .ca-tl-dot{position:absolute;left:-22px;top:3px;width:12px;height:12px;border-radius:50%;border:2px solid var(--border2);background:var(--navy2)}
  .ca-tl-dot.done{background:var(--teal);border-color:var(--teal)}
  .ca-tl-dot.due{background:var(--amber);border-color:var(--amber);box-shadow:0 0 0 3px rgba(245,166,35,.2)}
  .ca-tl-dot.missed{background:var(--red);border-color:var(--red)}
  .ca-tl-text{font-size:12px;color:#1B3C78;font-weight:500}
  .ca-tl-sub{font-size:11px;color:var(--slate);margin-top:1px}
  .ca-ai-report{background:var(--navy3);border:.5px solid rgba(167,139,250,.2);border-radius:var(--r);padding:1rem;margin-top:1rem}
  .ca-new-meeting-form{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.25rem;margin-top:1rem}
  .ca-rule-row{display:flex;align-items:flex-start;gap:10px;padding:.6rem 0;border-bottom:.5px solid var(--border)}
  .ca-rule-row:last-child{border-bottom:none}
  .ca-rule-icon{font-size:15px;flex-shrink:0;margin-top:1px}
  .ca-rule-state{font-size:12px;font-weight:700;color:var(--purple);min-width:90px;flex-shrink:0}
  .ca-rule-text{font-size:12px;color:var(--slate);line-height:1.5}
  .ca-alert-strip{display:flex;align-items:center;gap:10px;padding:.85rem 1.1rem;border-radius:var(--r);margin-bottom:8px;border:.5px solid}
  .ca-alert-strip.red{background:rgba(224,82,82,.07);border-color:rgba(224,82,82,.25)}
  .ca-alert-strip.amber{background:rgba(245,166,35,.07);border-color:rgba(245,166,35,.22)}
  .ca-alert-strip.green{background:rgba(58,191,126,.07);border-color:rgba(58,191,126,.22)}

  /* ── USPS Mailing (Lob) ── */
  .lob-grid{display:grid;grid-template-columns:380px 1fr;height:calc(100vh - 73px);overflow:hidden}
  .lob-form-panel{overflow-y:auto;padding:1.5rem;border-right:.5px solid var(--border)}
  .lob-preview-panel{background:#f0f0f0;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;overflow-y:auto;padding:2rem}
  .lob-section-label{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.08em;text-transform:uppercase;margin:1rem 0 .6rem}
  .lob-section-label:first-child{margin-top:0}
  .lob-type-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:.5rem}
  .lob-type-btn{padding:.7rem;border-radius:var(--r);border:.5px solid var(--border2);background:var(--navy3);color:var(--slate);font-size:12px;font-family:var(--sans);cursor:pointer;transition:all .15s;text-align:center}
  .lob-type-btn:hover{color:#1B3C78;border-color:var(--teal)}
  .lob-type-btn.active{background:rgba(42,99,214,.08);border-color:var(--teal);color:var(--teal)}
  .lob-type-icon{font-size:18px;display:block;margin-bottom:3px}
  .lob-recipient-row{display:flex;align-items:center;gap:8px;padding:.6rem .85rem;background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);margin-bottom:6px}
  .lob-recipient-cb{width:15px;height:15px;accent-color:var(--teal);cursor:pointer;flex-shrink:0}
  .lob-recipient-name{font-size:13px;font-weight:600;color:#1B3C78;flex:1}
  .lob-recipient-addr{font-size:11px;color:var(--slate)}
  .lob-cost-row{display:flex;justify-content:space-between;font-size:13px;padding:.5rem 0;border-bottom:.5px solid var(--border)}
  .lob-cost-row:last-child{border-bottom:none}
  .lob-send-btn{width:100%;background:#2A63D6;color:#fff;border:none;border-radius:var(--r);padding:12px;font-size:14px;font-weight:700;font-family:var(--sans);cursor:pointer;margin-top:.75rem;display:flex;align-items:center;justify-content:center;gap:8px;transition:opacity .15s}
  .lob-send-btn:hover{opacity:.9}.lob-send-btn:disabled{opacity:.4;cursor:not-allowed}
  .letter-preview{width:460px;min-height:600px;background:var(--navy3);border-radius:4px;box-shadow:0 8px 40px rgba(255,255,255,.25);padding:48px;font-family:'Times New Roman',serif;color:#000;position:relative;flex-shrink:0}
  .letter-preview .lp-logo{font-family:Arial,sans-serif;font-size:13px;font-weight:700;color:#1B3C78;letter-spacing:.05em;margin-bottom:4px}
  .letter-preview .lp-addr{font-size:11px;color:#555;margin-bottom:24px;line-height:1.5}
  .letter-preview .lp-date{font-size:12px;color:#333;margin-bottom:18px}
  .letter-preview .lp-to{font-size:12px;color:#333;margin-bottom:24px;line-height:1.6}
  .letter-preview .lp-subject{font-size:13px;font-weight:700;color:#000;margin-bottom:14px;text-decoration:underline}
  .letter-preview .lp-body{font-size:12px;color:#222;line-height:1.8;margin-bottom:16px}
  .letter-preview .lp-sig{font-size:12px;color:#333;margin-top:24px;line-height:1.7}
  .letter-preview .lp-stamp{position:absolute;top:24px;right:24px;width:50px;height:62px;background:linear-gradient(135deg,#0066cc,#004499);border-radius:3px;display:flex;align-items:center;justify-content:center;font-size:8px;color:#fff;font-weight:700;text-align:center;line-height:1.3}
  .letter-preview .lp-barcode{position:absolute;bottom:24px;left:48px;right:48px;height:20px;background:repeating-linear-gradient(90deg,#000 0,#000 2px,#fff 2px,#fff 4px);opacity:.7}
  .lob-tracking-row{display:flex;align-items:center;gap:12px;padding:.75rem 1rem;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);margin-bottom:8px}
  .lob-track-icon{font-size:20px;flex-shrink:0}
  .lob-track-status{font-size:11px;font-weight:600;padding:2px 8px;border-radius:20px}
  .lob-mailed{background:var(--teal3);color:var(--teal)}
  .lob-transit{background:var(--amber2);color:var(--amber)}
  .lob-delivered{background:var(--green2);color:var(--green)}

  /* ── Mobile-First Resident Portal ── */
  .mp-shell{min-height:100vh;background:#f0f2f5;display:flex;align-items:flex-start;justify-content:center;padding:2rem}
  .mp-device{width:390px;min-height:844px;background:#fff;border-radius:44px;overflow:hidden;box-shadow:0 30px 100px rgba(0,0,0,.4);border:8px solid #1a1a1a;position:relative;flex-shrink:0}
  .mp-device::before{content:"";position:absolute;top:0;left:50%;transform:translateX(-50%);width:126px;height:30px;background:#1a1a1a;border-radius:0 0 18px 18px;z-index:20}
  .mp-screen{height:100%;overflow-y:auto;-webkit-overflow-scrolling:touch;scroll-behavior:smooth}
  .mp-screen::-webkit-scrollbar{display:none}
  .mp-header{background:linear-gradient(160deg,#ffffff,#f5f6f8);padding:52px 20px 20px;position:relative}
  .mp-header-logo{font-size:16px;font-weight:700;color:rgba(255,255,255,.7);letter-spacing:.02em;margin-bottom:4px}
  .mp-header-logo span{color:#2A63D6}
  .mp-header-name{font-size:22px;font-weight:700;color:#fff;margin-bottom:2px}
  .mp-header-unit{font-size:13px;color:rgba(255,255,255,.55)}
  .mp-header-avatar{position:absolute;top:52px;right:20px;width:44px;height:44px;border-radius:50%;background:rgba(42,99,214,.15);border:2px solid #2A63D6;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;color:#2A63D6}
  .mp-cards-row{display:flex;gap:10px;padding:16px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
  .mp-cards-row::-webkit-scrollbar{display:none}
  .mp-mini-card{flex-shrink:0;width:130px;background:linear-gradient(135deg,#ffffff,#f5f6f8);border-radius:16px;padding:14px;box-shadow:0 4px 20px rgba(0,0,0,.15)}
  .mp-mini-card.green{background:linear-gradient(135deg,#0d2018,#1a3d2b)}
  .mp-mini-card.amber{background:linear-gradient(135deg,#251400,#3d2200)}
  .mp-mini-label{font-size:10px;color:rgba(0,0,0,.35);text-transform:uppercase;letter-spacing:.05em;margin-bottom:5px}
  .mp-mini-val{font-size:20px;font-weight:700;color:#fff}
  .mp-mini-sub{font-size:10px;color:rgba(0,0,0,.35);margin-top:2px}
  .mp-section-title{font-size:17px;font-weight:700;color:#111827;padding:16px 16px 8px}
  .mp-action-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;padding:0 16px 16px}
  .mp-action-btn{background:#fff;border-radius:16px;padding:16px;box-shadow:0 2px 12px rgba(0,0,0,.08);display:flex;flex-direction:column;align-items:flex-start;gap:6px;cursor:pointer;border:none;width:100%;transition:transform .15s,box-shadow .15s;text-align:left}
  .mp-action-btn:active{transform:scale(.97)}
  .mp-action-icon{width:36px;height:36px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px;margin-bottom:2px}
  .mp-action-label{font-size:13px;font-weight:600;color:#111827}
  .mp-action-sub{font-size:11px;color:#64748b}
  .mp-pay-sheet{background:#fff;border-radius:24px 24px 0 0;padding:24px 20px 32px;margin-top:8px}
  .mp-pay-amount{font-size:40px;font-weight:800;color:#111827;text-align:center;margin:8px 0}
  .mp-pay-due{font-size:13px;color:#64748b;text-align:center;margin-bottom:24px}
  .mp-apple-pay-btn{width:100%;height:52px;background:#000;color:#fff;border:none;border-radius:14px;font-size:16px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:10px;font-family:var(--sans);transition:opacity .15s}
  .mp-apple-pay-btn:hover{opacity:.88}
  .mp-google-pay-btn{width:100%;height:52px;background:#fff;color:#000;border:1.5px solid #e0e0e0;border-radius:14px;font-size:16px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:10px;font-family:var(--sans);transition:all .15s}
  .mp-google-pay-btn:hover{background:#f8f8f8}
  .mp-card-pay-btn{width:100%;height:52px;background:#111827;color:#fff;border:none;border-radius:14px;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;font-family:var(--sans);transition:background .15s}
  .mp-card-pay-btn:hover{background:#f5f6f8}
  .mp-divider{display:flex;align-items:center;gap:10px;margin:10px 0}
  .mp-divider-line{flex:1;height:1px;background:#eee}
  .mp-divider-text{font-size:12px;color:#bbb}
  .mp-request-form{padding:0 16px 24px}
  .mp-request-input{width:100%;background:#f5f6f8;border:1.5px solid #e8eaed;border-radius:12px;padding:12px 14px;font-size:14px;color:#111827;font-family:var(--sans);margin-bottom:10px;transition:border-color .2s}
  .mp-request-input:focus{outline:none;border-color:#2A63D6}
  textarea.mp-request-input{resize:vertical;min-height:90px;line-height:1.5}
  .mp-submit-btn{width:100%;background:#2A63D6;color:#ffffff;border:none;border-radius:14px;padding:14px;font-size:15px;font-weight:700;font-family:var(--sans);cursor:pointer;transition:background .15s}
  .mp-submit-btn:hover{background:#1B3C78}
  .mp-list-item{display:flex;align-items:flex-start;gap:12px;padding:12px 16px;border-bottom:1px solid #f0f2f5}
  .mp-list-item:last-child{border-bottom:none}
  .mp-list-icon{width:36px;height:36px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
  .mp-list-title{font-size:14px;font-weight:700;color:#111827}
  .mp-list-sub{font-size:12px;color:#64748b;margin-top:1px;line-height:1.4}
  .mp-badge{font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px}
  .mp-badge-red{background:#fde8e8;color:#dc2626}
  .mp-badge-green{background:#e8f5ee;color:#b91c1c}
  .mp-badge-amber{background:#fef3e2;color:#f59e0b}
  .mp-tab-bar{position:sticky;bottom:0;background:#fff;border-top:1px solid #e8eaed;display:grid;grid-template-columns:repeat(4,1fr);padding:8px 0 20px;z-index:10}
  .mp-tab{display:flex;flex-direction:column;align-items:center;gap:3px;cursor:pointer;font-size:10px;color:#64748b;font-weight:500;padding:4px;transition:color .15s}
  .mp-tab.active{color:#2A63D6}
  .mp-tab-icon{font-size:20px}
  .mp-success-screen{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:40px 24px;text-align:center}
  .mp-success-icon{width:72px;height:72px;border-radius:50%;background:#e8f5ee;display:flex;align-items:center;justify-content:center;font-size:32px;margin-bottom:16px}
  .mp-outer-wrap{display:grid;grid-template-columns:390px 1fr;gap:2rem;align-items:flex-start;height:calc(100vh - 73px);overflow:hidden}
  .mp-controls{overflow-y:auto;padding:1.5rem}
  .mp-preview-wrap{display:flex;align-items:center;justify-content:center;overflow-y:auto;padding:2rem 1rem;background:var(--navy3);border-left:.5px solid var(--border)}
  .legal-wrap{display:grid;grid-template-columns:280px 1fr;height:calc(100vh - 73px)}
  .legal-sidebar{border-right:.5px solid var(--border);display:flex;flex-direction:column;overflow:hidden}
  .legal-sidebar-top{padding:1rem;border-bottom:.5px solid var(--border)}
  .legal-topics{overflow-y:auto;flex:1;padding:.5rem}
  .legal-topic{padding:.65rem .85rem;border-radius:var(--r);cursor:pointer;transition:background .15s;margin-bottom:2px}
  .legal-topic:hover{background:rgba(61,75,97,.04)}
  .legal-topic.active{background:rgba(245,166,35,.1);border:.5px solid rgba(245,166,35,.25)}
  .legal-topic-name{font-size:13px;font-weight:600;color:#1B3C78}
  .legal-topic-sub{font-size:11px;color:var(--slate);margin-top:2px}
  .legal-chat{display:flex;flex-direction:column;height:100%}
  .legal-chat-header{padding:1rem 1.5rem;border-bottom:.5px solid var(--border);display:flex;align-items:center;gap:12px;flex-shrink:0}
  .legal-chat-icon{width:36px;height:36px;border-radius:50%;background:rgba(245,166,35,.15);border:.5px solid rgba(245,166,35,.3);display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .legal-chat-title{font-size:14px;font-weight:500}
  .legal-chat-sub{font-size:11px;color:var(--slate);margin-top:1px}
  .legal-messages{flex:1;overflow-y:auto;padding:1.25rem 1.5rem;display:flex;flex-direction:column;gap:1rem}
  .legal-msg{display:flex;gap:10px;align-items:flex-start}
  .legal-msg.user{flex-direction:row-reverse}
  .legal-avatar{width:28px;height:28px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600}
  .legal-avatar.bot{background:rgba(245,166,35,.2);color:var(--amber)}
  .legal-avatar.user{background:var(--teal3);color:var(--teal)}
  .legal-bubble{max-width:72%;padding:.75rem 1rem;border-radius:14px;font-size:13px;line-height:1.65}
  .legal-bubble.bot{background:var(--navy2);border:.5px solid var(--border2);border-radius:4px 14px 14px 14px;color:var(--ice)}
  .legal-bubble.user{background:rgba(42,99,214,.12);border:.5px solid rgba(42,99,214,.25);border-radius:14px 4px 14px 14px;color:#1B3C78;font-weight:500}
  .legal-disclaimer{font-size:11px;color:var(--slate);margin-top:.5rem;padding:.5rem .75rem;background:rgba(245,166,35,.05);border-radius:6px;border-left:2px solid rgba(245,166,35,.4)}
  .legal-input-row{padding:1rem 1.5rem;border-top:.5px solid var(--border);display:flex;gap:10px;flex-shrink:0}
  .legal-input{flex:1;background:var(--navy3);border:1px solid rgba(255,255,255,.15);border-radius:var(--r);padding:10px 14px;font-size:13px;color:#1B3C78;font-family:var(--sans);resize:none;line-height:1.5;min-height:42px;max-height:120px}
  .legal-input:focus{outline:none;border-color:var(--amber)}
  .legal-send{background:var(--amber);color:#fff;border:none;border-radius:var(--r);padding:0 16px;font-size:13px;font-weight:600;font-family:var(--sans);cursor:pointer;transition:opacity .15s;flex-shrink:0}
  .legal-send:hover{opacity:.88}
  .legal-send:disabled{opacity:.35;cursor:not-allowed}
  .legal-thinking{display:flex;gap:4px;align-items:center;padding:.5rem 0}
  .legal-dot{width:6px;height:6px;border-radius:50%;background:var(--amber);animation:ldot .9s infinite}
  .legal-dot:nth-child(2){animation-delay:.2s}.legal-dot:nth-child(3){animation-delay:.4s}
  @keyframes ldot{0%,60%,100%{opacity:.2}30%{opacity:1}}
  .legal-category-pill{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:600;padding:3px 10px;border-radius:20px;background:rgba(245,166,35,.12);color:var(--amber);border:.5px solid rgba(245,166,35,.25);margin-bottom:1rem}
  .legal-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:.75rem;color:var(--slate);text-align:center;padding:2rem}
  .legal-empty-icon{width:52px;height:52px;border-radius:50%;background:rgba(245,166,35,.1);border:.5px solid rgba(245,166,35,.2);display:flex;align-items:center;justify-content:center;margin-bottom:.5rem}
  .legal-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:.75rem}
  .legal-chip{font-size:12px;padding:5px 11px;border-radius:20px;background:var(--navy2);border:.5px solid var(--border2);color:var(--slate);cursor:pointer;transition:all .15s}
  .legal-chip:hover{border-color:rgba(245,166,35,.4);color:var(--amber)}

  /* Flyer Generator */
  .flyer-layout{display:grid;grid-template-columns:340px 1fr;height:calc(100vh - 73px);overflow:hidden}
  .flyer-controls{border-right:.5px solid var(--border);overflow-y:auto;padding:1.25rem}
  .flyer-preview-pane{background:#1a1a2e;display:flex;flex-direction:column;align-items:center;overflow-y:auto;padding:2rem}
  .flyer-section-label{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.1em;text-transform:uppercase;margin:1rem 0 .5rem}
  .flyer-section-label:first-child{margin-top:0}
  .flyer-type-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:.25rem}
  .flyer-type-btn{padding:.6rem .75rem;border-radius:var(--r);border:.5px solid var(--border2);background:transparent;color:var(--slate);font-size:12px;font-family:var(--sans);cursor:pointer;transition:all .15s;text-align:left}
  .flyer-type-btn:hover{color:#1B3C78;border-color:var(--border2);background:rgba(61,75,97,.05)}
  .flyer-type-btn.active{background:rgba(42,99,214,.10);border-color:var(--teal);color:var(--teal)}
  .flyer-type-icon{font-size:16px;display:block;margin-bottom:3px}
  .flyer-type-name{font-size:11px;font-weight:600}
  .fi-flyer{width:100%;background:var(--navy3);border:1px solid rgba(255,255,255,.15);border-radius:var(--r);padding:8px 11px;font-size:13px;color:#1B3C78;font-family:var(--sans);margin-bottom:.6rem}
  .fi-flyer:focus{outline:none;border-color:var(--teal)}
  textarea.fi-flyer{resize:vertical;min-height:72px;line-height:1.5}
  select.fi-flyer option{background:var(--navy3)}
  .color-scheme-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:.75rem}
  .cs-btn{height:32px;border-radius:6px;border:2px solid transparent;cursor:pointer;transition:border-color .15s;position:relative;overflow:hidden}
  .cs-btn.active{border-color:#1B3C78}
  .cs-btn span{position:absolute;inset:0;opacity:.3;background:repeating-linear-gradient(45deg,rgba(255,255,255,.10) 0,rgba(255,255,255,.10) 1px,transparent 1px,transparent 6px)}
  .gen-btn{width:100%;background:linear-gradient(135deg,var(--teal),var(--teal2));color:#fff;border:none;border-radius:var(--r);padding:11px;font-size:13px;font-weight:700;font-family:var(--sans);cursor:pointer;margin-top:.5rem;transition:opacity .2s;letter-spacing:.02em}
  .gen-btn:hover{opacity:.9}.gen-btn:disabled{opacity:.4;cursor:not-allowed}
  .gen-btn-sec{width:100%;background:transparent;color:var(--slate);border:.5px solid var(--border2);border-radius:var(--r);padding:8px;font-size:12px;font-family:var(--sans);cursor:pointer;margin-top:.5rem;transition:all .15s}
  .gen-btn-sec:hover{color:#1B3C78;background:rgba(61,75,97,.05)}

  /* Flyer canvas */
  .flyer-canvas{width:600px;min-height:800px;border-radius:12px;overflow:hidden;position:relative;flex-shrink:0;box-shadow:0 24px 80px rgba(255,255,255,.6)}
  .flyer-canvas *{box-sizing:border-box}
  .flyer-dl-bar{display:flex;gap:8px;margin-top:1.25rem;width:600px}
  .flyer-dl-btn{flex:1;padding:9px;border-radius:var(--r);font-size:12px;font-weight:600;font-family:var(--sans);cursor:pointer;border:.5px solid var(--border2);background:var(--navy2);color:var(--slate);transition:all .15s;text-align:center}
  .flyer-dl-btn:hover{color:#1B3C78;border-color:var(--border2);background:rgba(61,75,97,.07)}
  .flyer-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:.75rem;color:var(--slate);text-align:center}
  .gen-progress{width:100%;height:3px;background:var(--border);border-radius:2px;overflow:hidden;margin-top:.75rem}
  .gen-progress-fill{height:100%;background:linear-gradient(90deg,var(--teal),var(--purple));border-radius:2px;transition:width .4s ease}
  .img-gen-status{font-size:11px;color:var(--slate);margin-top:.4rem;text-align:center;font-style:italic}

  /* ── Homeowner Portal ── */
  .op-wrap{min-height:100vh;background:var(--navy);background-image:radial-gradient(ellipse 60% 40% at 80% 10%,rgba(42,99,214,.07) 0%,transparent 65%),radial-gradient(ellipse 40% 35% at 10% 90%,rgba(167,139,250,.06) 0%,transparent 60%)}
  .op-header{background:var(--navy2);border-bottom:.5px solid var(--border);padding:0 2rem;display:flex;align-items:center;justify-content:space-between;height:60px;position:sticky;top:0;z-index:10}
  .op-logo{font-family:'Inter',system-ui,sans-serif;font-size:20px}.op-logo span{color:var(--teal)}
  .op-hoa{font-size:12px;color:var(--slate);margin-left:10px}
  .op-user{display:flex;align-items:center;gap:10px}
  .op-avatar{width:30px;height:30px;border-radius:50%;background:var(--teal3);border:.5px solid var(--teal);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;color:var(--teal)}
  .op-name{font-size:13px;font-weight:500}
  .op-unit{font-size:11px;color:var(--slate)}
  .op-signout{background:transparent;border:.5px solid var(--border2);border-radius:var(--r);padding:5px 12px;font-size:12px;color:var(--slate);cursor:pointer;font-family:var(--sans);transition:all .15s;margin-left:8px}
  .op-signout:hover{color:var(--red);border-color:var(--red)}
  .op-body{max-width:860px;margin:0 auto;padding:2rem 1.5rem}
  .op-welcome-title{font-family:'Inter',system-ui,sans-serif;font-size:28px;color:#1B3C78;margin-bottom:4px}
  .op-welcome-sub{font-size:14px;color:var(--slate);margin-bottom:2rem}
  .op-cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;margin-bottom:2rem}
  .op-card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.1rem 1.2rem}
  .op-card-label{font-size:11px;color:var(--slate);font-weight:500;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px}
  .op-card-val{font-size:24px;font-weight:600;color:#1B3C78}
  .op-card-val.green{color:var(--green)}.op-card-val.red{color:var(--red)}.op-card-val.amber{color:var(--amber)}
  .op-card-sub{font-size:11px;color:var(--slate);margin-top:3px}
  .op-section-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:.85rem}
  .op-section-title{font-size:15px;font-weight:500;color:#1B3C78}
  .op-item{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);padding:.9rem 1.1rem;margin-bottom:8px;display:flex;align-items:flex-start;gap:12px}
  .op-item-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;margin-top:5px}
  .op-item-title{font-size:13px;font-weight:500;color:#1B3C78;margin-bottom:2px}
  .op-item-sub{font-size:12px;color:var(--slate);line-height:1.4}
  .op-item-meta{font-size:11px;color:var(--slate);margin-top:4px}
  .op-empty{text-align:center;padding:2rem;color:var(--slate);font-size:13px;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r)}
  .op-ann{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1rem 1.25rem;margin-bottom:10px}
  .op-ann-title{font-size:14px;font-weight:500;color:#1B3C78;margin-bottom:.4rem}
  .op-ann-body{font-size:13px;color:var(--slate);line-height:1.6}
  .op-ann-date{font-size:11px;color:var(--slate);margin-top:.4rem}
  .op-pay-amount{font-size:32px;font-weight:600;color:var(--red);margin-bottom:4px}
  .op-pay-due{font-size:13px;color:var(--slate);margin-bottom:1.25rem}
  .op-pay-btn{background:var(--teal);color:#fff;border:none;border-radius:var(--r);padding:11px 28px;font-size:14px;font-weight:600;font-family:var(--sans);cursor:pointer;transition:background .15s}
  .op-pay-btn:hover{background:var(--teal2)}.op-pay-btn:disabled{opacity:.4;cursor:not-allowed}
  .op-input{width:100%;background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:9px 12px;font-size:13px;color:#1B3C78;font-family:var(--sans);margin-bottom:.85rem}
  .op-input:focus{outline:none;border-color:var(--teal)}
  textarea.op-input{resize:vertical;min-height:90px;line-height:1.5}
  select.op-input option{background:var(--navy3)}
  .op-lbl{font-size:11px;font-weight:600;color:var(--slate);letter-spacing:.05em;text-transform:uppercase;display:block;margin-bottom:6px}
  .op-submit{background:var(--teal);color:#fff;border:none;border-radius:var(--r);padding:10px 24px;font-size:13px;font-weight:600;font-family:var(--sans);cursor:pointer;transition:background .15s}
  .op-submit:hover{background:var(--teal2)}
  .op-tab{flex:1;text-align:center;padding:8px;font-size:13px;font-weight:500;color:var(--slate);border-radius:8px;cursor:pointer;transition:all .15s}
  .op-tab.active{background:var(--navy3);color:#1B3C78}
  .op-success{background:rgba(58,191,126,.1);border:.5px solid rgba(58,191,126,.3);border-radius:var(--r);padding:.85rem 1rem;font-size:13px;color:var(--green);margin-bottom:1rem}
  .op-choice-btn{background:var(--navy2);border:.5px solid var(--border2);border-radius:var(--r2);padding:1.25rem;cursor:pointer;transition:border-color .2s;text-align:left;width:100%}
  .op-choice-btn:hover{border-color:var(--teal)}
  .pw-toggle{display:flex;gap:4px;background:var(--navy3);border-radius:var(--r);padding:3px;margin-bottom:1.5rem}
  .pw-tab{flex:1;text-align:center;padding:7px;font-size:12px;font-weight:500;border-radius:7px;cursor:pointer;color:var(--slate);transition:all .15s}
  .pw-tab.active{background:var(--navy2);color:#1B3C78}

  /* ── Support Chat Widget ── */
  .support-fab{position:fixed;bottom:24px;right:24px;width:52px;height:52px;border-radius:50%;background:#2A63D6;color:#fff;border:none;cursor:pointer;font-size:22px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(42,99,214,.4);z-index:900;transition:all .2s}
  .support-fab:hover{background:#1B3C78;transform:scale(1.08)}
  .support-fab.open{background:#374151;font-size:18px}
  .support-panel{position:fixed;bottom:88px;right:24px;width:380px;max-height:580px;background:#fff;border-radius:16px;box-shadow:0 8px 40px rgba(0,0,0,.18);border:1px solid rgba(0,0,0,.09);z-index:900;display:flex;flex-direction:column;overflow:hidden;animation:supportSlide .2s ease}
  @keyframes supportSlide{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  .support-header{background:#2A63D6;padding:1rem 1.25rem;display:flex;align-items:center;gap:10px;flex-shrink:0}
  .support-header-avatar{width:36px;height:36px;border-radius:50%;background:rgba(255,255,255,.25);display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
  .support-header-name{font-size:14px;font-weight:700;color:#fff}
  .support-header-sub{font-size:11px;color:rgba(255,255,255,.8);margin-top:1px}
  .support-online{width:8px;height:8px;border-radius:50%;background:#22c55e;margin-left:auto;box-shadow:0 0 0 2px rgba(255,255,255,.4);flex-shrink:0;animation:pulse 2s infinite}
  .support-messages{flex:1;overflow-y:auto;padding:1rem;display:flex;flex-direction:column;gap:.75rem;background:#f9fafb}
  .support-messages::-webkit-scrollbar{width:3px}
  .support-messages::-webkit-scrollbar-thumb{background:#e5e7eb;border-radius:2px}
  .support-msg{display:flex;gap:8px;align-items:flex-end}
  .support-msg.user{flex-direction:row-reverse;align-self:flex-end;max-width:85%}
  .support-msg.bot{align-self:flex-start;max-width:90%}
  .support-msg-avatar{width:26px;height:26px;border-radius:50%;background:#2A63D6;color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0}
  .support-msg-avatar.user{background:#374151}
  .support-bubble{padding:.65rem .9rem;border-radius:14px;font-size:13px;line-height:1.6;word-break:break-word}
  .support-bubble.bot{background:#fff;color:#111827;border:1px solid rgba(0,0,0,.09);border-radius:4px 14px 14px 14px;box-shadow:0 1px 3px rgba(0,0,0,.06);font-weight:500}
  .support-bubble.user{background:#2A63D6;color:#fff;border-radius:14px 4px 14px 14px;font-weight:500}
  .support-bubble.thinking{background:#fff;border:1px solid rgba(0,0,0,.09);display:flex;gap:5px;align-items:center;padding:.75rem 1rem;border-radius:4px 14px 14px 14px}
  .thinking-dot{width:7px;height:7px;border-radius:50%;background:#d1d5db;animation:thinkDot 1.2s ease infinite}
  .thinking-dot:nth-child(2){animation-delay:.2s}
  .thinking-dot:nth-child(3){animation-delay:.4s}
  @keyframes thinkDot{0%,60%,100%{transform:translateY(0);background:#d1d5db}30%{transform:translateY(-5px);background:#2A63D6}}
  .support-time{font-size:10px;color:#9ca3af;margin-top:3px;padding:0 2px}
  .support-chips{display:flex;gap:5px;flex-wrap:wrap;padding:.6rem 1rem;background:#f9fafb;border-top:1px solid #f3f4f6;flex-shrink:0}
  .support-chip{font-size:11px;padding:4px 10px;border-radius:20px;background:#fff;border:1px solid rgba(42,99,214,.3);color:#2A63D6;cursor:pointer;font-weight:600;transition:all .15s;white-space:nowrap}
  .support-chip:hover{background:#2A63D6;color:#fff;border-color:#2A63D6}
  .support-input-row{display:flex;gap:8px;padding:.75rem;border-top:1px solid #e5e7eb;background:#fff;flex-shrink:0;align-items:flex-end}
  .support-input{flex:1;background:#f9fafb;border:1.5px solid #e5e7eb;border-radius:10px;padding:8px 11px;font-size:13px;color:#111827;font-family:inherit;resize:none;min-height:38px;max-height:90px;line-height:1.45;transition:border-color .15s}
  .support-input:focus{outline:none;border-color:#2A63D6}
  .support-send{width:36px;height:36px;border-radius:50%;background:#2A63D6;color:#fff;border:none;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .15s}
  .support-send:hover{background:#1B3C78}
  .support-send:disabled{background:#e5e7eb;cursor:not-allowed}
  .support-footer-bar{font-size:10px;color:#9ca3af;text-align:center;padding:.4rem;background:#fff;border-top:1px solid #f3f4f6;flex-shrink:0}
  .support-escalate{background:#fff7ed;border:1px solid rgba(42,99,214,.2);border-radius:10px;padding:.75rem;font-size:12px;color:#374151;line-height:1.55;font-weight:500}
  .support-escalate strong{color:#2A63D6;display:block;margin-bottom:3px;font-size:13px}
  .support-escalate-link{display:inline-flex;align-items:center;gap:4px;margin-top:.5rem;font-size:12px;font-weight:700;color:#2A63D6;cursor:pointer;background:none;border:none;font-family:inherit;padding:0;text-decoration:underline}
  .vt-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:1.5rem}
  .vt-card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.25rem}
  .vt-card-title{font-size:11px;font-weight:700;color:var(--slate);letter-spacing:.07em;text-transform:uppercase;margin-bottom:1rem}
  .wo-item{display:flex;align-items:flex-start;gap:12px;padding:.85rem 1rem;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);margin-bottom:8px;transition:border-color .15s;cursor:pointer}
  .wo-item:hover{border-color:var(--border2)}
  .wo-item.selected{border-color:var(--teal);background:rgba(45,212,160,.04)}
  .wo-priority-bar{width:3px;border-radius:2px;flex-shrink:0;align-self:stretch;min-height:40px}
  .wo-title{font-size:13px;font-weight:500;color:#1B3C78;margin-bottom:3px}
  .wo-meta{font-size:11px;color:var(--slate);line-height:1.5}
  .wo-status-pill{font-size:10px;font-weight:600;padding:2px 8px;border-radius:20px;flex-shrink:0;white-space:nowrap}
  .wo-detail-pane{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);overflow:hidden}
  .wo-detail-header{padding:1.25rem 1.5rem;border-bottom:.5px solid var(--border);background:var(--navy3)}
  .wo-detail-body{padding:1.25rem 1.5rem}
  .wo-field-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:1rem}
  .wo-field{margin-bottom:.85rem}
  .wo-field-label{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.07em;text-transform:uppercase;margin-bottom:4px}
  .wo-field-val{font-size:13px;color:#1B3C78;line-height:1.5}
  .wo-field-val.muted{color:var(--slate)}
  .wo-section-divider{height:.5px;background:var(--border);margin:1.25rem 0}
  .vendor-card{background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:.85rem 1rem;margin-bottom:8px;display:flex;align-items:center;gap:12px;cursor:pointer;transition:border-color .15s}
  .vendor-card:hover{border-color:var(--teal)}
  .vendor-card.selected{border-color:var(--teal);background:rgba(42,99,214,.06)}
  .vendor-avatar{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0}
  .vendor-name{font-size:13px;font-weight:500;color:#1B3C78}
  .vendor-meta{font-size:11px;color:var(--slate);margin-top:2px}
  .vendor-rating{display:flex;gap:2px;margin-top:3px}
  .star{font-size:11px}
  .invoice-row{display:flex;align-items:center;gap:10px;padding:.65rem .85rem;background:var(--navy3);border:.5px solid var(--border);border-radius:var(--r);margin-bottom:6px}
  .invoice-icon{width:28px;height:28px;border-radius:6px;background:rgba(245,166,35,.15);display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .invoice-name{font-size:13px;font-weight:500;color:#1B3C78;flex:1}
  .invoice-amount{font-size:13px;font-weight:600;color:var(--amber)}
  .invoice-status{font-size:10px;font-weight:600;padding:2px 7px;border-radius:20px}
  .inv-paid{background:var(--green2);color:var(--green)}
  .inv-pending{background:var(--amber2);color:var(--amber)}
  .cost-est-box{background:var(--navy3);border:.5px solid rgba(167,139,250,.25);border-radius:var(--r);padding:1rem;margin-top:.75rem}
  .cost-est-label{font-size:10px;font-weight:700;color:var(--purple);letter-spacing:.07em;text-transform:uppercase;margin-bottom:.5rem;display:flex;align-items:center;gap:5px}
  .cost-range{display:flex;align-items:baseline;gap:8px;margin-bottom:.4rem}
  .cost-low{font-size:22px;font-weight:700;color:var(--green)}
  .cost-high{font-size:22px;font-weight:700;color:var(--amber)}
  .cost-separator{font-size:14px;color:var(--slate)}
  .cost-breakdown{font-size:12px;color:var(--slate);line-height:1.6}
  .upload-zone{border:1.5px dashed var(--border2);border-radius:var(--r);padding:1.5rem;text-align:center;cursor:pointer;transition:border-color .2s;margin-top:.5rem}
  .upload-zone:hover{border-color:var(--teal)}
  .upload-zone-text{font-size:13px;color:var(--slate)}
  .upload-zone-sub{font-size:11px;color:var(--slate);margin-top:4px}
  .wo-timeline{position:relative;padding-left:20px}
  .wo-timeline::before{content:"";position:absolute;left:5px;top:8px;bottom:8px;width:1.5px;background:var(--border2)}
  .wo-tl-item{position:relative;padding-bottom:14px}
  .wo-tl-dot{position:absolute;left:-20px;top:3px;width:10px;height:10px;border-radius:50%;border:1.5px solid var(--border2);background:var(--navy2)}
  .wo-tl-dot.done{background:var(--teal);border-color:var(--teal)}
  .wo-tl-dot.active{background:var(--amber);border-color:var(--amber);box-shadow:0 0 0 3px rgba(245,166,35,.2)}
  .wo-tl-text{font-size:12px;color:#1B3C78}
  .wo-tl-time{font-size:11px;color:var(--slate);margin-top:1px}

  /* ── Budget & Finance ── */
  .bf-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:1.5rem}
  .bf-card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.1rem 1.25rem}
  .bf-card.surplus{border-color:rgba(42,99,214,.20)}
  .bf-card.deficit{border-color:rgba(224,82,82,.25)}
  .bf-label{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.07em;text-transform:uppercase;margin-bottom:6px}
  .bf-amount{font-size:26px;font-weight:700;line-height:1}
  .bf-amount.income{color:var(--teal)}
  .bf-amount.expense{color:var(--red)}
  .bf-amount.surplus{color:var(--green)}
  .bf-amount.deficit{color:var(--red)}
  .bf-amount.neutral{color:#1B3C78}
  .bf-sub{font-size:12px;color:var(--slate);margin-top:5px}
  .bf-bar-row{margin-bottom:16px}
  .bf-bar-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:5px}
  .bf-bar-label{font-size:13px;color:#1B3C78;font-weight:500}
  .bf-bar-vals{display:flex;gap:12px;font-size:12px}
  .bf-track{height:8px;background:var(--navy3);border-radius:4px;overflow:hidden;position:relative}
  .bf-fill-budget{height:100%;border-radius:4px;background:var(--border2);position:absolute;top:0;left:0}
  .bf-fill-actual{height:100%;border-radius:4px;position:absolute;top:0;left:0;transition:width .5s ease}
  .bf-fill-over{background:var(--red)}
  .bf-fill-under{background:var(--teal)}
  .bf-fill-close{background:var(--amber)}
  .bf-legend{display:flex;gap:16px;margin-bottom:1rem;font-size:12px;color:var(--slate)}
  .bf-legend-dot{width:10px;height:10px;border-radius:2px;flex-shrink:0}
  .tx-row{display:flex;align-items:center;gap:12px;padding:.75rem 1rem;border-bottom:.5px solid var(--border);transition:background .15s}
  .tx-row:last-child{border-bottom:none}
  .tx-row:hover{background:rgba(61,75,97,.02)}
  .tx-cat-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
  .tx-desc{font-size:13px;font-weight:500;color:#1B3C78;flex:1}
  .tx-cat{font-size:11px;color:var(--slate)}
  .tx-date{font-size:11px;color:var(--slate);min-width:70px;text-align:right}
  .tx-amount{font-size:14px;font-weight:600;min-width:80px;text-align:right}
  .tx-amount.inc{color:var(--teal)}
  .tx-amount.exp{color:var(--red)}
  .bf-report-box{background:var(--navy3);border:.5px solid rgba(167,139,250,.2);border-radius:var(--r2);padding:1.5rem;margin-top:1rem}
  .bf-report-header{font-family:'Inter',system-ui,sans-serif;font-size:18px;color:#1B3C78;margin-bottom:.35rem}
  .bf-report-sub{font-size:12px;color:var(--slate);margin-bottom:1.25rem;padding-bottom:1rem;border-bottom:.5px solid var(--border)}
  .bf-report-body{font-size:14px;color:var(--ice);line-height:1.85;white-space:pre-wrap}
  .bf-report-section{margin-bottom:1.25rem}
  .bf-month-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 14px;background:var(--navy3);border:.5px solid var(--border2);border-radius:20px;font-size:12px;color:var(--slate);cursor:pointer;transition:all .15s}
  .bf-month-pill.active{background:rgba(42,99,214,.10);border-color:var(--teal);color:var(--teal)}
  .bf-month-pill:hover:not(.active){border-color:var(--border2);color:#1B3C78}
  .bf-tx-form{display:grid;grid-template-columns:2fr 1fr 1fr 1fr auto;gap:8px;align-items:end;margin-bottom:1rem}
  .budget-edit-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}

  /* ── Document Vault ── */
  .dv-layout{display:grid;grid-template-columns:300px 1fr;height:calc(100vh - 73px);overflow:hidden}
  .dv-sidebar{border-right:.5px solid var(--border);display:flex;flex-direction:column;overflow:hidden;background:var(--navy2)}
  .dv-sidebar-top{padding:1rem;border-bottom:.5px solid var(--border);flex-shrink:0}
  .dv-doc-list{overflow-y:auto;flex:1;padding:.5rem}
  .dv-doc-item{padding:.7rem .9rem;border-radius:var(--r);cursor:pointer;transition:background .15s;margin-bottom:3px;border:.5px solid transparent}
  .dv-doc-item:hover{background:rgba(61,75,97,.04)}
  .dv-doc-item.active{background:rgba(42,99,214,.07);border-color:rgba(42,99,214,.15)}
  .dv-doc-icon{width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
  .dv-doc-name{font-size:13px;font-weight:600;color:#1B3C78;margin-bottom:2px;line-height:1.3}
  .dv-doc-meta{font-size:11px;color:var(--slate)}
  .dv-main{display:flex;flex-direction:column;overflow:hidden}
  .dv-search-bar{padding:1rem 1.5rem;border-bottom:.5px solid var(--border);flex-shrink:0;background:var(--navy2)}
  .dv-search-input{width:100%;background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:10px 14px;font-size:14px;color:#1B3C78;font-family:var(--sans)}
  .dv-search-input:focus{outline:none;border-color:var(--teal)}
  .dv-search-input::placeholder{color:var(--slate)}
  .dv-content{flex:1;overflow-y:auto;padding:1.5rem}
  .dv-answer-box{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);overflow:hidden;margin-bottom:1.25rem}
  .dv-answer-header{padding:.85rem 1.25rem;background:rgba(42,99,214,.06);border-bottom:.5px solid var(--border);display:flex;align-items:center;gap:8px}
  .dv-answer-label{font-size:11px;font-weight:700;color:var(--teal);letter-spacing:.06em;text-transform:uppercase}
  .dv-answer-body{padding:1.25rem;font-size:14px;color:#1B3C78;line-height:1.8;white-space:pre-wrap;font-weight:500}
  .dv-source-chip{display:inline-flex;align-items:center;gap:5px;font-size:11px;padding:3px 10px;border-radius:20px;background:var(--navy3);border:.5px solid var(--border2);color:var(--slate);margin:.5rem .35rem 0 0}
  .dv-quote-box{background:var(--navy3);border-left:3px solid var(--teal);border-radius:0 var(--r) var(--r) 0;padding:.85rem 1rem;margin:1rem 0;font-size:13px;color:var(--ice);line-height:1.7;font-style:italic}
  .dv-history-item{padding:.65rem .85rem;border-radius:var(--r);cursor:pointer;transition:background .15s;margin-bottom:3px;border:.5px solid var(--border)}
  .dv-history-item:hover{background:rgba(61,75,97,.04)}
  .dv-history-q{font-size:13px;color:#1B3C78;line-height:1.4;margin-bottom:3px}
  .dv-history-meta{font-size:11px;color:var(--slate)}
  .dv-upload-zone{border:1.5px dashed var(--border2);border-radius:var(--r2);padding:2.5rem 2rem;text-align:center;cursor:pointer;transition:all .2s;background:var(--navy3)}
  .dv-upload-zone:hover{border-color:var(--teal);background:rgba(45,212,160,.04)}
  .dv-upload-zone.dragging{border-color:var(--teal);background:rgba(42,99,214,.08)}
  .dv-section-label{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.08em;text-transform:uppercase;margin:1rem 0 .6rem;padding:0 .9rem}
  .dv-chip-row{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:1rem}
  .dv-chip{font-size:12px;padding:5px 12px;border-radius:20px;background:var(--navy2);border:.5px solid var(--border2);color:var(--slate);cursor:pointer;transition:all .15s;white-space:nowrap}
  .dv-chip:hover{border-color:rgba(42,99,214,.30);color:var(--teal)}
  .dv-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:.75rem;color:var(--slate);text-align:center;padding:2rem}
  .dv-tag{display:inline-block;font-size:10px;font-weight:600;padding:2px 8px;border-radius:20px;margin-left:6px}
  .dv-tag-ccr{background:rgba(42,99,214,.12);color:var(--teal)}
  .dv-tag-bylaw{background:rgba(167,139,250,.12);color:var(--purple)}
  .dv-tag-rule{background:rgba(245,166,35,.12);color:var(--amber)}
  .dv-tag-other{background:rgba(138,153,179,.12);color:var(--slate)}
  .dv-read-pane{font-size:13px;color:var(--ice);line-height:1.85;white-space:pre-wrap;padding:1.5rem;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);max-height:calc(100vh - 260px);overflow-y:auto}

  /* ── Insurance & Compliance Tracker ── */
  .ic-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:12px;margin-bottom:1.5rem}
  .ic-policy-card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.1rem 1.25rem;position:relative;overflow:hidden;cursor:pointer;transition:border-color .15s}
  .ic-policy-card:hover{border-color:var(--border2)}
  .ic-policy-card.urgent{border-color:rgba(224,82,82,.4)}
  .ic-policy-card.warning{border-color:rgba(245,166,35,.35)}
  .ic-policy-card.good{border-color:rgba(42,99,214,.15)}
  .ic-urgency-strip{position:absolute;top:0;left:0;right:0;height:3px}
  .ic-urgency-strip.red{background:var(--red)}
  .ic-urgency-strip.amber{background:var(--amber)}
  .ic-urgency-strip.green{background:var(--teal)}
  .ic-policy-icon{font-size:22px;margin-bottom:8px}
  .ic-policy-name{font-size:14px;font-weight:700;color:#1B3C78;margin-bottom:3px}
  .ic-policy-provider{font-size:12px;color:var(--slate);margin-bottom:10px}
  .ic-policy-meta{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:10px}
  .ic-meta-label{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px}
  .ic-meta-val{font-size:13px;font-weight:600;color:#1B3C78}
  .ic-days-badge{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;padding:4px 10px;border-radius:20px;margin-top:4px}
  .ic-days-red{background:var(--red2);color:var(--red)}
  .ic-days-amber{background:var(--amber2);color:var(--amber)}
  .ic-days-green{background:var(--green2);color:var(--green)}
  .ic-coverage-bar{height:5px;background:var(--navy3);border-radius:3px;overflow:hidden;margin-top:8px}
  .ic-coverage-fill{height:100%;border-radius:3px;transition:width .5s}
  .ic-checklist-item{display:flex;align-items:flex-start;gap:10px;padding:.75rem 0;border-bottom:.5px solid var(--border)}
  .ic-checklist-item:last-child{border-bottom:none}
  .ic-check-box{width:18px;height:18px;border-radius:5px;border:1.5px solid var(--border2);flex-shrink:0;margin-top:1px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}
  .ic-check-box.checked{background:var(--teal);border-color:var(--teal)}
  .ic-check-text{font-size:13px;color:#1B3C78;line-height:1.5;flex:1}
  .ic-check-text.done{color:var(--slate);text-decoration:line-through}
  .ic-check-cat{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.05em;text-transform:uppercase;margin-top:3px}
  .ic-check-priority{font-size:10px;font-weight:600;padding:1px 7px;border-radius:20px;flex-shrink:0;margin-top:2px}
  .ic-pri-high{background:var(--red2);color:var(--red)}
  .ic-pri-med{background:var(--amber2);color:var(--amber)}
  .ic-pri-low{background:var(--green2);color:var(--green)}
  .ic-timeline{position:relative;padding-left:24px}
  .ic-timeline::before{content:"";position:absolute;left:7px;top:10px;bottom:10px;width:1.5px;background:var(--border2)}
  .ic-tl-item{position:relative;padding-bottom:18px;display:flex;flex-direction:column;gap:2px}
  .ic-tl-dot{position:absolute;left:-24px;top:3px;width:14px;height:14px;border-radius:50%;border:2px solid var(--border2);background:var(--navy2);display:flex;align-items:center;justify-content:center}
  .ic-tl-dot.overdue{background:var(--red);border-color:var(--red)}
  .ic-tl-dot.due-soon{background:var(--amber);border-color:var(--amber);box-shadow:0 0 0 3px rgba(245,166,35,.2)}
  .ic-tl-dot.upcoming{background:var(--navy3);border-color:var(--border2)}
  .ic-tl-dot.done{background:var(--teal);border-color:var(--teal)}
  .ic-tl-title{font-size:13px;font-weight:500;color:#1B3C78}
  .ic-tl-meta{font-size:12px;color:var(--slate)}
  .ic-tl-date{font-size:11px;font-weight:600}
  .ic-vendor-row{display:flex;align-items:center;gap:12px;padding:.85rem 1rem;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);margin-bottom:8px}
  .ic-vendor-status{width:10px;height:10px;border-radius:50%;flex-shrink:0}
  .ic-ai-checklist{background:var(--navy3);border:.5px solid rgba(167,139,250,.2);border-radius:var(--r2);padding:1.25rem;margin-top:1rem}
  .ic-ai-checklist-header{font-size:11px;font-weight:700;color:var(--purple);letter-spacing:.07em;text-transform:uppercase;margin-bottom:1rem;display:flex;align-items:center;gap:6px}
  .ic-progress-ring{position:relative;display:inline-flex;align-items:center;justify-content:center}
  .ic-modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .ic-alert-banner{border-radius:var(--r);padding:.85rem 1.1rem;margin-bottom:10px;display:flex;align-items:center;gap:12px}
  .ic-alert-banner.red{background:rgba(224,82,82,.08);border:.5px solid rgba(224,82,82,.25)}
  .ic-alert-banner.amber{background:rgba(245,166,35,.08);border:.5px solid rgba(245,166,35,.25)}
  .ic-stat-ring{width:64px;height:64px;flex-shrink:0}

  /* ── Onboarding Wizard ── */
  .ob-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:2rem;background:var(--navy);background-image:radial-gradient(ellipse 70% 50% at 50% 0%,rgba(42,99,214,.10) 0%,transparent 65%)}
  .ob-card{background:var(--navy2);border:.5px solid var(--border2);border-radius:var(--r2);width:100%;max-width:580px;overflow:hidden}
  .ob-progress-bar{height:3px;background:var(--border);position:relative}
  .ob-progress-fill{height:100%;background:linear-gradient(90deg,var(--teal),var(--teal2));border-radius:2px;transition:width .4s ease}
  .ob-step-header{padding:2rem 2rem 1.25rem}
  .ob-step-num{font-size:11px;font-weight:700;color:var(--teal);letter-spacing:.08em;text-transform:uppercase;margin-bottom:.4rem}
  .ob-step-title{font-family:'Inter',system-ui,sans-serif;font-size:24px;color:#1B3C78;margin-bottom:.4rem}
  .ob-step-sub{font-size:13px;color:var(--slate);line-height:1.6}
  .ob-body{padding:1.25rem 2rem}
  .ob-footer{padding:1.25rem 2rem;border-top:.5px solid var(--border);display:flex;align-items:center;justify-content:space-between;gap:10px}
  .ob-skip{font-size:13px;color:var(--slate);cursor:pointer;transition:color .15s;background:none;border:none;font-family:var(--sans)}.ob-skip:hover{color:#1B3C78}
  .ob-next{background:var(--teal);color:#fff;border:none;border-radius:var(--r);padding:10px 24px;font-size:14px;font-weight:600;font-family:var(--sans);cursor:pointer;transition:all .15s;display:flex;align-items:center;gap:6px}
  .ob-next:hover{background:var(--teal2)}.ob-next:disabled{opacity:.4;cursor:not-allowed}
  .ob-back{background:transparent;color:var(--slate);border:.5px solid var(--border2);border-radius:var(--r);padding:10px 18px;font-size:13px;font-family:var(--sans);cursor:pointer;transition:all .15s}.ob-back:hover{color:#1B3C78}
  .ob-input-group{margin-bottom:1rem}
  .ob-label{font-size:11px;font-weight:700;color:#3D4B61;letter-spacing:.06em;text-transform:uppercase;display:block;margin-bottom:6px}
  .ob-input{width:100%;background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:10px 13px;font-size:14px;color:#1B3C78;font-family:var(--sans);transition:border-color .2s}
  .ob-input:focus{outline:none;border-color:var(--teal)}
  .ob-input-row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .ob-role-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:1rem}
  .ob-role-btn{padding:.85rem 1rem;border-radius:var(--r);border:.5px solid var(--border2);background:var(--navy3);color:var(--slate);font-size:13px;font-family:var(--sans);cursor:pointer;transition:all .15s;text-align:left}
  .ob-role-btn:hover{border-color:var(--border2);color:#1B3C78}
  .ob-role-btn.selected{border-color:var(--teal);background:rgba(42,99,214,.08);color:var(--teal)}
  .ob-role-icon{font-size:18px;display:block;margin-bottom:4px}
  .ob-role-name{font-size:12px;font-weight:600}
  .ob-role-desc{font-size:11px;color:var(--slate);margin-top:1px}
  .ob-member-row{display:flex;gap:8px;align-items:center;margin-bottom:8px}
  .ob-upload-zone{border:1.5px dashed var(--border2);border-radius:var(--r);padding:1.5rem;text-align:center;cursor:pointer;transition:border-color .2s;margin-bottom:1rem}
  .ob-upload-zone:hover{border-color:var(--teal)}
  .ob-doc-chip{display:inline-flex;align-items:center;gap:6px;font-size:12px;padding:4px 10px;border-radius:20px;background:rgba(42,99,214,.10);border:.5px solid rgba(42,99,214,.15);color:var(--teal);margin:.25rem}
  .ob-check-row{display:flex;align-items:flex-start;gap:10px;padding:.65rem 0;border-bottom:.5px solid var(--border)}
  .ob-check-row:last-child{border-bottom:none}
  .ob-check-icon{font-size:16px;flex-shrink:0;margin-top:1px}
  .ob-check-label{font-size:13px;font-weight:600;color:#1B3C78}
  .ob-check-sub{font-size:12px;color:var(--slate);margin-top:1px}
  .ob-complete-icon{width:64px;height:64px;border-radius:50%;background:rgba(42,99,214,.15);border:.5px solid rgba(42,99,214,.25);display:flex;align-items:center;justify-content:center;font-size:28px;margin:0 auto 1.25rem}

  /* ── Stripe / Billing ── */
  .stripe-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:2rem;background:var(--navy);background-image:radial-gradient(ellipse 60% 45% at 60% 10%,rgba(45,212,160,.09) 0%,transparent 65%)}
  .stripe-card{background:var(--navy2);border:.5px solid var(--border2);border-radius:var(--r2);width:100%;max-width:780px;overflow:hidden}
  .stripe-header{padding:2rem;border-bottom:.5px solid var(--border);display:flex;align-items:center;justify-content:space-between}
  .stripe-logo{font-family:'Inter',system-ui,sans-serif;font-size:22px}.stripe-logo span{color:var(--teal)}
  .stripe-body{display:grid;grid-template-columns:1fr 1fr;min-height:480px}
  .stripe-plans{padding:1.75rem;border-right:.5px solid var(--border)}
  .stripe-checkout{padding:1.75rem}
  .sp-card{border:.5px solid var(--border2);border-radius:var(--r);padding:1rem;margin-bottom:10px;cursor:pointer;transition:all .15s;position:relative}
  .sp-card:hover{border-color:var(--border2);background:rgba(61,75,97,.03)}
  .sp-card.selected{border-color:var(--teal);background:rgba(42,99,214,.05)}
  .sp-pop{position:absolute;top:-10px;right:12px;background:var(--teal);color:#fff;font-size:10px;font-weight:700;padding:2px 10px;border-radius:20px}
  .sp-name{font-size:14px;font-weight:700;color:#1B3C78;margin-bottom:3px}
  .sp-price{font-size:22px;font-weight:700;color:#1B3C78;font-family:'Inter',system-ui,sans-serif}.sp-mo{font-size:12px;color:var(--slate);margin-left:3px}
  .sp-desc{font-size:12px;color:var(--slate);margin-top:3px}
  .sp-features{display:flex;flex-direction:column;gap:4px;margin-top:.75rem}
  .sp-feat{font-size:12px;color:var(--slate);display:flex;gap:5px;align-items:flex-start}.sp-feat::before{content:"✓";color:var(--teal);flex-shrink:0}
  .cc-label{font-size:11px;font-weight:700;color:#3D4B61;letter-spacing:.06em;text-transform:uppercase;display:block;margin-bottom:6px}
  .cc-input{width:100%;background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:10px 13px;font-size:14px;color:#1B3C78;font-family:var(--sans);margin-bottom:12px;transition:border-color .2s}
  .cc-input:focus{outline:none;border-color:var(--teal)}
  .cc-row{display:grid;grid-template-columns:1fr 1fr;gap:10px}
  .cc-fake-card{background:linear-gradient(135deg,#f1f5f9,#d1d5db);border-radius:10px;padding:1.25rem;margin-bottom:1.25rem;position:relative;overflow:hidden}
  .cc-fake-card::before{content:"";position:absolute;top:-30px;right:-30px;width:100px;height:100px;border-radius:50%;background:rgba(42,99,214,.08)}
  .cc-card-num{font-size:16px;color:rgba(255,255,255,.8);letter-spacing:.15em;margin-bottom:.85rem;font-family:monospace}
  .cc-card-row{display:flex;justify-content:space-between;font-size:11px;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.06em}
  .cc-card-val{font-size:13px;color:#1B3C78;font-weight:500;margin-top:2px}
  .stripe-btn{width:100%;background:var(--teal);color:#fff;border:none;border-radius:var(--r);padding:13px;font-size:15px;font-weight:700;font-family:var(--sans);cursor:pointer;margin-top:1rem;transition:all .15s;display:flex;align-items:center;justify-content:center;gap:8px}
  .stripe-btn:hover{background:var(--teal2)}.stripe-btn:disabled{opacity:.4;cursor:not-allowed}
  .trial-badge{background:rgba(42,99,214,.10);border:.5px solid rgba(42,99,214,.15);border-radius:var(--r);padding:.6rem .85rem;font-size:12px;color:var(--teal);margin-bottom:1.25rem;display:flex;align-items:center;gap:6px}
  .secure-row{display:flex;align-items:center;justify-content:center;gap:6px;font-size:11px;color:var(--slate);margin-top:.75rem}
  .toggle-annual{display:flex;align-items:center;gap:10px;margin-bottom:1.25rem;font-size:13px;color:var(--slate)}
  .toggle-switch{width:36px;height:20px;background:var(--navy3);border-radius:10px;position:relative;cursor:pointer;transition:background .2s;border:.5px solid var(--border2)}
  .toggle-switch.on{background:var(--teal)}
  .toggle-knob{width:14px;height:14px;border-radius:50%;background:var(--white);position:absolute;top:2px;left:2px;transition:left .2s}
  .toggle-switch.on .toggle-knob{left:18px}

  /* ── Settings / Domain ── */
  .settings-grid{display:grid;grid-template-columns:220px 1fr;height:calc(100vh - 73px);overflow:hidden}
  .settings-nav{border-right:.5px solid var(--border);padding:1rem 0;overflow-y:auto}
  .settings-nav-item{display:flex;align-items:center;gap:8px;padding:.55rem 1.1rem;font-size:13px;color:var(--slate);cursor:pointer;transition:all .15s;border-left:2px solid transparent;margin:1px 0}
  .settings-nav-item:hover{color:#1B3C78;background:rgba(61,75,97,.04)}
  .settings-nav-item.active{color:var(--teal);border-left-color:var(--teal);background:rgba(42,99,214,.07)}
  .settings-nav-section{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.08em;text-transform:uppercase;padding:.75rem 1.1rem .3rem}
  .settings-content{overflow-y:auto;padding:2rem}
  .settings-section{margin-bottom:2rem}
  .settings-section-title{font-size:15px;font-weight:700;color:#1B3C78;margin-bottom:.25rem}
  .settings-section-sub{font-size:13px;color:var(--slate);margin-bottom:1.25rem}
  .settings-row{display:flex;align-items:center;justify-content:space-between;padding:.85rem 1.1rem;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);margin-bottom:8px}
  .settings-row-label{font-size:13px;font-weight:600;color:#1B3C78}
  .settings-row-sub{font-size:12px;color:var(--slate);margin-top:2px}
  .domain-preview{background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:1rem 1.25rem;margin-bottom:1rem;font-family:monospace;font-size:13px;color:var(--teal)}
  .plan-current{background:rgba(42,99,214,.07);border:.5px solid rgba(42,99,214,.15);border-radius:var(--r2);padding:1.25rem;margin-bottom:1rem;display:flex;align-items:center;gap:1.25rem}
  .plan-name-big{font-size:20px;font-weight:700;color:#1B3C78;font-family:'Inter',system-ui,sans-serif}
  .plan-price-big{font-size:28px;font-weight:700;color:var(--teal)}
  .invoice-list-row{display:flex;align-items:center;justify-content:space-between;padding:.65rem .85rem;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);margin-bottom:6px;font-size:13px}
  .member-pill{display:inline-flex;align-items:center;gap:7px;padding:.5rem .85rem;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);margin-bottom:8px;width:100%}
  .member-av{width:26px;height:26px;border-radius:50%;background:var(--teal3);color:var(--teal);display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;flex-shrink:0}

  /* ── Electronic Voting ── */
  .ev-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:1.5rem}
  .ev-ballot{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);overflow:hidden}
  .ev-ballot.active{border-color:rgba(42,99,214,.25)}
  .ev-ballot.closed{border-color:rgba(138,153,179,.2)}
  .ev-ballot.urgent{border-color:rgba(245,166,35,.3)}
  .ev-ballot-header{padding:1rem 1.25rem;border-bottom:.5px solid var(--border)}
  .ev-ballot-body{padding:1rem 1.25rem}
  .ev-type-pill{font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px;display:inline-block;margin-bottom:.5rem}
  .ev-type-election{background:rgba(42,99,214,.12);color:var(--teal)}
  .ev-type-resolution{background:rgba(167,139,250,.12);color:var(--purple)}
  .ev-type-special{background:rgba(245,166,35,.12);color:var(--amber)}
  .ev-title{font-size:15px;font-weight:700;color:#1B3C78;margin-bottom:.25rem}
  .ev-desc{font-size:12px;color:var(--slate);line-height:1.5;margin-bottom:.85rem}
  .ev-meta-row{display:flex;gap:12px;font-size:12px;color:var(--slate);margin-bottom:.85rem;flex-wrap:wrap}
  .ev-meta-item{display:flex;align-items:center;gap:4px}
  .ev-option{background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:.75rem 1rem;margin-bottom:8px;cursor:pointer;transition:all .15s;display:flex;align-items:center;gap:12px}
  .ev-option:hover{border-color:var(--teal);background:rgba(42,99,214,.05)}
  .ev-option.selected{border-color:var(--teal);background:rgba(42,99,214,.08)}
  .ev-option.voted{cursor:default}
  .ev-option-radio{width:16px;height:16px;border-radius:50%;border:1.5px solid var(--border2);flex-shrink:0;display:flex;align-items:center;justify-content:center;transition:all .15s}
  .ev-option-radio.selected{border-color:var(--teal);background:var(--teal)}
  .ev-option-radio-dot{width:6px;height:6px;border-radius:50%;background:var(--navy)}
  .ev-option-label{font-size:13px;font-weight:600;color:#1B3C78;flex:1}
  .ev-option-count{font-size:12px;color:var(--slate)}
  .ev-result-bar{height:5px;background:var(--navy3);border-radius:3px;margin-top:5px;overflow:hidden}
  .ev-result-fill{height:100%;border-radius:3px;background:var(--teal);transition:width .5s ease}
  .ev-vote-btn{width:100%;padding:10px;border-radius:var(--r);border:none;background:var(--teal);color:#fff;font-size:13px;font-weight:600;font-family:var(--sans);cursor:pointer;margin-top:.85rem;transition:background .15s}
  .ev-vote-btn:hover{background:var(--teal2)}.ev-vote-btn:disabled{opacity:.4;cursor:not-allowed}
  .ev-voted-badge{display:flex;align-items:center;gap:6px;font-size:12px;color:var(--teal);background:rgba(42,99,214,.08);border:.5px solid rgba(42,99,214,.15);border-radius:var(--r);padding:8px 12px;margin-top:.85rem}
  .ev-quorum-bar{height:6px;background:var(--navy3);border-radius:3px;overflow:hidden;margin-top:6px}
  .ev-quorum-fill{height:100%;border-radius:3px;transition:width .5s}
  .ev-new-form{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.25rem;margin-top:1rem}
  .ev-candidate-row{display:flex;gap:8px;align-items:center;margin-bottom:8px}
  .ev-stats-row{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:1.25rem}

  /* ── Amenity Reservations ── */
  .am-layout{display:grid;grid-template-columns:260px 1fr;height:calc(100vh - 73px);overflow:hidden}
  .am-sidebar{border-right:.5px solid var(--border);overflow-y:auto;background:var(--navy2)}
  .am-sidebar-top{padding:1rem;border-bottom:.5px solid var(--border)}
  .am-amenity{padding:.7rem .9rem;cursor:pointer;transition:background .15s;border-left:2px solid transparent;margin:2px 0}
  .am-amenity:hover{background:rgba(61,75,97,.04)}
  .am-amenity.active{background:rgba(42,99,214,.07);border-left-color:var(--teal)}
  .am-amenity-icon{font-size:20px;margin-bottom:3px}
  .am-amenity-name{font-size:13px;font-weight:600;color:#1B3C78}
  .am-amenity-meta{font-size:11px;color:var(--slate)}
  .am-main{overflow-y:auto}
  .am-cal-header{display:grid;grid-template-columns:repeat(7,1fr);gap:4px;margin-bottom:4px}
  .am-day-label{font-size:11px;color:var(--slate);text-align:center;padding:4px 0;font-weight:600}
  .am-cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:4px}
  .am-day{min-height:70px;background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);padding:.4rem .5rem;cursor:pointer;transition:all .15s;position:relative}
  .am-day:hover{border-color:var(--teal);background:rgba(45,212,160,.04)}
  .am-day.today{border-color:rgba(42,99,214,.30);background:rgba(42,99,214,.05)}
  .am-day.other-month{opacity:.4}
  .am-day-num{font-size:12px;font-weight:600;color:#1B3C78;margin-bottom:3px}
  .am-day-num.today{color:var(--teal)}
  .am-booking-chip{font-size:10px;padding:1px 5px;border-radius:4px;margin-bottom:2px;truncate:ellipsis;overflow:hidden;white-space:nowrap;display:block}
  .am-booking-mine{background:rgba(42,99,214,.15);color:var(--teal)}
  .am-booking-other{background:rgba(138,153,179,.15);color:var(--slate)}
  .am-booking-blocked{background:rgba(224,82,82,.15);color:var(--red)}
  .am-detail{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.25rem;margin-bottom:1rem}
  .am-rule{display:flex;align-items:flex-start;gap:8px;padding:.5rem 0;border-bottom:.5px solid var(--border);font-size:13px;color:var(--slate)}
  .am-rule:last-child{border-bottom:none}
  .am-rule-icon{font-size:14px;flex-shrink:0;margin-top:1px}
  .am-booking-list-item{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);padding:.85rem 1rem;margin-bottom:8px;display:flex;align-items:center;gap:12px}
  .am-status-badge{font-size:11px;font-weight:600;padding:3px 9px;border-radius:20px}
  .am-confirmed{background:var(--green2);color:var(--green)}
  .am-pending{background:var(--amber2);color:var(--amber)}
  .am-cancelled{background:rgba(138,153,179,.12);color:var(--slate)}

  /* ── General Ledger ── */
  .gl-layout{display:grid;grid-template-columns:220px 1fr;height:calc(100vh - 73px);overflow:hidden}
  .gl-nav{border-right:.5px solid var(--border);padding:.75rem 0;overflow-y:auto}
  .gl-nav-section{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.08em;text-transform:uppercase;padding:.65rem 1rem .3rem}
  .gl-nav-item{padding:.55rem 1rem;font-size:13px;color:#3D4B61;font-weight:500;cursor:pointer;transition:all .15s;display:flex;align-items:center;justify-content:space-between;border-left:2px solid transparent;margin:1px 0}
  .gl-nav-item:hover{color:#1B3C78;background:rgba(61,75,97,.04)}
  .gl-nav-item.active{color:var(--teal);border-left-color:var(--teal);background:rgba(42,99,214,.07)}
  .gl-nav-balance{font-size:11px;font-weight:600}
  .gl-main{overflow-y:auto;padding:1.5rem}
  .gl-account-card{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.1rem 1.25rem;margin-bottom:10px;cursor:pointer;transition:border-color .2s}
  .gl-account-card:hover{border-color:var(--border2)}
  .gl-account-card.expanded{border-color:var(--teal)}
  .gl-account-header{display:flex;align-items:center;gap:12px}
  .gl-account-num{font-size:12px;color:var(--slate);min-width:50px;font-family:monospace}
  .gl-account-name{font-size:14px;font-weight:600;color:#1B3C78;flex:1}
  .gl-account-type{font-size:10px;font-weight:600;padding:2px 7px;border-radius:20px}
  .gl-type-asset{background:var(--teal3);color:var(--teal)}
  .gl-type-liability{background:var(--red2);color:var(--red)}
  .gl-type-equity{background:var(--purple2);color:var(--purple)}
  .gl-type-income{background:var(--green2);color:var(--green)}
  .gl-type-expense{background:var(--amber2);color:var(--amber)}
  .gl-account-balance{font-size:16px;font-weight:600}
  .gl-je-row{display:grid;grid-template-columns:90px 1fr 100px 100px 80px;gap:8px;padding:.6rem .85rem;border-bottom:.5px solid var(--border);font-size:12px;color:#1B3C78;font-weight:500;align-items:center}
  .gl-je-row:last-child{border-bottom:none}
  .gl-je-header{font-size:10px;font-weight:700;color:var(--slate);letter-spacing:.06em;text-transform:uppercase}
  .gl-debit{color:var(--teal);font-weight:600;text-align:right}
  .gl-credit{color:var(--red);font-weight:600;text-align:right}
  .gl-trial-row{display:grid;grid-template-columns:60px 1fr 1fr 110px 110px;gap:8px;padding:.55rem .85rem;border-bottom:.5px solid var(--border);font-size:12px;align-items:center}
  .gl-trial-row.total{border-top:1.5px solid var(--border2);border-bottom:none;font-weight:600;color:#1B3C78;background:var(--navy3)}
  .gl-bs-section{margin-bottom:1.5rem}
  .gl-bs-title{font-size:12px;font-weight:700;color:var(--slate);letter-spacing:.06em;text-transform:uppercase;margin-bottom:.65rem;padding-bottom:.4rem;border-bottom:.5px solid var(--border)}
  .gl-bs-row{display:flex;justify-content:space-between;font-size:13px;font-weight:500;color:#1B3C78;padding:.4rem .5rem;border-bottom:.5px solid var(--border)}
  .gl-bs-row:last-child{border-bottom:none}
  .gl-bs-row.subtotal{font-weight:600;color:#1B3C78;background:rgba(61,75,97,.03)}
  .gl-bs-row.total-row{font-weight:700;color:var(--teal);background:rgba(42,99,214,.05);border-top:1.5px solid var(--border2)}
  .gl-je-form{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .gl-entry-line{display:grid;grid-template-columns:1fr 100px 100px 28px;gap:8px;align-items:end;margin-bottom:8px}
  .gl-totals-check{display:flex;gap:12px;font-size:12px;padding:.65rem .85rem;background:var(--navy3);border-radius:var(--r);margin-top:.75rem}
  .gl-balanced{color:var(--teal);font-weight:600}.gl-unbalanced{color:var(--red);font-weight:600}

  /* ── Bank Feed ── */
  .bf-page{padding:2rem}
  .plaid-connect-card{background:var(--navy2);border:.5px solid var(--border2);border-radius:var(--r2);padding:2rem;text-align:center;max-width:480px;margin:0 auto}
  .plaid-logo{width:52px;height:52px;border-radius:14px;background:linear-gradient(135deg,#00b560,#009147);display:flex;align-items:center;justify-content:center;margin:0 auto 1rem;font-size:24px}
  .plaid-title{font-size:20px;font-weight:600;color:#1B3C78;margin-bottom:.5rem}
  .plaid-sub{font-size:13px;color:var(--slate);margin-bottom:1.5rem;line-height:1.6}
  .plaid-features{display:flex;flex-direction:column;gap:8px;margin-bottom:1.75rem;text-align:left}
  .plaid-feature{display:flex;align-items:center;gap:10px;font-size:13px;color:var(--slate)}
  .plaid-feature-icon{width:28px;height:28px;border-radius:50%;background:rgba(42,99,214,.12);display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0}
  .plaid-btn{width:100%;background:linear-gradient(135deg,#00b560,#009147);color:#1B3C78;border:none;border-radius:var(--r);padding:12px;font-size:14px;font-weight:600;font-family:var(--sans);cursor:pointer;transition:opacity .15s;display:flex;align-items:center;justify-content:center;gap:8px}
  .plaid-btn:hover{opacity:.9}.plaid-btn:disabled{opacity:.45;cursor:not-allowed}
  .plaid-secure{font-size:11px;color:var(--slate);margin-top:.75rem;display:flex;align-items:center;justify-content:center;gap:5px}
  .bank-connected-card{background:var(--navy2);border:.5px solid rgba(42,99,214,.15);border-radius:var(--r2);padding:1rem 1.25rem;display:flex;align-items:center;gap:14px;margin-bottom:1.25rem}
  .bank-icon{width:40px;height:40px;border-radius:10px;background:linear-gradient(135deg,#00b560,#009147);display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}
  .bank-name{font-size:14px;font-weight:700;color:#1B3C78}
  .bank-meta{font-size:12px;color:var(--slate);margin-top:2px}
  .bank-status{display:flex;align-items:center;gap:5px;font-size:12px;color:var(--teal);margin-left:auto}
  .bank-status-dot{width:7px;height:7px;border-radius:50%;background:var(--teal);animation:pulse 2s infinite}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
  .tx-import-row{display:flex;align-items:center;gap:12px;padding:.75rem 1rem;border-bottom:.5px solid var(--border);transition:background .15s}
  .tx-import-row:last-child{border-bottom:none}
  .tx-import-row:hover{background:rgba(61,75,97,.02)}
  .tx-import-cb{width:16px;height:16px;accent-color:var(--teal);cursor:pointer;flex-shrink:0}
  .tx-import-merchant{font-size:13px;font-weight:600;color:#1B3C78;flex:1}
  .tx-import-date{font-size:11px;color:var(--slate);min-width:75px}
  .tx-import-cat{font-size:11px;color:var(--slate);min-width:120px}
  .tx-import-acct{min-width:160px}
  .tx-import-amount{font-size:13px;font-weight:600;min-width:80px;text-align:right}
  .tx-import-amount.debit{color:var(--red)}
  .tx-import-amount.credit{color:var(--teal)}
  .tx-import-status{font-size:10px;font-weight:600;padding:2px 7px;border-radius:20px;flex-shrink:0;min-width:60px;text-align:center}
  .tx-new{background:rgba(245,166,35,.12);color:var(--amber)}
  .tx-posted{background:var(--green2);color:var(--green)}
  .bf-summary-row{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:1.5rem}
  .plaid-modal-overlay{position:fixed;inset:0;background:rgba(10,10,10,.88);display:flex;align-items:center;justify-content:center;z-index:200;padding:1rem}
  .plaid-modal{background:var(--navy2);border:.5px solid var(--border2);border-radius:var(--r2);width:100%;max-width:440px;overflow:hidden}
  .plaid-modal-header{padding:1.25rem 1.5rem;border-bottom:.5px solid var(--border);display:flex;align-items:center;gap:10px}
  .plaid-modal-body{padding:1.5rem}
  .bank-option{display:flex;align-items:center;gap:12px;padding:.85rem 1rem;border:.5px solid var(--border2);border-radius:var(--r);margin-bottom:8px;cursor:pointer;transition:all .15s}
  .bank-option:hover{border-color:var(--teal);background:rgba(45,212,160,.04)}
  .bank-option.selected{border-color:var(--teal);background:rgba(42,99,214,.07)}
  .bank-option-icon{width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
  .plaid-step-dots{display:flex;gap:6px;justify-content:center;margin-bottom:1.25rem}
  .plaid-step-dot{width:7px;height:7px;border-radius:50%;background:var(--border2);transition:background .2s}
  .plaid-step-dot.active{background:var(--teal)}
  .rule-row{display:flex;align-items:center;gap:10px;padding:.65rem .85rem;background:var(--navy3);border-radius:var(--r);margin-bottom:6px;font-size:13px}
  .rule-keyword{color:var(--amber);font-family:monospace;flex:1}
  .rule-arrow{color:var(--slate);flex-shrink:0}
  .rule-account{color:var(--teal);flex:1}

  /* ── Legal Pre-Send Scanner ── */
  .ls-wrap{max-width:860px;margin:0 auto}
  .ls-compose{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r2);padding:1.5rem;margin-bottom:1.5rem}
  .ls-compose-title{font-size:15px;font-weight:600;color:#1B3C78;margin-bottom:.25rem}
  .ls-compose-sub{font-size:13px;color:var(--slate);margin-bottom:1.25rem}
  .ls-type-row{display:flex;gap:8px;margin-bottom:1rem;flex-wrap:wrap}
  .ls-type-btn{padding:5px 14px;border-radius:20px;border:.5px solid var(--border2);background:transparent;color:var(--slate);font-size:12px;font-family:var(--sans);cursor:pointer;transition:all .15s}
  .ls-type-btn.active{background:rgba(42,99,214,.10);border-color:var(--teal);color:var(--teal)}
  .ls-textarea{width:100%;background:var(--navy3);border:.5px solid var(--border2);border-radius:var(--r);padding:12px 14px;font-size:13px;color:#1B3C78;font-family:var(--sans);resize:vertical;min-height:160px;line-height:1.7;transition:border-color .2s}
  .ls-textarea:focus{outline:none;border-color:var(--teal)}
  .ls-actions{display:flex;gap:10px;margin-top:1rem;align-items:center;flex-wrap:wrap}
  .ls-scan-btn{background:linear-gradient(135deg,var(--amber),#e0901a);color:#fff;border:none;border-radius:var(--r);padding:10px 22px;font-size:13px;font-weight:700;font-family:var(--sans);cursor:pointer;display:flex;align-items:center;gap:7px;transition:opacity .15s}
  .ls-scan-btn:hover{opacity:.88}.ls-scan-btn:disabled{opacity:.4;cursor:not-allowed}
  .ls-result-card{border-radius:var(--r2);overflow:hidden;margin-bottom:1.25rem;border:.5px solid var(--border)}
  .ls-result-header{padding:.85rem 1.25rem;display:flex;align-items:center;gap:10px}
  .ls-result-icon{font-size:22px;flex-shrink:0}
  .ls-result-verdict{font-size:15px;font-weight:700}
  .ls-result-sub{font-size:12px;margin-top:2px}
  .ls-result-body{padding:1.25rem}
  .ls-result-card.clear{border-color:rgba(58,191,126,.3)}
  .ls-result-card.clear .ls-result-header{background:rgba(58,191,126,.08)}
  .ls-result-card.clear .ls-result-verdict{color:var(--green)}
  .ls-result-card.warn{border-color:rgba(245,166,35,.3)}
  .ls-result-card.warn .ls-result-header{background:rgba(245,166,35,.08)}
  .ls-result-card.warn .ls-result-verdict{color:var(--amber)}
  .ls-result-card.block{border-color:rgba(224,82,82,.35)}
  .ls-result-card.block .ls-result-header{background:rgba(224,82,82,.1)}
  .ls-result-card.block .ls-result-verdict{color:var(--red)}
  .ls-issue{background:var(--navy3);border-radius:var(--r);padding:.85rem 1rem;margin-bottom:.75rem;border-left:3px solid var(--border2)}
  .ls-issue.high{border-left-color:var(--red)}
  .ls-issue.medium{border-left-color:var(--amber)}
  .ls-issue.low{border-left-color:var(--teal)}
  .ls-issue-title{font-size:13px;font-weight:600;color:#1B3C78;margin-bottom:4px;display:flex;align-items:center;gap:7px}
  .ls-issue-body{font-size:12px;color:#3D4B61;font-weight:500;line-height:1.65}
  .ls-issue-fix{font-size:12px;color:var(--teal);margin-top:6px;font-style:italic}
  .ls-rewrite{background:var(--navy3);border:.5px solid rgba(42,99,214,.15);border-radius:var(--r);padding:1rem;margin-top:1rem;font-size:13px;color:var(--ice);line-height:1.75;white-space:pre-wrap}
  .ls-send-row{display:flex;gap:10px;margin-top:1.25rem;padding-top:1rem;border-top:.5px solid var(--border);flex-wrap:wrap;align-items:center}
  .ls-history-item{background:var(--navy2);border:.5px solid var(--border);border-radius:var(--r);padding:.85rem 1.1rem;margin-bottom:8px;cursor:pointer;transition:border-color .15s}
  .ls-history-item:hover{border-color:var(--border2)}
  .ls-history-snippet{font-size:13px;color:#1B3C78;margin-bottom:4px}
  .ls-history-meta{font-size:11px;color:var(--slate);display:flex;gap:10px;flex-wrap:wrap}
  .ls-risk-pill{font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px}
  .lsr-clear{background:var(--green2);color:var(--green)}
  .lsr-warn{background:var(--amber2);color:var(--amber)}
  .lsr-block{background:var(--red2);color:var(--red)}
  .ls-intercept-overlay{position:fixed;inset:0;background:rgba(10,10,10,.9);display:flex;align-items:center;justify-content:center;z-index:200;padding:1rem}
  .ls-intercept-modal{background:var(--navy2);border:.5px solid var(--border2);border-radius:var(--r2);width:100%;max-width:620px;max-height:88vh;overflow-y:auto}
  .ls-intercept-header{padding:1.1rem 1.5rem;border-bottom:.5px solid var(--border);display:flex;align-items:center;gap:10px;background:rgba(245,166,35,.06)}
  .ls-scanning-anim{display:flex;flex-direction:column;align-items:center;padding:2.5rem;gap:.85rem}
  .ls-scan-ring{width:56px;height:56px;border:3px solid var(--border2);border-top-color:var(--amber);border-radius:50%;animation:spin .8s linear infinite}
  .ls-scan-lines{display:flex;flex-direction:column;gap:6px;width:100%;max-width:280px}
  .ls-scan-line{height:10px;border-radius:5px;animation:scanPulse 1.2s ease infinite}
  @keyframes scanPulse{0%,100%{opacity:.2}50%{opacity:.7}}
  .ls-score-ring{position:relative;display:inline-flex;align-items:center;justify-content:center}
`;

// ─── DEMO MODE ────────────────────────────────────────────────────────────────
// true  = runs in Claude artifact preview (uses INIT_* sample data, fake login)
// false = runs on Netlify with real Supabase + real auth (set before deploying)
const DEMO_MODE = false; // set to true for Claude preview only

// Global HOA name accessor — components call getOrgName() to get real org name
// Falls back to "Your HOA" if not set yet
let _currentOrgName = "Your HOA";
let _currentOrgEmail = "";
const setOrgContext = (org) => {
  if (org?.name) _currentOrgName = org.name;
  if (org?.email) _currentOrgEmail = org.email;
};
const getOrgName = () => _currentOrgName;
const getOrgEmail = () => _currentOrgEmail || `board@${_currentOrgName.toLowerCase().replace(/[^a-z0-9]+/g,'-')}.hoa`;

// ─── AI API ───────────────────────────────────────────────────────────────────
// ─── Prompts ──────────────────────────────────────────────────────────────────
const P = {
  violation: v => `Write a formal HOA violation notice letter. Unit: ${v.unit} | Owner: ${v.owner} | Type: ${v.type} | Date: ${v.date} | Description: ${v.description}. Firm but professional. 14-day corrective action deadline. Under 220 words. Include salutation and sign-off.`,
  duesReminder: (o, days) => `Write a HOA dues reminder email. Owner: ${o.name} | Unit: ${o.unit} | Amount owed: $${o.duesOwed} | Days overdue: ${days}. Tone: ${days < 15 ? "friendly" : days < 30 ? "firm" : "formal delinquency notice"}. Start with Subject: line. Under 180 words.`,
  announcement: (topic, tone) => `Write a community HOA email about: "${topic}". Tone: ${tone}. HOA: ${getOrgName()}. Start with Subject: line. Under 160 words.`,
  agenda: (v, d) => `Create a formal HOA board meeting agenda for ${getOrgName()} HOA. Open violations: ${v}. Unpaid dues: ${d}. Include: Call to Order, Minutes Approval, Financial Report, Violations Update, Maintenance, Old Business, New Business, Open Forum, Adjournment. Fill each section with the data provided.`,
  risk: o => `Brief 2-sentence HOA payment risk assessment and one recommended action for: ${o.name}, Unit ${o.unit}, Owed: $${o.duesOwed}, Paid YTD: $${o.duesPaid}, Notes: ${o.notes || "none"}.`,
  sequence: o => `Create a 3-step HOA dues email sequence for: ${o.name}, Unit ${o.unit}, Total owed: $${o.duesOwed}. Step 1 (Day 7 - friendly, Subject line + body <80 words), Step 2 (Day 30 - firm, Subject line + body <80 words), Step 3 (Day 60 - formal delinquency demand, Subject line + body <80 words). Label each step clearly.`,
  faq: q => `You are an HOA manager for ${getOrgName()} HOA. Answer this homeowner question in 2-3 helpful sentences: "${q}"`,
};

// ─── Sample Data ──────────────────────────────────────────────────────────────
const INIT_OWNERS = [
  { id: 1, name: "Margaret Chen", unit: "101", email: "m.chen@email.com", phone: "503-555-0142", moveIn: "2019-03-15", status: "active", duesOwed: 0, duesPaid: 3600, notes: "", emailOptIn: true },
  { id: 2, name: "Robert Walsh", unit: "102", email: "rwalsh@email.com", phone: "503-555-0187", moveIn: "2021-07-01", status: "active", duesOwed: 300, duesPaid: 2700, notes: "On payment plan", emailOptIn: true },
  { id: 3, name: "Priya Nair", unit: "201", email: "p.nair@email.com", phone: "503-555-0231", moveIn: "2020-01-10", status: "active", duesOwed: 0, duesPaid: 3600, notes: "", emailOptIn: true },
  { id: 4, name: "Damien Torres", unit: "202", email: "dtorres@email.com", phone: "503-555-0398", moveIn: "2022-05-20", status: "delinquent", duesOwed: 900, duesPaid: 900, notes: "Missed 3 payments", emailOptIn: true },
  { id: 5, name: "Susan Park", unit: "301", email: "spark@email.com", phone: "503-555-0411", moveIn: "2018-11-01", status: "active", duesOwed: 0, duesPaid: 3600, notes: "", emailOptIn: true },
  { id: 6, name: "James Okafor", unit: "302", email: "jokafor@email.com", phone: "503-555-0562", moveIn: "2023-02-14", status: "active", duesOwed: 150, duesPaid: 450, notes: "", emailOptIn: false },
];
const INIT_VIOLATIONS = [
  { id: 1, unit: "202", owner: "Damien Torres", type: "Noise", description: "Loud music after 10pm on three consecutive nights", date: "2024-03-18", status: "open", severity: "medium", letter: "", emailSent: false },
  { id: 2, unit: "102", owner: "Robert Walsh", type: "Parking", description: "Unauthorized vehicle in visitor space #4 for 7+ days", date: "2024-03-10", status: "resolved", severity: "low", letter: "", emailSent: true },
  { id: 3, unit: "302", owner: "James Okafor", type: "Trash", description: "Trash bins left at curb three days past pickup", date: "2024-03-22", status: "open", severity: "low", letter: "", emailSent: false },
  { id: 4, unit: "202", owner: "Damien Torres", type: "Landscaping", description: "Private patio overgrown, dead plants visible from common area", date: "2024-02-28", status: "warning", severity: "medium", letter: "", emailSent: true },
];
const INIT_DUES = [
  { id: 1, unit: "101", owner: "Margaret Chen", amount: 300, due: "2024-04-01", paid: "2024-03-29", status: "paid" },
  { id: 2, unit: "102", owner: "Robert Walsh", amount: 300, due: "2024-04-01", paid: null, status: "partial" },
  { id: 3, unit: "201", owner: "Priya Nair", amount: 300, due: "2024-04-01", paid: "2024-04-01", status: "paid" },
  { id: 4, unit: "202", owner: "Damien Torres", amount: 300, due: "2024-04-01", paid: null, status: "unpaid" },
  { id: 5, unit: "301", owner: "Susan Park", amount: 300, due: "2024-04-01", paid: "2024-03-31", status: "paid" },
  { id: 6, unit: "302", owner: "James Okafor", amount: 300, due: "2024-04-01", paid: null, status: "partial" },
];
const INIT_LOG = [
  { id: 1, to: "rwalsh@email.com", owner: "Robert Walsh", unit: "102", subject: "Parking Violation Notice — Unit 102", type: "violation", sent: "2024-03-10 09:12", status: "delivered" },
  { id: 2, to: "dtorres@email.com", owner: "Damien Torres", unit: "202", subject: "HOA Landscaping Violation — Unit 202", type: "violation", sent: "2024-03-01 10:44", status: "delivered" },
  { id: 3, to: "m.chen@email.com", owner: "Margaret Chen", unit: "101", subject: "April Dues Receipt — HOA Board", type: "dues", sent: "2024-03-29 14:02", status: "delivered" },
];

const INIT_MAINTENANCE = [
  { id: 1, unit: "101", owner: "Margaret Chen", category: "Plumbing", title: "Hallway pipe leak", description: "Small drip from the pipe under the hallway sink, started 3 days ago.", submitted: "2024-03-20", status: "in-progress", priority: "medium", boardNote: "Vendor scheduled for Thursday." },
  { id: 2, unit: "202", owner: "Damien Torres", category: "Electrical", title: "Parking lot light out", description: "Light fixture #4 in the east parking lot has been out for two weeks.", submitted: "2024-03-15", status: "open", priority: "low", boardNote: "" },
  { id: 3, unit: "301", owner: "Susan Park", category: "Common Area", title: "Pool gate latch broken", description: "The self-closing latch on the pool gate is not catching properly — safety concern with children.", submitted: "2024-03-22", status: "resolved", priority: "high", boardNote: "Fixed by maintenance crew on 3/24." },
];

const INIT_ANNOUNCEMENTS = [
  { id: 1, title: "Pool opening — May 1st", body: "We're pleased to announce the community pool will open for the season on May 1st. Pool hours will be 7am–10pm daily. Please review the pool rules posted at the gate.", date: "2024-04-15", type: "event" },
  { id: 2, title: "Parking reminder — visitor spaces", body: "A reminder that visitor parking spaces are limited to 72-hour stays. Vehicles parked beyond this limit will be subject to towing at the owner's expense. Please ensure your guests are aware.", date: "2024-04-10", type: "notice" },
  { id: 3, title: "April board meeting recap", body: "The April board meeting was held on April 8th. Key decisions: approved Q2 landscaping contract, discussed pool opening timeline, reviewed delinquency policy. Full minutes available upon request.", date: "2024-04-09", type: "update" },
  { id: 4, title: "Spring landscaping begins next week", body: "Our landscaping crew will begin spring cleanup the week of April 22nd. This includes lawn aeration, mulching of common beds, and pruning of hedgerows. Thank you for your patience with any temporary noise.", date: "2024-04-05", type: "maintenance" },
];

// Owner portal credentials (email → unit mapping)
const OWNER_CREDENTIALS = {
  "m.chen@email.com":    { password: "chen101",   unit: "101", name: "Margaret Chen" },
  "rwalsh@email.com":    { password: "walsh102",  unit: "102", name: "Robert Walsh" },
  "p.nair@email.com":    { password: "nair201",   unit: "201", name: "Priya Nair" },
  "dtorres@email.com":   { password: "torres202", unit: "202", name: "Damien Torres" },
  "spark@email.com":     { password: "park301",   unit: "301", name: "Susan Park" },
  "jokafor@email.com":   { password: "okafor302", unit: "302", name: "James Okafor" },
};

const INIT_VENDORS = [
  { id: 1, name: "Pacific Plumbing Co.", category: "Plumbing", contact: "Mike Reyes", phone: "503-555-0801", email: "mike@pacificplumbing.com", rating: 5, jobs: 8, totalSpend: 4200, licensed: true, insured: true, notes: "Preferred vendor — responsive, fair pricing" },
  { id: 2, name: "Bright Spark Electric", category: "Electrical", contact: "Dana Torres", phone: "503-555-0942", email: "dana@brightspark.com", rating: 4, jobs: 5, totalSpend: 2800, licensed: true, insured: true, notes: "Good for panel work and exterior lighting" },
  { id: 3, name: "GreenScape Landscaping", category: "Landscaping", contact: "Carlos Mena", phone: "503-555-0374", email: "carlos@greenscape.com", rating: 5, jobs: 24, totalSpend: 18600, licensed: true, insured: true, notes: "Quarterly contract — monthly maintenance" },
  { id: 4, name: "ProClean Janitorial", category: "Cleaning", contact: "Sandra Wu", phone: "503-555-0611", email: "sandra@proclean.com", rating: 4, jobs: 36, totalSpend: 9200, licensed: false, insured: true, notes: "Weekly common area cleaning" },
  { id: 5, name: "Cascade Roofing", category: "Roofing", contact: "Bob Kimura", phone: "503-555-0223", email: "bob@cascaderoofing.com", rating: 3, jobs: 2, totalSpend: 6400, licensed: true, insured: true, notes: "Used once for carport repair — adequate" },
];

const INIT_WORKORDERS = [
  { id: 1, title: "Hallway pipe leak — Unit 101", category: "Plumbing", description: "Small drip from under-sink pipe in Unit 101 hallway. Owner reported 3 days ago.", status: "in-progress", priority: "high", vendorId: 1, unit: "101", created: "2024-03-20", scheduled: "2024-03-28", completed: null, estimatedCost: 280, actualCost: null, invoices: [{ id: 1, name: "Labor estimate", amount: 280, status: "pending" }], timeline: [{ label: "Work order created", date: "Mar 20", done: true }, { label: "Vendor assigned", date: "Mar 21", done: true }, { label: "Scheduled inspection", date: "Mar 28", done: false, active: true }, { label: "Repair completed", date: "—", done: false }], aiEstimate: null },
  { id: 2, title: "Parking lot light #4 replacement", category: "Electrical", description: "Exterior light fixture #4 in east parking lot is out. Safety concern at night.", status: "open", priority: "medium", vendorId: null, unit: "Common", created: "2024-03-15", scheduled: null, completed: null, estimatedCost: null, actualCost: null, invoices: [], timeline: [{ label: "Work order created", date: "Mar 15", done: true }, { label: "Vendor assigned", date: "—", done: false, active: true }, { label: "Repair scheduled", date: "—", done: false }, { label: "Completed", date: "—", done: false }], aiEstimate: null },
  { id: 3, title: "Pool gate latch repair", category: "Common Area", description: "Self-closing latch on pool gate not catching. Safety issue — children could access unsupervised.", status: "resolved", priority: "high", vendorId: 2, unit: "Pool", created: "2024-03-22", scheduled: "2024-03-24", completed: "2024-03-24", estimatedCost: 150, actualCost: 175, invoices: [{ id: 1, name: "Labor & parts", amount: 175, status: "paid" }], timeline: [{ label: "Work order created", date: "Mar 22", done: true }, { label: "Vendor assigned", date: "Mar 22", done: true }, { label: "Repair completed", date: "Mar 24", done: true }, { label: "Invoice paid", date: "Mar 26", done: true }], aiEstimate: null },
  { id: 4, title: "Spring landscaping — common areas", category: "Landscaping", description: "Annual spring cleanup: lawn aeration, mulching, hedge trimming, flower bed prep.", status: "open", priority: "low", vendorId: 3, unit: "Common", created: "2024-04-01", scheduled: "2024-04-22", completed: null, estimatedCost: 1800, actualCost: null, invoices: [], timeline: [{ label: "Work order created", date: "Apr 1", done: true }, { label: "Vendor confirmed", date: "Apr 2", done: true }, { label: "Work begins", date: "Apr 22", done: false, active: true }, { label: "Final walkthrough", date: "—", done: false }], aiEstimate: null },
];

// ─── E-Voting data ────────────────────────────────────────────────────────────
const INIT_BALLOTS = [
  {
    id: 1, type: "election", title: "Board of Directors Election — 2024", status: "active",
    desc: "Elect two board members to serve 2-year terms beginning January 2025. All owners in good standing may vote.",
    opens: "2024-04-15", closes: "2024-05-15", quorum: 30, totalUnits: 6,
    options: [
      { id: 1, label: "Sarah Kim (incumbent)", subtitle: "Board President — running for re-election", votes: 3 },
      { id: 2, label: "Tom Nguyen", subtitle: "Unit 104 — first-time candidate, finance background", votes: 2 },
      { id: 3, label: "Patricia Osei", subtitle: "Unit 203 — community liaison experience", votes: 2 },
      { id: 4, label: "David Reyes (incumbent)", subtitle: "Treasurer — running for re-election", votes: 4 },
    ],
    maxChoices: 2, votedUnits: ["101", "201", "301"], myVote: null,
  },
  {
    id: 2, type: "resolution", title: "Special Assessment — Pool Resurfacing", status: "active",
    desc: "Approve a one-time special assessment of $800 per unit to fund pool deck resurfacing and equipment replacement, scheduled for September 2024.",
    opens: "2024-04-20", closes: "2024-05-05", quorum: 30, totalUnits: 6,
    options: [
      { id: 1, label: "Yes — approve the $800 special assessment", subtitle: "", votes: 3 },
      { id: 2, label: "No — do not approve at this time", subtitle: "", votes: 1 },
      { id: 3, label: "Abstain", subtitle: "", votes: 0 },
    ],
    maxChoices: 1, votedUnits: ["101", "201", "301", "302"], myVote: null,
  },
  {
    id: 3, type: "special", title: "Adopt Revised Pet Policy", status: "closed",
    desc: "Adopt the updated pet policy including breed restrictions aligned with current insurance requirements and updated ESA accommodation procedures.",
    opens: "2024-03-01", closes: "2024-03-31", quorum: 30, totalUnits: 6,
    options: [
      { id: 1, label: "Approve revised pet policy", subtitle: "", votes: 5 },
      { id: 2, label: "Reject — keep current policy", subtitle: "", votes: 1 },
    ],
    maxChoices: 1, votedUnits: ["101", "102", "201", "202", "301", "302"], myVote: 1,
  },
];

// ─── Amenity data ─────────────────────────────────────────────────────────────
const INIT_AMENITIES = [
  { id: 1, name: "Community Pool", icon: "🏊", capacity: 20, maxHours: 4, depositAmount: 0, rules: ["Open May 1–Sep 30 · 7am–10pm", "Max 4 guests per unit", "Children under 14 need adult supervision", "No glass containers", "Shower before entering"], color: "var(--teal)" },
  { id: 2, name: "Clubhouse", icon: "🏠", capacity: 30, maxHours: 4, depositAmount: 75, rules: ["Reserve 7 days in advance · $75 deposit", "Max 30 guests · Cleanup required", "Alcohol permitted for residents 21+", "No amplified music after 9pm", "Deposit forfeited if not cleaned"], color: "var(--purple)" },
  { id: 3, name: "BBQ / Picnic Area", icon: "🔥", capacity: 15, maxHours: 3, depositAmount: 0, rules: ["First-come, first-served · No reservation needed", "Clean grill after each use", "Quiet hours apply after 9pm", "No open flames beyond designated grills"], color: "var(--amber)" },
  { id: 4, name: "Fitness Center", icon: "💪", capacity: 6, maxHours: 2, depositAmount: 0, rules: ["Open 5am–11pm daily", "Wipe down equipment after use", "No personal training sessions for hire", "Max 2 guests per resident"], color: "var(--green)" },
  { id: 5, name: "Tennis / Pickleball Court", icon: "🎾", capacity: 4, maxHours: 2, depositAmount: 0, rules: ["Reserve up to 7 days in advance", "Max 2 hours when others are waiting", "Proper court shoes required", "Lights off at 10pm"], color: "var(--red)" },
];

const TODAY_DATE = new Date();

function makeBookings() {
  const bookings = [];
  let id = 1;
  const data = [
    { amenityId: 2, unit: "101", owner: "Margaret Chen",   date: "2024-04-27", start: "14:00", end: "18:00", status: "confirmed", notes: "Birthday party" },
    { amenityId: 1, unit: "201", owner: "Priya Nair",      date: "2024-04-28", start: "10:00", end: "12:00", status: "confirmed", notes: "" },
    { amenityId: 5, unit: "301", owner: "Susan Park",      date: "2024-04-26", start: "09:00", end: "11:00", status: "confirmed", notes: "" },
    { amenityId: 2, unit: "202", owner: "Damien Torres",   date: "2024-05-04", start: "16:00", end: "20:00", status: "pending",   notes: "Cinco de Mayo gathering" },
    { amenityId: 4, unit: "102", owner: "Robert Walsh",    date: "2024-04-25", start: "06:00", end: "07:00", status: "confirmed", notes: "" },
    { amenityId: 1, unit: "302", owner: "James Okafor",    date: "2024-05-01", start: "13:00", end: "16:00", status: "confirmed", notes: "Pool opening day" },
    { amenityId: 2, unit: "101", owner: "Margaret Chen",   date: "2024-05-10", start: "11:00", end: "15:00", status: "confirmed", notes: "Book club" },
  ];
  data.forEach(d => bookings.push({ id: id++, ...d }));
  return bookings;
}
const INIT_BOOKINGS = makeBookings();

// ─── General Ledger data ──────────────────────────────────────────────────────
const GL_ACCOUNTS = [
  { num: "1010", name: "Operating Checking",      type: "asset",     normal: "debit",  balance: 14820.50 },
  { num: "1020", name: "Reserve Fund — Savings",  type: "asset",     normal: "debit",  balance: 42500.00 },
  { num: "1030", name: "Petty Cash",              type: "asset",     normal: "debit",  balance: 150.00 },
  { num: "1100", name: "Assessments Receivable",  type: "asset",     normal: "debit",  balance: 1350.00 },
  { num: "2010", name: "Accounts Payable",        type: "liability", normal: "credit", balance: 2280.00 },
  { num: "2020", name: "Prepaid Assessments",     type: "liability", normal: "credit", balance: 300.00 },
  { num: "3010", name: "Retained Earnings",       type: "equity",    normal: "credit", balance: 38640.50 },
  { num: "3020", name: "Current Year Surplus",    type: "equity",    normal: "credit", balance: 17600.00 },
  { num: "4010", name: "HOA Assessments",         type: "income",    normal: "credit", balance: 17100.00 },
  { num: "4020", name: "Late Fees",               type: "income",    normal: "credit", balance: 500.00 },
  { num: "4030", name: "Clubhouse Rental Income", type: "income",    normal: "credit", balance: 225.00 },
  { num: "5010", name: "Landscaping",             type: "expense",   normal: "debit",  balance: 1480.00 },
  { num: "5020", name: "Maintenance & Repairs",   type: "expense",   normal: "debit",  balance: 675.00 },
  { num: "5030", name: "Utilities",               type: "expense",   normal: "debit",  balance: 700.00 },
  { num: "5040", name: "Insurance Premiums",       type: "expense",   normal: "debit",  balance: 840.00 },
  { num: "5050", name: "Management Fees",         type: "expense",   normal: "debit",  balance: 500.00 },
  { num: "5060", name: "Reserve Contributions",   type: "expense",   normal: "debit",  balance: 1000.00 },
  { num: "5070", name: "Admin & Legal",           type: "expense",   normal: "debit",  balance: 350.00 },
  { num: "5080", name: "Bank Service Charges",    type: "expense",   normal: "debit",  balance: 30.00 },
];

const INIT_JOURNAL = [
  { id: 1, date: "2024-04-01", ref: "JE-001", memo: "April assessments — Units 101, 201, 301", entries: [{ account: "1100", debit: 900, credit: 0 }, { account: "4010", debit: 0, credit: 900 }] },
  { id: 2, date: "2024-04-01", ref: "JE-002", memo: "Cash received — Units 101, 201, 301 dues", entries: [{ account: "1010", debit: 900, credit: 0 }, { account: "1100", debit: 0, credit: 900 }] },
  { id: 3, date: "2024-04-05", ref: "JE-003", memo: "GreenScape landscaping — spring cleanup deposit", entries: [{ account: "5010", debit: 900, credit: 0 }, { account: "2010", debit: 0, credit: 900 }] },
  { id: 4, date: "2024-04-08", ref: "JE-004", memo: "Portland General Electric — common areas", entries: [{ account: "5030", debit: 187, credit: 0 }, { account: "2010", debit: 0, credit: 187 }] },
  { id: 5, date: "2024-04-10", ref: "JE-005", memo: "Late fee — Unit 202 (Damien Torres)", entries: [{ account: "1010", debit: 50, credit: 0 }, { account: "4020", debit: 0, credit: 50 }] },
  { id: 6, date: "2024-04-12", ref: "JE-006", memo: "Pacific Plumbing — Unit 101 pipe repair", entries: [{ account: "5020", debit: 280, credit: 0 }, { account: "1010", debit: 0, credit: 280 }] },
  { id: 7, date: "2024-04-15", ref: "JE-007", memo: "HOA Insurance quarterly premium", entries: [{ account: "5040", debit: 420, credit: 0 }, { account: "1010", debit: 0, credit: 420 }] },
  { id: 8, date: "2024-04-20", ref: "JE-008", memo: "Board-Armor management fee — April", entries: [{ account: "5050", debit: 250, credit: 0 }, { account: "1010", debit: 0, credit: 250 }] },
  { id: 9, date: "2024-04-22", ref: "JE-009", memo: "Reserve fund contribution — April", entries: [{ account: "5060", debit: 500, credit: 0 }, { account: "1020", debit: 500, credit: 0 }, { account: "1010", debit: 0, credit: 500 }, { account: "5060", debit: 0, credit: 500 }] },
];
const BUDGET_CATEGORIES = [
  { id: "dues",        name: "HOA Dues Income",      type: "income",  color: "#dc2626" },
  { id: "latefees",    name: "Late Fees",             type: "income",  color: "#3abf7e" },
  { id: "landscaping", name: "Landscaping",           type: "expense", color: "#f59e0b" },
  { id: "maintenance", name: "Maintenance & Repairs", type: "expense", color: "#dc2626" },
  { id: "utilities",   name: "Utilities",             type: "expense", color: "#a78bfa" },
  { id: "insurance",   name: "Insurance",             type: "expense", color: "#f472b6" },
  { id: "management",  name: "Management Fees",       type: "expense", color: "#60a5fa" },
  { id: "reserves",    name: "Reserve Fund",          type: "expense", color: "#34d399" },
  { id: "admin",       name: "Admin & Legal",         type: "expense", color: "#fb923c" },
  { id: "other",       name: "Other",                 type: "expense", color: "#64748b" },
];

const INIT_BUDGETS = {
  "2024-04": { dues: 1800, latefees: 150, landscaping: 800, maintenance: 600, utilities: 300, insurance: 420, management: 250, reserves: 500, admin: 100, other: 80 },
  "2024-03": { dues: 1800, latefees: 100, landscaping: 600, maintenance: 400, utilities: 280, insurance: 420, management: 250, reserves: 500, admin: 80, other: 60 },
  "2024-02": { dues: 1800, latefees: 50,  landscaping: 200, maintenance: 300, utilities: 310, insurance: 420, management: 250, reserves: 500, admin: 60, other: 40 },
};

const INIT_TRANSACTIONS = [
  { id: 1,  month: "2024-04", date: "2024-04-01", desc: "April dues — Unit 101 (Margaret Chen)",   cat: "dues",        type: "income",  amount: 300 },
  { id: 2,  month: "2024-04", date: "2024-04-01", desc: "April dues — Unit 201 (Priya Nair)",      cat: "dues",        type: "income",  amount: 300 },
  { id: 3,  month: "2024-04", date: "2024-04-01", desc: "April dues — Unit 301 (Susan Park)",      cat: "dues",        type: "income",  amount: 300 },
  { id: 4,  month: "2024-04", date: "2024-04-05", desc: "GreenScape — spring cleanup deposit",      cat: "landscaping", type: "expense", amount: 900 },
  { id: 5,  month: "2024-04", date: "2024-04-08", desc: "Portland General Electric — common areas", cat: "utilities",   type: "expense", amount: 187 },
  { id: 6,  month: "2024-04", date: "2024-04-10", desc: "Late fee — Unit 202 (Damien Torres)",      cat: "latefees",    type: "income",  amount: 50  },
  { id: 7,  month: "2024-04", date: "2024-04-12", desc: "Pacific Plumbing — Unit 101 pipe repair",  cat: "maintenance", type: "expense", amount: 280 },
  { id: 8,  month: "2024-04", date: "2024-04-15", desc: "HOA Insurance premium (quarterly)",        cat: "insurance",   type: "expense", amount: 420 },
  { id: 9,  month: "2024-04", date: "2024-04-20", desc: "Board-Armor management fee",                   cat: "management",  type: "expense", amount: 250 },
  { id: 10, month: "2024-04", date: "2024-04-22", desc: "Reserve fund contribution",                cat: "reserves",    type: "expense", amount: 500 },
  { id: 11, month: "2024-03", date: "2024-03-01", desc: "March dues — Unit 101",                    cat: "dues",        type: "income",  amount: 300 },
  { id: 12, month: "2024-03", date: "2024-03-01", desc: "March dues — Unit 201",                    cat: "dues",        type: "income",  amount: 300 },
  { id: 13, month: "2024-03", date: "2024-03-01", desc: "March dues — Unit 301",                    cat: "dues",        type: "income",  amount: 300 },
  { id: 14, month: "2024-03", date: "2024-03-01", desc: "March dues partial — Unit 102",             cat: "dues",        type: "income",  amount: 150 },
  { id: 15, month: "2024-03", date: "2024-03-10", desc: "GreenScape — monthly maintenance",          cat: "landscaping", type: "expense", amount: 580 },
  { id: 16, month: "2024-03", date: "2024-03-12", desc: "Portland General Electric",                 cat: "utilities",   type: "expense", amount: 201 },
  { id: 17, month: "2024-03", date: "2024-03-22", desc: "Pool gate latch repair — Bright Spark",     cat: "maintenance", type: "expense", amount: 175 },
  { id: 18, month: "2024-03", date: "2024-03-24", desc: "Legal review — CC&R amendment",             cat: "admin",       type: "expense", amount: 350 },
  { id: 19, month: "2024-03", date: "2024-03-28", desc: "Reserve fund contribution",                 cat: "reserves",    type: "expense", amount: 500 },
  { id: 20, month: "2024-03", date: "2024-03-29", desc: "Board-Armor management fee",                    cat: "management",  type: "expense", amount: 250 },
  { id: 21, month: "2024-02", date: "2024-02-01", desc: "February dues — Units 101, 201, 301",       cat: "dues",        type: "income",  amount: 900 },
  { id: 22, month: "2024-02", date: "2024-02-08", desc: "Northwest Natural Gas — heat common",        cat: "utilities",   type: "expense", amount: 312 },
  { id: 23, month: "2024-02", date: "2024-02-15", desc: "Snow removal — parking lot",                 cat: "maintenance", type: "expense", amount: 220 },
  { id: 24, month: "2024-02", date: "2024-02-20", desc: "Reserve fund contribution",                  cat: "reserves",    type: "expense", amount: 500 },
  { id: 25, month: "2024-02", date: "2024-02-28", desc: "Board-Armor management fee",                     cat: "management",  type: "expense", amount: 250 },
];

// ─── Icons ────────────────────────────────────────────────────────────────────
const ICS = {
  home: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10",
  users: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75 M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
  dollar: "M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  alert: "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z M12 9v4 M12 17h.01",
  mail: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6",
  zap: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  brain: "M9.5 2a2.5 2.5 0 0 1 5 0v1a6 6 0 0 1 6 6v1a6 6 0 0 1-6 6v1a2.5 2.5 0 0 1-5 0v-1a6 6 0 0 1-6-6v-1a6 6 0 0 1 6-6z M12 8v4l3 3",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4",
  scale: "M12 3v18 M8 21h8 M3 6l3 6c0 1.66 1.34 3 3 3s3-1.34 3-3L9 6 M15 6l3 6c0 1.66 1.34 3 3 3s3-1.34 3-3l-3-6 M3 6h18",
  image: "M21 15l-5-5L5 21 M3 3h18v18H3z M8.5 8.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
  flyer: "M4 4h16v16H4z M9 9h6 M9 12h6 M9 15h4",
  portal: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10",
  wrench: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z",
  check2: "M20 6L9 17l-5-5",
  bell: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0",
  chart: "M18 20V10 M12 20V4 M6 20v-6",
  file2: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8",
  vault: "M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z M9 10a3 3 0 1 0 6 0 3 3 0 0 0-6 0z M12 7v1 M12 12v1 M9.5 8.5l.7.7 M14.8 13.8l-.7-.7 M7 10h1 M16 10h-1 M9.5 11.5l.7-.7 M14.8 8.2l-.7.7",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  vote: "M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  amenity: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10",
  ledger: "M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 0 3-3h7z",
  bank: "M3 22v-1h18v1 M3 10h18 M12 3L2 9h20L12 3z M6 10v9 M10 10v9 M14 10v9 M18 10v9",
  scan: "M3 7V5a2 2 0 0 1 2-2h2 M17 3h2a2 2 0 0 1 2 2v2 M21 17v2a2 2 0 0 1-2 2h-2 M7 21H5a2 2 0 0 1-2-2v-2 M7 12h10",
  audit: "M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  mail2: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6",
  phone: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.6a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.6a16 16 0 0 0 6 6l.94-.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z",
};
const Ic = ({ n, s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {(ICS[n] || "").split(" M").map((d, i) => <path key={i} d={(i ? "M" : "") + d} />)}
  </svg>
);

// ─── Official BoardArmor logo ──────────────────────────────────────────────────
// Charcoal roofline shield, silver center ridge, orange checkmark. Approved logo — do not alter without updating everywhere it's referenced.
function BoardArmorLogo({ size = 28 }) {
  const uid = useRef(`ba-logo-${Math.random().toString(36).slice(2, 9)}`).current;
  return (
    <svg width={size} height={size * 1.12} viewBox="0 0 100 112" aria-label="BoardArmor">
      <defs>
        <linearGradient id={`${uid}-left`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#9FBEF5" />
          <stop offset="100%" stopColor="#3E6FD6" />
        </linearGradient>
        <linearGradient id={`${uid}-right`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#20428A" />
          <stop offset="100%" stopColor="#0B1E42" />
        </linearGradient>
        <linearGradient id={`${uid}-ridge`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="50%" stopColor="#d5d9de" />
          <stop offset="100%" stopColor="#8B96AC" />
        </linearGradient>
        <linearGradient id={`${uid}-check`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#C9D2E4" />
        </linearGradient>
        <clipPath id={`${uid}-shield`}>
          <path d="M50 4 L18 26 L10 34 L10 50 C10 82 26 100 50 110 C74 100 90 82 90 50 L90 34 L82 26 Z" />
        </clipPath>
        <clipPath id={`${uid}-l`}><rect x="0" y="0" width="50" height="112" /></clipPath>
        <clipPath id={`${uid}-r`}><rect x="50" y="0" width="50" height="112" /></clipPath>
      </defs>
      <g clipPath={`url(#${uid}-shield)`}>
        <g clipPath={`url(#${uid}-l)`}><rect x="0" y="0" width="50" height="112" fill={`url(#${uid}-left)`} /></g>
        <g clipPath={`url(#${uid}-r)`}><rect x="50" y="0" width="50" height="112" fill={`url(#${uid}-right)`} /></g>
      </g>
      <path d="M50 4 L18 26 L10 34 L10 50 C10 82 26 100 50 110 C74 100 90 82 90 50 L90 34 L82 26 Z" fill="none" stroke="#16305C" strokeWidth="1.5" />
      <line x1="50" y1="4" x2="50" y2="110" stroke={`url(#${uid}-ridge)`} strokeWidth="2.5" />
      <path d="M35 55 L46 66 L67 40" fill="none" stroke={`url(#${uid}-check)`} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function Toast({ msg, onDone }) {
  useEffect(() => {
    const id = setTimeout(onDone, 3000);
    return () => clearTimeout(id);
  }, [onDone]);
  return <div className="toast">✓ {msg}</div>;
}

function AiBlock({ label, content, loading, children }) {
  return (
    <div className="aip">
      <div className="ail">{loading ? <span className="ai-spin" /> : <Ic n="zap" s={13} />}{label}</div>
      {loading && <div className="ais">Generating with AI…</div>}
      {!loading && content && <div className="aio">{content}</div>}
      {!loading && children}
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [mode, setMode]       = useState("signin");
  const [em, setEm]           = useState("");
  const [pw, setPw]           = useState("");
  const [name, setName]       = useState("");
  const [hoaName, setHoaName] = useState("");
  const [refCode, setRefCode] = useState(() => {
    // Auto-fill from URL ?ref= parameter
    try { return new URLSearchParams(window.location.search).get("ref") || ""; } catch { return ""; }
  });
  const [err, setErr]         = useState("");
  const [loading, setLoading] = useState(false);

  const go = async () => {
    if (!em || !pw) { setErr("Email and password are required."); return; }
    setErr(""); setLoading(true);

    try {
      if (mode === "signin") {
        // Step 1: Authenticate with Supabase
        const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({ email: em, password: pw });
        if (authErr) throw new Error(authErr.message);
        if (!authData?.user?.id) throw new Error("Sign in failed. Please try again.");

        // Step 2: Load member + org via server function (service role, no RLS issues)
        const res = await fetch("/.netlify/functions/get-member", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: authData.user.id }),
        });
        const result = await res.json();
        if (!res.ok) throw new Error("Account found but workspace not set up. Contact support@board-armor.com");

        const { member, org } = result;
        if (!member || !org) throw new Error("Could not load your HOA workspace. Please try again.");

        onLogin({
          id:       authData.user.id,
          name:     member.name,
          role:     member.role,
          initials: member.initials || member.name.trim().split(/\s+/).map(n => n[0]).join("").toUpperCase(),
          org,
          orgId:    member.org_id,
        });
      } else {
        // Validate inputs
        if (!name.trim())    throw new Error("Your name is required.");
        if (!hoaName.trim()) throw new Error("Your HOA name is required.");
        if (pw.length < 8)   throw new Error("Password must be at least 8 characters.");

        // Step 1: Create auth user in Supabase
        const { data: authData, error: authErr } = await supabase.auth.signUp({ email: em, password: pw });

        // Supabase returns identities=[] when the user already exists
        if (!authErr && (!authData?.user?.id || authData?.user?.identities?.length === 0)) {
          throw new Error("An account with this email already exists. Please sign in instead.");
        }
        if (authErr) throw new Error(authErr.message);
        if (!authData?.user?.id) throw new Error("Account creation failed. Please try again.");

        // Step 2: Create org + board member via server function (service role)
        const res = await fetch("/.netlify/functions/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId:  authData.user.id,
            name:    name.trim(),
            email:   em.toLowerCase().trim(),
            hoaName: hoaName.trim(),
          }),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || "Workspace setup failed. Please try again.");

        const { org, member } = result;
        if (!org?.id) throw new Error("Failed to create HOA workspace. Please contact support@board-armor.com");

        const initials = name.trim().split(/\s+/).map(n => n[0]).join("").toUpperCase().slice(0, 3);

        // Apply referral code if provided (non-blocking)
        if (refCode && refCode.trim().length >= 4) {
          fetch("/.netlify/functions/referral", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "apply", code: refCode.trim(), newOrgId: org.id }),
          }).catch(() => {});
        }

        onLogin({ id: authData.user.id, name: name.trim(), role: "president", initials, org, orgId: org.id });
      }
    } catch (e) {
      setErr(e.message || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lw"><div className="lc">
      <div className="ll" style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}><BoardArmorLogo size={30} />Board-<span>Armor</span></div>
      <div className="ls">HOA Management · AI-Powered · v3.0</div>
      {err && <div className="le">{err}</div>}
      {mode === "signup" && (
        <>
          <label className="ll2">Your name</label>
          <input className="li" placeholder="Sarah Kim" value={name} onChange={e => setName(e.target.value)} />
          <label className="ll2">HOA name</label>
          <input className="li" placeholder="Maplewood Commons HOA" value={hoaName} onChange={e => setHoaName(e.target.value)} />
          <label className="ll2">Referral code <span style={{fontWeight:400,color:"var(--slate)"}}>(optional)</span></label>
          <input className="li" placeholder="e.g. CARBON12" value={refCode} onChange={e => setRefCode(e.target.value.toUpperCase())} style={{textTransform:"uppercase",letterSpacing:2}} />
        </>
      )}
      <label className="ll2">Email</label>
      <input className="li" type="email" placeholder="board@yourhoa.com" value={em} onChange={e => setEm(e.target.value)} onKeyDown={e => e.key === "Enter" && go()} />
      <label className="ll2">Password</label>
      <input className="li" type="password" placeholder="••••••••" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && go()} />
      <button className="lb" onClick={go} disabled={loading}>
        {loading ? "Please wait…" : mode === "signin" ? "Sign in to Board Portal" : "Create your HOA workspace"}
      </button>
      <div className="lh">
        {mode === "signin"
          ? <span>New to Board-Armor? <span style={{ color: "var(--teal)", cursor: "pointer", fontWeight: 600 }} onClick={() => { setMode("signup"); setErr(""); setPw(""); }}>Create account →</span></span>
          : <span>Already have an account? <span style={{ color: "var(--teal)", cursor: "pointer", fontWeight: 600 }} onClick={() => { setMode("signin"); setErr(""); }}>Sign in →</span></span>}
      </div>
      <div style={{ marginTop: "1.25rem", textAlign: "center", fontSize: 11, color: "var(--slate)", lineHeight: 1.8 }}>
        By creating an account you agree to our{" "}
        <a href="https://board-armor.com/terms-of-service.html" target="_blank" rel="noopener noreferrer" style={{ color: "var(--teal)", textDecoration: "none" }}>Terms of Service</a>
        {" "}and{" "}
        <a href="https://board-armor.com/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: "var(--teal)", textDecoration: "none" }}>Privacy Policy</a>.
        <br />
        AI features are powered by Anthropic. <a href="https://board-armor.com/privacy-policy.html#ai" target="_blank" rel="noopener noreferrer" style={{ color: "var(--slate)", textDecoration: "underline" }}>Learn more</a>.
      </div>
    </div></div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ icon, title, subtitle, action, onAction }) {
  return (
    <div style={{ textAlign:"center", padding:"4rem 2rem", color:"var(--slate)" }}>
      <div style={{ fontSize:48, marginBottom:16 }}>{icon}</div>
      <div style={{ fontSize:18, fontWeight:600, color:"var(--ink)", marginBottom:8 }}>{title}</div>
      {subtitle && <div style={{ fontSize:14, color:"var(--slate)", maxWidth:400, margin:"0 auto 24px", lineHeight:1.6 }}>{subtitle}</div>}
      {action && <button className="btn" onClick={onAction}>{action}</button>}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ owners, violations, dues, emailLog, setPage, orgName = "Your HOA" }) {
  const totalOwed  = useMemo(() => dues.filter(d => d.status !== 'paid').reduce((s, d) => s + (Number(d.amount) || 0), 0), [dues]);
  const openV      = useMemo(() => violations.filter(v => v.status !== "resolved").length, [violations]);
  const paidC      = useMemo(() => dues.filter(d => d.status === "paid").length, [dues]);
  const delinquent = useMemo(() => owners.filter(o => o.duesOwed >= 600), [owners]);
  return (
    <div>
      <div className="ph"><div><div className="pt">Dashboard</div><div className="ps">{orgName} · {new Date().toLocaleDateString("en-US", {month:"long", year:"numeric"})}</div></div></div>
      <div className="pb">
        <div className="sr">
          <div className="sc"><div className="sl">Total units</div><div className="sv">{owners.length}</div></div>
          <div className="sc"><div className="sl">Dues collected</div><div className="sv t">{paidC}/{dues.length}</div></div>
          <div className="sc"><div className="sl">Outstanding</div><div className="sv a">{totalOwed > 0 ? `$${totalOwed.toLocaleString()}` : "$0"}</div></div>
          <div className="sc"><div className="sl">Open violations</div><div className="sv r">{openV}</div></div>
        </div>
        {delinquent.length > 0 && (
          <div style={{ background: "rgba(224,82,82,.07)", border: ".5px solid rgba(224,82,82,.25)", borderRadius: "var(--r2)", padding: "1rem 1.25rem", marginBottom: "1.5rem" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--red)", marginBottom: ".6rem", display: "flex", alignItems: "center", gap: 6 }}>
              <Ic n="alert" s={14} /> {delinquent.length} seriously delinquent account{delinquent.length > 1 ? "s" : ""} — immediate action recommended
            </div>
            {delinquent.map(o => (
              <div key={o.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderTop: ".5px solid rgba(224,82,82,.15)" }}>
                <span>{o.name} — Unit {o.unit}</span><span style={{ color: "var(--red)", fontWeight: 600 }}>${o.duesOwed} owed</span>
              </div>
            ))}
          </div>
        )}
        <div className="card-grid">
          <div className="card">
            <div className="card-title">Recent activity</div>
            {[
              ...dues.filter(d=>d.status==="paid").slice(0,2).map(d=>({ c:"var(--teal)", t:`${d.owner_name||d.owner||"Owner"} (${d.unit}) paid dues`, ts: d.paid_date||"Recently" })),
              ...violations.slice(0,2).map(v=>({ c:"var(--red)", t:`New violation — Unit ${v.unit}, ${v.type}`, ts: v.created_at ? new Date(v.created_at).toLocaleDateString() : "Recently" })),
              ...emailLog.slice(0,2).map(e=>({ c:"var(--amber)", t:`Email sent to Unit ${e.unit||"—"}: ${e.subject||e.type||"Notice"}`, ts: e.sent_at ? new Date(e.sent_at).toLocaleDateString() : "Recently" })),
            ].filter(Boolean).slice(0,5).map((a, i) => (
              <div className="act-item" key={i}><div className="act-dot" style={{ background: a.c }} /><div><div className="act-text">{a.t}</div><div className="act-time">{a.ts}</div></div></div>
            ))}
            {dues.length === 0 && violations.length === 0 && emailLog.length === 0 && (
              <div style={{color:"var(--slate)",fontSize:13,padding:"1rem 0",textAlign:"center"}}>No activity yet — add homeowners to get started</div>
            )}
          </div>
          <div className="card">
            <div className="card-title">Email log (recent)</div>
            {emailLog.slice(0, 5).map(e => (
              <div className="act-item" key={e.id}>
                <div className="act-dot" style={{ background: e.type === "violation" ? "var(--red)" : e.type === "dues" ? "var(--amber)" : "var(--teal)" }} />
                <div style={{ flex: 1 }}>
                  <div className="act-text">{e.owner} <span style={{ color: "var(--slate)", fontSize: 11 }}>· {e.unit}</span></div>
                  <div className="act-time">{e.subject.slice(0, 44)}{e.subject.length > 44 ? "…" : ""}</div>
                </div>
                <span className="bg bg-g" style={{ fontSize: 10 }}>sent</span>
              </div>
            ))}
            <button className="btn-g btn-sm" style={{ marginTop: 10, width: "100%" }} onClick={() => setPage("email")}>View all emails →</button>
          </div>
        </div>
        <div className="card-title" style={{ marginBottom: ".75rem" }}>AI features — quick access</div>
        <div className="feat-grid">
          {[
            { icon: "📧", name: "Email automation", desc: "Send AI-written notices to opted-in owners", tag: "ft-auto", pg: "email" },
            { icon: "⚠️", name: "Violation letters", desc: "AI-drafted formal violation notices", tag: "ft-ai", pg: "violations" },
            { icon: "🧠", name: "Risk scoring", desc: "Identify delinquency risk proactively", tag: "ft-ai", pg: "ai" },
            { icon: "📋", name: "Agenda builder", desc: "Meeting agendas from live data", tag: "ft-ai", pg: "ai" },
            { icon: "📣", name: "Announcements", desc: "Bulk community emails in one click", tag: "ft-auto", pg: "email" },
            { icon: "💬", name: "FAQ assistant", desc: "Instant AI answers for owner questions", tag: "ft-ai", pg: "ai" },
          ].map(f => (
            <div className="feat-card" key={f.name} onClick={() => setPage(f.pg)}>
              <div style={{ fontSize: 20, marginBottom: 8 }}>{f.icon}</div>
              <span className={`feat-tag ${f.tag}`}>{f.tag === "ft-ai" ? "AI" : "Automated"}</span>
              <div className="feat-name">{f.name}</div>
              <div className="feat-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Owner Modal ──────────────────────────────────────────────────────────────
function OwnerModal({ owner, onSave, onClose, dues = [], orgName = "Your HOA", orgEmail = "" }) {
  const realOwed = useMemo(() => owner ? dues.filter(d => d.unit === owner.unit && d.status !== "paid").reduce((s, d) => s + (Number(d.amount) || 0), 0) : 0, [owner, dues]);
  const [f, setF] = useState(() => {
    if (!owner) return { name: "", unit: "", email: "", phone: "", moveIn: "", status: "active", duesOwed: 0, duesPaid: 0, notes: "", emailOptIn: true };
    const owed = owner ? dues.filter(d => d.unit === owner.unit && d.status !== "paid").reduce((s, d) => s + (Number(d.amount) || 0), 0) : 0;
    const derivedStatus = owed > 0 ? "delinquent" : "active";
    return { ...owner, duesOwed: owed, status: derivedStatus, moveIn: owner.move_in || owner.moveIn || "", emailOptIn: owner.email_opt_in ?? owner.emailOptIn ?? true };
  });
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  return (
    <div className="mo" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="md">
        <div className="mt2">{owner ? "Edit homeowner" : "Add homeowner"}</div>
        <div className="ms2">Unit {owner?.unit || "—"}</div>
        <div className="fr">
          <div className="fg"><label className="fl">Full name</label><input className="fi" value={f.name} onChange={e => s("name", e.target.value)} /></div>
          <div className="fg"><label className="fl">Unit #</label><input className="fi" value={f.unit} onChange={e => s("unit", e.target.value)} /></div>
        </div>
        <div className="fr">
          <div className="fg"><label className="fl">Email</label><input className="fi" type="email" value={f.email} onChange={e => s("email", e.target.value)} /></div>
          <div className="fg"><label className="fl">Phone</label><input className="fi" value={f.phone} onChange={e => s("phone", e.target.value)} /></div>
        </div>
        <div className="fr">
          <div className="fg"><label className="fl">Move-in date</label><input className="fi" type="date" value={f.moveIn} onChange={e => s("moveIn", e.target.value)} /></div>
          <div className="fg"><label className="fl">Status</label>
            <select className="fi" value={f.status} onChange={e => s("status", e.target.value)}>
              <option value="active">Active</option><option value="delinquent">Delinquent</option><option value="renter">Renter</option>
            </select>
          </div>
        </div>
        <div className="fr">
          <div className="fg"><label className="fl">Past due balance ($)</label><input className="fi" type="number" min="0" placeholder="0" value={f.duesOwed} onChange={e => s("duesOwed", Number(e.target.value))} /></div>
          <div className="fg"><label className="fl">Paid YTD ($)</label><input className="fi" type="number" value={f.duesPaid} onChange={e => s("duesPaid", Number(e.target.value))} /></div>
        </div>
        <div className="fg"><label className="fl">Notes</label><input className="fi" value={f.notes} onChange={e => s("notes", e.target.value)} placeholder="Internal notes…" /></div>
        <div className="fg" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input type="checkbox" id="optin" checked={f.emailOptIn} onChange={e => s("emailOptIn", e.target.checked)} style={{ accentColor: "var(--teal)", width: 15, height: 15 }} />
          <label htmlFor="optin" style={{ fontSize: 13, color: "var(--slate)", cursor: "pointer" }}>Owner has opted in to receive email communications</label>
        </div>
        <div className="ma"><button className="btn-g" onClick={onClose}>Cancel</button><button className="btn" onClick={() => { onSave(f); onClose(); }}>Save homeowner</button></div>
      </div>
    </div>
  );
}

// ─── Directory ────────────────────────────────────────────────────────────────
function Directory({ owners, setOwners, dues = [], setDues, toast , orgName = "Your HOA", orgEmail = "" }) {
  const { orgId } = useAppCtx();
  const [search, setSearch] = useState(""); const [modal, setModal] = useState(null);
  const searchLower = useMemo(() => search.toLowerCase(), [search]);
  const filtered    = useMemo(
    () => owners.filter(o => o.name.toLowerCase().includes(searchLower) || o.unit.includes(search)),
    [owners, search, searchLower]
  );
  const emailOptInCount  = useMemo(() => owners.filter(o => o.email_opt_in).length, [owners]);
  const totalOutstanding = useMemo(() => dues.filter(d => d.status !== 'paid').reduce((s, d) => s + (Number(d.amount) || 0), 0), [dues]);

  const [archived, setArchived] = useState([]);
  const [showArchive, setShowArchive] = useState(false);

  const loadArchived = useCallback(async () => {
    const { data } = await db.homeowners.listAll(orgId);
    setArchived((data || []).filter(o => o.deleted_at !== null));
  }, [orgId]);

  const deleteOwner = useCallback(async o => {
    if (!window.confirm(`Archive ${o.name} (Unit ${o.unit})? Their dues and violation records will be preserved for auditing. You can restore them from the Archive tab.`)) return;
    try {
      const { error } = await db.homeowners.save(orgId, { id: o.id, deleted_at: new Date().toISOString() });
      if (error) throw error;
      setOwners(p => p.filter(x => x.id !== o.id));
      setArchived(p => [{ ...o, deleted_at: new Date().toISOString() }, ...p]);
      toast("Homeowner archived — records preserved");
    } catch (e) { toast("Archive failed — " + e.message); }
  }, [orgId, setOwners, toast]);

  const restoreOwner = useCallback(async o => {
    try {
      const { error } = await db.homeowners.save(orgId, { id: o.id, deleted_at: null });
      if (error) throw error;
      setArchived(p => p.filter(x => x.id !== o.id));
      toast(`${o.name} restored — refresh the page to see them in the active directory`);
    } catch (e) { toast("Restore failed — " + e.message); }
  }, [orgId, toast]);

  const permanentDelete = useCallback(async o => {
    if (!window.confirm(`PERMANENTLY delete all records for ${o.name} (Unit ${o.unit})? This cannot be undone and will remove dues and violation history.`)) return;
    try {
      await supabase.from("dues").delete().eq("org_id", orgId).eq("unit", o.unit);
      await supabase.from("violations").delete().eq("org_id", orgId).eq("unit", o.unit);
      const { error } = await db.homeowners.delete(o.id);
      if (error) throw error;
      setArchived(p => p.filter(x => x.id !== o.id));
      toast("Permanently deleted");
    } catch (e) { toast("Delete failed — " + e.message); }
  }, [orgId, toast]);

  const save = useCallback(async f => {
    if (!f.unit?.trim()) { toast("Unit number is required"); return; }
    try {
      const isNew = !f.id;
      const payload = {
        name: f.name, unit: f.unit, email: f.email, phone: f.phone,
        status: f.status, dues_owed: f.duesOwed ?? f.dues_owed ?? 0,
        dues_paid: f.duesPaid ?? f.dues_paid ?? 0,
        notes: f.notes, email_opt_in: f.emailOptIn ?? f.email_opt_in ?? true,
        move_in: (f.moveIn ?? f.move_in) || null,
      };
      const { data, error } = await db.homeowners.save(orgId, f.id ? { ...payload, id: f.id } : payload);
      if (error) {
        if (error.code === "23505" || (error.message||"").includes("duplicate key") || (error.message||"").includes("unique constraint")) {
          toast(`Unit ${f.unit} already exists — please delete the existing record first or use a different unit number`);
          return;
        }
        throw error;
      }
      // If editing, update or create dues record
      if (f.id) {
        const newAmount = Number(f.duesOwed || f.dues_owed || 0);
        const newStatus = newAmount > 0 ? "unpaid" : "paid";
        const existingDue = dues?.find(d => d.unit === f.unit);
        if (existingDue?.id) {
          const { data: updatedDue } = await db.dues.save(orgId, { id: existingDue.id, amount: newAmount, status: newStatus });
          if (updatedDue) setDues(p => p.map(d => d.id === existingDue.id ? { ...d, amount: newAmount, status: newStatus } : d));
        } else if (newAmount > 0) {
          // No dues record exists — create one
          const firstOfNextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString().split("T")[0];
          const { data: newDue } = await db.dues.save(orgId, {
            homeowner_id: f.id, owner_name: f.name, unit: f.unit,
            amount: newAmount, due_date: firstOfNextMonth, status: "unpaid",
          });
          if (newDue) setDues(p => [newDue, ...p]);
        }
      }
      setOwners(p => f.id ? p.map(o => o.id === f.id ? data : o) : [...p, data]);
      // Auto-create a dues record for new homeowners
      if (isNew && data?.id) {
        const today = new Date();
        const firstOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1).toISOString().split("T")[0];
        const duesAmount = Number(f.duesOwed || f.dues_owed || 0);
        const { data: duesData, error: duesError } = await db.dues.save(orgId, {
          homeowner_id: data.id,
          owner_name: String(f.name || ""),
          unit: String(f.unit || ""),
          amount: duesAmount,
          due_date: firstOfNextMonth,
          status: duesAmount > 0 ? "unpaid" : "paid",
        });
        if (duesError) {
          console.error("Dues auto-create failed:", duesError);
          toast("Homeowner saved but dues record failed: " + (duesError.message || JSON.stringify(duesError)));
        } else if (duesData) {
          const normalized = { ...duesData, owner: duesData.owner_name ?? duesData.owner ?? f.name, owner_name: duesData.owner_name ?? f.name };
          setDues(p => [normalized, ...p]);
          toast("Homeowner saved");
        }
      } else {
        toast("Homeowner saved");
      }
    } catch (e) { toast("Save failed — " + e.message); }
  }, [orgId, setOwners, setDues, toast]);
  return (
    <div>
      <div className="ph">
        <div><div className="pt">Homeowner Directory</div><div className="ps">{owners.length} active units · {emailOptInCount} opted in to email</div></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className={!showArchive ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setShowArchive(false)}>Active</button>
          <button className={showArchive ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => { setShowArchive(true); loadArchived(); }}>
            Archive {archived.length > 0 && <span style={{ background: "var(--slate)", color: "#fff", borderRadius: 10, fontSize: 10, padding: "1px 6px", marginLeft: 4 }}>{archived.length}</span>}
          </button>
        </div>
      </div>
      <div className="pb">
        <div className="sr" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
          <div className="sc"><div className="sl">Total units</div><div className="sv">{owners.length}</div></div>
          <div className="sc"><div className="sl">Email opted-in</div><div className="sv t">{emailOptInCount}</div></div>
          <div className="sc"><div className="sl">Outstanding balance</div><div className="sv a">${totalOutstanding}</div></div>
        </div>
        <div className="tb">
          <input className="si" placeholder="Search name or unit…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn" onClick={() => setModal({})}>+ Add homeowner</button>
        </div>
        {!showArchive ? (
          <div className="tw">
            <table>
              <thead><tr><th>Unit</th><th>Owner</th><th>Email</th><th>Phone</th><th>Balance</th><th>Email comms</th><th>Status</th><th></th></tr></thead>
              <tbody>{filtered.map(o => (
                <tr key={o.id}>
                  <td><span style={{ fontWeight: 600, color: "var(--teal)" }}>{o.unit}</span></td>
                  <td><div style={{ fontWeight: 500 }}>{o.name}</div>{o.notes && <div style={{ fontSize: 11, color: "var(--slate)" }}>{o.notes}</div>}</td>
                  <td style={{ fontSize: 12, color: "var(--slate)" }}>{o.email}</td>
                  <td style={{ fontSize: 12, color: "var(--slate)" }}>{o.phone}</td>
                  <td style={{ fontWeight: 600, color: (() => { const unpaid = dues.filter(d => d.unit === o.unit && d.status !== "paid").reduce((s, d) => s + (Number(d.amount) || 0), 0); return unpaid > 0 ? "var(--red)" : "var(--green)"; })() }}>{(() => { const unpaid = dues.filter(d => d.unit === o.unit && d.status !== "paid").reduce((s, d) => s + (Number(d.amount) || 0), 0); return unpaid > 0 ? `$${unpaid.toLocaleString()}` : "Current"; })()}</td>
                  <td>{(o.emailOptIn || o.email_opt_in) ? <span className="bg bg-t">Opted in</span> : <span className="bg bg-s">Opted out</span>}</td>
                  <td>{(() => { const hasUnpaid = dues.some(d => d.unit === o.unit && d.status !== "paid" && Number(d.amount) > 0); const displayStatus = hasUnpaid ? (o.status || "active") : "active"; return <span className={`bg bg-${displayStatus === "active" ? "g" : displayStatus === "delinquent" ? "r" : "a"}`}>{displayStatus}</span>; })()}</td>
                  <td><div style={{ display: "flex", gap: 6 }}>
                    <button className="btn-g btn-sm" onClick={() => setModal(o)}>Edit</button>
                    <button className="btn-g btn-sm" style={{ color: "var(--red)" }} onClick={() => deleteOwner(o)}>Archive</button>
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : (
          <div>
            <div style={{ background: "rgba(99,102,241,.07)", border: ".5px solid rgba(99,102,241,.2)", borderRadius: "var(--r2)", padding: "12px 16px", marginBottom: "1rem", fontSize: 13, color: "var(--slate)" }}>
              <strong style={{ color: "var(--fg)" }}>Archived homeowners</strong> — dues and violation records are preserved for accounting and legal purposes. Restore to make active again, or purge to permanently delete all records.
            </div>
            {archived.length === 0
              ? <div style={{ textAlign: "center", padding: "3rem", color: "var(--slate)", fontSize: 14 }}>No archived homeowners</div>
              : <div className="tw"><table>
                  <thead><tr><th>Unit</th><th>Owner</th><th>Email</th><th>Archived</th><th>Last status</th><th></th></tr></thead>
                  <tbody>{archived.map(o => (
                    <tr key={o.id}>
                      <td><span style={{ fontWeight: 600, color: "var(--slate)" }}>{o.unit}</span></td>
                      <td><div style={{ fontWeight: 500 }}>{o.name}</div></td>
                      <td style={{ fontSize: 12, color: "var(--slate)" }}>{o.email}</td>
                      <td style={{ fontSize: 12, color: "var(--slate)" }}>{o.deleted_at ? new Date(o.deleted_at).toLocaleDateString() : "—"}</td>
                      <td>{(() => { const hasUnpaid = dues.some(d => d.unit === o.unit && d.status !== "paid" && Number(d.amount) > 0); const displayStatus = hasUnpaid ? (o.status || "active") : "active"; return <span className={`bg bg-${displayStatus === "active" ? "g" : displayStatus === "delinquent" ? "r" : "a"}`}>{displayStatus}</span>; })()}</td>
                      <td><div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-sm" style={{ background: "var(--teal)" }} onClick={() => restoreOwner(o)}>Restore</button>
                        <button className="btn-g btn-sm" style={{ color: "var(--red)" }} onClick={() => permanentDelete(o)}>Purge</button>
                      </div></td>
                    </tr>
                  ))}</tbody>
                </table></div>
            }
          </div>
        )}
      </div>
      {modal !== null && <OwnerModal owner={modal.id ? modal : null} onSave={save} onClose={() => setModal(null)} dues={dues} />}
    </div>
  );
}

// ─── Dues Ledger ──────────────────────────────────────────────────────────────
function DuesLedger({ dues, setDues, owners, setOwners, setEmailLog, toast , orgName = "Your HOA", orgEmail = "", stripeLink = "" }) {
  const { orgId } = useAppCtx();
  const [filter, setFilter] = useState("all"); const [search, setSearch] = useState("");
  const [emailModal, setEmailModal] = useState(null); const [aiText, setAiText] = useState(""); const [aiLoading, setAiLoading] = useState(false);

  const { totalC, totalO } = useMemo(() => dues.reduce(
    (acc, d) => { const amt = Number(d.amount) || 0; d.status === "paid" ? acc.totalC += amt : acc.totalO += amt; return acc; },
    { totalC: 0, totalO: 0 }
  ), [dues]);

  const searchLower = useMemo(() => search.toLowerCase(), [search]);
  const filtered = useMemo(
    () => dues.filter(d => (filter === "all" || d.status === filter) &&
      ((d.owner_name||d.owner||"").toLowerCase().includes(searchLower) || (d.unit||"").includes(search))),
    [dues, filter, search, searchLower]
  );

  const markPaid = useCallback(async id => {
    try {
      const dueRecord = dues.find(d => d.id === id);
      const { data, error } = await db.dues.markPaid(id);
      if (error) throw error;
      const record = Array.isArray(data) ? data[0] : data;
      if (!record) throw new Error("No data returned from payment update");
      const normalized = { ...record, owner: record.owner_name ?? record.owner ?? "", owner_name: record.owner_name ?? record.owner ?? "" };
      setDues(p => p.map(d => d.id === id ? normalized : d));
      toast("✓ Payment recorded");
    } catch (e) { toast("Update failed — " + e.message); }
  }, [orgId, dues, setDues, toast]);

  const openEmail = async due => {
    const owner = owners.find(o => o.unit === due.unit) || { name: due.owner, unit: due.unit, duesOwed: due.amount };
    setEmailModal({ due, owner }); setAiText(""); setAiLoading(true);
    try {
      const t = await callClaude(P.duesReminder(owner, due.status === "unpaid" ? 30 : 10));
      setAiText(t);
    } catch {
      toast("AI unavailable — try again in a moment");
    } finally {
      setAiLoading(false);
    }
  };

  const sendPaymentLink = async due => {
    const owner = owners.find(o => o.unit === due.unit);
    if (!owner?.email) { toast("No email on file for this owner"); return; }
    const amount = due.amount ? `$${Number(due.amount).toLocaleString()}` : "your dues balance";
    const subject = `Payment Request — ${orgName} HOA Dues (Unit ${due.unit})`;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body style="margin:0;padding:0;background:#f2f4f7;font-family:Inter,Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f2f4f7;padding:40px 20px;"><tr><td align="center"><table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;"><tr><td style="background:#1B3C78;border-radius:12px 12px 0 0;padding:24px 32px;"><span style="font-size:20px;font-weight:800;color:#ffffff;">🛡️ ${orgName}</span></td></tr><tr><td style="background:var(--navy3);padding:32px;color:#1B3C78;font-size:15px;line-height:1.7;"><p>Dear ${owner.name},</p><p>This is a payment request for your HOA dues for Unit ${due.unit}.</p><p><strong>Amount due: ${amount}</strong><br/>Due date: ${due.due_date || "Upon receipt"}</p><p>You can pay securely online using the button below:</p><p style="text-align:center;margin:32px 0;"><a href="${stripeLink}" style="background:#2A63D6;color:#ffffff;padding:16px 40px;border-radius:8px;text-decoration:none;font-weight:700;font-size:16px;display:inline-block;">💳 Pay Now</a></p><p style="font-size:13px;color:#6b7280;">Payments are processed securely via Stripe. If you have questions, please contact the board directly.</p></td></tr><tr><td style="background:#f9fafb;border-radius:0 0 12px 12px;padding:20px 32px;border-top:1px solid #e5e7eb;"><p style="margin:0;font-size:12px;color:#6b7280;">Sent via Board-Armor · <a href="https://board-armor.com" style="color:#2A63D6;text-decoration:none;">board-armor.com</a></p></td></tr></table></td></tr></table></body></html>`;
    try {
      const res = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: owner.email, subject, html, type: "payment_link" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Send failed");
      setEmailLog(p => [{ id: Date.now(), to: owner.email, owner_name: owner.name, unit: due.unit, subject, type: "payment_link", status: "delivered" }, ...p]);
      toast(`✓ Payment link sent to ${owner.email}`);
    } catch (e) { toast("Failed to send — " + e.message); }
  };

  const sendEmail = async () => {
    const { due, owner } = emailModal;
    const subj = (aiText.split("\n")[0] || "").replace(/^subject:\s*/i, "").replace(/^["']|["']$/g, "") || `Dues Notice — Unit ${due.unit}`;
    if (!owner?.email) { toast("No email address on file for this owner"); return; }
    try {
      const res = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: owner.email,
          subject: subj,
          text: aiText,
          type: "dues",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Send failed");
      setEmailLog(p => [{ id: Date.now(), to: owner.email, owner: due.owner, unit: due.unit, subject: subj, type: "dues", sent: new Date().toLocaleString(), status: "delivered" }, ...p]);
      toast(`✓ Email sent to ${owner.email}`);
      setEmailModal(null);
    } catch (err) {
      toast("Failed to send email — " + err.message);
    }
  };

  return (
    <div>
      <div className="ph"><div><div className="pt">Dues Ledger</div><div className="ps">{new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"})}</div></div></div>
      <div className="pb">
        <div className="sr">
          <div className="sc"><div className="sl">Billed</div><div className="sv">${dues.reduce((s, d) => s + (Number(d.amount) || 0), 0).toLocaleString()}</div></div>
          <div className="sc"><div className="sl">Collected</div><div className="sv t">${totalC}</div></div>
          <div className="sc"><div className="sl">Outstanding</div><div className="sv a">${totalO}</div></div>
          <div className="sc"><div className="sl">Collection rate</div><div className="sv t">{dues.length === 0 ? "—" : Math.round(totalC / Math.max(dues.reduce((s, d) => s + (Number(d.amount) || 0), 0), 1) * 100) + "%"}</div></div>
        </div>
        <div className="tb">
          <input className="si" placeholder="Search owner or unit…" value={search} onChange={e => setSearch(e.target.value)} />
          {["all", "paid", "partial", "unpaid"].map(f => <button key={f} className={filter === f ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setFilter(f)} style={{ textTransform: "capitalize" }}>{f}</button>)}
        </div>
        <div className="tw"><table>
          <thead><tr><th>Unit</th><th>Owner</th><th>Amount</th><th>Due date</th><th>Paid</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{filtered.map(d => (
            <tr key={d.id}>
              <td><span style={{ fontWeight: 600, color: "var(--teal)" }}>{d.unit || "—"}</span></td>
              <td style={{ fontWeight: 500 }}>{d.owner_name || d.owner || "—"}</td>
              <td style={{ fontWeight: 600, color: d.status === "paid" ? "var(--green)" : d.status === "partial" ? "var(--amber)" : "var(--red)" }}>${Number(d.amount || 0).toLocaleString()}</td>
              <td style={{ color: "var(--slate)", fontSize: 12 }}>{d.due_date || d.due || "—"}</td>
              <td style={{ color: "var(--slate)", fontSize: 12 }}>{d.paid_date || d.paid || "—"}</td>
              <td><span className={`bg bg-${d.status === "paid" ? "g" : d.status === "partial" ? "a" : "r"}`}>{d.status}</span></td>
              <td><div style={{ display: "flex", gap: 6 }}>
                {d.status !== "paid" && <button className="btn btn-sm" onClick={() => markPaid(d.id)}>Mark paid</button>}
                {d.status !== "paid" && stripeLink && <button className="btn-g btn-sm" style={{ color: "var(--teal)" }} onClick={() => sendPaymentLink(d)}>💳 Send payment link</button>}
                {d.status !== "paid" && (owners.find(o => o.unit === d.unit)?.emailOptIn || owners.find(o => o.unit === d.unit)?.email_opt_in) &&
                  <button className="btn-g btn-sm" style={{ color: "var(--teal)", display: "flex", alignItems: "center", gap: 4 }} onClick={() => openEmail(d)}><Ic n="mail" s={12} /> Email</button>}
              </div></td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
      {emailModal && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setEmailModal(null)}>
          <div className="md md-lg">
            <div className="mt2">AI-Drafted Dues Notice</div>
            <div className="ms2">Ready to send to {emailModal.owner?.email}</div>
            <AiBlock label="AI Dues Reminder" content={aiText} loading={aiLoading} />
            {aiText && !aiLoading && (
              <div className="ep">
                <div className="eh">
                  <div className="ef"><span className="ef-l">To:</span><span className="ef-v">{emailModal.owner?.email}</span></div>
                  <div className="ef"><span className="ef-l">From:</span><span className="ef-v">{orgEmail}</span></div>
                </div>
                <div className="eb">{aiText}</div>
              </div>
            )}
            <div className="ma">
              <button className="btn-g" onClick={() => setEmailModal(null)}>Cancel</button>
              <button className="btn-g" onClick={async () => { setAiLoading(true); setAiText(""); try { const t = await callClaude(P.duesReminder(emailModal.owner, 30)); setAiText(t); } catch { toast("Regeneration failed — try again"); } finally { setAiLoading(false); } }}>Regenerate</button>
              <button className="btn" onClick={sendEmail} disabled={!aiText || aiLoading}>Send email →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Violations ───────────────────────────────────────────────────────────────
function ViolationModal({ violation, owners, setEmailLog, onSave, onClose, toast , orgName = "Your HOA", orgEmail = "" }) {
  const { orgId } = useAppCtx();
  const [f, setF] = useState(violation || { unit: "", owner: "", type: "Noise", description: "", date: new Date().toISOString().split("T")[0], status: "open", severity: "medium", letter: "", emailSent: false });
  const [letter, setLetter] = useState(f.letter || ""); const [loading, setLoading] = useState(false);
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  const gen = async () => { const snapshot = { ...f }; setLoading(true); try { const t = await callClaude(P.violation(snapshot)); setLetter(t); setF(p => ({ ...p, letter: t })); } catch { toast("Letter generation failed — try again"); } finally { setLoading(false); } };
  const sendNow = async () => {
    const owner = owners.find(o => o.unit === f.unit);
    if ((!owner?.emailOptIn && !owner?.email_opt_in) || !letter) return;
    if (!owner?.email) { toast("No email on file for this owner"); return; }
    const subject = `HOA Violation Notice — ${f.type} (Unit ${f.unit})`;
    try {
      const res = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: owner.email, subject, text: letter, type: "violation" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Send failed");
      // Log to Supabase email_log
      try {
        const { data: logEntry } = await supabase.from("email_log").insert({
          org_id: orgId,
          to_email: owner.email, owner_name: f.owner, unit: f.unit,
          subject, type: "violation", status: "delivered",
        }).select().single();
        setEmailLog(p => [logEntry || { id: Date.now(), to_email: owner.email, owner_name: f.owner, unit: f.unit, subject, type: "violation", status: "delivered" }, ...p]);
      } catch (_) {
        setEmailLog(p => [{ id: Date.now(), to_email: owner.email, owner_name: f.owner, unit: f.unit, subject, type: "violation", status: "delivered" }, ...p]);
      }
      setF(p => ({ ...p, emailSent: true }));
      toast(`✓ Notice sent to ${owner.email}`);
    } catch (e) { toast("Failed to send — " + e.message); }
  };
  return (
    <div className="mo" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="md md-lg">
        <div className="mt2">{violation ? "Edit violation" : "Log violation"}</div>
        <div className="ms2">{orgName}</div>
        <div className="fr">
          <div className="fg"><label className="fl">Unit #</label><input className="fi" value={f.unit} onChange={e => s("unit", e.target.value)} /></div>
          <div className="fg"><label className="fl">Owner</label><input className="fi" value={f.owner} onChange={e => s("owner", e.target.value)} /></div>
        </div>
        <div className="fr">
          <div className="fg"><label className="fl">Type</label>
            <select className="fi" value={f.type} onChange={e => s("type", e.target.value)}>
              {["Noise", "Parking", "Trash", "Landscaping", "Pets", "Structural", "Other"].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="fg"><label className="fl">Severity</label>
            <select className="fi" value={f.severity} onChange={e => s("severity", e.target.value)}>
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
          </div>
        </div>
        <div className="fr">
          <div className="fg"><label className="fl">Date observed</label><input className="fi" type="date" value={f.date} onChange={e => s("date", e.target.value)} /></div>
          <div className="fg"><label className="fl">Status</label>
            <select className="fi" value={f.status} onChange={e => s("status", e.target.value)}>
              <option value="open">Open</option><option value="warning">Warning sent</option><option value="resolved">Resolved</option>
            </select>
          </div>
        </div>
        <div className="fg"><label className="fl">Description</label><input className="fi" value={f.description} onChange={e => s("description", e.target.value)} placeholder="Describe the violation…" /></div>
        <div className="aip">
          <div className="ail">{loading ? <span className="ai-spin" /> : <Ic n="zap" s={13} />}AI Violation Letter Writer</div>
          {!letter && !loading && <button className="btn btn-sm" onClick={gen} disabled={!f.description}>Generate formal letter</button>}
          {loading && <div className="ais">Drafting letter…</div>}
          {letter && !loading && (
            <>
              <div className="aio">{letter}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <button className="btn-g btn-sm" onClick={gen}>Regenerate</button>
                {owners.find(o => o.unit === f.unit)?.emailOptIn && !f.emailSent &&
                  <button className="btn btn-sm btn-pu" onClick={sendNow}><Ic n="mail" s={12} /> Send email now</button>}
                {f.emailSent && <span className="bg bg-t" style={{ padding: "5px 10px" }}>Email sent ✓</span>}
              </div>
            </>
          )}
        </div>
        <div className="ma"><button className="btn-g" onClick={onClose}>Cancel</button><button className="btn" onClick={async () => { await onSave({ ...f, letter }); onClose(); }}>Save violation</button></div>
      </div>
    </div>
  );
}

function Violations({ violations, setViolations, owners, setEmailLog, toast , orgName = "Your HOA", orgEmail = "" }) {
  const activeUnits = useMemo(() => new Set(owners.map(o => o.unit)), [owners]);
  const { orgId } = useAppCtx();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [modal, setModal] = useState(null);
  const filtered = violations.filter(v => activeUnits.has(v.unit) && (filter === "all" || v.status === filter) && ((v.owner_name||v.owner||"").toLowerCase().includes(search.toLowerCase()) || (v.unit||"").includes(search) || (v.type||"").toLowerCase().includes(search.toLowerCase())));

  const save = async f => {
    try {
      const payload = {
        unit: f.unit, owner_name: f.owner||f.owner_name, type: f.type,
        description: f.description, date_observed: f.date||f.date_observed,
        status: f.status, severity: f.severity,
        letter: f.letter, email_sent: f.emailSent??f.email_sent??false,
      };
      const { data, error } = await db.violations.save(orgId, f.id ? { ...payload, id: f.id } : payload);
      if (error) throw error;
      const normalized = { ...data, owner: data.owner_name ?? data.owner ?? "", owner_name: data.owner_name ?? data.owner ?? "", date: data.date_observed ?? data.date, emailSent: data.email_sent ?? false };
      setViolations(p => f.id ? p.map(v => v.id === f.id ? normalized : v) : [normalized, ...p]);
      toast("✓ Violation logged");
    } catch (e) { toast("Save failed — " + e.message); }
  };

  const resolve = async id => {
    try {
      const { data, error } = await db.violations.save(orgId, { id, status: "resolved" });
      if (error) throw error;
      setViolations(p => p.map(v => v.id === id ? data : v));
      toast("Resolved");
    } catch (e) { toast("Update failed — " + e.message); }
  };

  const sendEmailDirect = async v => {
    const owner = owners.find(o => o.unit === v.unit);
    if (!owner?.email_opt_in && !owner?.emailOptIn) { toast("Owner has not opted in to email notices — edit their record to enable email"); return; }
    if (!owner?.email) { toast("No email address on file for this owner"); return; }
    const subject = `HOA Violation Notice — ${v.type} (Unit ${v.unit})`;
    const body = v.letter || `Dear ${v.owner},\n\nThis notice is to inform you of a recorded violation at Unit ${v.unit}.\n\nViolation type: ${v.type}\nDate: ${v.date}\nDescription: ${v.description}\n\nPlease address this matter promptly. If you have questions, contact the HOA board.\n\nRegards,\nYour HOA Board`;
    try {
      const res = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: owner.email, subject, text: body, type: "violation" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Send failed");
      try {
        const { data: log } = await db.emailLog.add(orgId, {
          to_email: owner.email, owner_name: v.owner_name||v.owner, unit: v.unit,
          subject, type: "violation", status: "delivered",
        });
        setEmailLog(p => [log, ...p]);
      } catch (_) {}
      await db.violations.save(orgId, { id: v.id, email_sent: true });
      setViolations(p => p.map(x => x.id === v.id ? { ...x, email_sent: true } : x));
      toast(`✓ Violation notice sent to ${owner.email}`);
    } catch (e) { toast("Failed to send notice — " + e.message); }
  };
  return (
    <div>
      <div className="ph"><div><div className="pt">Violation Log</div><div className="ps">{violations.filter(v => v.status !== "resolved").length} open violations</div></div></div>
      <div className="pb">
        <div className="sr" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
          <div className="sc"><div className="sl">Open</div><div className="sv r">{violations.filter(v => v.status === "open").length}</div></div>
          <div className="sc"><div className="sl">Warning sent</div><div className="sv a">{violations.filter(v => v.status === "warning").length}</div></div>
          <div className="sc"><div className="sl">Resolved</div><div className="sv t">{violations.filter(v => v.status === "resolved").length}</div></div>
        </div>
        <div className="tb">
          <input className="si" placeholder="Search unit, owner, type…" value={search} onChange={e => setSearch(e.target.value)} />
          {["all", "open", "warning", "resolved"].map(f => <button key={f} className={filter === f ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setFilter(f)} style={{ textTransform: "capitalize" }}>{f}</button>)}
          <button className="btn" onClick={() => setModal({})}>+ Log violation</button>
        </div>
        <div className="tw"><table>
          <thead><tr><th>Unit</th><th>Owner</th><th>Type</th><th>Date</th><th>Severity</th><th>Status</th><th>Email</th><th>Actions</th></tr></thead>
          <tbody>{filtered.map(v => (
            <tr key={v.id}>
              <td><span style={{ fontWeight: 600, color: "var(--teal)" }}>{v.unit}</span></td>
              <td><div style={{ fontWeight: 500 }}>{v.owner}</div><div style={{ fontSize: 11, color: "var(--slate)" }}>{v.description.slice(0, 36)}{v.description.length > 36 ? "…" : ""}</div></td>
              <td style={{ fontSize: 12 }}>{v.type}</td>
              <td style={{ color: "var(--slate)", fontSize: 12 }}>{v.date}</td>
              <td><span style={{ fontSize: 12, color: v.severity === "high" ? "var(--red)" : v.severity === "medium" ? "var(--amber)" : "var(--slate)", fontWeight: 500, textTransform: "capitalize" }}>{v.severity}</span></td>
              <td><span className={`bg bg-${v.status === "resolved" ? "s" : v.status === "open" ? "r" : "a"}`}>{v.status}</span></td>
              <td>{v.emailSent ? <span className="bg bg-t" style={{ fontSize: 10 }}>Sent ✓</span> : <span className="bg bg-s" style={{ fontSize: 10 }}>Pending</span>}</td>
              <td><div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                <button className="btn-g btn-sm" onClick={() => setModal(v)}>Edit/AI</button>
                {v.status !== "resolved" && <button className="btn btn-sm" onClick={() => resolve(v.id)}>Resolve</button>}
                {!v.emailSent && v.letter && owners.find(o => o.unit === v.unit)?.emailOptIn &&
                  <button className="btn-g btn-sm" style={{ color: "var(--teal)", display: "flex", alignItems: "center", gap: 3 }} onClick={() => sendEmailDirect(v)}><Ic n="mail" s={11} /></button>}
              </div></td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
      {modal !== null && <ViolationModal violation={modal.id ? modal : null} owners={owners} setEmailLog={setEmailLog} onSave={save} onClose={() => setModal(null)} toast={toast} />}
    </div>
  );
}

// ─── Email Center ─────────────────────────────────────────────────────────────
function EmailCenter({ owners, emailLog, setEmailLog, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [tab, setTab] = useState("log");
  const [annTopic, setAnnTopic] = useState(""); const [annTone, setAnnTone] = useState("friendly");
  const [annText, setAnnText] = useState(""); const [annLoading, setAnnLoading] = useState(false);
  const [seqOwner, setSeqOwner] = useState(null); const [seqText, setSeqText] = useState(""); const [seqLoading, setSeqLoading] = useState(false);

  const optedIn = owners.filter(o => o.emailOptIn);
  const delinquent = owners.filter(o => o.duesOwed > 0 && o.emailOptIn);

  const genAnn = async () => {
    if (!annTopic) return; setAnnLoading(true);
    const t = await callClaude(P.announcement(annTopic, annTone));
    setAnnText(t); setAnnLoading(false);
  };

  const sendAnn = () => {
    const subj = (annText.split("\n")[0] || "").replace(/^subject:\s*/i, "").replace(/^["']|["']$/g, "") || annTopic;
    optedIn.forEach(o => setEmailLog(p => [{ id: Date.now() + Math.random(), to: o.email, owner: o.name, unit: o.unit, subject: subj, type: "announcement", sent: new Date().toLocaleString(), status: "delivered" }, ...p]));
    toast(`Announcement sent to ${optedIn.length} homeowners`); setAnnText(""); setAnnTopic("");
  };

  const genSeq = async o => {
    setSeqOwner(o); setSeqText(""); setSeqLoading(true);
    const t = await callClaude(P.sequence(o), 1100);
    setSeqText(t); setSeqLoading(false);
  };

  return (
    <div>
      <div className="ph"><div><div className="pt">Email Center</div><div className="ps">{emailLog.length} emails sent · {optedIn.length} opted-in homeowners</div></div></div>
      <div className="pb">
        <div className="sr" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
          <div className="sc"><div className="sl">Opted-in</div><div className="sv t">{optedIn.length}/{owners.length}</div></div>
          <div className="sc"><div className="sl">Total sent</div><div className="sv">{emailLog.length}</div></div>
          <div className="sc"><div className="sl">Delinquent w/ email</div><div className="sv a">{delinquent.length}</div></div>
        </div>
        <div className="tabs">
          {[["log", "Email log"], ["announce", "Announcements"], ["sequence", "Auto sequences"]].map(([k, l]) =>
            <div key={k} className={`tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>{l}</div>
          )}
        </div>

        {tab === "log" && (
          <div className="tw"><table>
            <thead><tr><th>To</th><th>Owner</th><th>Unit</th><th>Subject</th><th>Type</th><th>Sent</th><th>Status</th></tr></thead>
            <tbody>
              {emailLog.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--slate)", padding: "2rem" }}>No emails sent yet.</td></tr>}
              {emailLog.map(e => (
                <tr key={e.id}>
                  <td style={{ fontSize: 12, color: "var(--slate)" }}>{e.to}</td>
                  <td style={{ fontWeight: 500 }}>{e.owner}</td>
                  <td><span style={{ color: "var(--teal)", fontWeight: 600 }}>{e.unit}</span></td>
                  <td style={{ fontSize: 12 }}>{e.subject.slice(0, 46)}{e.subject.length > 46 ? "…" : ""}</td>
                  <td><span className={`bg bg-${e.type === "violation" ? "r" : e.type === "dues" ? "a" : "t"}`}>{e.type}</span></td>
                  <td style={{ fontSize: 11, color: "var(--slate)" }}>{e.sent}</td>
                  <td><span className="bg bg-g">{e.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}

        {tab === "announce" && (
          <div className="card">
            <div className="card-title">Community-wide announcement</div>
            <p style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1rem" }}>AI drafts a professional announcement emailed to all {optedIn.length} opted-in homeowners.</p>
            <div className="fg"><label className="fl">Announcement topic</label>
              <input className="fi" value={annTopic} onChange={e => setAnnTopic(e.target.value)} placeholder="e.g. Pool closure, Guest parking reminder, Summer BBQ event…" />
            </div>
            <div className="fg" style={{ marginBottom: "1rem" }}><label className="fl">Tone</label>
              <select className="fi" value={annTone} onChange={e => setAnnTone(e.target.value)} style={{ maxWidth: 220 }}>
                <option value="friendly">Friendly &amp; warm</option>
                <option value="professional">Professional &amp; formal</option>
                <option value="urgent">Urgent &amp; important</option>
                <option value="celebratory">Celebratory</option>
              </select>
            </div>
            <button className="btn" onClick={genAnn} disabled={!annTopic || annLoading}>{annLoading ? "Generating…" : "Generate announcement"}</button>
            {(annLoading || annText) && (
              <AiBlock label="AI Community Announcement" content={annText} loading={annLoading}>
                {annText && <>
                  <div className="ep" style={{ marginTop: 10 }}>
                    <div className="eh">
                      <div className="ef"><span className="ef-l">To:</span><span className="ef-v">All homeowners ({optedIn.length} recipients)</span></div>
                      <div className="ef"><span className="ef-l">From:</span><span className="ef-v">{orgEmail}</span></div>
                    </div>
                    <div className="eb">{annText}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="btn-g btn-sm" onClick={genAnn}>Regenerate</button>
                    <button className="btn btn-sm" onClick={sendAnn}>Send to all {optedIn.length} homeowners →</button>
                  </div>
                </>}
              </AiBlock>
            )}
          </div>
        )}

        {tab === "sequence" && (
          <div>
            <p style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1.25rem" }}>Generate a 3-step escalating follow-up email sequence for any delinquent homeowner. Tone escalates automatically from friendly → firm → legal demand.</p>
            {delinquent.length === 0 && <div style={{ textAlign: "center", color: "var(--slate)", padding: "2rem", fontSize: 13 }}>No delinquent opted-in homeowners found.</div>}
            {delinquent.map(o => (
              <div className="del-card" key={o.id}>
                <div className="del-info">
                  <div className="del-name">{o.name}</div>
                  <div className="del-sub">Unit {o.unit} · {o.email} · {o.notes || "No notes"}</div>
                </div>
                <span style={{ fontSize: 18, fontWeight: 600, color: "var(--red)" }}>${o.duesOwed}</span>
                <button className="btn btn-sm btn-pu" onClick={() => genSeq(o)}>Generate sequence</button>
              </div>
            ))}
            {(seqLoading || seqText) && seqOwner && (
              <div className="card" style={{ marginTop: "1rem" }}>
                <div className="card-title">3-step sequence — {seqOwner.name} (Unit {seqOwner.unit})</div>
                <div style={{ display: "flex", gap: 12, marginBottom: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="seq-dot sd1">1</div><span style={{ fontSize: 12, color: "var(--teal)" }}>Day 7 — Friendly</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="seq-dot sd2">2</div><span style={{ fontSize: 12, color: "var(--amber)" }}>Day 30 — Firm</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="seq-dot sd3">3</div><span style={{ fontSize: 12, color: "var(--red)" }}>Day 60 — Legal demand</span>
                  </div>
                </div>
                <AiBlock label="Generated sequence" content={seqText} loading={seqLoading}>
                  {seqText && <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => {
                    setEmailLog(p => [{ id: Date.now(), to: seqOwner.email, owner: seqOwner.name, unit: seqOwner.unit, subject: `Dues Follow-up Sequence — Unit ${seqOwner.unit}`, type: "dues", sent: new Date().toLocaleString(), status: "queued" }, ...p]);
                    toast(`Sequence queued for ${seqOwner.name}`);
                  }}>Queue sequence for {seqOwner.name} →</button>}
                </AiBlock>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI Intelligence Hub ──────────────────────────────────────────────────────
function AIHub({ owners, violations, dues, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [tab, setTab] = useState("risk");
  const [riskNotes, setRiskNotes] = useState({});
  const [riskLoading, setRiskLoading] = useState(null);
  const [agenda, setAgenda] = useState(""); const [agendaLoading, setAgendaLoading] = useState(false);
  const [faqQ, setFaqQ] = useState(""); const [faqA, setFaqA] = useState(""); const [faqLoading, setFaqLoading] = useState(false);

  const riskScore = o => o.duesOwed === 0 ? { s: 5, l: "low" } : o.duesOwed >= 600 ? { s: 88, l: "high" } : o.duesOwed >= 300 ? { s: 52, l: "medium" } : { s: 22, l: "low" };

  const analyzeOwner = async o => {
    setRiskLoading(o.id);
    try {
      const t = await callClaude(P.risk(o));
      setRiskNotes(p => ({ ...p, [o.id]: t }));
    } catch {
      toast("Risk analysis unavailable — try again");
    } finally {
      setRiskLoading(null);
    }
  };

  const genAgenda = async () => {
    setAgendaLoading(true);
    try {
      const openV = violations.filter(v => v.status !== "resolved").map(v => `${v.type} at Unit ${v.unit} (${v.severity})`).join("; ") || "None";
      const unpaid = dues.filter(d => d.status !== "paid").map(d => `Unit ${d.unit} owes $${d.amount}`).join("; ") || "All current";
      const t = await callClaude(P.agenda(openV, unpaid), 1200);
      setAgenda(t);
    } catch {
      toast("Agenda generation failed — try again");
    } finally {
      setAgendaLoading(false);
    }
  };

  const askFaq = async () => {
    if (!faqQ) return;
    setFaqLoading(true); setFaqA("");
    try {
      const t = await callClaude(P.faq(faqQ));
      setFaqA(t);
    } catch {
      toast("FAQ assistant unavailable — try again");
    } finally {
      setFaqLoading(false);
    }
  };

  const patternMap = violations.reduce((acc, v) => { acc[v.unit] = (acc[v.unit] || []); acc[v.unit].push(v.type); return acc; }, {});
  const repeaters = Object.entries(patternMap).filter(([, types]) => types.length >= 2).map(([unit, types]) => ({ unit, count: types.length, types, owner: violations.find(v => v.unit === unit)?.owner }));

  return (
    <div>
      <div className="ph"><div><div className="pt">AI Intelligence Hub</div><div className="ps">Risk scoring · Pattern detection · Agenda builder · FAQ assistant</div></div></div>
      <div className="pb">
        <div className="tabs">
          {[["risk", "Risk scoring"], ["patterns", "Violation patterns"], ["agenda", "Agenda builder"], ["faq", "FAQ assistant"]].map(([k, l]) =>
            <div key={k} className={`tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>{l}</div>
          )}
        </div>

        {tab === "risk" && (
          <div>
            <p style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1.25rem" }}>AI-scored payment risk per homeowner. Click "AI analyze" for a personalized assessment and recommended board action.</p>
            {owners.map(o => {
              const { s, l } = riskScore(o);
              return (
                <div className="del-card" key={o.id} style={{ flexDirection: "column", alignItems: "stretch", gap: ".75rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <div style={{ flex: 1 }}>
                      <div className="del-name">{o.name} <span style={{ color: "var(--slate)", fontWeight: 400, fontSize: 12 }}>· Unit {o.unit}</span></div>
                      <div className="del-sub">Owed: ${o.duesOwed} · Paid YTD: ${o.duesPaid} · {o.notes || "No notes"}</div>
                    </div>
                    <span className={`bg bg-${l === "low" ? "g" : l === "medium" ? "a" : "r"}`} style={{ fontSize: 12, padding: "3px 10px" }}>{l} risk</span>
                    <button className="btn-g btn-sm" onClick={() => analyzeOwner(o)} disabled={riskLoading === o.id}>{riskLoading === o.id ? "Analyzing…" : "AI analyze"}</button>
                  </div>
                  <div className="risk-bar-wrap">
                    <div className="risk-bar"><div className={`risk-fill risk-${l}`} style={{ width: `${s}%` }} /></div>
                    <span style={{ fontSize: 12, color: "var(--slate)", minWidth: 30 }}>{s}%</span>
                  </div>
                  {riskNotes[o.id] && (
                    <div style={{ background: "var(--navy3)", borderRadius: "var(--r)", padding: ".75rem", fontSize: 13, color: "var(--ice)", lineHeight: 1.6 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--purple)", letterSpacing: ".05em", textTransform: "uppercase", display: "block", marginBottom: 4 }}>AI Assessment</span>
                      {riskNotes[o.id]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "patterns" && (
          <div>
            <p style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1.25rem" }}>AI flags units with repeat violations or escalating behavior requiring board escalation.</p>
            {repeaters.length === 0 ? <div style={{ textAlign: "center", color: "var(--slate)", padding: "2rem", fontSize: 13 }}>No repeat violation patterns detected.</div>
              : repeaters.map(({ unit, count, types, owner }) => (
                <div className="del-card" key={unit} style={{ borderLeft: "2px solid var(--red)" }}>
                  <div className="del-info">
                    <div className="del-name">Unit {unit} — {owner}</div>
                    <div className="del-sub">{count} violations: {types.join(", ")}</div>
                  </div>
                  <span className="bg bg-r">{count} violations</span>
                  <span className="bg bg-p" style={{ marginLeft: 6 }}>Escalation recommended</span>
                </div>
              ))}
            <div style={{ marginTop: "1.5rem" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: ".75rem" }}>Violation frequency by unit</div>
              {Object.entries(patternMap).sort((a, b) => b[1].length - a[1].length).map(([unit, types]) => (
                <div key={unit} style={{ display: "flex", alignItems: "center", gap: "1rem", padding: ".6rem 0", borderBottom: ".5px solid var(--border)" }}>
                  <span style={{ fontWeight: 600, color: "var(--teal)", minWidth: 56 }}>Unit {unit}</span>
                  <div style={{ flex: 1, background: "var(--navy3)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                    <div style={{ width: `${(types.length / Math.max(...Object.values(patternMap).map(t => t.length))) * 100}%`, height: "100%", background: types.length >= 3 ? "var(--red)" : types.length >= 2 ? "var(--amber)" : "var(--teal)", borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 12, color: "var(--slate)", minWidth: 80 }}>{types.length} violation{types.length > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "agenda" && (
          <div>
            <p style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1.25rem" }}>AI reads your live data — open violations, outstanding dues, maintenance items — and generates a complete formal board meeting agenda.</p>
            <div className="card" style={{ marginBottom: "1rem" }}>
              <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: ".75rem" }}>AI will pull from your current data:</div>
              <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", fontSize: 13 }}>
                <span><span style={{ color: "var(--red)", fontWeight: 600 }}>{violations.filter(v => v.status !== "resolved").length}</span> open violations</span>
                <span><span style={{ color: "var(--amber)", fontWeight: 600 }}>{dues.filter(d => d.status !== "paid").length}</span> unpaid dues</span>
                <span><span style={{ color: "var(--teal)", fontWeight: 600 }}>1</span> maintenance topic</span>
              </div>
            </div>
            <button className="btn" onClick={genAgenda} disabled={agendaLoading}>{agendaLoading ? "Building agenda…" : "Generate board meeting agenda"}</button>
            {(agendaLoading || agenda) && (
              <AiBlock label="AI Board Meeting Agenda" content={agenda} loading={agendaLoading}>
                {agenda && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="btn-g btn-sm" onClick={genAgenda}>Regenerate</button>
                    <button className="btn btn-sm" onClick={() => { navigator.clipboard?.writeText(agenda); toast("Agenda copied!"); }}>Copy agenda</button>
                  </div>
                )}
              </AiBlock>
            )}
          </div>
        )}

        {tab === "faq" && (
          <div>
            <p style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1.25rem" }}>Use AI to instantly answer common homeowner questions. Great for board members responding to emails or walk-up questions.</p>
            <div className="card">
              <div className="card-title">Quick answer common questions</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1rem" }}>
                {["Can I install a fence?", "What are quiet hours?", "How do I dispute a violation?", "Can I Airbnb my unit?", "When are dues due?", "Can I have a pet?"].map(q => (
                  <button key={q} className="btn-g btn-sm" onClick={() => { setFaqQ(q); setFaqA(""); }}>{q}</button>
                ))}
              </div>
              <div className="fg">
                <label className="fl">Homeowner question</label>
                <input className="fi" value={faqQ} onChange={e => setFaqQ(e.target.value)} placeholder="Type any homeowner question…" onKeyDown={e => e.key === "Enter" && askFaq()} />
              </div>
              <button className="btn btn-sm" onClick={askFaq} disabled={!faqQ || faqLoading} style={{ marginTop: 4 }}>{faqLoading ? "Looking up…" : "Get AI answer"}</button>
              {(faqLoading || faqA) && <AiBlock label="AI Answer" content={faqA} loading={faqLoading} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Flyer Generator ──────────────────────────────────────────────────────────
const FLYER_TYPES = [
  { id: "event", label: "Community Event", icon: "🎉", color: "#dc2626", accent: "#ffffff" },
  { id: "notice", label: "Important Notice", icon: "📢", color: "#dc2626", accent: "#fff" },
  { id: "maintenance", label: "Maintenance", icon: "🔧", color: "#f59e0b", accent: "#ffffff" },
  { id: "meeting", label: "Board Meeting", icon: "📋", color: "#a78bfa", accent: "#fff" },
  { id: "safety", label: "Safety Alert", icon: "🚨", color: "#dc2626", accent: "#fff" },
  { id: "seasonal", label: "Seasonal / HOA News", icon: "🌿", color: "#3abf7e", accent: "#fff" },
];

const COLOR_SCHEMES = [
  { id: "navy", name: "Navy", bg: "#ffffff", header: "#e8eaed", text: "#ffffff", accent: "#dc2626" },
  { id: "crisp", name: "Crisp", bg: "#f8f9fa", header: "#1a1a2e", text: "#1a1a2e", accent: "#dc2626" },
  { id: "warm", name: "Warm", bg: "#1c1208", header: "#2d1f0a", text: "#fef3e2", accent: "#f59e0b" },
  { id: "slate", name: "Slate", bg: "#1e2433", header: "#252d40", text: "#e8edf5", accent: "#a78bfa" },
  { id: "forest", name: "Forest", bg: "#0d1f14", header: "#122a1c", text: "#e8f5ee", accent: "#3abf7e" },
  { id: "rose", name: "Rose", bg: "#1f0d12", header: "#2d1118", text: "#fdeef2", accent: "#f472b6" },
  { id: "pro", name: "Pro White", bg: "#ffffff", header: "#e8eaed", text: "#e8eaed", accent: "#dc2626" },
  { id: "midnight", name: "Midnight", bg: "#09090f", header: "#111122", text: "#e8edf5", accent: "#818cf8" },
];

// Build a Picsum URL seeded on the topic for deterministic placeholder images
function getImageUrl(topic, w = 600, h = 280) {
  const seed = Math.abs([...topic].reduce((s, c) => s + c.charCodeAt(0), 0)) % 1000;
  return `https://picsum.photos/seed/${seed}/${w}/${h}`;
}

// AI generates flyer structured content
async function generateFlyerContent(formData) {
  const prompt = `You are a professional HOA communications designer. Generate flyer content for a homeowners association communication.

Flyer type: ${formData.type}
Title/subject: ${formData.title}
Key details: ${formData.details}
Date/time: ${formData.datetime || "Not specified"}
Location: ${formData.location || "Not specified"}
Contact: ${formData.contact || "HOA Board"}
Tone: ${formData.tone}

Return ONLY a JSON object (no markdown, no backticks) with exactly these fields:
{
  "headline": "Short punchy headline (max 8 words)",
  "subheadline": "Supporting subtitle (max 12 words)",
  "body": "Main body paragraph (2-3 sentences, warm and clear)",
  "bullets": ["key point 1", "key point 2", "key point 3"],
  "callToAction": "CTA button text (3-5 words)",
  "imagePrompt": "Detailed description for an image that would complement this flyer (describe a real-world photo scene, not illustration)",
  "footerNote": "Short footer disclaimer or contact note (1 sentence)",
  "urgency": "none|low|medium|high"
}`;

  const txt = await callClaude(prompt, 800);
  try {
    const clean = txt.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
  } catch {
    return {
      headline: formData.title || "Community Notice",
      subheadline: `From the ${getOrgName()} HOA Board`,
      body: formData.details || "Please see the details below regarding this important community communication.",
      bullets: ["Check dates and times carefully", "Contact the board with questions", "Share with all household members"],
      callToAction: "Learn More",
      imagePrompt: formData.title,
      footerNote: `${getOrgName()} HOA · ${getOrgEmail()}`,
      urgency: "low",
    };
  }
}

// Render the actual flyer as HTML string injected into an iframe-like div
function FlyerCanvas({ content, scheme, flyerType, imageUrl, generating , orgName = "Your HOA", orgEmail = "" }) {
  if (!content && !generating) return null;
  const cs = COLOR_SCHEMES.find(c => c.id === scheme) || COLOR_SCHEMES[0];
  const ft = FLYER_TYPES.find(t => t.id === flyerType) || FLYER_TYPES[0];
  const isDark = ["navy", "warm", "slate", "forest", "rose", "midnight"].includes(scheme);

  const urgencyBg = { none: "transparent", low: "#1d4731", medium: "#7c3d10", high: "#7c1010" };
  const urgencyColor = { none: "transparent", low: "#3abf7e", medium: "#f59e0b", high: "#dc2626" };
  const urgencyText = { none: "", low: "For your information", medium: "Action may be required", high: "URGENT — Immediate attention required" };

  return (
    <div className="flyer-canvas" style={{ background: cs.bg, fontFamily: "'Inter', Georgia, serif" }}>
      {/* Header band */}
      <div style={{ background: cs.header, padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: cs.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
            {ft.icon}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: cs.accent, fontFamily: "'Inter', sans-serif", letterSpacing: ".06em", textTransform: "uppercase" }}>{orgName}</div>
            <div style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,.35)" : "rgba(255,255,255,.45)", fontFamily: "'Inter', sans-serif", marginTop: 1 }}>{ft.label}</div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,.4)" : "rgba(255,255,255,.4)", fontFamily: "'Inter', sans-serif" }}>
          {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </div>
      </div>

      {/* Hero image */}
      {generating ? (
        <div style={{ width: "100%", height: 240, background: cs.header, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 }}>
          <div style={{ width: 36, height: 36, border: `3px solid ${cs.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin .7s linear infinite" }} />
          <div style={{ fontSize: 12, color: isDark ? "rgba(255,255,255,.4)" : "rgba(255,255,255,.4)", fontFamily: "'Inter', sans-serif" }}>Generating image…</div>
        </div>
      ) : (
        <div style={{ position: "relative", width: "100%", height: 240, overflow: "hidden" }}>
          <img src={imageUrl} alt="flyer visual" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to bottom, transparent 40%, ${cs.bg}ee)` }} />
          {content?.urgency && content.urgency !== "none" && (
            <div style={{ position: "absolute", top: 12, right: 12, background: urgencyBg[content.urgency], border: `1px solid ${urgencyColor[content.urgency]}`, borderRadius: 20, padding: "4px 12px", fontSize: 10, fontWeight: 700, color: urgencyColor[content.urgency], fontFamily: "'Inter', sans-serif", letterSpacing: ".05em", textTransform: "uppercase" }}>
              {urgencyText[content.urgency]}
            </div>
          )}
        </div>
      )}

      {/* Body content */}
      <div style={{ padding: "28px 36px 24px" }}>
        {generating ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[80, 50, 100, 60, 90].map((w, i) => (
              <div key={i} style={{ height: i === 0 ? 32 : i === 1 ? 20 : 14, width: `${w}%`, background: isDark ? "rgba(255,255,255,.08)" : "rgba(255,255,255,.08)", borderRadius: 6 }} />
            ))}
          </div>
        ) : content ? (
          <>
            <div style={{ fontSize: 34, fontWeight: 700, color: cs.text, lineHeight: 1.15, marginBottom: 10, fontFamily: "'Inter', Georgia, serif" }}>
              {content.headline}
            </div>
            <div style={{ fontSize: 16, color: cs.accent, marginBottom: 18, fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
              {content.subheadline}
            </div>
            <div style={{ width: 48, height: 3, background: cs.accent, borderRadius: 2, marginBottom: 18 }} />
            <p style={{ fontSize: 14, color: isDark ? "rgba(255,255,255,.78)" : "rgba(255,255,255,.72)", lineHeight: 1.7, marginBottom: 20, fontFamily: "'Inter', sans-serif" }}>
              {content.body}
            </p>
            {content.bullets?.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                {content.bullets.map((b, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: cs.accent, flexShrink: 0, marginTop: 6 }} />
                    <div style={{ fontSize: 13, color: isDark ? "rgba(255,255,255,.7)" : "rgba(255,255,255,.68)", fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>{b}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "inline-block", background: cs.accent, color: ["crisp", "warm", "pro"].includes(scheme) ? "#ffffff" : cs.header, padding: "11px 28px", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "'Inter', sans-serif", letterSpacing: ".04em" }}>
              {content.callToAction}
            </div>
          </>
        ) : null}
      </div>

      {/* Footer */}
      <div style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,.08)" : "rgba(255,255,255,.08)"}`, padding: "14px 36px", display: "flex", alignItems: "center", justifyContent: "space-between", background: cs.header }}>
        <div style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,.4)" : "rgba(255,255,255,.4)", fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
          {content?.footerNote || `${orgName} HOA · ${orgEmail}`}
        </div>
        <div style={{ fontSize: 11, color: cs.accent, fontFamily: "'Inter', serif", fontWeight: 700 }}>Board-Armor</div>
      </div>
    </div>
  );
}

function FlyerGenerator({ toast , orgName = "Your HOA", orgEmail = "" }) {
  const [flyerType, setFlyerType] = useState("event");
  const [scheme, setScheme] = useState("navy");
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [datetime, setDatetime] = useState("");
  const [location, setLocation] = useState("");
  const [contact, setContact] = useState("");
  const [tone, setTone] = useState("friendly");
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState("");
  const [history, setHistory] = useState([]);
  const flyerRef = useRef(null);

  const canGenerate = title.trim().length > 2;

  const generate = async () => {
    if (!canGenerate || generating) return;
    setGenerating(true); setContent(null); setImageUrl(null); setProgress(10);
    setStatusMsg("AI is writing your flyer copy…");

    const formData = { type: flyerType, title, details, datetime, location, contact, tone };
    const generated = await generateFlyerContent(formData);
    setContent(generated); setProgress(55); setStatusMsg("Sourcing a matching image…");

    // Use Picsum seeded on the image prompt for a relevant-looking placeholder
    const img = getImageUrl(generated.imagePrompt || title);
    setImageUrl(img); setProgress(85); setStatusMsg("Composing final layout…");

    await new Promise(r => setTimeout(r, 600));
    setProgress(100); setStatusMsg("");
    setHistory(p => [{ id: Date.now(), title, type: flyerType, scheme, content: generated, imageUrl: img }, ...p.slice(0, 7)]);
    setGenerating(false);
    toast("Flyer generated!");
  };

  const regenerateImage = () => {
    const seed = Date.now() % 1000;
    setImageUrl(`https://picsum.photos/seed/${seed}/600/280`);
    toast("New image loaded");
  };

  const printFlyer = () => {
    const flyerEl = flyerRef.current;
    if (!flyerEl) return;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${title}</title><style>body{margin:0;display:flex;justify-content:center;padding:20px;background:#888}@media print{body{padding:0;background:none}}</style><link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet"></head><body>${flyerEl.outerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 800);
  };

  const loadHistoryItem = (item) => {
    setContent(item.content); setImageUrl(item.imageUrl);
    setFlyerType(item.type); setScheme(item.scheme); setTitle(item.title);
  };

  return (
    <div>
      <div className="ph">
        <div><div className="pt">AI Flyer Generator</div><div className="ps">Create professional HOA communications with AI-generated copy and imagery</div></div>
      </div>
      <div className="flyer-layout">
        {/* Controls panel */}
        <div className="flyer-controls">

          <div className="flyer-section-label">Flyer type</div>
          <div className="flyer-type-grid">
            {FLYER_TYPES.map(ft => (
              <button key={ft.id} className={`flyer-type-btn ${flyerType === ft.id ? "active" : ""}`} onClick={() => setFlyerType(ft.id)}>
                <span className="flyer-type-icon">{ft.icon}</span>
                <span className="flyer-type-name">{ft.label}</span>
              </button>
            ))}
          </div>

          <div className="flyer-section-label">Content</div>
          <input className="fi-flyer" placeholder="Flyer title / subject *" value={title} onChange={e => setTitle(e.target.value)} />
          <textarea className="fi-flyer" placeholder="Key details, what homeowners need to know…" value={details} onChange={e => setDetails(e.target.value)} rows={3} />
          <input className="fi-flyer" placeholder="Date & time (optional)" value={datetime} onChange={e => setDatetime(e.target.value)} />
          <input className="fi-flyer" placeholder="Location / venue (optional)" value={location} onChange={e => setLocation(e.target.value)} />
          <input className="fi-flyer" placeholder="Contact info (optional)" value={contact} onChange={e => setContact(e.target.value)} />

          <div className="flyer-section-label">Tone</div>
          <select className="fi-flyer" value={tone} onChange={e => setTone(e.target.value)}>
            <option value="friendly">Friendly &amp; warm</option>
            <option value="professional">Professional &amp; formal</option>
            <option value="urgent">Urgent &amp; direct</option>
            <option value="celebratory">Celebratory</option>
            <option value="informational">Informational</option>
          </select>

          <div className="flyer-section-label">Color scheme</div>
          <div className="color-scheme-grid">
            {COLOR_SCHEMES.map(cs => (
              <div key={cs.id} title={cs.name} className={`cs-btn ${scheme === cs.id ? "active" : ""}`}
                style={{ background: `linear-gradient(135deg, ${cs.bg} 50%, ${cs.accent})` }}
                onClick={() => setScheme(cs.id)}>
                <span />
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: ".5rem" }}>
            Selected: <span style={{ color: "var(--white)" }}>{COLOR_SCHEMES.find(c => c.id === scheme)?.name}</span>
          </div>

          {progress > 0 && progress < 100 && (
            <div>
              <div className="gen-progress"><div className="gen-progress-fill" style={{ width: `${progress}%` }} /></div>
              <div className="img-gen-status">{statusMsg}</div>
            </div>
          )}

          <button className="gen-btn" onClick={generate} disabled={!canGenerate || generating}>
            {generating ? "✦ Generating…" : "✦ Generate Flyer"}
          </button>

          {content && (
            <>
              <button className="gen-btn-sec" onClick={generate} disabled={generating}>↺ Regenerate copy</button>
              <button className="gen-btn-sec" onClick={regenerateImage} disabled={generating}>↺ Try different image</button>
              <button className="gen-btn-sec" onClick={printFlyer}>⎙ Print / Save as PDF</button>
            </>
          )}

          {history.length > 0 && (
            <>
              <div className="flyer-section-label" style={{ marginTop: "1.25rem" }}>Recent flyers</div>
              {history.map(h => (
                <div key={h.id} onClick={() => loadHistoryItem(h)} style={{ padding: ".5rem .75rem", borderRadius: "var(--r)", border: ".5px solid var(--border2)", marginBottom: 6, cursor: "pointer", transition: "border-color .15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(42,99,214,.30)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,.13)"}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "var(--white)" }}>{h.title}</div>
                  <div style={{ fontSize: 10, color: "var(--slate)", marginTop: 2 }}>{FLYER_TYPES.find(f => f.id === h.type)?.label} · {COLOR_SCHEMES.find(c => c.id === h.scheme)?.name}</div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Preview pane */}
        <div className="flyer-preview-pane">
          {!content && !generating ? (
            <div className="flyer-empty">
              <div style={{ fontSize: 52, marginBottom: ".5rem" }}>✦</div>
              <div style={{ fontSize: 16, fontWeight: 500, color: "var(--white)" }}>Your flyer will appear here</div>
              <div style={{ fontSize: 13, color: "var(--slate)", maxWidth: 300, lineHeight: 1.6 }}>Fill in the details on the left and click Generate Flyer. AI will write professional copy and source a matching image automatically.</div>
              <div style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: 8, width: "100%", maxWidth: 400 }}>
                {[
                  { icon: "✦", label: "AI writes headline, body, and call-to-action" },
                  { icon: "🖼", label: "Contextual image sourced to match your topic" },
                  { icon: "🎨", label: "8 professional color schemes to choose from" },
                  { icon: "⎙", label: "Print or save as PDF directly from the browser" },
                ].map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", background: "rgba(255,255,255,.04)", borderRadius: "var(--r)", padding: ".65rem 1rem" }}>
                    <span style={{ fontSize: 16 }}>{f.icon}</span>
                    <span style={{ fontSize: 13, color: "var(--slate)" }}>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div ref={flyerRef}>
                <FlyerCanvas content={content} scheme={scheme} flyerType={flyerType} imageUrl={imageUrl} generating={generating} />
              </div>
              {content && !generating && (
                <div className="flyer-dl-bar">
                  <div className="flyer-dl-btn" onClick={printFlyer} style={{ cursor: "pointer" }}>⎙ Print / PDF</div>
                  <div className="flyer-dl-btn" onClick={() => { navigator.clipboard?.writeText(
                    `${content.headline}\n\n${content.subheadline}\n\n${content.body}\n\n${content.bullets?.join("\n")}\n\n${content.footerNote}`
                  ); toast("Copy copied to clipboard"); }} style={{ cursor: "pointer" }}>⎋ Copy text</div>
                  <div className="flyer-dl-btn" onClick={regenerateImage} style={{ cursor: "pointer" }}>↺ New image</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Legal Advisor ────────────────────────────────────────────────────────────
const LEGAL_SYSTEM = `You are an experienced HOA legal advisor and paralegal assistant embedded in a homeowners association management platform. You provide practical, actionable legal guidance specifically for HOA boards and property managers.

Your expertise covers:
- HOA governance: CC&Rs, bylaws, rules enforcement, board authority and fiduciary duties
- Collections and liens: dues enforcement, delinquency procedures, assessment liens, foreclosure thresholds
- Fair Housing Act and disability accommodations (reasonable modifications, reasonable accommodations)
- Neighbor disputes, nuisance violations, and enforcement procedures
- Open meeting laws, executive session rules, board election procedures
- State-specific HOA statutes (you know major states: CA Davis-Stirling Act, FL HOA Act Ch. 720, TX PCA, AZ Planned Communities Act, NV NRS 116, WA HOA Act, etc.)
- Contract review guidance for vendors, landscaping, management companies
- Discrimination claims and protected class issues
- Records inspection rights of homeowners
- Alternative dispute resolution: mediation, arbitration clauses
- Landlord-tenant law as it intersects with HOA governance: tenant due process, unit owner responsibility for tenant violations, HOA authority over tenants, lease addendum requirements
- Rental property management law: lease violations, eviction procedures (unlawful detainer), security deposit rules, habitability standards, notice requirements for entry
- State-specific landlord-tenant statutes: NV NRS 118A, OR ORS 90, CA Civil Code 1940+, FL Chapter 83, TX Property Code 91-92, AZ ARS 33-1301, WA RCW 59.18
- Tenant due process rights within HOA communities: proper notice, hearing rights, board authority limits over non-owner occupants
- Unit owner liability for tenant conduct in HOA communities

IMPORTANT RULES:
1. Always include a brief disclaimer at the end of substantive legal guidance that this is general information and they should consult a licensed HOA attorney for their specific situation.
2. Be specific and practical — give the board actual next steps, not vague platitudes.
3. When a situation is clearly urgent or high-risk (foreclosure, discrimination claim, litigation threat), escalate clearly and recommend immediate attorney consultation.
4. Reference relevant statutes, code sections, or legal standards when you know them.
5. Structure longer answers with clear sections when helpful.
6. Never encourage illegal actions or advise the board to violate fair housing laws.
7. If asked about a specific state, apply that state's HOA law. If state is unknown, give general guidance and note that state law varies.`;

const LEGAL_TOPICS = [
  { id: "collections", name: "Dues & collections", sub: "Liens, delinquency, foreclosure", icon: "💰", chips: ["Can we put a lien on a unit?", "When can we foreclose for unpaid dues?", "How do we set up a payment plan?", "What's the process for sending a formal demand?"] },
  { id: "enforcement", name: "Violation enforcement", sub: "Fines, hearings, due process", icon: "⚖️", chips: ["Can we fine without a hearing?", "What's proper notice for a violation hearing?", "Owner ignoring violations — what are our options?", "Can we tow unauthorized vehicles?"] },
  { id: "fair-housing", name: "Fair Housing & ADA", sub: "Accommodations, discrimination", icon: "🏛️", chips: ["Owner requesting a service animal exception", "What is a reasonable accommodation?", "Can we deny a modification request?", "Someone claims we discriminated — what now?"] },
  { id: "governance", name: "Board governance", sub: "Meetings, elections, authority", icon: "📜", chips: ["Can we hold a meeting without a quorum?", "When must we go into executive session?", "Board member wants to resign mid-term", "Owner wants to recall the board"] },
  { id: "disputes", name: "Neighbor disputes", sub: "Nuisance, noise, harassment", icon: "🤝", chips: ["Two owners in a property line dispute", "Owner harassing board members", "Noise complaint that won't resolve", "Dog bite incident in common area"] },
  { id: "vendors", name: "Contracts & vendors", sub: "Agreements, liability, insurance", icon: "📋", chips: ["What should a vendor contract include?", "Contractor damaged common area — who pays?", "Can we terminate a management company?", "What insurance must vendors carry?"] },
  { id: "records", name: "Records & disclosure", sub: "Homeowner inspection rights", icon: "📁", chips: ["Owner demanding to see all financials", "Do we have to share meeting minutes?", "Can we charge for records requests?", "What records can we keep private?"] },
  { id: "emergency", name: "Urgent / litigation", sub: "Lawsuits, threats, cease & desist", icon: "🚨", chips: ["We received a lawsuit summons", "Owner sent a cease and desist letter", "Board member threatening to sue", "Criminal activity in common area"] },
  { id: "landlord-tenant", name: "Landlord-Tenant Law", sub: "Renters, leases, evictions, due process", icon: "🏢", chips: ["What due process does a tenant have in our HOA?", "Owner's tenant is violating CC&Rs — what can we do?", "Can we evict a tenant directly as the HOA?", "What notice is required before entering a rental unit?", "Tenant claims uninhabitable conditions — board liability?", "Security deposit dispute between owner and tenant", "How do lease addendum requirements work in an HOA?", "Tenant harassing neighbors — board options"] },
];

function LegalAdvisor({ orgName = "Your HOA", orgEmail = "" }) {
  const [activeTopic, setActiveTopic] = useState(null);
  const [conversations, setConversations] = useState({});
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const msgs = activeTopic ? (conversations[activeTopic.id] || []) : [];

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  const sendMessage = async (text) => {
    const q = (text || input).trim();
    if (!q || !activeTopic || loading) return;
    setInput("");

    const userMsg = { role: "user", content: q };
    const updatedMsgs = [...msgs, userMsg];
    setConversations(p => ({ ...p, [activeTopic.id]: updatedMsgs }));
    setLoading(true);
    setTimeout(scrollToBottom, 50);

    try {
      const apiMsgs = updatedMsgs.map(m => ({ role: m.role, content: m.content }));
      const reply = await callClaudeChat(apiMsgs, LEGAL_SYSTEM + `\n\nCurrent topic area: ${activeTopic.name} — ${activeTopic.sub}`, 1800);
      const assistantMsg = { role: "assistant", content: reply };
      setConversations(p => ({ ...p, [activeTopic.id]: [...updatedMsgs, assistantMsg] }));
    } catch (err) {
      // Show error message instead of rolling back — keeps conversation visible
      const errMsg = { role: "assistant", content: "⚠️ Sorry, I couldn't get a response. Please try again in a moment." };
      setConversations(p => ({ ...p, [activeTopic.id]: [...updatedMsgs, errMsg] }));
      console.error("Legal AI error:", err);
    } finally {
      setLoading(false);
      setTimeout(scrollToBottom, 50);
    }
  };

  const clearTopic = () => {
    if (!activeTopic) return;
    setConversations(p => ({ ...p, [activeTopic.id]: [] }));
  };

  const selectTopic = (topic) => {
    setActiveTopic(topic);
    setInput("");
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">AI Legal Advisor</div>
          <div className="ps">HOA legal guidance · Governance · Fair Housing · Collections · Disputes</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, background: "rgba(245,166,35,.12)", color: "var(--amber)", border: ".5px solid rgba(245,166,35,.3)", borderRadius: 20, padding: "3px 10px", fontWeight: 600 }}>
            ⚠ General guidance only — not legal advice
          </span>
        </div>
      </div>
      <div className="legal-wrap">
        {/* Sidebar — topic list */}
        <div className="legal-sidebar">
          <div className="legal-sidebar-top">
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 }}>Legal topics</div>
            <div style={{ fontSize: 12, color: "var(--slate)", lineHeight: 1.5 }}>Select a topic to start a conversation. Each topic maintains its own history.</div>
          </div>
          <div className="legal-topics">
            {LEGAL_TOPICS.map(t => {
              const topicMsgs = conversations[t.id] || [];
              const hasHistory = topicMsgs.length > 0;
              return (
                <div key={t.id} className={`legal-topic ${activeTopic?.id === t.id ? "active" : ""}`} onClick={() => selectTopic(t)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{t.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div className="legal-topic-name">{t.name}</div>
                      <div className="legal-topic-sub">{t.sub}</div>
                    </div>
                    {hasHistory && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--amber)", flexShrink: 0 }} />}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding: ".75rem 1rem", borderTop: ".5px solid var(--border)", fontSize: 11, color: "var(--slate)", lineHeight: 1.6 }}>
            This tool provides general HOA legal guidance. Always consult a licensed attorney for specific legal decisions.
          </div>
        </div>

        {/* Chat panel */}
        <div className="legal-chat">
          {!activeTopic ? (
            <div className="legal-empty">
              <div className="legal-empty-icon">
                <Ic n="scale" s={22} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 500, color: "var(--white)" }}>Select a legal topic</div>
              <div style={{ fontSize: 13, color: "var(--slate)", maxWidth: 320 }}>Choose a topic from the left panel to start getting AI-powered HOA legal guidance. Each topic keeps its own conversation history.</div>
              <div style={{ marginTop: "1.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, maxWidth: 480 }}>
                {LEGAL_TOPICS.slice(0, 4).map(t => (
                  <div key={t.id} onClick={() => selectTopic(t)} style={{ background: "var(--navy2)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: ".85rem", cursor: "pointer", transition: "border-color .15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.4)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,.13)"}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{t.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)" }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 2 }}>{t.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="legal-chat-header">
                <div className="legal-chat-icon">
                  <span style={{ fontSize: 18 }}>{activeTopic.icon}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="legal-chat-title">{activeTopic.name}</div>
                  <div className="legal-chat-sub">{activeTopic.sub}</div>
                </div>
                {msgs.length > 0 && (
                  <button onClick={clearTopic} style={{ background: "transparent", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "5px 10px", fontSize: 12, color: "var(--slate)", cursor: "pointer", fontFamily: "var(--sans)" }}>
                    Clear history
                  </button>
                )}
              </div>

              <div className="legal-messages">
                {msgs.length === 0 && (
                  <div style={{ padding: "1rem 0" }}>
                    <div className="legal-category-pill">
                      <Ic n="scale" s={12} /> {activeTopic.name}
                    </div>
                    <div style={{ fontSize: 14, color: "var(--white)", fontWeight: 500, marginBottom: ".5rem" }}>
                      Ask any {activeTopic.name.toLowerCase()} question
                    </div>
                    <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1rem" }}>
                      Get practical HOA legal guidance on {activeTopic.sub.toLowerCase()}. Or start with one of these common questions:
                    </div>
                    <div className="legal-chips">
                      {activeTopic.chips.map(c => (
                        <div key={c} className="legal-chip" onClick={() => sendMessage(c)}>{c}</div>
                      ))}
                    </div>
                  </div>
                )}

                {msgs.map((m, i) => (
                  <div key={i} className={`legal-msg ${m.role}`}>
                    <div className={`legal-avatar ${m.role === "assistant" ? "bot" : "user"}`}>
                      {m.role === "assistant" ? "⚖" : "B"}
                    </div>
                    <div>
                      <div className={`legal-bubble ${m.role === "assistant" ? "bot" : "user"}`}>
                        {m.content}
                      </div>
                      {m.role === "assistant" && (
                        <div className="legal-disclaimer">
                          This is general legal information for educational purposes, not legal advice. Consult a licensed HOA attorney before taking legal action.
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="legal-msg">
                    <div className="legal-avatar bot">⚖</div>
                    <div className="legal-bubble bot">
                      <div className="legal-thinking">
                        <div className="legal-dot" /><div className="legal-dot" /><div className="legal-dot" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="legal-input-row">
                <textarea
                  ref={inputRef}
                  className="legal-input"
                  placeholder={`Ask about ${activeTopic.name.toLowerCase()}…`}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  rows={1}
                />
                <button className="legal-send" onClick={() => sendMessage()} disabled={!input.trim() || loading}>
                  {loading ? "…" : "Send →"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Vendor & Work Order Tracker ─────────────────────────────────────────────
function VendorTracker({ workOrders, setWorkOrders, vendors, setVendors, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [tab, setTab] = useState("workorders");
  const [selectedWO, setSelectedWO] = useState(null);
  const [woFilter, setWoFilter] = useState("all");
  const [woModal, setWoModal] = useState(false);
  const [vendorModal, setVendorModal] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [editVendor, setEditVendor] = useState(null);

  // New work order form state
  const [woForm, setWoForm] = useState({ title: "", category: "Plumbing", description: "", priority: "medium", vendorId: "", unit: "Common", scheduled: "" });
  const wos = woFilter === "all" ? workOrders : workOrders.filter(w => w.status === woFilter);

  // New vendor form
  const [vForm, setVForm] = useState({ name: "", category: "Plumbing", contact: "", phone: "", email: "", licensed: true, insured: true, notes: "" });

  const priorityColor = p => p === "high" ? "var(--red)" : p === "medium" ? "var(--amber)" : "var(--slate)";
  const statusColor   = s => s === "resolved" || s === "completed" ? "var(--green)" : s === "in-progress" ? "var(--amber)" : "var(--red)";
  const statusBg      = s => s === "resolved" || s === "completed" ? "var(--green2)" : s === "in-progress" ? "var(--amber2)" : "var(--red2)";

  const totalSpend = workOrders.reduce((s, w) => s + (w.actualCost || w.estimatedCost || 0), 0);
  const openWOs    = workOrders.filter(w => w.status !== "resolved" && w.status !== "completed").length;
  const pendingInv = workOrders.flatMap(w => w.invoices).filter(i => i.status === "pending").length;

  const getAiEstimate = async (wo) => {
    setAiLoading(wo.id);
    const vendorName = vendors.find(v => v.id === wo.vendorId)?.name || "unknown vendor";
    const prompt = `You are an HOA facilities manager with deep knowledge of contractor pricing in the US Pacific Northwest. Provide a cost estimate for this maintenance job:

Job: ${wo.title}
Category: ${wo.category}
Description: ${wo.description}
Location: ${wo.unit === "Common" ? "Common area" : `Unit ${wo.unit}`}
Vendor type: ${vendorName}

Return ONLY a JSON object (no markdown):
{
  "low": 150,
  "high": 350,
  "typical": 220,
  "laborHours": "2-3 hours",
  "breakdown": "Labor: $120-180 (2-3 hrs @ $60/hr). Parts/materials: $30-170. Service call fee: $0-50.",
  "notes": "One sentence of practical advice for the board.",
  "urgency": "Schedule within 1-2 weeks"
}`;
    try {
      const txt = await callClaude(prompt, 500);
      const clean = txt.replace(/```json|```/g, "").trim();
      const est = JSON.parse(clean);
      setWorkOrders(prev => prev.map(w => w.id === wo.id ? { ...w, aiEstimate: est } : w));
      if (selectedWO?.id === wo.id) setSelectedWO(prev => ({ ...prev, aiEstimate: est }));
    } catch { toast("Estimate error — try again"); }
    setAiLoading(null);
  };

  const createWO = () => {
    const newWO = {
      id: Date.now(), ...woForm, vendorId: woForm.vendorId ? Number(woForm.vendorId) : null,
      created: new Date().toISOString().split("T")[0], completed: null,
      estimatedCost: null, actualCost: null, invoices: [], aiEstimate: null,
      timeline: [
        { label: "Work order created", date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }), done: true },
        { label: woForm.vendorId ? "Vendor assigned" : "Vendor assignment needed", date: woForm.vendorId ? "Today" : "—", done: !!woForm.vendorId, active: !woForm.vendorId },
        { label: "Work scheduled", date: woForm.scheduled || "—", done: false, active: !!woForm.vendorId },
        { label: "Completed", date: "—", done: false },
      ],
    };
    setWorkOrders(p => [newWO, ...p]);
    setWoModal(false); setSelectedWO(newWO);
    setWoForm({ title: "", category: "Plumbing", description: "", priority: "medium", vendorId: "", unit: "Common", scheduled: "" });
    toast("Work order created");
  };

  const assignVendor = (woId, vendorId) => {
    setWorkOrders(p => p.map(w => w.id === woId ? {
      ...w, vendorId,
      timeline: w.timeline.map((t, i) => i === 1 ? { ...t, done: true, active: false, date: "Today" } : i === 2 ? { ...t, active: true } : t)
    } : w));
    if (selectedWO?.id === woId) setSelectedWO(p => ({ ...p, vendorId, timeline: p.timeline.map((t, i) => i === 1 ? { ...t, done: true, active: false, date: "Today" } : i === 2 ? { ...t, active: true } : t) }));
    toast("Vendor assigned");
  };

  const updateStatus = (woId, status) => {
    const completed = (status === "resolved" || status === "completed") ? new Date().toISOString().split("T")[0] : null;
    setWorkOrders(p => p.map(w => w.id === woId ? { ...w, status, completed,
      timeline: w.timeline.map((t, i) => i === w.timeline.length - 1 ? { ...t, done: !!completed, date: completed ? new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—" } : t)
    } : w));
    if (selectedWO?.id === woId) setSelectedWO(p => ({ ...p, status, completed }));
    toast(`Status updated to ${status}`);
  };

  const addInvoice = (woId, name, amount) => {
    const inv = { id: Date.now(), name, amount: Number(amount), status: "pending" };
    setWorkOrders(p => p.map(w => w.id === woId ? { ...w, invoices: [...w.invoices, inv] } : w));
    if (selectedWO?.id === woId) setSelectedWO(p => ({ ...p, invoices: [...p.invoices, inv] }));
    toast("Invoice added");
  };

  const markInvoicePaid = (woId, invId) => {
    setWorkOrders(p => p.map(w => w.id === woId ? { ...w, invoices: w.invoices.map(i => i.id === invId ? { ...i, status: "paid" } : i) } : w));
    if (selectedWO?.id === woId) setSelectedWO(p => ({ ...p, invoices: p.invoices.map(i => i.id === invId ? { ...i, status: "paid" } : i) }));
    toast("Invoice marked paid");
  };

  const saveVendor = (f) => {
    if (f.id) { setVendors(p => p.map(v => v.id === f.id ? f : v)); toast("Vendor updated"); }
    else { setVendors(p => [...p, { ...f, id: Date.now(), jobs: 0, totalSpend: 0, rating: 4 }]); toast("Vendor added"); }
    setVendorModal(false); setEditVendor(null);
  };

  const Stars = ({ n }) => "★★★★★".split("").map((s, i) => <span key={i} className="star" style={{ color: i < n ? "var(--amber)" : "var(--border2)" }}>{s}</span>);

  // Invoice adder inline state
  const [invName, setInvName] = useState(""); const [invAmt, setInvAmt] = useState("");

  return (
    <div>
      <div className="ph">
        <div><div className="pt">Vendor & Work Order Tracker</div>
          <div className="ps">{openWOs} open work orders · ${totalSpend.toLocaleString()} total spend tracked · {vendors.length} vendors</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-g btn-sm" onClick={() => { setEditVendor(null); setVendorModal(true); }}>+ Add vendor</button>
          <button className="btn btn-sm" onClick={() => setWoModal(true)}>+ New work order</button>
        </div>
      </div>

      <div className="pb">
        {/* Stats */}
        <div className="sr">
          <div className="sc"><div className="sl">Open orders</div><div className="sv r">{openWOs}</div></div>
          <div className="sc"><div className="sl">In progress</div><div className="sv a">{workOrders.filter(w => w.status === "in-progress").length}</div></div>
          <div className="sc"><div className="sl">Pending invoices</div><div className="sv amber">{pendingInv}</div></div>
          <div className="sc"><div className="sl">Total spend</div><div className="sv t">${totalSpend.toLocaleString()}</div></div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          {[["workorders", "Work Orders"], ["vendors", "Vendor Directory"], ["spending", "Spend Summary"]].map(([k, l]) =>
            <div key={k} className={`tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>{l}</div>
          )}
        </div>

        {/* ── Work Orders tab ── */}
        {tab === "workorders" && (
          <div style={{ display: "grid", gridTemplateColumns: selectedWO ? "320px 1fr" : "1fr", gap: 16 }}>
            {/* WO list */}
            <div>
              <div className="tb" style={{ marginBottom: "1rem" }}>
                {["all", "open", "in-progress", "resolved"].map(f =>
                  <button key={f} className={woFilter === f ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setWoFilter(f)} style={{ textTransform: "capitalize" }}>{f}</button>
                )}
              </div>
              {wos.length === 0 && <div className="op-empty">No work orders found.</div>}
              {wos.map(wo => {
                const vendor = vendors.find(v => v.id === wo.vendorId);
                return (
                  <div key={wo.id} className={`wo-item ${selectedWO?.id === wo.id ? "selected" : ""}`} onClick={() => setSelectedWO(wo)}>
                    <div className="wo-priority-bar" style={{ background: priorityColor(wo.priority) }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="wo-title">{wo.title}</div>
                      <div className="wo-meta">
                        {wo.category} · {wo.unit === "Common" ? "Common area" : `Unit ${wo.unit}`}<br />
                        {vendor ? vendor.name : <span style={{ color: "var(--red)" }}>No vendor assigned</span>}
                      </div>
                      {wo.scheduled && <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 3 }}>Scheduled {wo.scheduled}</div>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                      <span className="wo-status-pill" style={{ background: statusBg(wo.status), color: statusColor(wo.status) }}>{wo.status}</span>
                      {(wo.estimatedCost || wo.actualCost) && <span style={{ fontSize: 12, color: "var(--slate)" }}>${wo.actualCost || wo.estimatedCost}</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* WO detail pane */}
            {selectedWO && (() => {
              const wo = workOrders.find(w => w.id === selectedWO.id) || selectedWO;
              const vendor = vendors.find(v => v.id === wo.vendorId);
              return (
                <div className="wo-detail-pane">
                  <div className="wo-detail-header">
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--white)", marginBottom: 4 }}>{wo.title}</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <span className="wo-status-pill" style={{ background: statusBg(wo.status), color: statusColor(wo.status) }}>{wo.status}</span>
                          <span style={{ fontSize: 11, color: priorityColor(wo.priority), fontWeight: 600, textTransform: "capitalize" }}>{wo.priority} priority</span>
                          <span style={{ fontSize: 11, color: "var(--slate)" }}>{wo.category}</span>
                        </div>
                      </div>
                      <button onClick={() => setSelectedWO(null)} style={{ background: "transparent", border: "none", color: "var(--slate)", cursor: "pointer", fontSize: 16, padding: 4 }}>✕</button>
                    </div>
                  </div>
                  <div className="wo-detail-body">
                    {/* Description */}
                    <div className="wo-field" style={{ marginBottom: "1.25rem" }}>
                      <div className="wo-field-label">Description</div>
                      <div className="wo-field-val">{wo.description}</div>
                    </div>

                    <div className="wo-field-row">
                      <div className="wo-field"><div className="wo-field-label">Location</div><div className="wo-field-val">{wo.unit === "Common" ? "Common area" : `Unit ${wo.unit}`}</div></div>
                      <div className="wo-field"><div className="wo-field-label">Created</div><div className="wo-field-val">{wo.created}</div></div>
                      <div className="wo-field"><div className="wo-field-label">Scheduled</div><div className="wo-field-val muted">{wo.scheduled || "Not scheduled"}</div></div>
                      <div className="wo-field"><div className="wo-field-label">Completed</div><div className="wo-field-val muted">{wo.completed || "—"}</div></div>
                    </div>

                    <div className="wo-section-divider" />

                    {/* Status controls */}
                    <div style={{ marginBottom: "1.25rem" }}>
                      <div className="wo-field-label" style={{ marginBottom: 8 }}>Update status</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {["open", "in-progress", "resolved"].map(s => (
                          <button key={s} className={wo.status === s ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => updateStatus(wo.id, s)} style={{ textTransform: "capitalize" }}>{s}</button>
                        ))}
                      </div>
                    </div>

                    {/* Vendor assignment */}
                    <div style={{ marginBottom: "1.25rem" }}>
                      <div className="wo-field-label" style={{ marginBottom: 8 }}>Assigned vendor</div>
                      {vendor ? (
                        <div className="vendor-card selected" style={{ cursor: "default" }}>
                          <div className="vendor-avatar" style={{ background: "var(--teal3)", color: "var(--teal)" }}>{vendor.name.slice(0, 2).toUpperCase()}</div>
                          <div style={{ flex: 1 }}>
                            <div className="vendor-name">{vendor.name}</div>
                            <div className="vendor-meta">{vendor.contact} · {vendor.phone}</div>
                            <div className="vendor-rating"><Stars n={vendor.rating} /></div>
                          </div>
                          <button className="btn-g btn-sm" onClick={() => assignVendor(wo.id, null)} style={{ fontSize: 11 }}>Change</button>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: 8 }}>Select a vendor to assign:</div>
                          {vendors.filter(v => v.category === wo.category || true).slice(0, 3).map(v => (
                            <div key={v.id} className="vendor-card" onClick={() => assignVendor(wo.id, v.id)}>
                              <div className="vendor-avatar" style={{ background: "var(--navy4)", color: "var(--slate)" }}>{v.name.slice(0, 2).toUpperCase()}</div>
                              <div style={{ flex: 1 }}>
                                <div className="vendor-name">{v.name}</div>
                                <div className="vendor-meta">{v.category} · {v.jobs} jobs</div>
                                <div className="vendor-rating"><Stars n={v.rating} /></div>
                              </div>
                              <span style={{ fontSize: 11, color: "var(--teal)" }}>Assign →</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* AI Cost Estimate */}
                    <div style={{ marginBottom: "1.25rem" }}>
                      <div className="wo-field-label" style={{ marginBottom: 8 }}>AI cost estimate</div>
                      {!wo.aiEstimate ? (
                        <button className="btn-g btn-sm" style={{ color: "var(--purple)", borderColor: "rgba(167,139,250,.3)" }}
                          onClick={() => getAiEstimate(wo)} disabled={aiLoading === wo.id}>
                          {aiLoading === wo.id ? <><span className="ai-spin" style={{ width: 11, height: 11, borderColor: "var(--purple2)", borderTopColor: "var(--purple)" }} /> Analyzing…</> : "✦ Get AI cost estimate"}
                        </button>
                      ) : (
                        <div className="cost-est-box">
                          <div className="cost-est-label"><Ic n="zap" s={11} />AI Estimate</div>
                          <div className="cost-range">
                            <span className="cost-low">${wo.aiEstimate.low}</span>
                            <span className="cost-separator">–</span>
                            <span className="cost-high">${wo.aiEstimate.high}</span>
                            <span style={{ fontSize: 12, color: "var(--slate)", marginLeft: 6 }}>typical ${wo.aiEstimate.typical}</span>
                          </div>
                          <div className="cost-breakdown">{wo.aiEstimate.breakdown}</div>
                          {wo.aiEstimate.notes && <div style={{ fontSize: 12, color: "var(--purple)", marginTop: 6, fontStyle: "italic" }}>{wo.aiEstimate.notes}</div>}
                          {wo.aiEstimate.urgency && <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 4 }}>⏱ {wo.aiEstimate.urgency}</div>}
                        </div>
                      )}
                    </div>

                    {/* Invoices */}
                    <div style={{ marginBottom: "1.25rem" }}>
                      <div className="wo-field-label" style={{ marginBottom: 8 }}>Invoices</div>
                      {wo.invoices.length === 0 && <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: 8 }}>No invoices attached yet.</div>}
                      {wo.invoices.map(inv => (
                        <div className="invoice-row" key={inv.id}>
                          <div className="invoice-icon"><span style={{ fontSize: 14 }}>🧾</span></div>
                          <div className="invoice-name">{inv.name}</div>
                          <span className="invoice-amount">${inv.amount}</span>
                          <span className={`invoice-status ${inv.status === "paid" ? "inv-paid" : "inv-pending"}`}>{inv.status}</span>
                          {inv.status === "pending" && <button className="btn btn-sm" onClick={() => markInvoicePaid(wo.id, inv.id)}>Mark paid</button>}
                        </div>
                      ))}
                      {/* Add invoice */}
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <input className="fi" placeholder="Invoice description" value={invName} onChange={e => setInvName(e.target.value)} style={{ flex: 2, padding: "7px 10px", fontSize: 12 }} />
                        <input className="fi" placeholder="$amount" type="number" value={invAmt} onChange={e => setInvAmt(e.target.value)} style={{ flex: 1, padding: "7px 10px", fontSize: 12, maxWidth: 90 }} />
                        <button className="btn btn-sm" onClick={() => { if (invName && invAmt) { addInvoice(wo.id, invName, invAmt); setInvName(""); setInvAmt(""); } }} disabled={!invName || !invAmt}>Add</button>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div>
                      <div className="wo-field-label" style={{ marginBottom: 10 }}>Progress timeline</div>
                      <div className="wo-timeline">
                        {wo.timeline.map((t, i) => (
                          <div key={i} className="wo-tl-item">
                            <div className={`wo-tl-dot ${t.done ? "done" : t.active ? "active" : ""}`} />
                            <div className="wo-tl-text" style={{ color: t.done ? "var(--white)" : t.active ? "var(--amber)" : "var(--slate)" }}>{t.label}</div>
                            <div className="wo-tl-time">{t.date}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Vendor Directory tab ── */}
        {tab === "vendors" && (
          <div>
            <div className="feat-grid">
              {vendors.map(v => (
                <div key={v.id} style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.1rem 1.25rem" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                    <div className="vendor-avatar" style={{ background: "var(--teal3)", color: "var(--teal)", width: 40, height: 40 }}>{v.name.slice(0, 2).toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--white)" }}>{v.name}</div>
                      <div style={{ fontSize: 12, color: "var(--slate)", marginTop: 2 }}>{v.category} · {v.contact}</div>
                      <div className="vendor-rating" style={{ marginTop: 4 }}>{"★★★★★".split("").map((s, i) => <span key={i} style={{ fontSize: 12, color: i < v.rating ? "var(--amber)" : "var(--border2)" }}>{s}</span>)}</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                    <div style={{ background: "var(--navy3)", borderRadius: "var(--r)", padding: ".5rem .75rem" }}>
                      <div style={{ fontSize: 10, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".05em" }}>Jobs done</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: "var(--white)" }}>{v.jobs}</div>
                    </div>
                    <div style={{ background: "var(--navy3)", borderRadius: "var(--r)", padding: ".5rem .75rem" }}>
                      <div style={{ fontSize: 10, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".05em" }}>Total spend</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: "var(--teal)" }}>${v.totalSpend.toLocaleString()}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: v.licensed ? "var(--green2)" : "var(--red2)", color: v.licensed ? "var(--green)" : "var(--red)", fontWeight: 600 }}>
                      {v.licensed ? "Licensed" : "Not licensed"}
                    </span>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: v.insured ? "var(--green2)" : "var(--red2)", color: v.insured ? "var(--green)" : "var(--red)", fontWeight: 600 }}>
                      {v.insured ? "Insured" : "Not insured"}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--slate)", lineHeight: 1.5, marginBottom: 12 }}>{v.notes}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <div style={{ fontSize: 12, color: "var(--slate)", flex: 1 }}>{v.phone}</div>
                    <button className="btn-g btn-sm" onClick={() => { setEditVendor(v); setVendorModal(true); }}>Edit</button>
                    <button className="btn btn-sm" onClick={() => { setWoModal(true); setWoForm(p => ({ ...p, vendorId: v.id })); }}>New WO</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Spend Summary tab ── */}
        {tab === "spending" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div className="vt-card">
                <div className="vt-card-title">Spend by category</div>
                {["Plumbing", "Electrical", "Landscaping", "Cleaning", "Roofing", "Common Area"].map(cat => {
                  const catWOs = workOrders.filter(w => w.category === cat);
                  const spend = catWOs.reduce((s, w) => s + (w.actualCost || w.estimatedCost || 0), 0);
                  const pct = totalSpend ? Math.round(spend / totalSpend * 100) : 0;
                  if (!spend) return null;
                  return (
                    <div key={cat} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                        <span style={{ color: "var(--white)" }}>{cat}</span>
                        <span style={{ color: "var(--teal)", fontWeight: 600 }}>${spend.toLocaleString()}</span>
                      </div>
                      <div style={{ height: 6, background: "var(--navy3)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: "var(--teal)", borderRadius: 3 }} />
                      </div>
                      <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 2 }}>{pct}% of total · {catWOs.length} job{catWOs.length !== 1 ? "s" : ""}</div>
                    </div>
                  );
                })}
              </div>
              <div className="vt-card">
                <div className="vt-card-title">Spend by vendor</div>
                {[...vendors.filter(v => v.totalSpend > 0)].sort((a, b) => b.totalSpend - a.totalSpend).map(v => {
                  const pct = totalSpend ? Math.round(v.totalSpend / totalSpend * 100) : 0;
                  return (
                    <div key={v.id} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                        <span style={{ color: "var(--white)" }}>{v.name}</span>
                        <span style={{ color: "var(--amber)", fontWeight: 600 }}>${v.totalSpend.toLocaleString()}</span>
                      </div>
                      <div style={{ height: 6, background: "var(--navy3)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: "var(--amber)", borderRadius: 3 }} />
                      </div>
                      <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 2 }}>{pct}% · {v.jobs} jobs · ★{v.rating}</div>
                    </div>
                  );
                })}
                <div style={{ borderTop: ".5px solid var(--border)", paddingTop: "1rem", marginTop: ".5rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--white)" }}>Total tracked</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "var(--teal)" }}>${totalSpend.toLocaleString()}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--slate)", marginTop: 4 }}>{workOrders.length} work orders · {vendors.length} active vendors</div>
                </div>
              </div>
            </div>

            {/* Invoice summary */}
            <div style={{ marginTop: 16 }}>
              <div className="vt-card">
                <div className="vt-card-title">All invoices</div>
                {workOrders.flatMap(w => w.invoices.map(i => ({ ...i, woTitle: w.title, woId: w.id }))).length === 0 && (
                  <div style={{ fontSize: 13, color: "var(--slate)" }}>No invoices yet.</div>
                )}
                {workOrders.flatMap(w => w.invoices.map(i => ({ ...i, woTitle: w.title, woId: w.id }))).map(inv => (
                  <div className="invoice-row" key={`${inv.woId}-${inv.id}`}>
                    <div className="invoice-icon"><span style={{ fontSize: 14 }}>🧾</span></div>
                    <div style={{ flex: 1 }}>
                      <div className="invoice-name" style={{ fontSize: 13 }}>{inv.name}</div>
                      <div style={{ fontSize: 11, color: "var(--slate)" }}>{inv.woTitle}</div>
                    </div>
                    <span className="invoice-amount">${inv.amount}</span>
                    <span className={`invoice-status ${inv.status === "paid" ? "inv-paid" : "inv-pending"}`}>{inv.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Work Order Modal */}
      {woModal && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setWoModal(false)}>
          <div className="md md-lg">
            <div className="mt2">New work order</div>
            <div className="ms2">Log a maintenance job and assign a vendor</div>
            <div className="fg"><label className="fl">Title</label><input className="fi" value={woForm.title} onChange={e => setWoForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Parking lot light replacement" /></div>
            <div className="fr">
              <div className="fg"><label className="fl">Category</label>
                <select className="fi" value={woForm.category} onChange={e => setWoForm(p => ({ ...p, category: e.target.value }))}>
                  {["Plumbing", "Electrical", "HVAC", "Landscaping", "Cleaning", "Roofing", "Common Area", "Structural", "Pest Control", "Other"].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="fg"><label className="fl">Priority</label>
                <select className="fi" value={woForm.priority} onChange={e => setWoForm(p => ({ ...p, priority: e.target.value }))}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High / Urgent</option>
                </select>
              </div>
            </div>
            <div className="fr">
              <div className="fg"><label className="fl">Location / Unit</label>
                <input className="fi" value={woForm.unit} onChange={e => setWoForm(p => ({ ...p, unit: e.target.value }))} placeholder="Common, Pool, Unit 101…" />
              </div>
              <div className="fg"><label className="fl">Scheduled date</label>
                <input className="fi" type="date" value={woForm.scheduled} onChange={e => setWoForm(p => ({ ...p, scheduled: e.target.value }))} />
              </div>
            </div>
            <div className="fg"><label className="fl">Description</label>
              <textarea className="fi" value={woForm.description} onChange={e => setWoForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the work needed in detail…" rows={3} />
            </div>
            <div className="fg"><label className="fl">Assign vendor (optional)</label>
              <select className="fi" value={woForm.vendorId} onChange={e => setWoForm(p => ({ ...p, vendorId: e.target.value }))}>
                <option value="">— Select vendor —</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name} ({v.category})</option>)}
              </select>
            </div>
            <div className="ma"><button className="btn-g" onClick={() => setWoModal(false)}>Cancel</button><button className="btn" onClick={createWO} disabled={!woForm.title || !woForm.description}>Create work order</button></div>
          </div>
        </div>
      )}

      {/* Vendor Modal */}
      {vendorModal && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setVendorModal(false)}>
          <div className="md">
            <div className="mt2">{editVendor ? "Edit vendor" : "Add vendor"}</div>
            <div className="ms2">Vendor directory</div>
            {(() => {
              const f = editVendor || vForm;
              const set = (k, v) => editVendor ? setEditVendor(p => ({ ...p, [k]: v })) : setVForm(p => ({ ...p, [k]: v }));
              return (
                <>
                  <div className="fr">
                    <div className="fg"><label className="fl">Company name</label><input className="fi" value={f.name} onChange={e => set("name", e.target.value)} /></div>
                    <div className="fg"><label className="fl">Category</label>
                      <select className="fi" value={f.category} onChange={e => set("category", e.target.value)}>
                        {["Plumbing", "Electrical", "HVAC", "Landscaping", "Cleaning", "Roofing", "Common Area", "Structural", "Pest Control", "Other"].map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="fr">
                    <div className="fg"><label className="fl">Contact name</label><input className="fi" value={f.contact} onChange={e => set("contact", e.target.value)} /></div>
                    <div className="fg"><label className="fl">Phone</label><input className="fi" value={f.phone} onChange={e => set("phone", e.target.value)} /></div>
                  </div>
                  <div className="fg"><label className="fl">Email</label><input className="fi" type="email" value={f.email} onChange={e => set("email", e.target.value)} /></div>
                  <div style={{ display: "flex", gap: 20, marginBottom: "1rem" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--slate)", cursor: "pointer" }}>
                      <input type="checkbox" checked={f.licensed} onChange={e => set("licensed", e.target.checked)} style={{ accentColor: "var(--teal)", width: 15, height: 15 }} />Licensed
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--slate)", cursor: "pointer" }}>
                      <input type="checkbox" checked={f.insured} onChange={e => set("insured", e.target.checked)} style={{ accentColor: "var(--teal)", width: 15, height: 15 }} />Insured
                    </label>
                  </div>
                  <div className="fg"><label className="fl">Notes</label><input className="fi" value={f.notes} onChange={e => set("notes", e.target.value)} placeholder="Internal notes about this vendor…" /></div>
                  <div className="ma"><button className="btn-g" onClick={() => { setVendorModal(false); setEditVendor(null); }}>Cancel</button><button className="btn" onClick={() => saveVendor(f)} disabled={!f.name}>Save vendor</button></div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Board Maintenance View ───────────────────────────────────────────────────
function MaintenanceBoard({ maintenance, setMaintenance, toast , orgName = "Your HOA", orgEmail = "" }) {
  const { orgId } = useAppCtx();
  const [filter, setFilter] = useState("all");
  const [noteModal, setNoteModal] = useState(null);
  const [noteText, setNoteText] = useState("");

  const filtered = filter === "all" ? maintenance : maintenance.filter(m => m.status === filter);
  const sc = s => s === "resolved" ? "var(--green)" : s === "in-progress" ? "var(--amber)" : "var(--red)";
  const pc = p => p === "high" ? "var(--red)" : p === "medium" ? "var(--amber)" : "var(--slate)";

  const updateStatus = async (id, status) => {
    try {
      const { data, error } = await db.maintenance.save(orgId, { id, status });
      if (error) throw error;
      setMaintenance(p => p.map(m => m.id === id ? data : m));
      toast("Status updated");
    } catch (e) { toast("Update failed — " + e.message); }
  };

  const saveNote = async () => {
    try {
      const { data, error } = await db.maintenance.save(orgId, { id: noteModal, board_note: noteText });
      if (error) throw error;
      setMaintenance(p => p.map(m => m.id === noteModal ? data : m));
      toast("Note saved — homeowner will see this"); setNoteModal(null); setNoteText("");
    } catch (e) { toast("Save failed — " + e.message); }
  };

  return (
    <div>
      <div className="ph"><div><div className="pt">Maintenance Requests</div><div className="ps">{maintenance.filter(m => m.status === "open").length} open · submitted by homeowners via portal</div></div></div>
      <div className="pb">
        <div className="sr" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
          <div className="sc"><div className="sl">Open</div><div className="sv r">{maintenance.filter(m => m.status === "open").length}</div></div>
          <div className="sc"><div className="sl">In progress</div><div className="sv a">{maintenance.filter(m => m.status === "in-progress").length}</div></div>
          <div className="sc"><div className="sl">Resolved</div><div className="sv t">{maintenance.filter(m => m.status === "resolved").length}</div></div>
        </div>
        <div className="tb">
          {["all", "open", "in-progress", "resolved"].map(f => <button key={f} className={filter === f ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setFilter(f)} style={{ textTransform: "capitalize" }}>{f}</button>)}
        </div>
        <div className="tw"><table>
          <thead><tr><th>Unit</th><th>Owner</th><th>Category</th><th>Issue</th><th>Submitted</th><th>Priority</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{filtered.map(m => (
            <tr key={m.id}>
              <td><span style={{ fontWeight: 600, color: "var(--teal)" }}>{m.unit}</span></td>
              <td style={{ fontWeight: 500 }}>{m.owner}</td>
              <td style={{ fontSize: 12 }}>{m.category}</td>
              <td>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{m.title}</div>
                <div style={{ fontSize: 11, color: "var(--slate)" }}>{m.description.slice(0, 44)}{m.description.length > 44 ? "…" : ""}</div>
                {m.boardNote && <div style={{ fontSize: 11, color: "var(--teal)", marginTop: 3, fontStyle: "italic" }}>Note: {m.boardNote}</div>}
              </td>
              <td style={{ color: "var(--slate)", fontSize: 12 }}>{m.submitted}</td>
              <td><span style={{ fontSize: 12, color: pc(m.priority), fontWeight: 600, textTransform: "capitalize" }}>{m.priority}</span></td>
              <td><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${sc(m.status)}18`, color: sc(m.status), fontWeight: 600, textTransform: "capitalize" }}>{m.status}</span></td>
              <td>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {m.status === "open" && <button className="btn btn-sm" onClick={() => updateStatus(m.id, "in-progress")}>Start</button>}
                  {m.status === "in-progress" && <button className="btn btn-sm" onClick={() => updateStatus(m.id, "resolved")}>Resolve</button>}
                  <button className="btn-g btn-sm" onClick={() => { setNoteModal(m.id); setNoteText(m.boardNote || ""); }}>Note</button>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
      {noteModal && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setNoteModal(null)}>
          <div className="md">
            <div className="mt2">Board note for homeowner</div>
            <div className="ms2">This note is visible to the homeowner in their portal</div>
            <div className="fg"><label className="fl">Note</label>
              <textarea className="fi" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="e.g. Vendor scheduled for Thursday. We'll follow up after inspection." rows={4} />
            </div>
            <div className="ma"><button className="btn-g" onClick={() => setNoteModal(null)}>Cancel</button><button className="btn" onClick={saveNote}>Save note</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Homeowner Portal ─────────────────────────────────────────────────────────
function OwnerLogin({ onLogin , orgName = "Your HOA", orgEmail = "" }) {
  const [mode, setMode] = useState("owner"); // "owner" | "board-demo"
  const [em, setEm] = useState(""); const [pw, setPw] = useState(""); const [err, setErr] = useState("");

  const go = () => {
    const cred = OWNER_CREDENTIALS[em.toLowerCase().trim()];
    if (cred && cred.password === pw.trim()) {
      onLogin({ email: em, unit: cred.unit, name: cred.name, initials: cred.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() });
    } else {
      setErr("Invalid email or password. Try one of the demo accounts below.");
    }
  };

  return (
    <div className="lw">
      <div className="lc" style={{ maxWidth: 420, width: "100%" }}>
        <div className="ll" style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}><BoardArmorLogo size={30} />Board-<span>Armor</span></div>
        <div className="ls">Homeowner Portal · {orgName}</div>

        <div className="pw-toggle">
          <div className={`pw-tab ${mode === "owner" ? "active" : ""}`} onClick={() => { setMode("owner"); setErr(""); }}>Homeowner login</div>
          <div className={`pw-tab ${mode === "board-demo" ? "active" : ""}`} onClick={() => { setMode("board-demo"); setErr(""); }}>Demo accounts</div>
        </div>

        {mode === "owner" ? (
          <>
            {err && <div className="le">{err}</div>}
            <label className="ll2">Email address</label>
            <input className="li" type="email" placeholder="your@email.com" value={em} onChange={e => setEm(e.target.value)} onKeyDown={e => e.key === "Enter" && go()} />
            <label className="ll2">Password</label>
            <input className="li" type="password" placeholder="••••••••" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && go()} />
            <button className="lb" onClick={go}>Sign in to my portal</button>
            <div className="lh" style={{ marginTop: ".75rem" }}>Forgot your password? Contact the board at {orgEmail}</div>
          </>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: "1rem", lineHeight: 1.6 }}>Click any account to auto-fill credentials and sign in.</div>
            {Object.entries(OWNER_CREDENTIALS).map(([email, c]) => (
              <div key={email} onClick={() => { setEm(email); setPw(c.password); setMode("owner"); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: ".65rem .85rem", background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", marginBottom: 8, cursor: "pointer", transition: "border-color .15s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(42,99,214,.30)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,.13)"}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--teal3)", border: ".5px solid var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, color: "var(--teal)", flexShrink: 0 }}>
                  {c.name.split(" ").map(w => w[0]).join("").slice(0, 2)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)" }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: "var(--slate)" }}>Unit {c.unit} · {email}</div>
                </div>
                <div style={{ fontSize: 11, color: "var(--teal)" }}>Sign in →</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HomeownerPortal({ owners, dues, violations, maintenance, setMaintenance, announcements, onClose , orgName = "Your HOA", orgEmail = "" }) {
  const [portalUser, setPortalUser] = useState(null);
  const [tab, setTab] = useState("home");
  const [mrForm, setMrForm] = useState({ category: "Plumbing", title: "", description: "", priority: "medium" });
  const [mrSubmitted, setMrSubmitted] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);

  if (!portalUser) return <OwnerLogin onLogin={setPortalUser} />;

  const owner = owners.find(o => o.unit === portalUser?.unit) || {};
  const myDues = dues.filter(d => d.unit === portalUser?.unit);
  const myViolations = violations.filter(v => v.unit === portalUser?.unit);
  const myRequests = maintenance.filter(m => m.unit === portalUser?.unit);
  const openRequests = myRequests.filter(m => m.status !== "resolved").length;
  const openViolations = myViolations.filter(v => v.status !== "resolved").length;
  const totalOwed = owner.duesOwed || 0;

  const submitRequest = () => {
    if (!mrForm.title || !mrForm.description) return;
    setMaintenance(prev => [...prev, {
      id: Date.now(), unit: portalUser?.unit, owner: portalUser?.name,
      category: mrForm.category, title: mrForm.title, description: mrForm.description,
      submitted: new Date().toISOString().split("T")[0], status: "open",
      priority: mrForm.priority, boardNote: ""
    }]);
    setMrForm({ category: "Plumbing", title: "", description: "", priority: "medium" });
    setMrSubmitted(true);
    setTimeout(() => setMrSubmitted(false), 4000);
  };

  const simulatePay = () => { setPaySuccess(true); setTimeout(() => setPaySuccess(false), 4000); };

  const statusColor = s => s === "resolved" ? "var(--green)" : s === "in-progress" ? "var(--amber)" : "var(--red)";
  const statusLabel = s => s === "in-progress" ? "In progress" : s === "resolved" ? "Resolved" : "Open";
  const annColor = t => t === "event" ? "var(--teal)" : t === "notice" ? "var(--amber)" : t === "maintenance" ? "var(--purple)" : "var(--slate)";

  const tabs = [
    { id: "home", label: "Home" },
    { id: "dues", label: `Dues${totalOwed > 0 ? " !" : ""}` },
    { id: "maintenance", label: `Requests${openRequests > 0 ? ` (${openRequests})` : ""}` },
    { id: "violations", label: `Violations${openViolations > 0 ? ` (${openViolations})` : ""}` },
    { id: "announcements", label: "Announcements" },
  ];

  return (
    <div className="op-wrap">
      {/* Sticky header */}
      <div className="op-header">
        <div style={{ display: "flex", alignItems: "center" }}>
          <div className="op-logo" style={{ display: "flex", alignItems: "center", gap: 8 }}><BoardArmorLogo size={22} />Board-<span>Armor</span></div>
          <div className="op-hoa">· {orgName}</div>
        </div>
        <div className="op-user">
          <div className="op-avatar">{portalUser?.initials}</div>
          <div>
            <div className="op-name">{portalUser?.name}</div>
            <div className="op-unit">Unit {portalUser?.unit}</div>
          </div>
          <button className="op-signout" onClick={() => setPortalUser(null)}>Sign out</button>
          <button className="op-signout" style={{ marginLeft: 4, color: "var(--slate)" }} onClick={onClose}>← Board view</button>
        </div>
      </div>

      <div className="op-body">
        {/* Welcome */}
        <div className="op-welcome-title">Welcome back, {portalUser?.name.split(" ")[0]}.</div>
        <div className="op-welcome-sub">{orgName} HOA · Unit {portalUser?.unit}</div>

        {/* Summary cards */}
        <div className="op-cards">
          <div className="op-card">
            <div className="op-card-label">Dues balance</div>
            <div className={`op-card-val ${totalOwed > 0 ? "red" : "green"}`}>{totalOwed > 0 ? `$${totalOwed}` : "Current"}</div>
            <div className="op-card-sub">{totalOwed > 0 ? "Payment overdue" : "No balance due"}</div>
          </div>
          <div className="op-card">
            <div className="op-card-label">Open violations</div>
            <div className={`op-card-val ${openViolations > 0 ? "amber" : "green"}`}>{openViolations}</div>
            <div className="op-card-sub">{openViolations > 0 ? "Requires attention" : "No active violations"}</div>
          </div>
          <div className="op-card">
            <div className="op-card-label">Maintenance requests</div>
            <div className={`op-card-val ${openRequests > 0 ? "amber" : "green"}`}>{myRequests.length}</div>
            <div className="op-card-sub">{openRequests > 0 ? `${openRequests} open` : "All resolved"}</div>
          </div>
          <div className="op-card">
            <div className="op-card-label">Announcements</div>
            <div className="op-card-val">{announcements.length}</div>
            <div className="op-card-sub">From the board</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r)", padding: 4, marginBottom: "1.5rem" }}>
          {tabs.map(t => (
            <div key={t.id} className={`op-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>{t.label}</div>
          ))}
        </div>

        {/* ── Home tab ── */}
        {tab === "home" && (
          <div>
            {/* Alerts */}
            {totalOwed > 0 && (
              <div style={{ background: "rgba(224,82,82,.08)", border: ".5px solid rgba(224,82,82,.25)", borderRadius: "var(--r2)", padding: "1rem 1.25rem", marginBottom: "1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--red)", marginBottom: 3 }}>Payment overdue — ${totalOwed}</div>
                  <div style={{ fontSize: 12, color: "var(--slate)" }}>Your dues balance is outstanding. Please pay to avoid late fees.</div>
                </div>
                <button style={{ background: "var(--red)", color: "#fff", border: "none", borderRadius: "var(--r)", padding: "8px 18px", fontSize: 13, fontWeight: 600, fontFamily: "var(--sans)", cursor: "pointer", flexShrink: 0 }} onClick={() => setTab("dues")}>Pay now →</button>
              </div>
            )}
            {openViolations > 0 && (
              <div style={{ background: "rgba(245,166,35,.08)", border: ".5px solid rgba(245,166,35,.25)", borderRadius: "var(--r2)", padding: "1rem 1.25rem", marginBottom: "1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--amber)", marginBottom: 3 }}>You have {openViolations} open violation{openViolations > 1 ? "s" : ""}</div>
                  <div style={{ fontSize: 12, color: "var(--slate)" }}>Please review and take corrective action to avoid escalation.</div>
                </div>
                <button style={{ background: "var(--amber)", color: "var(--navy)", border: "none", borderRadius: "var(--r)", padding: "8px 18px", fontSize: 13, fontWeight: 600, fontFamily: "var(--sans)", cursor: "pointer", flexShrink: 0 }} onClick={() => setTab("violations")}>View →</button>
              </div>
            )}

            {/* Latest announcements preview */}
            <div className="op-section-head"><div className="op-section-title">Latest announcements</div><div style={{ fontSize: 12, color: "var(--teal)", cursor: "pointer" }} onClick={() => setTab("announcements")}>View all →</div></div>
            {announcements.slice(0, 2).map(a => (
              <div className="op-ann" key={a.id}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".4rem" }}>
                  <div className="op-ann-title">{a.title}</div>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: `${annColor(a.type)}18`, color: annColor(a.type) }}>{a.type}</span>
                </div>
                <div className="op-ann-body">{a.body}</div>
                <div className="op-ann-date">{a.date}</div>
              </div>
            ))}

            {/* Recent maintenance */}
            {myRequests.length > 0 && (
              <>
                <div className="op-section-head" style={{ marginTop: "1.5rem" }}><div className="op-section-title">Your recent requests</div><div style={{ fontSize: 12, color: "var(--teal)", cursor: "pointer" }} onClick={() => setTab("maintenance")}>View all →</div></div>
                {myRequests.slice(0, 2).map(r => (
                  <div className="op-item" key={r.id}>
                    <div className="op-item-dot" style={{ background: statusColor(r.status) }} />
                    <div style={{ flex: 1 }}>
                      <div className="op-item-title">{r.title}</div>
                      <div className="op-item-sub">{r.category} · Submitted {r.submitted}</div>
                      {r.boardNote && <div style={{ fontSize: 12, color: "var(--teal)", marginTop: 4, fontStyle: "italic" }}>Board: {r.boardNote}</div>}
                    </div>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${statusColor(r.status)}18`, color: statusColor(r.status), fontWeight: 600, flexShrink: 0 }}>{statusLabel(r.status)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Dues tab ── */}
        {tab === "dues" && (
          <div>
            <div className="op-section-head"><div className="op-section-title">Dues & payments</div></div>
            {paySuccess && <div className="op-success">✓ Payment of ${totalOwed} submitted successfully! Allow 1–2 business days for processing.</div>}
            {totalOwed > 0 && !paySuccess && (
              <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.5rem", marginBottom: "1.25rem" }}>
                <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: 4 }}>Current balance</div>
                <div className="op-pay-amount">${totalOwed}</div>
                <div className="op-pay-due">Due immediately · Late fees apply after 30 days</div>
                <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: "1.25rem" }}>Payment methods accepted: Credit card, ACH bank transfer, check</div>
                <button className="op-pay-btn" onClick={simulatePay}>Pay ${totalOwed} online →</button>
                <div style={{ fontSize: 11, color: "var(--slate)", marginTop: ".75rem" }}>Or mail check to: {orgName}, contact board for mailing address</div>
              </div>
            )}
            {totalOwed === 0 && !paySuccess && (
              <div className="op-empty">Your account is current — no balance due. Thank you!</div>
            )}
            <div className="op-section-head" style={{ marginTop: "1.25rem" }}><div className="op-section-title">Payment history</div></div>
            {myDues.length === 0 && <div className="op-empty">No payment history found.</div>}
            {myDues.map(d => (
              <div className="op-item" key={d.id}>
                <div className="op-item-dot" style={{ background: d.status === "paid" ? "var(--green)" : d.status === "partial" ? "var(--amber)" : "var(--red)" }} />
                <div style={{ flex: 1 }}>
                  <div className="op-item-title">${d.amount} · ${new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"})} dues</div>
                  <div className="op-item-sub">Due {d.due}{d.paid ? ` · Paid ${d.paid}` : ""}</div>
                </div>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: d.status === "paid" ? "var(--green2)" : d.status === "partial" ? "var(--amber2)" : "var(--red2)", color: d.status === "paid" ? "var(--green)" : d.status === "partial" ? "var(--amber)" : "var(--red)" }}>{d.status}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Maintenance tab ── */}
        {tab === "maintenance" && (
          <div>
            <div className="op-section-head"><div className="op-section-title">Maintenance requests</div></div>
            {mrSubmitted && <div className="op-success">✓ Request submitted! The board will review and respond within 3–5 business days.</div>}
            {myRequests.length === 0 && <div className="op-empty" style={{ marginBottom: "1.25rem" }}>No requests yet.</div>}
            {myRequests.map(r => (
              <div className="op-item" key={r.id}>
                <div className="op-item-dot" style={{ background: statusColor(r.status) }} />
                <div style={{ flex: 1 }}>
                  <div className="op-item-title">{r.title}</div>
                  <div className="op-item-sub">{r.category} · {r.description.slice(0, 60)}{r.description.length > 60 ? "…" : ""}</div>
                  <div className="op-item-meta">Submitted {r.submitted}</div>
                  {r.boardNote && <div style={{ fontSize: 12, color: "var(--teal)", marginTop: 5, fontStyle: "italic", background: "rgba(42,99,214,.06)", padding: "4px 8px", borderRadius: 6, borderLeft: "2px solid var(--teal)" }}>Board update: {r.boardNote}</div>}
                </div>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${statusColor(r.status)}18`, color: statusColor(r.status), fontWeight: 600, flexShrink: 0, marginTop: 2 }}>{statusLabel(r.status)}</span>
              </div>
            ))}

            {/* New request form */}
            <div style={{ marginTop: "1.5rem" }}>
              <div className="op-section-head"><div className="op-section-title">Submit a new request</div></div>
              <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.25rem" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: ".85rem" }}>
                  <div>
                    <label className="op-lbl">Category</label>
                    <select className="op-input" value={mrForm.category} onChange={e => setMrForm(p => ({ ...p, category: e.target.value }))}>
                      {["Plumbing", "Electrical", "HVAC", "Common Area", "Structural", "Pest Control", "Landscaping", "Other"].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="op-lbl">Priority</label>
                    <select className="op-input" value={mrForm.priority} onChange={e => setMrForm(p => ({ ...p, priority: e.target.value }))}>
                      <option value="low">Low — not urgent</option>
                      <option value="medium">Medium — needs attention</option>
                      <option value="high">High — urgent / safety</option>
                    </select>
                  </div>
                </div>
                <label className="op-lbl">Issue title</label>
                <input className="op-input" placeholder="Brief description of the issue" value={mrForm.title} onChange={e => setMrForm(p => ({ ...p, title: e.target.value }))} />
                <label className="op-lbl">Full description</label>
                <textarea className="op-input" placeholder="Describe the issue in detail — when it started, location, any relevant context…" value={mrForm.description} onChange={e => setMrForm(p => ({ ...p, description: e.target.value }))} />
                <button className="op-submit" onClick={submitRequest} disabled={!mrForm.title || !mrForm.description} style={{ opacity: (!mrForm.title || !mrForm.description) ? .4 : 1 }}>Submit request →</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Violations tab ── */}
        {tab === "violations" && (
          <div>
            <div className="op-section-head"><div className="op-section-title">Your violations</div></div>
            {myViolations.length === 0 && <div className="op-empty">No violations on record for Unit {portalUser?.unit}. Keep up the great work!</div>}
            {myViolations.map(v => (
              <div className="op-item" key={v.id} style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div className="op-item-dot" style={{ background: v.status === "resolved" ? "var(--green)" : v.status === "warning" ? "var(--amber)" : "var(--red)", marginTop: 5 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                      <div className="op-item-title">{v.type} violation</div>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: v.status === "resolved" ? "var(--green2)" : v.status === "warning" ? "var(--amber2)" : "var(--red2)", color: v.status === "resolved" ? "var(--green)" : v.status === "warning" ? "var(--amber)" : "var(--red)", fontWeight: 600 }}>{v.status}</span>
                    </div>
                    <div className="op-item-sub" style={{ marginTop: 4 }}>{v.description}</div>
                    <div className="op-item-meta">Observed {v.date} · Severity: {v.severity}</div>
                  </div>
                </div>
                {v.status !== "resolved" && (
                  <div style={{ background: "rgba(224,82,82,.06)", border: ".5px solid rgba(224,82,82,.18)", borderRadius: "var(--r)", padding: ".65rem .85rem", fontSize: 12, color: "var(--slate)", lineHeight: 1.5 }}>
                    <span style={{ color: "var(--red)", fontWeight: 600 }}>Action required:</span> Please correct this violation within 14 days to avoid further action. Contact the board at {orgEmail} with any questions or to dispute this notice.
                  </div>
                )}
                {v.status === "resolved" && (
                  <div style={{ background: "rgba(58,191,126,.06)", border: ".5px solid rgba(58,191,126,.2)", borderRadius: "var(--r)", padding: ".65rem .85rem", fontSize: 12, color: "var(--green)" }}>
                    ✓ This violation has been resolved. No further action is required.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Announcements tab ── */}
        {tab === "announcements" && (
          <div>
            <div className="op-section-head"><div className="op-section-title">Community announcements</div></div>
            {announcements.length === 0 && <div className="op-empty">No announcements at this time.</div>}
            {announcements.map(a => (
              <div className="op-ann" key={a.id}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".5rem" }}>
                  <div className="op-ann-title">{a.title}</div>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: `${annColor(a.type)}18`, color: annColor(a.type), flexShrink: 0 }}>{a.type}</span>
                </div>
                <div className="op-ann-body">{a.body}</div>
                <div className="op-ann-date">{a.date}</div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: "3rem", paddingTop: "1.5rem", borderTop: ".5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 12, color: "var(--slate)" }}>{orgName} HOA · {orgEmail}</div>
          <div style={{ fontSize: 11, color: "var(--slate)" }}>Powered by <span style={{ color: "var(--teal)", fontFamily: "var(--serif)" }}>Armor</span></div>
        </div>
      </div>
    </div>
  );
}

// ─── Budget & Financial Report ────────────────────────────────────────────────
function BudgetFinance({ owners, dues, toast , orgName = "Your HOA", orgEmail = "" }) {
  // Generate last 3 months dynamically
  const MONTHS = Array.from({length:3}, (_, i) => {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
  });
  const MONTH_LABELS = Object.fromEntries(MONTHS.map(m => {
    const [y, mo] = m.split("-");
    return [m, new Date(y, mo-1).toLocaleDateString("en-US", {month:"long", year:"numeric"})];
  }));
  const [activeMonth, setActiveMonth] = useState(MONTHS[0]);
  const [tab, setTab] = useState("overview");
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [editBudget, setEditBudget] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState({});
  const [txForm, setTxForm] = useState({ desc: "", cat: "dues", type: "income", amount: "", date: new Date().toISOString().split("T")[0] });
  const [reportText, setReportText] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");

  // ── Derived numbers ──────────────────────────────────────────────────────────
  const monthTx  = useMemo(() => transactions.filter(t => t.month === activeMonth), [transactions, activeMonth]);
  const budget   = budgets[activeMonth] || {};

  // Single-pass over monthTx for both income and expense totals
  const { totalIncome, totalExpense } = useMemo(() => monthTx.reduce(
    (acc, t) => { t.type === "income" ? acc.totalIncome += t.amount : acc.totalExpense += t.amount; return acc; },
    { totalIncome: 0, totalExpense: 0 }
  ), [monthTx]);
  const netSurplus   = totalIncome - totalExpense;

  // Static category totals computed once, not on every filter access
  const { budgetIncome, budgetExpense } = useMemo(() => BUDGET_CATEGORIES.reduce(
    (acc, c) => { c.type === "income" ? acc.budgetIncome += (budget[c.id]||0) : acc.budgetExpense += (budget[c.id]||0); return acc; },
    { budgetIncome: 0, budgetExpense: 0 }
  ), [budget]);

  // Build a map of catId → actual amount once, instead of filter+reduce per category per render
  const catActualMap = useMemo(() => monthTx.reduce(
    (acc, t) => { acc[t.cat] = (acc[t.cat] || 0) + t.amount; return acc; }, {}
  ), [monthTx]);

  const catActual = useCallback((catId) => catActualMap[catId] || 0, [catActualMap]);
  const catBudget = useCallback((catId) => budget[catId] || 0, [budget]);
  const catPct    = useCallback((catId) => { const b = catBudget(catId); return b ? Math.min(catActual(catId) / b * 100, 150) : 0; }, [catActual, catBudget]);
  const fillClass = (pct, type) => type === "income" ? (pct >= 95 ? "bf-fill-under" : "bf-fill-close") : pct > 110 ? "bf-fill-over" : pct > 85 ? "bf-fill-close" : "bf-fill-under";

  // 3-month trend
  const trendData = MONTHS.map(m => {
    const mtx = transactions.filter(t => t.month === m);
    return { month: MONTH_LABELS[m].split(" ")[0], income: mtx.filter(t => t.type === "income").reduce((s, t) => s + t.amount, 0), expense: mtx.filter(t => t.type === "expense").reduce((s, t) => s + t.amount, 0) };
  }).reverse();
  const trendMax = Math.max(...trendData.flatMap(d => [d.income, d.expense]), 1);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const addTransaction = () => {
    if (!txForm.desc || !txForm.amount) return;
    setTransactions(p => [...p, { ...txForm, id: Date.now(), month: activeMonth, amount: Number(txForm.amount) }]);
    setTxForm({ desc: "", cat: "dues", type: "income", amount: "", date: new Date().toISOString().split("T")[0] });
    toast("Transaction added");
  };

  const deleteTransaction = (id) => { setTransactions(p => p.filter(t => t.id !== id)); toast("Removed"); };

  const saveBudget = () => {
    setBudgets(p => ({ ...p, [activeMonth]: { ...p[activeMonth], ...Object.fromEntries(Object.entries(budgetDraft).map(([k, v]) => [k, Number(v)])) } }));
    setEditBudget(false); toast("Budget updated");
  };

  // ── AI Treasurer Report ───────────────────────────────────────────────────────
  const generateReport = async () => {
    setReportLoading(true); setReportText("");
    const incomeLines  = BUDGET_CATEGORIES.filter(c => c.type === "income").map(c => `${c.name}: actual $${catActual(c.id)}, budget $${catBudget(c.id)}`).join("\n");
    const expenseLines = BUDGET_CATEGORIES.filter(c => c.type === "expense").map(c => `${c.name}: actual $${catActual(c.id)}, budget $${catBudget(c.id)}`).join("\n");
    const overBudget   = BUDGET_CATEGORIES.filter(c => c.type === "expense" && catActual(c.id) > catBudget(c.id) * 1.05).map(c => c.name);
    const underBudget  = BUDGET_CATEGORIES.filter(c => c.type === "expense" && catActual(c.id) < catBudget(c.id) * 0.8 && catBudget(c.id) > 0).map(c => c.name);

    const prompt = `You are the treasurer of ${orgName} writing a professional monthly financial report narrative for the board meeting packet.

Month: ${MONTH_LABELS[activeMonth]}
HOA: ${orgName}

INCOME SUMMARY:
${incomeLines}
Total income: $${totalIncome} | Budgeted: $${budgetIncome}

EXPENSE SUMMARY:
${expenseLines}
Total expenses: $${totalExpense} | Budgeted: $${budgetExpense}

Net ${netSurplus >= 0 ? "surplus" : "deficit"}: $${Math.abs(netSurplus)}
Over-budget categories: ${overBudget.join(", ") || "none"}
Under-budget categories: ${underBudget.join(", ") || "none"}

Write a professional treasurer's report narrative in 3 paragraphs:

Paragraph 1 — Financial Summary: Overview of income vs budget, collection status, net position.
Paragraph 2 — Expense Analysis: Notable variances, over/under budget items, explanations and context.
Paragraph 3 — Forward Look: Reserve fund health, upcoming expenses to watch, recommendations for the board.

Tone: professional, factual, clear. Write as if presenting at a board meeting. No headers, no bullet points — flowing narrative paragraphs only.`;

    const txt = await callClaude(prompt, 1000);
    setReportText(txt);
    setReportLoading(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const fmtAmt = (n, type) => `${type === "expense" ? "-" : "+"}$${n.toLocaleString()}`;
  const catColor = (catId) => BUDGET_CATEGORIES.find(c => c.id === catId)?.color || "#64748b";
  const catName  = (catId) => BUDGET_CATEGORIES.find(c => c.id === catId)?.name || catId;

  const filteredTx = filterCat === "all" ? monthTx : monthTx.filter(t => t.cat === filterCat);

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">Budget & Financial Reports</div>
          <div className="ps">{orgName} HOA · Income, expenses, budget tracking & AI treasurer report</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {MONTHS.map(m => (
            <div key={m} className={`bf-month-pill ${activeMonth === m ? "active" : ""}`} onClick={() => { setActiveMonth(m); setReportText(""); }}>
              {MONTH_LABELS[m]}
            </div>
          ))}
        </div>
      </div>

      <div className="pb">
        {/* ── KPI row ── */}
        <div className="bf-grid">
          <div className="bf-card">
            <div className="bf-label">Total income</div>
            <div className="bf-amount income">${totalIncome.toLocaleString()}</div>
            <div className="bf-sub">Budget: ${budgetIncome.toLocaleString()} · {budgetIncome ? Math.round(totalIncome/budgetIncome*100) : 0}% collected</div>
          </div>
          <div className="bf-card">
            <div className="bf-label">Total expenses</div>
            <div className="bf-amount expense">${totalExpense.toLocaleString()}</div>
            <div className="bf-sub">Budget: ${budgetExpense.toLocaleString()} · {budgetExpense ? Math.round(totalExpense/budgetExpense*100) : 0}% spent</div>
          </div>
          <div className={`bf-card ${netSurplus >= 0 ? "surplus" : "deficit"}`}>
            <div className="bf-label">Net {netSurplus >= 0 ? "surplus" : "deficit"}</div>
            <div className={`bf-amount ${netSurplus >= 0 ? "surplus" : "deficit"}`}>${Math.abs(netSurplus).toLocaleString()}</div>
            <div className="bf-sub">{netSurplus >= 0 ? "Operating in the black" : "Over budget this month"}</div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="tabs">
          {[["overview", "Budget vs Actual"], ["transactions", "Transactions"], ["trend", "3-Month Trend"], ["report", "AI Treasurer Report"]].map(([k, l]) =>
            <div key={k} className={`tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>{l}</div>
          )}
        </div>

        {/* ────────── OVERVIEW TAB ────────── */}
        {tab === "overview" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Income */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--teal)", textTransform: "uppercase", letterSpacing: ".05em", fontSize: 11 }}>Income</div>
                <div className="bf-legend">
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div className="bf-legend-dot" style={{ background: "var(--teal)" }} /><span>Actual</span></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div className="bf-legend-dot" style={{ background: "var(--border2)" }} /><span>Budget</span></div>
                </div>
              </div>
              {BUDGET_CATEGORIES.filter(c => c.type === "income").map(c => {
                const actual = catActual(c.id); const bud = catBudget(c.id); const pct = bud ? Math.min(actual / bud * 100, 100) : 0;
                return (
                  <div key={c.id} className="bf-bar-row">
                    <div className="bf-bar-head">
                      <div className="bf-bar-label">{c.name}</div>
                      <div className="bf-bar-vals">
                        <span style={{ color: "var(--teal)", fontWeight: 600 }}>${actual.toLocaleString()}</span>
                        <span style={{ color: "var(--slate)" }}>/ ${bud.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="bf-track">
                      <div className="bf-fill-budget" style={{ width: "100%" }} />
                      <div className={`bf-fill-actual ${fillClass(pct, "income")}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 3 }}>{Math.round(pct)}% of budget · {actual < bud ? `$${(bud - actual).toLocaleString()} remaining` : "✓ On target"}</div>
                  </div>
                );
              })}
            </div>

            {/* Expenses */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--red)", textTransform: "uppercase", letterSpacing: ".05em" }}>Expenses</div>
                <button className="btn-g btn-sm" onClick={() => { setBudgetDraft(Object.fromEntries(Object.entries(budget).map(([k, v]) => [k, v]))); setEditBudget(true); }}>Edit budgets</button>
              </div>
              {BUDGET_CATEGORIES.filter(c => c.type === "expense").map(c => {
                const actual = catActual(c.id); const bud = catBudget(c.id); const pct = bud ? Math.min(actual / bud * 100, 150) : 0;
                const over = bud > 0 && actual > bud * 1.05;
                return (
                  <div key={c.id} className="bf-bar-row">
                    <div className="bf-bar-head">
                      <div className="bf-bar-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.color, flexShrink: 0 }} />
                        {c.name}
                        {over && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--red)", background: "var(--red2)", padding: "1px 6px", borderRadius: 20 }}>Over</span>}
                      </div>
                      <div className="bf-bar-vals">
                        <span style={{ color: over ? "var(--red)" : "var(--white)", fontWeight: 600 }}>${actual.toLocaleString()}</span>
                        <span style={{ color: "var(--slate)" }}>/ ${bud.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="bf-track">
                      <div className="bf-fill-budget" style={{ width: "100%" }} />
                      <div className={`bf-fill-actual ${fillClass(pct, "expense")}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <div style={{ fontSize: 11, color: over ? "var(--red)" : "var(--slate)", marginTop: 3 }}>
                      {Math.round(pct)}% of budget · {over ? `$${(actual - bud).toLocaleString()} over` : bud ? `$${(bud - actual).toLocaleString()} remaining` : "No budget set"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ────────── TRANSACTIONS TAB ────────── */}
        {tab === "transactions" && (
          <div>
            {/* Add transaction */}
            <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--slate)", letterSpacing: ".05em", textTransform: "uppercase", marginBottom: ".85rem" }}>Add transaction</div>
              <div className="bf-tx-form">
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Description</div>
                  <input className="fi" value={txForm.desc} onChange={e => setTxForm(p => ({ ...p, desc: e.target.value }))} placeholder="e.g. April dues — Unit 205" style={{ fontSize: 12, padding: "7px 10px" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Category</div>
                  <select className="fi" value={txForm.cat} onChange={e => { const cat = BUDGET_CATEGORIES.find(c => c.id === e.target.value); setTxForm(p => ({ ...p, cat: e.target.value, type: cat?.type || p.type })); }} style={{ fontSize: 12, padding: "7px 10px" }}>
                    {BUDGET_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Type</div>
                  <select className="fi" value={txForm.type} onChange={e => setTxForm(p => ({ ...p, type: e.target.value }))} style={{ fontSize: 12, padding: "7px 10px" }}>
                    <option value="income">Income</option><option value="expense">Expense</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Amount ($)</div>
                  <input className="fi" type="number" value={txForm.amount} onChange={e => setTxForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" style={{ fontSize: 12, padding: "7px 10px" }} />
                </div>
                <button className="btn btn-sm" onClick={addTransaction} disabled={!txForm.desc || !txForm.amount} style={{ alignSelf: "flex-end", height: 36 }}>Add</button>
              </div>
            </div>

            {/* Filter by category */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1rem" }}>
              <button className={filterCat === "all" ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setFilterCat("all")}>All</button>
              {BUDGET_CATEGORIES.filter(c => catActual(c.id) > 0).map(c => (
                <button key={c.id} className={filterCat === c.id ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setFilterCat(c.id)} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: c.color }} />{c.name.split(" ")[0]}
                </button>
              ))}
            </div>

            <div className="tw">
              <table>
                <thead><tr><th>Date</th><th>Description</th><th>Category</th><th>Type</th><th style={{ textAlign: "right" }}>Amount</th><th></th></tr></thead>
                <tbody>
                  {[...filteredTx].sort((a, b) => b.date.localeCompare(a.date)).map(tx => (
                    <tr key={tx.id}>
                      <td style={{ fontSize: 12, color: "var(--slate)", whiteSpace: "nowrap" }}>{tx.date}</td>
                      <td style={{ fontWeight: 500 }}>{tx.desc}</td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: catColor(tx.cat), flexShrink: 0 }} />
                          {catName(tx.cat)}
                        </span>
                      </td>
                      <td><span className={`bg ${tx.type === "income" ? "bg-t" : "bg-r"}`}>{tx.type}</span></td>
                      <td style={{ textAlign: "right" }}>
                        <span className={`tx-amount ${tx.type === "income" ? "inc" : "exp"}`}>{fmtAmt(tx.amount, tx.type)}</span>
                      </td>
                      <td><button className="btn-g btn-sm" style={{ fontSize: 11, color: "var(--slate)" }} onClick={() => deleteTransaction(tx.id)}>✕</button></td>
                    </tr>
                  ))}
                  {filteredTx.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--slate)", padding: "2rem" }}>No transactions for this period.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Totals footer */}
            <div style={{ display: "flex", gap: 16, justifyContent: "flex-end", marginTop: ".75rem", fontSize: 13, fontWeight: 600 }}>
              <span style={{ color: "var(--teal)" }}>Income: +${totalIncome.toLocaleString()}</span>
              <span style={{ color: "var(--red)" }}>Expenses: -${totalExpense.toLocaleString()}</span>
              <span style={{ color: netSurplus >= 0 ? "var(--green)" : "var(--red)" }}>Net: {netSurplus >= 0 ? "+" : "-"}${Math.abs(netSurplus).toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* ────────── TREND TAB ────────── */}
        {tab === "trend" && (
          <div>
            <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.5rem" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)", marginBottom: "1.5rem" }}>3-month income vs expense</div>
              {/* Bar chart */}
              <div style={{ display: "flex", gap: 32, alignItems: "flex-end", height: 200, paddingBottom: "2rem", borderBottom: ".5px solid var(--border)" }}>
                {trendData.map((d, i) => (
                  <div key={i} style={{ flex: 1, display: "flex", gap: 8, alignItems: "flex-end", justifyContent: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ fontSize: 11, color: "var(--teal)", fontWeight: 600 }}>${d.income.toLocaleString()}</div>
                      <div style={{ width: 36, background: "var(--teal)", borderRadius: "4px 4px 0 0", height: `${Math.round(d.income / trendMax * 160)}px`, minHeight: 4, transition: "height .4s" }} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ fontSize: 11, color: "var(--red)", fontWeight: 600 }}>${d.expense.toLocaleString()}</div>
                      <div style={{ width: 36, background: "var(--red)", borderRadius: "4px 4px 0 0", height: `${Math.round(d.expense / trendMax * 160)}px`, minHeight: 4, transition: "height .4s" }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 32, marginTop: ".75rem" }}>
                {trendData.map((d, i) => (
                  <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 12, color: "var(--slate)" }}>{d.month}</div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 20, justifyContent: "center", marginTop: "1rem", fontSize: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 12, borderRadius: 2, background: "var(--teal)" }} /><span style={{ color: "var(--slate)" }}>Income</span></div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 12, borderRadius: 2, background: "var(--red)" }} /><span style={{ color: "var(--slate)" }}>Expenses</span></div>
              </div>
            </div>

            {/* Per-category 3-month table */}
            <div style={{ marginTop: 16 }}>
              <div className="tw">
                <table>
                  <thead><tr><th>Category</th>{MONTHS.map(m => <th key={m} style={{ textAlign: "right" }}>{MONTH_LABELS[m].split(" ")[0]}</th>)}<th style={{ textAlign: "right" }}>3-mo total</th></tr></thead>
                  <tbody>
                    {BUDGET_CATEGORIES.map(c => {
                      const monthly = MONTHS.map(m => transactions.filter(t => t.month === m && t.cat === c.id).reduce((s, t) => s + t.amount, 0));
                      const total = monthly.reduce((s, v) => s + v, 0);
                      if (!total) return null;
                      return (
                        <tr key={c.id}>
                          <td><span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />{c.name}</span></td>
                          {monthly.map((v, i) => <td key={i} style={{ textAlign: "right", fontWeight: 500, color: c.type === "income" ? "var(--teal)" : v > 0 ? "var(--white)" : "var(--slate)" }}>{v > 0 ? `$${v.toLocaleString()}` : "—"}</td>)}
                          <td style={{ textAlign: "right", fontWeight: 700, color: c.type === "income" ? "var(--teal)" : "var(--white)" }}>${total.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: ".5px solid var(--border2)" }}>
                      <td style={{ fontWeight: 700 }}>Net surplus / deficit</td>
                      {MONTHS.map(m => {
                        const mtx = transactions.filter(t => t.month === m);
                        const net = mtx.filter(t => t.type === "income").reduce((s, t) => s + t.amount, 0) - mtx.filter(t => t.type === "expense").reduce((s, t) => s + t.amount, 0);
                        return <td key={m} style={{ textAlign: "right", fontWeight: 700, color: net >= 0 ? "var(--green)" : "var(--red)" }}>{net >= 0 ? "+" : ""}${net.toLocaleString()}</td>;
                      })}
                      <td style={{ textAlign: "right", fontWeight: 700, color: "var(--slate)" }}>—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ────────── AI REPORT TAB ────────── */}
        {tab === "report" && (
          <div>
            <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.25rem", marginBottom: "1rem" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)", marginBottom: ".35rem" }}>AI Treasurer Report — {MONTH_LABELS[activeMonth]}</div>
              <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: "1rem" }}>
                AI reads your actual transactions and budget targets for {MONTH_LABELS[activeMonth]}, then writes a complete professional treasurer narrative — ready to paste into your board meeting packet.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13, color: "var(--slate)", marginBottom: "1.25rem" }}>
                <span><span style={{ color: "var(--teal)", fontWeight: 600 }}>${totalIncome.toLocaleString()}</span> income</span>
                <span>·</span>
                <span><span style={{ color: "var(--red)", fontWeight: 600 }}>${totalExpense.toLocaleString()}</span> expenses</span>
                <span>·</span>
                <span><span style={{ color: netSurplus >= 0 ? "var(--green)" : "var(--red)", fontWeight: 600 }}>${Math.abs(netSurplus).toLocaleString()}</span> net {netSurplus >= 0 ? "surplus" : "deficit"}</span>
                <span>·</span>
                <span><span style={{ color: "var(--white)", fontWeight: 600 }}>{monthTx.length}</span> transactions</span>
              </div>
              <button className="btn" onClick={generateReport} disabled={reportLoading} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {reportLoading ? <><span className="ai-spin" /> Generating report…</> : "✦ Generate treasurer report"}
              </button>
            </div>

            {reportLoading && (
              <div className="bf-report-box">
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span className="ai-spin" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  <div style={{ fontSize: 13, color: "var(--slate)" }}>AI is analyzing your financials and writing the report…</div>
                </div>
              </div>
            )}

            {reportText && !reportLoading && (
              <div className="bf-report-box">
                <div className="bf-report-header">Treasurer's Report — {MONTH_LABELS[activeMonth]}</div>
                <div className="bf-report-sub">${orgName} Homeowners Association · Prepared by Board-Armor AI · For board review</div>
                <div className="bf-report-body">{reportText}</div>
                <div style={{ display: "flex", gap: 10, marginTop: "1.25rem", paddingTop: "1rem", borderTop: ".5px solid var(--border)" }}>
                  <button className="btn-g btn-sm" onClick={generateReport}>Regenerate</button>
                  <button className="btn btn-sm" onClick={() => { navigator.clipboard?.writeText(`TREASURER'S REPORT — ${MONTH_LABELS[activeMonth]}\n${orgName} HOA\n\n${reportText}`); toast("Report copied to clipboard"); }}>Copy report</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Budget Modal */}
      {editBudget && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setEditBudget(false)}>
          <div className="md md-lg">
            <div className="mt2">Edit monthly budget — {MONTH_LABELS[activeMonth]}</div>
            <div className="ms2">Set budget targets for each income and expense category</div>
            <div style={{ marginBottom: ".5rem", fontSize: 12, fontWeight: 600, color: "var(--teal)", textTransform: "uppercase", letterSpacing: ".05em" }}>Income budgets</div>
            <div className="budget-edit-grid" style={{ marginBottom: "1.25rem" }}>
              {BUDGET_CATEGORIES.filter(c => c.type === "income").map(c => (
                <div className="fg" key={c.id}>
                  <label className="fl" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />{c.name}
                  </label>
                  <input className="fi" type="number" value={budgetDraft[c.id] ?? budget[c.id] ?? 0} onChange={e => setBudgetDraft(p => ({ ...p, [c.id]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div style={{ marginBottom: ".5rem", fontSize: 12, fontWeight: 600, color: "var(--red)", textTransform: "uppercase", letterSpacing: ".05em" }}>Expense budgets</div>
            <div className="budget-edit-grid">
              {BUDGET_CATEGORIES.filter(c => c.type === "expense").map(c => (
                <div className="fg" key={c.id}>
                  <label className="fl" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />{c.name}
                  </label>
                  <input className="fi" type="number" value={budgetDraft[c.id] ?? budget[c.id] ?? 0} onChange={e => setBudgetDraft(p => ({ ...p, [c.id]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="ma"><button className="btn-g" onClick={() => setEditBudget(false)}>Cancel</button><button className="btn" onClick={saveBudget}>Save budget</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onboarding Wizard ───────────────────────────────────────────────────────
const OB_ROLES = [
  { id: "president",  icon: "👑", name: "President",  desc: "Presides, executes contracts" },
  { id: "treasurer",  icon: "💰", name: "Treasurer",  desc: "Finances, dues, reports" },
  { id: "secretary",  icon: "📋", name: "Secretary",  desc: "Minutes, records, notices" },
  { id: "member",     icon: "👤", name: "Board member", desc: "General board duties" },
  { id: "manager",    icon: "🏢", name: "Manager",    desc: "Property management firm" },
  { id: "owner",      icon: "🔑", name: "Owner/admin", desc: "Full account access" },
];

function HomeownerStep({ obOwners, setObOwners, addObOwner, updateObOwner, removeObOwner }) {
  const [csvDrag, setCsvDrag] = useState(false);
  const [csvError, setCsvError] = useState("");
  const [csvSuccess, setCsvSuccess] = useState(0);
  const [mode, setMode] = useState("manual"); // "manual" | "csv"

  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return [];
    // Detect header row
    const firstLower = lines[0].toLowerCase();
    const hasHeader = firstLower.includes("unit") || firstLower.includes("name") || firstLower.includes("email");
    const dataLines = hasHeader ? lines.slice(1) : lines;
    return dataLines.map(line => {
      // Handle quoted CSV
      const cols = line.match(/(".*?"|[^,]+)/g)?.map(c => c.replace(/^"|"$/g, "").trim()) || [];
      return { id: Date.now() + Math.random(), unit: cols[0] || "", name: cols[1] || "", email: cols[2] || "", phone: cols[3] || "" };
    }).filter(r => r.unit || r.name);
  };

  const handleCsvDrop = (e) => {
    e.preventDefault(); setCsvDrag(false); setCsvError("");
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".csv") && !file.name.endsWith(".txt")) { setCsvError("Please drop a .csv file."); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCSV(ev.target.result);
      if (!parsed.length) { setCsvError("No valid rows found. Check your CSV format."); return; }
      setObOwners(prev => {
        // Remove the single empty placeholder row if present
        const cleaned = prev.filter(r => r.unit || r.name);
        return [...cleaned, ...parsed];
      });
      setCsvSuccess(parsed.length);
    };
    reader.readAsText(file);
  };

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCSV(ev.target.result);
      if (!parsed.length) { setCsvError("No valid rows found."); return; }
      setObOwners(prev => {
        const cleaned = prev.filter(r => r.unit || r.name);
        return [...cleaned, ...parsed];
      });
      setCsvSuccess(parsed.length);
    };
    reader.readAsText(file);
  };

  return (
    <div>
      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1rem" }}>
        {[{ id: "manual", label: "✏️ Add manually" }, { id: "csv", label: "📂 Import CSV" }].map(m => (
          <button key={m.id} onClick={() => { setMode(m.id); setCsvError(""); setCsvSuccess(0); }}
            style={{ flex: 1, padding: ".5rem", borderRadius: "var(--r)", border: `1.5px solid ${mode === m.id ? "var(--teal)" : "var(--border2)"}`, background: mode === m.id ? "rgba(20,184,166,.1)" : "var(--navy3)", color: mode === m.id ? "var(--teal)" : "var(--slate)", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all .2s" }}>
            {m.label}
          </button>
        ))}
      </div>

      {mode === "csv" ? (
        <div>
          {/* CSV drop zone */}
          <div
            style={{ border: `2px dashed ${csvDrag ? "var(--teal)" : "var(--border2)"}`, borderRadius: "var(--r)", padding: "1.5rem", textAlign: "center", background: csvDrag ? "rgba(20,184,166,.07)" : "var(--navy3)", cursor: "pointer", transition: "all .2s", marginBottom: ".75rem" }}
            onDragOver={e => { e.preventDefault(); setCsvDrag(true); }}
            onDragLeave={() => setCsvDrag(false)}
            onDrop={handleCsvDrop}
            onClick={() => document.getElementById("ob-csv-input").click()}
          >
            <input id="ob-csv-input" type="file" accept=".csv,.txt,.xlsx,.xls" style={{ display: "none" }} onChange={handleCsvFile} />
            <div style={{ fontSize: 28, marginBottom: ".4rem" }}>{csvSuccess ? "✅" : "📋"}</div>
            {csvSuccess
              ? <div style={{ fontSize: 14, fontWeight: 600, color: "var(--teal)" }}>{csvSuccess} homeowner{csvSuccess > 1 ? "s" : ""} imported!</div>
              : <>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--white)", marginBottom: ".3rem" }}>{csvDrag ? "Drop your CSV here" : "Drag & drop or click to browse"}</div>
                  <div style={{ fontSize: 12, color: "var(--slate)" }}>CSV format: Unit, Name, Email, Phone</div>
                </>
            }
          </div>
          {csvError && <div style={{ color: "#f87171", fontSize: 12, marginBottom: ".5rem" }}>{csvError}</div>}
          {/* Template download hint */}
          <div style={{ background: "var(--navy3)", borderRadius: "var(--r)", padding: ".75rem 1rem", fontSize: 12, color: "var(--slate)", marginBottom: ".75rem" }}>
            <strong style={{ color: "var(--white)", display: "block", marginBottom: ".3rem" }}>CSV format (columns in order):</strong>
            <code style={{ color: "var(--teal)", fontSize: 11 }}>Unit, Full Name, Email, Phone</code><br />
            <span style={{ fontSize: 11 }}>First row can be a header — it will be skipped automatically.</span>
          </div>
          {/* Show imported rows preview */}
          {obOwners.filter(r => r.unit || r.name).length > 0 && (
            <div style={{ background: "var(--navy3)", borderRadius: "var(--r)", padding: ".75rem", maxHeight: 200, overflowY: "auto" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: ".5rem" }}>Imported homeowners ({obOwners.filter(r => r.unit || r.name).length})</div>
              {obOwners.filter(r => r.unit || r.name).slice(0, 8).map((h, i) => (
                <div key={h.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--white)", padding: ".25rem 0", borderBottom: "1px solid var(--border2)" }}>
                  <span style={{ color: "var(--teal)", minWidth: 60 }}>Unit {h.unit}</span>
                  <span style={{ flex: 1, paddingLeft: 8 }}>{h.name}</span>
                  <span style={{ color: "var(--slate)", fontSize: 11 }}>{h.email}</span>
                </div>
              ))}
              {obOwners.filter(r => r.unit || r.name).length > 8 && (
                <div style={{ fontSize: 11, color: "var(--slate)", textAlign: "center", paddingTop: ".4rem" }}>+{obOwners.filter(r => r.unit || r.name).length - 8} more</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: ".75rem" }}>Add homeowners one at a time. Only Unit # and Name are required.</div>
          <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: ".75rem" }}>
            {obOwners.map((h, idx) => (
              <div key={h.id} style={{ background: "var(--navy3)", borderRadius: "var(--r)", padding: ".75rem", marginBottom: ".5rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".4rem" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--teal)" }}>Homeowner {idx + 1}</span>
                  {obOwners.length > 1 && <button onClick={() => removeObOwner(h.id)} style={{ background: "transparent", border: "none", color: "var(--slate)", cursor: "pointer", fontSize: 14 }}>✕</button>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <input className="ob-input" style={{ marginBottom: 0 }} placeholder="Unit # *" value={h.unit} onChange={e => updateObOwner(h.id, "unit", e.target.value)} />
                  <input className="ob-input" style={{ marginBottom: 0 }} placeholder="Full name *" value={h.name} onChange={e => updateObOwner(h.id, "name", e.target.value)} />
                  <input className="ob-input" style={{ marginBottom: 0 }} placeholder="Email" value={h.email} onChange={e => updateObOwner(h.id, "email", e.target.value)} />
                  <input className="ob-input" style={{ marginBottom: 0 }} placeholder="Phone" value={h.phone} onChange={e => updateObOwner(h.id, "phone", e.target.value)} />
                </div>
              </div>
            ))}
          </div>
          <button className="btn-g" style={{ fontSize: 12 }} onClick={addObOwner}>+ Add another homeowner</button>
        </div>
      )}

      <div style={{ background: "rgba(42,99,214,.06)", border: ".5px solid rgba(42,99,214,.15)", borderRadius: "var(--r)", padding: ".65rem 1rem", fontSize: 12, color: "var(--slate)", marginTop: ".75rem" }}>
        💡 You can always add more homeowners from the <strong style={{ color: "var(--white)" }}>Homeowner Directory</strong> after setup.
      </div>
    </div>
  );
}

function GettingStarted({ owners, setOwners, toast, orgName = "Your HOA", orgEmail = "", setPage }) {
  const { orgId } = useAppCtx();
  const [obOwners,     setObOwners]     = useState([{ id: 1, unit: "", name: "", email: "", phone: "" }]);
  const [importing,    setImporting]    = useState(false);
  const [members,      setMembers]      = useState([{ id: 1, email: "", role: "treasurer" }]);
  const [invitingBoard,setInvitingBoard]= useState(false);

  const addObOwner    = () => setObOwners(p => [...p, { id: Date.now(), unit: "", name: "", email: "", phone: "" }]);
  const updateObOwner = (id, k, v) => setObOwners(p => p.map(h => h.id === id ? { ...h, [k]: v } : h));
  const removeObOwner = (id) => setObOwners(p => p.length > 1 ? p.filter(h => h.id !== id) : p);

  const addMember    = () => setMembers(p => [...p, { id: Date.now(), email: "", role: "member" }]);
  const updateMember = (id, k, v) => setMembers(p => p.map(m => m.id === id ? { ...m, [k]: v } : m));
  const removeMember = (id) => setMembers(p => p.filter(m => m.id !== id));

  const importHomeowners = async () => {
    const rows = obOwners.filter(r => r.unit?.trim() && r.name?.trim());
    if (!rows.length) { toast("Add at least one homeowner with a unit and name first"); return; }
    setImporting(true);
    let ok = 0, failed = 0;
    const saved = [];
    for (const r of rows) {
      try {
        const { data, error } = await db.homeowners.save(orgId, {
          name: r.name, unit: r.unit, email: r.email, phone: r.phone,
          status: "current", dues_owed: 0, dues_paid: 0, email_opt_in: true,
        });
        if (error) throw error;
        if (data) { saved.push(data); ok++; }
      } catch (e) { failed++; }
    }
    if (saved.length) setOwners(prev => [...prev, ...saved]);
    setImporting(false);
    setObOwners([{ id: Date.now(), unit: "", name: "", email: "", phone: "" }]);
    if (ok) toast(`✅ Imported ${ok} homeowner${ok > 1 ? "s" : ""}${failed ? ` (${failed} failed — check for duplicate units)` : ""}`);
    else toast("Import failed — check for duplicate unit numbers");
  };

  const inviteBoard = async () => {
    const toInvite = members.filter(m => m.email && m.email.includes("@"));
    if (!toInvite.length) { toast("Add at least one board member email first"); return; }
    setInvitingBoard(true);
    await Promise.allSettled(toInvite.map(m =>
      fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: m.email,
          subject: `You've been invited to join ${orgName || "your HOA"} on Board-Armor`,
          text: `Hi,\n\nYou've been added as a board member (${m.role || "Member"}) for ${orgName || "your HOA"} on Board-Armor — an AI-powered HOA management platform.\n\nClick the link below to create your account and get started:\n\nhttps://app.board-armor.com\n\nIf you have any questions, contact your board president directly.\n\nWelcome aboard,\nThe Board-Armor Team`,
          type: "board-invite",
        }),
      }).catch(() => null)
    ));
    setInvitingBoard(false);
    toast(`✅ Invited ${toInvite.length} board member${toInvite.length > 1 ? "s" : ""}`);
    setMembers([{ id: Date.now(), email: "", role: "member" }]);
  };

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">Getting Started</div>
          <div className="ps">Finish setting up {orgName || "your HOA"} — come back to this anytime.</div>
        </div>
      </div>

      <div style={{ display: "grid", gap: "1rem", maxWidth: 720 }}>

        {/* Import homeowners — real save, appears in Homeowner Directory immediately */}
        <div className="card" style={{ padding: "1.25rem" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: ".25rem" }}>🏠 Import your homeowners</div>
          <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1rem" }}>Drag and drop a CSV, or add homeowners manually. They'll appear in your Homeowner Directory immediately.</div>
          <HomeownerStep obOwners={obOwners} setObOwners={setObOwners} addObOwner={addObOwner} updateObOwner={updateObOwner} removeObOwner={removeObOwner} />
          <button className="btn" style={{ marginTop: "1rem" }} onClick={importHomeowners} disabled={importing}>
            {importing ? "Importing…" : "Save homeowners →"}
          </button>
        </div>

        {/* Invite board — sends real invite emails */}
        <div className="card" style={{ padding: "1.25rem" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: ".25rem" }}>👥 Invite your board</div>
          <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1rem" }}>Board members get an email with a link to create their own login.</div>
          {members.map(m => (
            <div key={m.id} className="ob-member-row">
              <input className="ob-input" style={{ flex: 2, marginBottom: 0 }} placeholder="email@example.com" value={m.email} onChange={e => updateMember(m.id, "email", e.target.value)} />
              <select className="ob-input" style={{ flex: 1, marginBottom: 0 }} value={m.role} onChange={e => updateMember(m.id, "role", e.target.value)}>
                {OB_ROLES.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <button onClick={() => removeMember(m.id)} style={{ background: "transparent", border: "none", color: "var(--slate)", cursor: "pointer", fontSize: 16, padding: "0 4px", flexShrink: 0 }}>✕</button>
            </div>
          ))}
          <button className="btn-g" style={{ marginTop: 4, fontSize: 12 }} onClick={addMember}>+ Add another board member</button>
          <div><button className="btn" style={{ marginTop: "1rem" }} onClick={inviteBoard} disabled={invitingBoard}>{invitingBoard ? "Sending…" : "Send invites →"}</button></div>
        </div>

        {/* Documents — real Document Vault, not a duplicate uploader */}
        <div className="card" style={{ padding: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: ".25rem" }}>📁 Upload your documents</div>
            <div style={{ fontSize: 13, color: "var(--slate)" }}>Drag in CC&Rs, bylaws, and rules — the AI reads them instantly in the Document Vault.</div>
          </div>
          <button className="btn-g" onClick={() => setPage("vault")}>Go to Document Vault →</button>
        </div>

        {/* HOA profile */}
        <div className="card" style={{ padding: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: ".25rem" }}>🏛️ Complete your HOA profile</div>
            <div style={{ fontSize: 13, color: "var(--slate)" }}>Address, unit count, dues amount, and fiscal year.</div>
          </div>
          <button className="btn-g" onClick={() => setPage("settings")}>Go to Settings →</button>
        </div>

        {/* Insurance */}
        <div className="card" style={{ padding: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: ".25rem" }}>🛡️ Add insurance & compliance info</div>
            <div style={{ fontSize: 13, color: "var(--slate)" }}>Carrier, policy number, and renewal dates — with automatic expiry alerts.</div>
          </div>
          <button className="btn-g" onClick={() => setPage("compliance")}>Go to Insurance & Compliance →</button>
        </div>

      </div>
    </div>
  );
}

// ─── Stripe Checkout ──────────────────────────────────────────────────────────
// ─── Paywall Screen ───────────────────────────────────────────────────────────
function PaywallScreen({ orgName = "Your HOA", orgId, onSuccess }) {
  const [plan,    setPlan]    = useState("starter");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const PLANS = [
    { id: "starter",  name: "Starter",    mo: 69,  desc: "Up to 75 units",  features: ["All features included", "Up to 75 units", "Up to 500 emails/mo"] },
    { id: "pro",      name: "Pro",        mo: 109,  desc: "76–150 units", pop: true, features: ["All features included", "76–150 units", "Up to 1,500 emails/mo"] },
    { id: "premium",  name: "Enterprise", mo: 299, desc: "151+ units",       features: ["All features included", "151+ units", "Unlimited emails"] },
  ];
  const selected = PLANS.find(p => p.id === plan);

  const handleCheckout = () => {
    setError(""); setLoading(true);
    fetch("/.netlify/functions/stripe-checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan, orgName, orgId }),
    })
    .then(r => r.json())
    .then(d => {
      if (d.url) { window.location.href = d.url; }
      else { setError(d.error || "Checkout failed. Try again."); setLoading(false); }
    })
    .catch(() => { setError("Network error. Please try again."); setLoading(false); });
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--navy)", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", backgroundImage: "radial-gradient(ellipse 60% 45% at 60% 10%,rgba(45,212,160,.09) 0%,transparent 65%)" }}>
      <div style={{ width: "100%", maxWidth: 560 }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "var(--white)", letterSpacing: "-0.5px" }}>🛡️ Board-<span style={{ color: "var(--teal)" }}>Armor</span></div>
          <div style={{ fontSize: 13, color: "var(--slate)", marginTop: 4 }}>AI-Powered HOA Management · Built for Self-Managed Boards</div>
        </div>

        {/* Card */}
        <div style={{ background: "var(--navy2)", border: ".5px solid var(--border2)", borderRadius: "var(--r2)", overflow: "hidden" }}>

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,rgba(42,99,214,.12),rgba(20,184,166,.08))", borderBottom: ".5px solid var(--border)", padding: "1.5rem 2rem" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--white)", marginBottom: ".35rem" }}>Activate your free trial</div>
            <div style={{ fontSize: 13, color: "var(--slate)", lineHeight: 1.6 }}>
              {orgName} is set up and ready. Choose a plan to start your <strong style={{ color: "var(--white)" }}>30-day free trial</strong> — your card won't be charged until day 31. Cancel anytime.
            </div>
          </div>

          <div style={{ padding: "1.5rem 2rem" }}>

            {/* Plan selector */}
            <div style={{ marginBottom: "1.25rem" }}>
              {PLANS.map(p => (
                <div key={p.id}
                  onClick={() => setPlan(p.id)}
                  style={{ position: "relative", background: plan === p.id ? "rgba(20,184,166,.08)" : "var(--navy3)", border: `1.5px solid ${plan === p.id ? "var(--teal)" : "var(--border2)"}`, borderRadius: "var(--r)", padding: "1rem 1.25rem", marginBottom: ".5rem", cursor: "pointer", transition: "all .15s" }}
                >
                  {p.pop && <div style={{ position: "absolute", top: -10, right: 12, background: "var(--orange)", color: "var(--white)", fontSize: 10, fontWeight: 700, padding: "2px 10px", borderRadius: 20, textTransform: "uppercase", letterSpacing: ".06em" }}>Most popular</div>}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".3rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${plan === p.id ? "var(--teal)" : "var(--border2)"}`, background: plan === p.id ? "var(--teal)" : "transparent", flexShrink: 0, transition: "all .15s" }} />
                      <span style={{ fontWeight: 700, color: "var(--white)", fontSize: 15 }}>{p.name}</span>
                      <span style={{ fontSize: 12, color: "var(--slate)" }}>{p.desc}</span>
                    </div>
                    <span style={{ fontWeight: 700, color: plan === p.id ? "var(--teal)" : "var(--white)", fontSize: 16 }}>${p.mo}<span style={{ fontSize: 11, fontWeight: 400, color: "var(--slate)" }}>/mo</span></span>
                  </div>
                  {plan === p.id && (
                    <div style={{ display: "flex", gap: 8, marginLeft: 26, flexWrap: "wrap" }}>
                      {p.features.map(f => <span key={f} style={{ fontSize: 11, color: "var(--teal)", background: "rgba(20,184,166,.1)", borderRadius: 4, padding: "2px 8px" }}>✓ {f}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Trust badges */}
            <div style={{ display: "flex", gap: 16, marginBottom: "1.25rem", flexWrap: "wrap" }}>
              {["🔒 256-bit SSL", "✅ Cancel anytime", "🎁 30 days free"].map(b => (
                <span key={b} style={{ fontSize: 12, color: "var(--slate)" }}>{b}</span>
              ))}
            </div>

            {error && <div style={{ background: "rgba(248,113,113,.1)", border: ".5px solid rgba(248,113,113,.3)", borderRadius: "var(--r)", padding: ".65rem 1rem", fontSize: 13, color: "#f87171", marginBottom: "1rem" }}>{error}</div>}

            {/* CTA */}
            <button
              onClick={handleCheckout}
              disabled={loading}
              style={{ width: "100%", padding: "1rem", background: loading ? "var(--border)" : "var(--orange)", color: loading ? "var(--slate)" : "var(--white)", border: "none", borderRadius: "var(--r)", fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", transition: "all .2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              {loading
                ? <><span className="ai-spin" style={{ width: 16, height: 16, borderColor: "rgba(255,255,255,.3)", borderTopColor: "var(--white)" }} />Redirecting to checkout…</>
                : `Start free trial — ${selected?.name} plan →`}
            </button>

            <div style={{ textAlign: "center", marginTop: ".85rem", fontSize: 12, color: "var(--slate)" }}>
              Then ${selected?.mo}/month after 30 days · Cancel before day 30 and pay nothing
            </div>
          </div>
        </div>

        {/* Sign out link */}
        <div style={{ textAlign: "center", marginTop: "1.25rem" }}>
          <span
            onClick={() => { supabase.auth.signOut(); window.location.reload(); }}
            style={{ fontSize: 12, color: "var(--slate)", cursor: "pointer", textDecoration: "underline" }}
          >Sign out</span>
        </div>
      </div>
    </div>
  );
}

function StripeCheckout({ onSuccess, onBack, initialPlan = "pro" , orgName = "Your HOA", orgEmail = "", orgId }) {
  const [plan,    setPlan]    = useState(initialPlan);
  const [annual,  setAnnual]  = useState(false);
  const [cardNum, setCardNum] = useState("");
  const [expiry,  setExpiry]  = useState("");
  const [cvc,     setCvc]     = useState("");
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const PLANS = [
    { id: "starter",  name: "Starter",    mo: 69,  desc: "Up to 75 units",  features: ["All features included", "Up to 75 units", "Up to 500 emails/mo"] },
    { id: "pro",      name: "Pro",        mo: 109,  desc: "76–150 units", pop: true, features: ["All features included", "76–150 units", "Up to 1,500 emails/mo"] },
    { id: "premium",  name: "Enterprise", mo: 299, desc: "151+ units",       features: ["All features included", "151+ units", "Unlimited emails"] },
  ];
  const selected = PLANS.find(p => p.id === plan);
  const price = annual ? Math.round(selected.mo * 12 * 0.8) : selected.mo;
  const period = annual ? "/year" : "/month";
  const savings = annual ? `Save $${Math.round(selected.mo * 12 * 0.2)}/yr` : "Save 20% annually";

  const formatCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
  const formatExp  = (v) => { const d = v.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? d.slice(0, 2) + "/" + d.slice(2) : d; };

  const handleSubmit = () => {
    if (!name || !email || cardNum.replace(/\s/g,"").length < 16 || expiry.length < 5 || cvc.length < 3) {
      setError("Please fill in all payment fields."); return;
    }
    setError(""); setLoading(true);
    // Call real Stripe checkout
    fetch('/.netlify/functions/stripe-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, email, orgName: name, orgId }),
    })
    .then(r => r.json())
    .then(d => {
      if (d.url) { window.location.href = d.url; }
      else { setError(d.error || 'Checkout failed. Try again.'); setLoading(false); }
    })
    .catch(() => { setError('Network error. Try again.'); setLoading(false); });
  };

  const maskedNum = cardNum ? cardNum.padEnd(19, "•").slice(0, 19) : "•••• •••• •••• ••••";
  const maskedExp = expiry || "MM/YY";

  return (
    <div className="stripe-wrap">
      <div className="stripe-card">
        <div className="stripe-header">
          <div className="stripe-logo" style={{ display: "flex", alignItems: "center", gap: 8 }}><BoardArmorLogo size={22} />Board-<span>Armor</span></div>
          <div style={{ fontSize: 13, color: "var(--slate)" }}>Secure checkout · 30-day free trial</div>
          {onBack && <button className="btn-g btn-sm" onClick={onBack}>← Back</button>}
        </div>

        <div className="stripe-body">
          {/* Left — plan selector */}
          <div className="stripe-plans">
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--slate)", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: "1rem" }}>Choose your plan</div>

            <div className="toggle-annual" onClick={() => setAnnual(a => !a)} style={{ cursor: "pointer" }}>
              <div className={`toggle-switch ${annual ? "on" : ""}`}><div className="toggle-knob" /></div>
              <span>Annual billing</span>
              <span style={{ color: "var(--teal)", fontSize: 12, fontWeight: 600 }}>{savings}</span>
            </div>

            {PLANS.map(p => (
              <div key={p.id} className={`sp-card ${plan === p.id ? "selected" : ""}`} onClick={() => setPlan(p.id)}>
                {p.pop && <div className="sp-pop">Most popular</div>}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span className="sp-name">{p.name}</span>
                  <span>
                    <span style={{ fontSize: 20, fontWeight: 700, color: "var(--white)", fontFamily: "var(--serif)" }}>${annual ? Math.round(p.mo * 0.8) : p.mo}</span>
                    <span className="sp-mo">/mo{annual ? " (billed annually)" : ""}</span>
                  </span>
                </div>
                <div className="sp-features" style={{ marginTop: ".5rem" }}>
                  {p.features.map(f => <div key={f} className="sp-feat">{f}</div>)}
                </div>
              </div>
            ))}
          </div>

          {/* Right — payment form */}
          <div className="stripe-checkout">
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--slate)", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: "1rem" }}>Payment details</div>

            <div className="trial-badge">
              🎁 <strong>30-day free trial</strong> — you won't be charged until {new Date(Date.now() + 30*86400000).toLocaleDateString("en-US", { month: "long", day: "numeric" })}
            </div>

            {/* Animated card preview */}
            <div className="cc-fake-card">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: ".5rem" }}>
                <div style={{ fontFamily: "var(--serif)", fontSize: 16, color: "var(--teal)" }}>Board-Armor</div>
                <div style={{ fontSize: 20 }}>💳</div>
              </div>
              <div className="cc-card-num">{maskedNum}</div>
              <div className="cc-card-row">
                <div><div>Card holder</div><div className="cc-card-val">{name || "Your name"}</div></div>
                <div><div>Expires</div><div className="cc-card-val">{maskedExp}</div></div>
                <div><div>Plan</div><div className="cc-card-val" style={{ color: "var(--teal)" }}>{selected?.name}</div></div>
              </div>
            </div>

            <label className="cc-label">Full name</label>
            <input className="cc-input" placeholder="Sarah Kim" value={name} onChange={e => setName(e.target.value)} />
            <label className="cc-label">Email address</label>
            <input className="cc-input" type="email" placeholder="sarah@myhoq.com" value={email} onChange={e => setEmail(e.target.value)} />
            <label className="cc-label">Card number</label>
            <input className="cc-input" placeholder="1234 5678 9012 3456" value={cardNum} onChange={e => setCardNum(formatCard(e.target.value))} maxLength={19} />
            <div className="cc-row">
              <div>
                <label className="cc-label">Expiry</label>
                <input className="cc-input" placeholder="MM/YY" value={expiry} onChange={e => setExpiry(formatExp(e.target.value))} maxLength={5} />
              </div>
              <div>
                <label className="cc-label">CVC</label>
                <input className="cc-input" placeholder="123" value={cvc} onChange={e => setCvc(e.target.value.replace(/\D/g,"").slice(0,4))} maxLength={4} />
              </div>
            </div>

            {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: ".5rem" }}>{error}</div>}

            <div style={{ background: "var(--navy3)", borderRadius: "var(--r)", padding: ".75rem 1rem", fontSize: 13, display: "flex", justifyContent: "space-between", marginBottom: ".25rem" }}>
              <span style={{ color: "var(--slate)" }}>Due today</span>
              <span style={{ fontWeight: 700, color: "var(--teal)" }}>$0.00</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--slate)", textAlign: "right", marginBottom: ".25rem" }}>
              Then ${price}{period} after 30-day trial
            </div>

            <button className="stripe-btn" onClick={handleSubmit} disabled={loading}>
              {loading ? <><span className="ai-spin" style={{ width: 14, height: 14, borderColor: "rgba(15,31,61,.3)", borderTopColor: "var(--navy)" }} />Processing…</> : `Start free trial — ${selected?.name} plan`}
            </button>

            <div className="secure-row">
              <span>🔒</span>
              <span>Secured by Stripe · 256-bit SSL encryption · Cancel anytime</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Settings ────────────────────────────────────────────────────────────────
// ─── Admin Dashboard ─────────────────────────────────────────────────────────
function AdminDashboard({ supabaseUrl, supabaseKey }) {
  const [orgs, setOrgs]       = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [sort, setSort]       = useState("created_at");

  const srvHeaders = {
    "Content-Type": "application/json",
    "apikey": supabaseKey,
    "Authorization": `Bearer ${supabaseKey}`,
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [orgRes, memRes] = await Promise.all([
          fetch(`${supabaseUrl}/rest/v1/organizations?select=*&order=created_at.desc`, { headers: srvHeaders }),
          fetch(`${supabaseUrl}/rest/v1/board_members?select=*&order=created_at.desc`, { headers: srvHeaders }),
        ]);
        const orgData = await orgRes.json();
        const memData = await memRes.json();
        setOrgs(Array.isArray(orgData) ? orgData : []);
        setMembers(Array.isArray(memData) ? memData : []);
      } catch(e) { setError(e.message); }
      setLoading(false);
    };
    load();
  }, []);

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const statusColor = (s, plan) => {
    if (s === "active" && plan && plan.toLowerCase() === "trial") return "#94a3b8";
    if (s === "active") return "#22c55e";
    if (s === "trialing") return "#14b8a6";
    if (s === "past_due") return "#dc2626";
    if (s === "cancelled") return "#ef4444";
    return "#64748b";
  };
  const statusLabel = (s, plan) => {
    if (s === "active" && plan && plan.toLowerCase() === "trial") return "Test data";
    if (s === "active") return "Active ✓";
    if (s === "trialing") return "Trialing";
    if (s === "past_due") return "Past due";
    if (s === "cancelled") return "Cancelled";
    return "No subscription";
  };

  const sorted = [...orgs].sort((a, b) => {
    if (sort === "created_at") return new Date(b.created_at) - new Date(a.created_at);
    if (sort === "plan_status") return (a.plan_status || "").localeCompare(b.plan_status || "");
    if (sort === "name") return (a.name || "").localeCompare(b.name || "");
    return 0;
  });

  const stats = {
    total: orgs.length,
    active: orgs.filter(o => o.plan_status === "active" && o.plan?.toLowerCase() !== "trial").length,
    trialing: orgs.filter(o => o.plan_status === "trialing").length,
    noSub: orgs.filter(o => !o.plan_status || o.plan_status === "cancelled").length,
    mrr: orgs.filter(o => o.plan_status === "active" && o.plan?.toLowerCase() !== "trial").reduce((acc, o) => {
      const prices = { starter: 69, pro: 109, enterprise: 299 };
      return acc + (prices[o.plan] || 69);
    }, 0),
  };

  if (loading) return <div style={{ padding: "2rem", textAlign: "center", color: "var(--slate)" }}>Loading admin data...</div>;
  if (error) return <div style={{ padding: "2rem", color: "#ef4444" }}>Error: {error}</div>;

  return (
    <div>
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: "var(--white)", marginBottom: 4 }}>🔐 Admin Dashboard</div>
        <div style={{ fontSize: 12, color: "var(--slate)" }}>Only visible to admin accounts. Real-time data from Supabase.</div>
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: "1.5rem" }}>
        {[
          { label: "Total Signups", value: stats.total, color: "var(--white)" },
          { label: "Active Paying", value: stats.active, color: "#22c55e" },
          { label: "In Trial", value: stats.trialing, color: "#14b8a6" },
          { label: "No Subscription", value: stats.noSub, color: "var(--slate)" },
          { label: "MRR", value: `$${stats.mrr}`, color: "var(--orange)" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1rem", textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color, letterSpacing: "-0.5px", marginBottom: 3 }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "var(--slate)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Sort controls */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1rem", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--slate)" }}>Sort by:</span>
        {[["created_at", "Newest"], ["name", "Name"], ["plan_status", "Status"]].map(([id, label]) => (
          <button key={id} onClick={() => setSort(id)}
            style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, border: `1px solid ${sort === id ? "var(--teal)" : "var(--border2)"}`, background: sort === id ? "rgba(20,184,166,.1)" : "var(--navy3)", color: sort === id ? "var(--teal)" : "var(--slate)", cursor: "pointer", fontWeight: sort === id ? 700 : 400 }}>
            {label}
          </button>
        ))}
      </div>

      {/* Orgs table */}
      <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr", padding: ".75rem 1rem", background: "rgba(255,255,255,.03)", borderBottom: ".5px solid var(--border2)" }}>
          {["HOA Name", "Email", "Plan", "Status", "Signed Up"].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</div>
          ))}
        </div>

        {sorted.length === 0 && (
          <div style={{ padding: "2rem", textAlign: "center", color: "var(--slate)", fontSize: 13 }}>
            No signups yet. Share BoardArmor to get your first customer!
          </div>
        )}

        {sorted.map((org, i) => {
          const orgMembers = members.filter(m => m.org_id === org.id);
          const primaryEmail = orgMembers[0]?.email || org.email || "—";
          return (
            <div key={org.id} style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr", padding: ".8rem 1rem", borderBottom: i < sorted.length - 1 ? ".5px solid var(--border2)" : "none", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)" }}>{org.name || "Unnamed HOA"}</div>
                <div style={{ fontSize: 11, color: "var(--slate)" }}>{orgMembers.length} member{orgMembers.length !== 1 ? "s" : ""}</div>
              </div>
              <div style={{ fontSize: 12, color: "var(--slate)", wordBreak: "break-all" }}>{primaryEmail}</div>
              <div style={{ fontSize: 12, color: "var(--white)", textTransform: "capitalize" }}>{org.plan || "—"}</div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: statusColor(org.plan_status, org.plan), background: `${statusColor(org.plan_status, org.plan)}18`, padding: "2px 8px", borderRadius: 10 }}>
                  {statusLabel(org.plan_status, org.plan)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--slate)" }}>{fmt(org.created_at)}</div>
            </div>
          );
        })}
      </div>

      {/* Board members breakdown */}
      <div style={{ marginTop: "1.5rem" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: ".75rem" }}>All Board Member Accounts ({members.length})</div>
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr", padding: ".75rem 1rem", background: "rgba(255,255,255,.03)", borderBottom: ".5px solid var(--border2)" }}>
            {["Name", "Email", "Role", "Joined"].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</div>
            ))}
          </div>
          {members.length === 0 && (
            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--slate)", fontSize: 13 }}>No board members yet.</div>
          )}
          {members.slice(0, 20).map((m, i) => (
            <div key={m.id} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr", padding: ".7rem 1rem", borderBottom: i < Math.min(members.length, 20) - 1 ? ".5px solid var(--border2)" : "none", alignItems: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--white)" }}>{m.first_name || ""} {m.last_name || ""}</div>
              <div style={{ fontSize: 12, color: "var(--slate)", wordBreak: "break-all" }}>{m.email || "—"}</div>
              <div style={{ fontSize: 11, color: "var(--teal)", textTransform: "capitalize" }}>{m.role || "—"}</div>
              <div style={{ fontSize: 11, color: "var(--slate)" }}>{fmt(m.created_at)}</div>
            </div>
          ))}
          {members.length > 20 && <div style={{ padding: ".75rem 1rem", fontSize: 12, color: "var(--slate)", textAlign: "center" }}>+{members.length - 20} more members</div>}
        </div>
      </div>

      {/* Refresh button */}
      <div style={{ marginTop: "1rem", textAlign: "right" }}>
        <button className="btn-g btn-sm" onClick={() => window.location.reload()} style={{ fontSize: 12, color: "var(--teal)" }}>↻ Refresh data</button>
      </div>
    </div>
  );
}

function Settings({ user, owners, toast, onUpgrade , orgName = "Your HOA", orgEmail = "" }) {
  const [activeSection, setActiveSection] = useState("hoa");
  const [hoaName,    setHoaName]    = useState(() => orgName || getOrgName() || "Your HOA");
  const [pubAdjName,  setPubAdjName]  = useState(localStorage.getItem("ba_pub_adj_name") || "");
  const [pubAdjPhone, setPubAdjPhone] = useState(localStorage.getItem("ba_pub_adj_phone") || "");
  const [pubAdjEmail, setPubAdjEmail] = useState(localStorage.getItem("ba_pub_adj_email") || "");
  const [hoaAddr,    setHoaAddr]    = useState(user?.org?.address || "");
  const [duesAmt,    setDuesAmt]    = useState("300");
  const [stripeLink,  setStripeLink]  = useState(() => localStorage.getItem("ba_stripe_link") || "");
  const [monthlyDues, setMonthlyDues] = useState(() => localStorage.getItem("ba_monthly_dues") || "");
  const [timezone,   setTimezone]   = useState("America/Los_Angeles");
  const [portalSlug, setPortalSlug] = useState(() => (orgName || getOrgName() || "your-hoa").toLowerCase().replace(/[^a-z0-9]+/g, "-"));
  const [customDom,  setCustomDom]  = useState("");
  const [domStatus,  setDomStatus]  = useState("idle"); // idle | checking | verified | error
  const [notifViol,  setNotifViol]  = useState(true);
  const [notifDues,  setNotifDues]  = useState(true);
  const [notifCOI,   setNotifCOI]   = useState(true);
  const [notifWeek,  setNotifWeek]  = useState(false);

  const boardMembers = [
    { name: "Sarah Kim",     role: "Board President",  email: "skim@maplewoodhoa.com",    initials: "SK" },
    { name: "David Reyes",   role: "Treasurer",        email: "dreyes@maplewoodhoa.com",  initials: "DR" },
    { name: "Linda Park",    role: "Secretary",        email: "lpark@maplewoodhoa.com",   initials: "LP" },
  ];

  const invoices = [
    
    
    
  ];

  const checkDomain = () => {
    if (!customDom) return;
    setDomStatus("checking");
    setTimeout(() => setDomStatus(customDom.includes(".") ? "verified" : "error"), 2000);
  };

  const Toggle = ({ on, onChange }) => (
    <div className={`toggle-switch ${on ? "on" : ""}`} onClick={onChange} style={{ cursor: "pointer" }}>
      <div className="toggle-knob" />
    </div>
  );

  const navItems = [
    { id: "hoa",      label: "HOA profile",      icon: "🏠" },
    { id: "payments",  label: "Payments",            icon: "💰" },
    { id: "members",  label: "Board members",     icon: "👥" },
    { id: "portal",   label: "Homeowner portal",  icon: "🔗" },
    { id: "domain",   label: "Custom domain",     icon: "🌐" },
    { id: "billing",  label: "Plan & billing",    icon: "💳" },
    { id: "referrals",label: "Refer & earn",      icon: "🎁" },
    { id: "notifs",   label: "Notifications",     icon: "🔔" },
    ...( ["garygatewood@gmail.com","cg5@gmail.com"].includes(user?.email || user?.org?.email) ? [{ id: "adminpanel", label: "Admin Dashboard", icon: "🔐" }] : [] ),
    { id: "data",     label: "Data & export",     icon: "📦" },
  ];

  return (
    <div>
      <div className="ph"><div><div className="pt">Settings</div><div className="ps">HOA profile, billing, portal, and notification preferences</div></div></div>
      <div className="settings-grid">
        {/* Left nav */}
        <div className="settings-nav">
          <div className="settings-nav-section">Account</div>
          {navItems.map(n => (
            <div key={n.id} className={`settings-nav-item ${activeSection === n.id ? "active" : ""}`} onClick={() => setActiveSection(n.id)}>
              <span style={{ fontSize: 15 }}>{n.icon}</span>{n.label}
            </div>
          ))}
        </div>

        {/* Right content */}
        <div className="settings-content">

          {/* ── Payments ── */}
          {activeSection === "payments" && (
            <div>
              <div className="settings-section">
                <div className="settings-section-title">Stripe Payment Link</div>
                <div className="settings-section-sub">Create a Payment Link in your Stripe dashboard, then paste it here. Board-Armor will include it in dues reminder emails so homeowners can pay online. Money goes directly to your HOA's Stripe account.</div>
                <div style={{ display: "flex", gap: 8, marginBottom: "1rem" }}>
                  <input className="fi" style={{ flex: 1 }} placeholder="https://buy.stripe.com/..." value={stripeLink} onChange={e => setStripeLink(e.target.value)} />
                  <button className="btn btn-sm" onClick={() => { localStorage.setItem("ba_stripe_link", stripeLink); toast("✓ Payment link saved"); }}>Save</button>
                </div>
                {stripeLink && <div style={{ fontSize: 12, color: "var(--green)", marginBottom: "1rem" }}>✓ Payment link set — will be included in dues emails</div>}
                <div style={{ background: "rgba(99,102,241,.07)", border: ".5px solid rgba(99,102,241,.2)", borderRadius: "var(--r2)", padding: "12px 16px", fontSize: 13, color: "var(--slate)" }}>
                  <strong style={{ color: "var(--fg)" }}>How to create a Stripe Payment Link:</strong><br/>
                  1. Go to <a href="https://dashboard.stripe.com/payment-links" target="_blank" rel="noreferrer" style={{ color: "var(--teal)" }}>dashboard.stripe.com/payment-links</a><br/>
                  2. Click + New payment link<br/>
                  3. Set the product name (e.g. "HOA Dues") and amount<br/>
                  4. Copy the link and paste it above
                </div>
              </div>
              <div className="settings-section">
                <div className="settings-section-title">Monthly dues amount</div>
                <div className="settings-section-sub">Default dues amount for your community.</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 14, color: "var(--slate)" }}>$</span>
                  <input className="fi" style={{ width: 120 }} type="number" placeholder="0" value={monthlyDues} onChange={e => setMonthlyDues(e.target.value)} />
                  <button className="btn btn-sm" onClick={() => { localStorage.setItem("ba_monthly_dues", monthlyDues); toast("✓ Dues amount saved"); }}>Save</button>
                </div>
              </div>
            </div>
          )}

          {/* ── HOA Profile ── */}
          {activeSection === "hoa" && (
            <div>
              <div className="settings-section">
                <div className="settings-section-title">HOA profile</div>
                <div className="settings-section-sub">This information appears on all communications, letters, and the homeowner portal.</div>
                <div className="fg" style={{ marginBottom: "1rem" }}><label className="fl">HOA / community name</label><input className="fi" value={hoaName} onChange={e => setHoaName(e.target.value)} /></div>
                <div className="fg" style={{ marginBottom: "1rem" }}><label className="fl">Mailing address</label><input className="fi" value={hoaAddr} onChange={e => setHoaAddr(e.target.value)} /></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: "1rem" }}>
                  <div className="fg"><label className="fl">Monthly dues ($)</label><input className="fi" type="number" value={duesAmt} onChange={e => setDuesAmt(e.target.value)} /></div>
                  <div className="fg"><label className="fl">Timezone</label>
                    <select className="fi" value={timezone} onChange={e => setTimezone(e.target.value)}>
                      <option value="America/Los_Angeles">Pacific (PT)</option>
                      <option value="America/Denver">Mountain (MT)</option>
                      <option value="America/Chicago">Central (CT)</option>
                      <option value="America/New_York">Eastern (ET)</option>
                    </select>
                  </div>
                </div>
                <button className="btn btn-sm" onClick={() => toast("HOA profile saved")}>Save changes</button>
              </div>

              {/* Public Adjuster */}
              <div className="settings-section" style={{ marginTop: "1.5rem" }}>
                <div className="settings-section-title">Public Adjuster</div>
                <div className="settings-section-sub">When a public adjuster is on file, a contact button appears in Emergency Response and is pre-filled in the Insurance Claim Tracker.</div>
                <button className="btn-g btn-sm" style={{ marginBottom: 10, color: "var(--teal)" }} onClick={() => { setPubAdjName("Mission Property Loss Consultants"); setPubAdjPhone("(833) 446-7728"); setPubAdjEmail("info@missionae.com"); toast("Filled in Mission PLC — click Save to apply"); }}>🤝 Use Mission PLC — Board-Armor's recommended partner</button>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: "1rem" }}>
                  <div className="fg">
                    <label className="fl">Company / Name</label>
                    <input className="fi" placeholder="e.g. Mission Property Loss Consultants" value={pubAdjName} onChange={e => setPubAdjName(e.target.value)} />
                  </div>
                  <div className="fg">
                    <label className="fl">Phone</label>
                    <input className="fi" placeholder="(833) 446-7728" value={pubAdjPhone} onChange={e => setPubAdjPhone(e.target.value)} />
                  </div>
                  <div className="fg">
                    <label className="fl">Email</label>
                    <input className="fi" placeholder="info@missionae.com" value={pubAdjEmail} onChange={e => setPubAdjEmail(e.target.value)} />
                  </div>
                </div>
                <button className="btn btn-sm" onClick={() => {
                  localStorage.setItem("ba_pub_adj_name", pubAdjName);
                  localStorage.setItem("ba_pub_adj_phone", pubAdjPhone);
                  localStorage.setItem("ba_pub_adj_email", pubAdjEmail);
                  toast("Public adjuster saved");
                }}>Save public adjuster</button>
              </div>
            </div>
          )}

          {/* ── Board Members ── */}
          {activeSection === "members" && (
            <div>
              <div className="settings-section">
                <div className="settings-section-title">Board members</div>
                <div className="settings-section-sub">All board members can log in to Board-Armor. Adjust roles to control access levels.</div>
                {boardMembers.map((m, i) => (
                  <div key={i} className="member-pill">
                    <div className="member-av">{m.initials}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)" }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: "var(--slate)" }}>{m.email}</div>
                    </div>
                    <span className="bg bg-t" style={{ fontSize: 11 }}>{m.role}</span>
                    {i > 0 && <button className="btn-g btn-sm" style={{ fontSize: 11, marginLeft: 6 }}>Remove</button>}
                  </div>
                ))}
                <div style={{ marginTop: "1rem" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--slate)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: ".75rem" }}>Invite new board member</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input className="fi" style={{ flex: 2 }} placeholder="email@example.com" />
                    <select className="fi" style={{ flex: 1 }}>
                      {OB_ROLES.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <button className="btn btn-sm" onClick={() => toast("Invitation sent")}>Invite</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Portal Settings ── */}
          {activeSection === "portal" && (
            <div>
              <div className="settings-section">
                <div className="settings-section-title">Homeowner portal URL</div>
                <div className="settings-section-sub">Your homeowners access the portal at this address. Share it in welcome emails or your community newsletter.</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: "1rem" }}>
                  <div style={{ fontSize: 13, color: "var(--slate)", flexShrink: 0 }}>board-armor.app/portal/</div>
                  <input className="fi" style={{ flex: 1 }} value={portalSlug} onChange={e => setPortalSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} />
                </div>
                <div className="domain-preview">board-armor.app/portal/{portalSlug}</div>
                <button className="btn btn-sm" onClick={() => toast("Portal URL updated")}>Save URL</button>
              </div>
              <div className="settings-section">
                <div className="settings-section-title">Portal appearance</div>
                <div className="settings-section-sub">Customize what homeowners see when they log in.</div>
                {[
                  { label: "Show dues balance", sub: "Homeowners can see their current balance and payment history", on: true },
                  { label: "Show violations", sub: "Homeowners can view their active and resolved violations", on: true },
                  { label: "Allow maintenance requests", sub: "Homeowners can submit new maintenance requests", on: true },
                  { label: "Show community announcements", sub: "Homeowners can read all board announcements", on: true },
                ].map((s, i) => (
                  <div key={i} className="settings-row">
                    <div><div className="settings-row-label">{s.label}</div><div className="settings-row-sub">{s.sub}</div></div>
                    <div className={`toggle-switch on`} style={{ cursor: "pointer", flexShrink: 0 }}><div className="toggle-knob" /></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Custom Domain ── */}
          {activeSection === "domain" && (
            <div>
              <div className="settings-section">
                <div className="settings-section-title">Custom domain</div>
                <div className="settings-section-sub">Point your own domain (e.g. portal.maplewoodcommons.org) to Board-Armor so homeowners see your community's branding. Available on Premium plan.</div>

                <div style={{ background: "rgba(58,191,126,.07)", border: ".5px solid rgba(58,191,126,.2)", borderRadius: "var(--r)", padding: ".85rem 1rem", marginBottom: "1.25rem", fontSize: 13, color: "var(--slate)" }}>
                  <span style={{ color: "var(--teal)", fontWeight: 600 }}>Available on all plans</span> · Point your own domain to Board-Armor so homeowners see your community branding.
                </div>

                <div style={{ opacity: 0.5 }}>
                  <label className="fl">Your custom domain</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: "1rem" }}>
                    <input className="fi" placeholder="portal.yourhoq.com" value={customDom} onChange={e => setCustomDom(e.target.value)} disabled />
                    <button className="btn btn-sm" onClick={checkDomain} disabled>Verify</button>
                  </div>
                </div>

                <div style={{ background: "var(--navy3)", borderRadius: "var(--r)", padding: "1rem 1.25rem" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)", marginBottom: ".75rem" }}>DNS setup instructions</div>
                  {[
                    { type: "CNAME", host: "portal", value: "portal.board-armor.app", ttl: "3600" },
                  ].map((r, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "80px 80px 1fr 60px", gap: 8, fontSize: 12, fontFamily: "monospace", background: "var(--navy2)", padding: ".6rem .85rem", borderRadius: "var(--r)", marginBottom: 6 }}>
                      <span style={{ color: "var(--amber)" }}>{r.type}</span>
                      <span style={{ color: "var(--slate)" }}>{r.host}</span>
                      <span style={{ color: "var(--teal)" }}>{r.value}</span>
                      <span style={{ color: "var(--slate)" }}>{r.ttl}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 12, color: "var(--slate)", marginTop: ".75rem" }}>Add this CNAME record to your domain provider (GoDaddy, Namecheap, Cloudflare, etc.). DNS changes can take up to 48 hours to propagate.</div>
                </div>
              </div>
            </div>
          )}

          {/* ── Billing ── */}
          {activeSection === "billing" && (
            <div>
              <div className="settings-section">
                <div className="settings-section-title">Current plan</div>
                <div className="plan-current">
                  <div>
                    <div style={{ fontSize: 11, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>Active plan</div>
                    <div className="plan-name-big">Pro</div>
                    <div style={{ fontSize: 13, color: "var(--slate)", marginTop: 3 }}>76–150 units · All features included · Priority support</div>
                  </div>
                  <div style={{ marginLeft: "auto", textAlign: "right" }}>
                    <div className="plan-price-big">$99</div>
                    <div style={{ fontSize: 12, color: "var(--slate)" }}>per month</div>
                    <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 4 }}>Next billing: May 1, 2024</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btn-sm" onClick={onUpgrade}>Change plan</button>
                  <button className="btn-g btn-sm" onClick={async () => {
                    try {
                      const res = await fetch("/.netlify/functions/stripe-portal", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ orgId: user?.orgId || user?.org?.id }),
                      });
                      const d = await res.json();
                      if (d.url) window.location.href = d.url;
                      else toast("Could not open billing portal. Please try again.");
                    } catch { toast("Could not open billing portal. Please try again."); }
                  }}>Manage subscription</button>
                  <button className="btn-g btn-sm" style={{ color: "var(--red)" }} onClick={async () => {
                    if (!window.confirm("This will open the Stripe portal where you can cancel. Your access continues until the end of the billing period.")) return;
                    try {
                      const res = await fetch("/.netlify/functions/stripe-portal", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ orgId: user?.orgId || user?.org?.id }),
                      });
                      const d = await res.json();
                      if (d.url) window.location.href = d.url;
                      else toast("Could not open billing portal. Please try again.");
                    } catch { toast("Could not open billing portal. Please try again."); }
                  }}>Cancel plan</button>
                </div>
              </div>
              <div className="settings-section">
                <div className="settings-section-title">Payment method</div>
                <div className="settings-row" style={{ marginBottom: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 20 }}>💳</span>
                    <div><div className="settings-row-label">Visa ending in 4242</div><div className="settings-row-sub">Expires 12/26</div></div>
                  </div>
                  <button className="btn-g btn-sm" onClick={async () => {
                    try {
                      const res = await fetch("/.netlify/functions/stripe-portal", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ orgId: user?.orgId || user?.org?.id }),
                      });
                      const d = await res.json();
                      if (d.url) window.location.href = d.url;
                      else toast("Could not open billing portal.");
                    } catch { toast("Could not open billing portal."); }
                  }}>Update card</button>
                </div>
              </div>
              <div className="settings-section">
                <div className="settings-section-title">Billing history</div>
                {invoices.map((inv, i) => (
                  <div key={i} className="invoice-list-row">
                    <span style={{ color: "var(--slate)", fontSize: 12 }}>{inv.date}</span>
                    <span style={{ flex: 1, paddingLeft: 16 }}>{inv.desc}</span>
                    <span style={{ fontWeight: 600 }}>{inv.amount}</span>
                    <span className="bg bg-g" style={{ marginLeft: 12 }}>{inv.status}</span>
                    <button className="btn-g btn-sm" style={{ marginLeft: 8, fontSize: 11 }} onClick={() => toast("Invoice downloaded")}>PDF</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Referrals ── */}
          {activeSection === "referrals" && (
            <div className="settings-section">
              <div className="settings-section-title">Refer &amp; Earn</div>
              <div className="settings-section-sub">Share your unique referral link. Every HOA that signs up using your link earns you 1 free month — no limit on referrals.</div>

              <div style={{background:"var(--navy2)",border:"1px solid var(--border)",borderRadius:"var(--r)",padding:"1.5rem",marginBottom:"1rem"}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--slate)",letterSpacing:1,marginBottom:8}}>YOUR REFERRAL LINK</div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <div style={{flex:1,background:"var(--navy3)",border:"1px solid var(--border)",borderRadius:6,padding:"10px 14px",fontSize:13,color:"var(--white)",fontFamily:"monospace",wordBreak:"break-all"}}>
                    {`https://board-armor.com?ref=${user?.org?.referral_code || "..."}`}
                  </div>
                  <button className="btn" onClick={() => {
                    navigator.clipboard.writeText(`https://board-armor.com?ref=${user?.org?.referral_code}`);
                    toast("Referral link copied!");
                  }}>Copy link</button>
                </div>
                <div style={{marginTop:12,fontSize:13,color:"var(--slate)"}}>
                  Your code: <span style={{fontWeight:700,color:"var(--orange)",letterSpacing:2,fontSize:16}}>{user?.org?.referral_code || "..."}</span>
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"1rem",marginBottom:"1rem"}}>
                <div style={{background:"var(--navy2)",border:"1px solid var(--border)",borderRadius:"var(--r)",padding:"1rem",textAlign:"center"}}>
                  <div style={{fontSize:28,fontWeight:800,color:"var(--orange)"}}>{user?.org?.referral_credits || 0}</div>
                  <div style={{fontSize:12,color:"var(--slate)",marginTop:4}}>Free months earned</div>
                </div>
                <div style={{background:"var(--navy2)",border:"1px solid var(--border)",borderRadius:"var(--r)",padding:"1rem",textAlign:"center"}}>
                  <div style={{fontSize:28,fontWeight:800,color:"var(--teal)"}}>{user?.org?.referral_credits || 0}</div>
                  <div style={{fontSize:12,color:"var(--slate)",marginTop:4}}>HOAs referred</div>
                </div>
                <div style={{background:"var(--navy2)",border:"1px solid var(--border)",borderRadius:"var(--r)",padding:"1rem",textAlign:"center"}}>
                  <div style={{fontSize:28,fontWeight:800,color:"var(--white)"}}>${(user?.org?.referral_credits || 0) * 49}</div>
                  <div style={{fontSize:12,color:"var(--slate)",marginTop:4}}>Value earned</div>
                </div>
              </div>

              <div style={{background:"rgba(42,99,214,.05)",border:"1px solid rgba(42,99,214,.15)",borderRadius:"var(--r)",padding:"1rem",marginBottom:"1rem"}}>
                <div style={{fontWeight:600,color:"var(--orange)",marginBottom:8,fontSize:13}}>How it works</div>
                <div style={{fontSize:13,color:"var(--ink2)",lineHeight:1.8}}>
                  1. Share your link or code with other HOA board members<br/>
                  2. They enter code <strong style={{color:"var(--white)",letterSpacing:1}}>{user?.org?.referral_code}</strong> when signing up<br/>
                  3. When they subscribe, you get 1 free month automatically<br/>
                  4. Refer 12 HOAs = full year free. No cap, no expiration.
                </div>
              </div>

              <div style={{display:"flex",gap:"0.75rem",flexWrap:"wrap"}}>
                <button className="btn" onClick={() => {
                  const msg = `I've been using Board-Armor for HOA management — AI-powered violation letters, dues tracking, legal compliance and more. Way better than spreadsheets. Use my referral link for a 30-day free trial: https://board-armor.com?ref=${user?.org?.referral_code}`;
                  window.open(`mailto:?subject=Check out Board-Armor for your HOA&body=${encodeURIComponent(msg)}`);
                }}>📧 Share via email</button>
                <button className="btn" onClick={() => {
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://board-armor.com?ref=${user?.org?.referral_code}`)}`);
                }}>👥 Share on Facebook</button>
                <button className="btn" onClick={() => {
                  navigator.clipboard.writeText(`Check out Board-Armor for HOA management — AI-powered, starts at $69/mo. Use my referral: https://board-armor.com?ref=${user?.org?.referral_code}`);
                  toast("Message copied — paste it anywhere!");
                }}>📋 Copy message</button>
              </div>
            </div>
          )}

          {/* ── Notifications ── */}
          {activeSection === "adminpanel" && (
            <AdminDashboard supabaseUrl={SUPABASE_URL} supabaseKey={SUPABASE_ANON} />
          )}

          {activeSection === "notifs" && (
            <div>
              <div className="settings-section">
                <div className="settings-section-title">Email notifications</div>
                <div className="settings-section-sub">Board-Armor sends these to all board members with email opted in.</div>
                {[
                  { label: "New violation submitted", sub: "When a violation is logged by any board member", on: notifViol, toggle: () => setNotifViol(v => !v) },
                  { label: "Dues payment received", sub: "When a homeowner marks dues as paid", on: notifDues, toggle: () => setNotifDues(v => !v) },
                  { label: "Vendor COI expiring", sub: "30 days before a vendor's certificate expires", on: notifCOI, toggle: () => setNotifCOI(v => !v) },
                  { label: "Weekly summary digest", sub: "Every Monday: open violations, unpaid dues, upcoming deadlines", on: notifWeek, toggle: () => setNotifWeek(v => !v) },
                ].map((s, i) => (
                  <div key={i} className="settings-row">
                    <div><div className="settings-row-label">{s.label}</div><div className="settings-row-sub">{s.sub}</div></div>
                    <div className={`toggle-switch ${s.on ? "on" : ""}`} onClick={s.toggle} style={{ cursor: "pointer", flexShrink: 0 }}><div className="toggle-knob" /></div>
                  </div>
                ))}
                <button className="btn btn-sm" style={{ marginTop: "1rem" }} onClick={() => toast("Notification preferences saved")}>Save preferences</button>
              </div>
            </div>
          )}

          {/* ── Data & Export ── */}
          {activeSection === "data" && (
            <div>
              <div className="settings-section">
                <div className="settings-section-title">Export your data</div>
                <div className="settings-section-sub">Download a copy of your HOA data at any time. Your data is always yours.</div>
                {[
                  { icon: "👥", label: "Homeowner directory", sub: "All units, owners, contact info, dues balances", format: "CSV" },
                  { icon: "💰", label: "Dues & payment history", sub: "Full payment ledger for all periods", format: "CSV" },
                  { icon: "⚠️", label: "Violation log", sub: "All violations, status, letters, and resolution dates", format: "CSV" },
                  { icon: "📊", label: "Financial transactions", sub: "Income and expense ledger with categories", format: "CSV" },
                  { icon: "📋", label: "Full data export", sub: "Everything above in a single ZIP archive", format: "ZIP" },
                ].map((d, i) => (
                  <div key={i} className="settings-row">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 18 }}>{d.icon}</span>
                      <div><div className="settings-row-label">{d.label}</div><div className="settings-row-sub">{d.sub}</div></div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="bg bg-s" style={{ fontSize: 11 }}>{d.format}</span>
                      <button className="btn btn-sm" onClick={() => toast(`${d.label} export downloaded`)}>Download</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="settings-section">
                <div className="settings-section-title" style={{ color: "var(--red)" }}>Danger zone</div>
                <div className="settings-section-sub">These actions are irreversible. Please be certain before proceeding.</div>
                <div className="settings-row" style={{ borderColor: "rgba(224,82,82,.2)" }}>
                  <div><div className="settings-row-label">Delete all data</div><div className="settings-row-sub">Permanently removes all homeowner, financial, and violation data. Cannot be undone.</div></div>
                  <button className="btn-g btn-sm" style={{ color: "var(--red)", borderColor: "rgba(224,82,82,.3)" }} onClick={() => toast("Please contact support to delete all data")}>Delete data</button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Bank Feed Integration ────────────────────────────────────────────────────
// NOTE: In production, replace PLAID_PROXY_URL calls with your Netlify serverless
// function that holds Plaid credentials server-side. The UI and logic below is
// 100% production-ready — only the API endpoint needs wiring.
const PLAID_PROXY_URL = "/api/plaid"; // → netlify/functions/plaid.js

const DEMO_BANKS = [
  { id: "chase",   name: "Chase Bank",             icon: "🏦", color: "#117ACA", accounts: ["Checking ••4821", "Reserve Savings ••2209"] },
  { id: "bofa",    name: "Bank of America",         icon: "🏛️", color: "#E31837", accounts: ["Checking ••3341"] },
  { id: "wells",   name: "Wells Fargo",             icon: "🔴", color: "#D71E28", accounts: ["Checking ••7782", "Savings ••1104"] },
  { id: "us",      name: "US Bank",                 icon: "🔵", color: "#002244", accounts: ["Checking ••5593"] },
  { id: "umpqua",  name: "Umpqua Bank",             icon: "🌲", color: "#005B35", accounts: ["HOA Checking ••8812"] },
];

// Auto-categorization rules: keyword → GL account number
const AUTO_RULES = [
  { keyword: "PORTLAND GENERAL",  account: "5030", label: "Utilities" },
  { keyword: "NW NATURAL",        account: "5030", label: "Utilities" },
  { keyword: "GREENSCAPE",        account: "5010", label: "Landscaping" },
  { keyword: "PACIFIC PLUMBING",  account: "5020", label: "Maintenance & Repairs" },
  { keyword: "BRIGHT SPARK",      account: "5020", label: "Maintenance & Repairs" },
  { keyword: "INSURANCE",         account: "5040", label: "Insurance Premiums" },
  { keyword: "BOARDIQ",           account: "5050", label: "Management Fees" },
  { keyword: "AMAZON",            account: "5080", label: "Admin & Supplies" },
  { keyword: "HOA DUES",          account: "4010", label: "HOA Assessments" },
  { keyword: "LATE FEE",          account: "4020", label: "Late Fees" },
];

function makeDemoTransactions() {
  return [
    { id: "plaid-1",  date: "2024-04-22", merchant: "PORTLAND GENERAL ELECTRIC", amount: -187.44, type: "debit",  pending: false, account: "Checking ••4821", glAccount: "5030", autoMatched: true,  imported: false, selected: true  },
    { id: "plaid-2",  date: "2024-04-21", merchant: "HOA DUES - UNIT 101",        amount:  300.00, type: "credit", pending: false, account: "Checking ••4821", glAccount: "4010", autoMatched: true,  imported: true,  selected: false },
    { id: "plaid-3",  date: "2024-04-20", merchant: "BOARDIQ MANAGEMENT FEE",     amount: -250.00, type: "debit",  pending: false, account: "Checking ••4821", glAccount: "5050", autoMatched: true,  imported: true,  selected: false },
    { id: "plaid-4",  date: "2024-04-19", merchant: "GREENSCAPE LANDSCAPING",     amount: -900.00, type: "debit",  pending: false, account: "Checking ••4821", glAccount: "5010", autoMatched: true,  imported: false, selected: true  },
    { id: "plaid-5",  date: "2024-04-18", merchant: "HOA DUES - UNIT 201",        amount:  300.00, type: "credit", pending: false, account: "Checking ••4821", glAccount: "4010", autoMatched: true,  imported: false, selected: true  },
    { id: "plaid-6",  date: "2024-04-17", merchant: "PACIFIC PLUMBING CO",        amount: -280.00, type: "debit",  pending: false, account: "Checking ••4821", glAccount: "5020", autoMatched: true,  imported: false, selected: true  },
    { id: "plaid-7",  date: "2024-04-16", merchant: "STATE FARM INSURANCE",       amount: -420.00, type: "debit",  pending: false, account: "Checking ••4821", glAccount: "5040", autoMatched: true,  imported: false, selected: true  },
    { id: "plaid-8",  date: "2024-04-15", merchant: "AMAZON.COM SUPPLIES",        amount:  -43.17, type: "debit",  pending: false, account: "Checking ••4821", glAccount: "5080", autoMatched: true,  imported: false, selected: true  },
    { id: "plaid-9",  date: "2024-04-14", merchant: "ZELLE PAYMENT - UNIT 301",   amount:  300.00, type: "credit", pending: false, account: "Checking ••4821", glAccount: "",     autoMatched: false, imported: false, selected: false },
    { id: "plaid-10", date: "2024-04-24", merchant: "PENDING - NW NATURAL GAS",   amount:  -98.33, type: "debit",  pending: true,  account: "Checking ••4821", glAccount: "5030", autoMatched: true,  imported: false, selected: false },
  ];
}

const GL_ACCOUNT_OPTIONS = [
  { num: "4010", name: "HOA Assessments" },
  { num: "4020", name: "Late Fees" },
  { num: "4030", name: "Clubhouse Rental Income" },
  { num: "5010", name: "Landscaping" },
  { num: "5020", name: "Maintenance & Repairs" },
  { num: "5030", name: "Utilities" },
  { num: "5040", name: "Insurance Premiums" },
  { num: "5050", name: "Management Fees" },
  { num: "5060", name: "Reserve Contributions" },
  { num: "5070", name: "Admin & Legal" },
  { num: "5080", name: "Admin & Supplies" },
  { num: "1010", name: "Operating Checking" },
  { num: "1020", name: "Reserve Fund — Savings" },
  { num: "2010", name: "Accounts Payable" },
];

function BankFeed({ toast , orgName = "Your HOA", orgEmail = "" }) {
  const [connected,   setConnected]   = useState(false);
  const [connectedBank, setConnectedBank] = useState(null);
  const [plaidModal,  setPlaidModal]  = useState(false);
  const [plaidStep,   setPlaidStep]   = useState(1); // 1=choose bank, 2=credentials, 3=accounts, 4=success
  const [selectedBank, setSelectedBank] = useState(null);
  const [transactions, setTxs]        = useState([]);
  const [filter,      setFilter]      = useState("unimported");
  const [syncing,     setSyncing]     = useState(false);
  const [lastSync,    setLastSync]    = useState("April 25, 2024 · 9:14am");
  const [showRules,   setShowRules]   = useState(false);
  const [importing,   setImporting]   = useState(false);
  const [importDone,  setImportDone]  = useState(false);

  // Timer refs to prevent setState on unmounted component
  const importT1 = useRef(null);
  const importT2 = useRef(null);
  useEffect(() => () => { clearTimeout(importT1.current); clearTimeout(importT2.current); }, []);

  const [plaidEmail,  setPlaidEmail]  = useState("");
  const [plaidPass,   setPlaidPass]   = useState("");
  const [selectedAcc, setSelectedAcc] = useState(["Checking ••4821"]);

  // Single pass over transactions for all derived values instead of 5 separate filter passes
  const txStats = useMemo(() => transactions.reduce((acc, t) => {
    if (!t.imported && !t.pending) acc.newCount++;
    if (!t.glAccount && !t.imported) acc.unmatchedCount++;
    if (t.selected && !t.imported && !t.pending) acc.selectedTxs.push(t);
    if (!t.imported) {
      if (t.type === "credit") acc.totalCredits += t.amount;
      if (t.type === "debit")  acc.totalDebits  += Math.abs(t.amount);
    }
    return acc;
  }, { newCount: 0, unmatchedCount: 0, selectedTxs: [], totalCredits: 0, totalDebits: 0 }), [transactions]);

  const { newCount, unmatchedCount, selectedTxs, totalCredits, totalDebits } = txStats;

  const txList = useMemo(() => transactions.filter(t =>
    filter === "all"        ? true :
    filter === "unimported" ? !t.imported && !t.pending :
    filter === "imported"   ? t.imported :
    filter === "pending"    ? t.pending : true
  ), [transactions, filter]);

  const toggleSelect = (id) => setTxs(p => p.map(t => t.id===id ? {...t, selected:!t.selected} : t));
  const selectAll    = () => setTxs(p => p.map(t => (!t.imported && !t.pending) ? {...t, selected:true} : t));
  const selectNone   = () => setTxs(p => p.map(t => ({...t, selected:false})));

  const setGlAccount = (id, acct) => setTxs(p => p.map(t => t.id===id ? {...t, glAccount:acct} : t));

  const syncNow = () => {
    setSyncing(true);
    setTimeout(() => { setSyncing(false); setLastSync("April 25, 2024 · " + new Date().toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})); toast("Bank feed synced — 2 new transactions"); }, 2000);
  };

  const importSelected = () => {
    if (!selectedTxs.length) return;
    if (selectedTxs.some(t => !t.glAccount)) { toast("Assign a GL account to all selected transactions first"); return; }
    const count = selectedTxs.length; // capture before async gap — closure would be stale
    setImporting(true);
    importT1.current = setTimeout(() => {
      setTxs(p => p.map(t => t.selected ? {...t, imported:true, selected:false} : t));
      setImporting(false); setImportDone(true);
      toast(`${count} transactions posted to General Ledger`);
      importT2.current = setTimeout(() => setImportDone(false), 3000);
    }, 1800);
  };

  const connectBank = () => {
    setConnected(true);
    setConnectedBank(selectedBank || DEMO_BANKS[0]);
    setPlaidModal(false); setPlaidStep(1); setSelectedBank(null);
    toast(`${(selectedBank || DEMO_BANKS[0]).name} connected successfully`);
  };

  const autoMatch = (tx) => {
    const rule = AUTO_RULES.find(r => tx.merchant.toUpperCase().includes(r.keyword));
    return rule ? rule.account : "";
  };

  const PlaidModal = () => (
    <div className="plaid-modal-overlay" onClick={e => e.target===e.currentTarget && setPlaidModal(false)}>
      <div className="plaid-modal">
        <div className="plaid-modal-header">
          <div style={{ width:28,height:28,borderRadius:8,background:"linear-gradient(135deg,#00b560,#009147)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14 }}>🏦</div>
          <div>
            <div style={{ fontSize:14,fontWeight:600,color:"var(--white)" }}>Connect your bank</div>
            <div style={{ fontSize:11,color:"var(--slate)" }}>Powered by Plaid · Bank-level security</div>
          </div>
          <button onClick={() => setPlaidModal(false)} style={{ marginLeft:"auto",background:"transparent",border:"none",color:"var(--slate)",cursor:"pointer",fontSize:18 }}>✕</button>
        </div>
        <div className="plaid-modal-body">
          <div className="plaid-step-dots">
            {[1,2,3,4].map(s => <div key={s} className={`plaid-step-dot ${plaidStep>=s?"active":""}`} />)}
          </div>

          {plaidStep === 1 && (
            <div>
              <div style={{ fontSize:13,color:"var(--slate)",marginBottom:"1rem" }}>Select your bank to begin the secure connection</div>
              {DEMO_BANKS.map(b => (
                <div key={b.id} className={`bank-option ${selectedBank?.id===b.id?"selected":""}`} onClick={() => setSelectedBank(b)}>
                  <div className="bank-option-icon" style={{ background:b.color+"22" }}>{b.icon}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13,fontWeight:500,color:"var(--white)" }}>{b.name}</div>
                    <div style={{ fontSize:11,color:"var(--slate)" }}>{b.accounts.join(" · ")}</div>
                  </div>
                  {selectedBank?.id===b.id && <span style={{ color:"var(--teal)",fontSize:16 }}>✓</span>}
                </div>
              ))}
              <div style={{ marginTop:"1rem",display:"flex",gap:8 }}>
                <button className="btn-g" style={{ flex:1 }} onClick={() => setPlaidModal(false)}>Cancel</button>
                <button className="btn" style={{ flex:2 }} onClick={() => setPlaidStep(2)} disabled={!selectedBank}>Continue →</button>
              </div>
            </div>
          )}

          {plaidStep === 2 && (
            <div>
              <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:"1.25rem" }}>
                <div className="bank-option-icon" style={{ background:selectedBank?.color+"22",width:36,height:36 }}>{selectedBank?.icon}</div>
                <div style={{ fontSize:14,fontWeight:600,color:"var(--white)" }}>{selectedBank?.name}</div>
              </div>
              <div style={{ fontSize:11,color:"var(--slate)",textTransform:"uppercase",letterSpacing:".05em",fontWeight:700,marginBottom:6 }}>Username / Email</div>
              <input className="ob-input" type="email" placeholder="your@bank.com" value={plaidEmail} onChange={e=>setPlaidEmail(e.target.value)} style={{ marginBottom:"1rem" }} />
              <div style={{ fontSize:11,color:"var(--slate)",textTransform:"uppercase",letterSpacing:".05em",fontWeight:700,marginBottom:6 }}>Password</div>
              <input className="ob-input" type="password" placeholder="••••••••" value={plaidPass} onChange={e=>setPlaidPass(e.target.value)} style={{ marginBottom:"1.25rem" }} />
              <div style={{ fontSize:11,color:"var(--slate)",marginBottom:"1.25rem",display:"flex",gap:6,alignItems:"center" }}>
                <span style={{ fontSize:13 }}>🔒</span>Your credentials are encrypted and sent directly to {selectedBank?.name}. Board-Armor never sees them.
              </div>
              <div style={{ display:"flex",gap:8 }}>
                <button className="btn-g" onClick={() => setPlaidStep(1)}>← Back</button>
                <button className="btn" style={{ flex:1 }} onClick={() => setPlaidStep(3)} disabled={!plaidEmail || !plaidPass}>Sign in →</button>
              </div>
            </div>
          )}

          {plaidStep === 3 && (
            <div>
              <div style={{ fontSize:13,color:"var(--slate)",marginBottom:"1rem" }}>Select which accounts to connect to Board-Armor</div>
              {selectedBank?.accounts.map(acc => (
                <div key={acc} className="bank-option" onClick={() => setSelectedAcc(p => p.includes(acc) ? p.filter(a=>a!==acc) : [...p, acc])}
                  style={{ borderColor: selectedAcc.includes(acc) ? "var(--teal)" : "var(--border2)", background: selectedAcc.includes(acc) ? "rgba(42,99,214,.07)" : "transparent" }}>
                  <div style={{ width:16,height:16,borderRadius:3,border:`1.5px solid ${selectedAcc.includes(acc)?"var(--teal)":"var(--border2)"}`,background:selectedAcc.includes(acc)?"var(--teal)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:11,color:"var(--navy)",fontWeight:700 }}>
                    {selectedAcc.includes(acc)?"✓":""}
                  </div>
                  <div style={{ fontSize:13,fontWeight:500,color:"var(--white)" }}>{acc}</div>
                </div>
              ))}
              <div style={{ display:"flex",gap:8,marginTop:"1.25rem" }}>
                <button className="btn-g" onClick={() => setPlaidStep(2)}>← Back</button>
                <button className="btn" style={{ flex:1 }} onClick={() => setPlaidStep(4)} disabled={!selectedAcc.length}>Connect {selectedAcc.length} account{selectedAcc.length!==1?"s":""}</button>
              </div>
            </div>
          )}

          {plaidStep === 4 && (
            <div style={{ textAlign:"center",padding:".5rem 0" }}>
              <div style={{ fontSize:40,marginBottom:"1rem" }}>✅</div>
              <div style={{ fontSize:16,fontWeight:600,color:"var(--white)",marginBottom:".5rem" }}>{selectedBank?.name} connected!</div>
              <div style={{ fontSize:13,color:"var(--slate)",marginBottom:"1.5rem",lineHeight:1.6 }}>
                {selectedAcc.length} account{selectedAcc.length!==1?"s":""} connected. Board-Armor will sync transactions daily and auto-categorize using your rules.
              </div>
              <button className="btn" onClick={connectBank} style={{ width:"100%" }}>Go to bank feed →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!connected) {
    return (
      <div>
        <div className="ph"><div><div className="pt">Bank Feed</div><div className="ps">Connect your HOA bank account to auto-import and categorize transactions</div></div></div>
        <div style={{ padding:"2rem",display:"flex",alignItems:"center",justifyContent:"center",minHeight:"60vh" }}>
          <div className="plaid-connect-card">
            <div className="plaid-logo">🏦</div>
            <div className="plaid-title">Connect your bank account</div>
            <div className="plaid-sub">Securely link your HOA's bank via Plaid to auto-import transactions, categorize them to GL accounts, and eliminate manual data entry forever.</div>
            <div className="plaid-features">
              {[
                { icon:"🔄", text:"Daily automatic transaction sync — 15,000+ institutions" },
                { icon:"🧠", text:"Smart auto-categorization matches transactions to GL accounts" },
                { icon:"📊", text:"One-click import posts directly to your General Ledger" },
                { icon:"🔒", text:"Bank-level 256-bit encryption · Read-only access · Plaid certified" },
                { icon:"⚡", text:"Eliminates manual bookkeeping — minutes vs hours" },
              ].map((f,i) => (
                <div key={i} className="plaid-feature">
                  <div className="plaid-feature-icon">{f.icon}</div>
                  <span>{f.text}</span>
                </div>
              ))}
            </div>
            <button className="plaid-btn" onClick={() => setPlaidModal(true)}>
              🏦 Connect bank account via Plaid
            </button>
            <div className="plaid-secure">🔒 Your credentials are never stored by Board-Armor</div>
            <div style={{ fontSize:11,color:"var(--slate)",marginTop:"1rem",lineHeight:1.6 }}>
              <strong style={{ color:"var(--white)" }}>For production deployment:</strong> Replace <code style={{ color:"var(--teal)",fontSize:11 }}>/api/plaid</code> with your Netlify serverless function holding your Plaid client credentials. All UI and auto-import logic is production-ready.
            </div>
          </div>
        </div>
        {plaidModal && <PlaidModal />}
      </div>
    );
  }

  return (
    <div>
      <div className="ph">
        <div><div className="pt">Bank Feed</div><div className="ps">Last synced: {lastSync} · {newCount} new transactions</div></div>
        <div style={{ display:"flex",gap:8 }}>
          <button className="btn-g btn-sm" onClick={() => setShowRules(r=>!r)}>⚙ Auto-categorization rules</button>
          <button className="btn-g btn-sm" onClick={() => { setConnected(false); setConnectedBank(null); }} style={{ color:"var(--slate)" }}>Disconnect</button>
          <button className="btn btn-sm" onClick={syncNow} disabled={syncing} style={{ display:"flex",alignItems:"center",gap:5 }}>
            {syncing ? <><span className="ai-spin" style={{ width:12,height:12,borderWidth:1.5 }} />Syncing…</> : "↻ Sync now"}
          </button>
        </div>
      </div>
      <div className="pb">
        {/* Connected bank */}
        <div className="bank-connected-card">
          <div className="bank-icon">{connectedBank?.icon || "🏦"}</div>
          <div>
            <div className="bank-name">{connectedBank?.name || "Chase Bank"}</div>
            <div className="bank-meta">HOA Operating Checking ••4821 · HOA Reserve Savings ••2209</div>
          </div>
          <div className="bank-status"><div className="bank-status-dot" />Live · syncs daily</div>
        </div>

        {/* Stats */}
        <div className="bf-summary-row">
          <div className="sc"><div className="sl">New transactions</div><div className="sv" style={{ color:"var(--amber)" }}>{newCount}</div></div>
          <div className="sc"><div className="sl">Unmatched</div><div className="sv" style={{ color:"var(--red)" }}>{unmatchedCount}</div></div>
          <div className="sc"><div className="sl">Credits (income)</div><div className="sv t">+${totalCredits.toFixed(2)}</div></div>
          <div className="sc"><div className="sl">Debits (expenses)</div><div className="sv" style={{ color:"var(--red)" }}>-${totalDebits.toFixed(2)}</div></div>
        </div>

        {/* Auto-categorization rules panel */}
        {showRules && (
          <div style={{ background:"var(--navy2)",border:".5px solid var(--border)",borderRadius:"var(--r2)",padding:"1.1rem 1.25rem",marginBottom:"1.25rem" }}>
            <div style={{ fontSize:13,fontWeight:600,color:"var(--white)",marginBottom:".75rem" }}>Auto-categorization rules</div>
            <div style={{ fontSize:12,color:"var(--slate)",marginBottom:"1rem" }}>When a transaction merchant name contains the keyword, it's automatically assigned to the GL account.</div>
            {AUTO_RULES.map((r,i) => (
              <div key={i} className="rule-row">
                <span style={{ fontSize:11,color:"var(--slate)",minWidth:60,fontFamily:"monospace" }}>contains</span>
                <span className="rule-keyword">"{r.keyword}"</span>
                <span className="rule-arrow">→</span>
                <span className="rule-account">{r.label}</span>
              </div>
            ))}
            <button className="btn-g btn-sm" style={{ marginTop:".75rem",fontSize:12 }}>+ Add custom rule</button>
          </div>
        )}

        {/* Filter + bulk actions toolbar */}
        <div className="tb">
          {[["unimported","New"],["imported","Imported"],["pending","Pending"],["all","All"]].map(([k,l]) =>
            <button key={k} className={filter===k?"btn btn-sm":"btn-g btn-sm"} onClick={() => setFilter(k)}>{l}</button>
          )}
          <div style={{ flex:1 }} />
          {selectedTxs.length > 0 && (
            <>
              <span style={{ fontSize:12,color:"var(--teal)" }}>{selectedTxs.length} selected</span>
              <button className="btn-g btn-sm" onClick={selectNone}>Deselect all</button>
              <button className="btn btn-sm" onClick={importSelected} disabled={importing || !selectedTxs.every(t=>t.glAccount)} style={{ display:"flex",alignItems:"center",gap:5 }}>
                {importing ? <><span className="ai-spin" style={{ width:12,height:12,borderWidth:1.5 }} />Posting…</> : `Post ${selectedTxs.length} to GL →`}
              </button>
            </>
          )}
          {filter === "unimported" && selectedTxs.length === 0 && (
            <button className="btn-g btn-sm" onClick={selectAll}>Select all new</button>
          )}
        </div>

        {importDone && (
          <div style={{ background:"rgba(58,191,126,.1)",border:".5px solid rgba(58,191,126,.3)",borderRadius:"var(--r)",padding:".85rem 1rem",fontSize:13,color:"var(--green)",marginBottom:"1rem",display:"flex",alignItems:"center",gap:8 }}>
            ✓ Transactions posted to General Ledger — your books are up to date
          </div>
        )}

        {/* Transaction list */}
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th style={{ width:36 }}></th>
                <th style={{ textAlign:"left" }}>Date</th>
                <th style={{ textAlign:"left" }}>Merchant / Description</th>
                <th style={{ textAlign:"left" }}>Account</th>
                <th style={{ textAlign:"left" }}>GL account</th>
                <th style={{ textAlign:"right" }}>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {txList.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign:"center",color:"var(--slate)",padding:"2rem",fontSize:13 }}>No transactions in this view.</td></tr>
              )}
              {txList.map(tx => (
                <tr key={tx.id} style={{ opacity: tx.imported ? .6 : 1 }}>
                  <td>
                    {!tx.imported && !tx.pending && (
                      <input type="checkbox" className="tx-import-cb" checked={tx.selected} onChange={() => toggleSelect(tx.id)} />
                    )}
                  </td>
                  <td style={{ fontFamily:"monospace",fontSize:11,color:"var(--slate)",whiteSpace:"nowrap" }}>{tx.date}</td>
                  <td>
                    <div style={{ fontWeight:500,fontSize:13,color:"var(--white)" }}>{tx.merchant}</div>
                    {tx.autoMatched && !tx.imported && <div style={{ fontSize:10,color:"var(--teal)",marginTop:1 }}>✦ Auto-matched</div>}
                    {tx.pending && <div style={{ fontSize:10,color:"var(--amber)",marginTop:1 }}>⏳ Pending — not importable yet</div>}
                  </td>
                  <td style={{ fontSize:12,color:"var(--slate)" }}>{tx.account}</td>
                  <td className="tx-import-acct">
                    {tx.imported ? (
                      <span style={{ fontSize:12,color:"var(--slate)" }}>{GL_ACCOUNT_OPTIONS.find(a=>a.num===tx.glAccount)?.name || "—"}</span>
                    ) : (
                      <select className="fi" style={{ fontSize:12,padding:"4px 8px",marginBottom:0 }}
                        value={tx.glAccount} onChange={e => setGlAccount(tx.id, e.target.value)}
                        disabled={tx.pending}>
                        <option value="">— Assign GL account —</option>
                        {GL_ACCOUNT_OPTIONS.map(a => <option key={a.num} value={a.num}>{a.num} · {a.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td>
                    <div className={`tx-import-amount ${tx.type==="debit"?"debit":"credit"}`} style={{ textAlign:"right" }}>
                      {tx.type==="credit" ? "+" : "-"}${Math.abs(tx.amount).toFixed(2)}
                    </div>
                  </td>
                  <td style={{ textAlign:"center" }}>
                    <span className={`tx-import-status ${tx.imported?"tx-posted":tx.pending?"":"tx-new"}`} style={{ background: tx.pending ? "rgba(138,153,179,.12)" : undefined, color: tx.pending ? "var(--slate)" : undefined }}>
                      {tx.imported ? "Posted" : tx.pending ? "Pending" : "New"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ fontSize:12,color:"var(--slate)",marginTop:".75rem",lineHeight:1.7 }}>
          <strong style={{ color:"var(--white)" }}>Production note:</strong> Transactions above are a realistic simulation. In production, the "Sync now" button calls <code style={{ color:"var(--teal)",fontSize:11 }}>POST /api/plaid/transactions</code> which proxies Plaid's Transactions API using your server-side client ID and secret. The import flow posts directly to the General Ledger as double-entry journal entries.
        </div>
      </div>

      {plaidModal && <PlaidModal />}
    </div>
  );
}

// ─── Electronic Voting ────────────────────────────────────────────────────────
function ElectronicVoting({ owners, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [ballots, setBallots] = useState([]);
  const [tab, setTab]         = useState("active");
  const [selected, setSelected] = useState({}); // ballotId → [optionIds]
  const [showNew, setShowNew] = useState(false);
  const [newBallot, setNewBallot] = useState({ title:"", type:"resolution", desc:"", closes:"", maxChoices:1 });
  const [candidates, setCandidates] = useState([{id:1,label:"",subtitle:""},{id:2,label:"",subtitle:""}]);

  const active = ballots.filter(b => b.status === "active");
  const closed = ballots.filter(b => b.status === "closed");

  const totalVotes = b => b.options.reduce((s, o) => s + o.votes, 0);
  const quorumMet  = b => (b.votedUnits.length / b.totalUnits * 100) >= b.quorum;
  const daysLeft   = b => Math.max(0, Math.ceil((new Date(b.closes) - new Date()) / 86400000));

  const toggleOption = (ballotId, optionId, maxChoices) => {
    setSelected(prev => {
      const cur = prev[ballotId] || [];
      if (cur.includes(optionId)) return { ...prev, [ballotId]: cur.filter(x => x !== optionId) };
      if (cur.length >= maxChoices) return { ...prev, [ballotId]: maxChoices === 1 ? [optionId] : [...cur.slice(1), optionId] };
      return { ...prev, [ballotId]: [...cur, optionId] };
    });
  };

  const castVote = (ballot) => {
    const choices = selected[ballot.id] || [];
    if (!choices.length) return;
    setBallots(prev => prev.map(b => b.id !== ballot.id ? b : {
      ...b,
      myVote: choices[0],
      votedUnits: [...b.votedUnits, "board"],
      options: b.options.map(o => choices.includes(o.id) ? { ...o, votes: o.votes + 1 } : o),
    }));
    toast(`Vote cast successfully on "${ballot.title}"`);
  };

  const createBallot = () => {
    const opts = (newBallot.type === "election" ? candidates : [
      { id:1, label:"Yes — approve", subtitle:"" },
      { id:2, label:"No — do not approve", subtitle:"" },
      { id:3, label:"Abstain", subtitle:"" },
    ]).map((o, i) => ({ ...o, id: i+1, votes: 0 }));
    setBallots(prev => [...prev, { ...newBallot, id: Date.now(), status:"active", opens: "2024-04-25", totalUnits: owners.length, quorum: 30, votedUnits: [], myVote: null, options: opts }]);
    setShowNew(false); toast("Ballot created and sent to all owners");
  };

  const BallotCard = ({ b }) => {
    const myVotes  = selected[b.id] || [];
    const hasVoted = !!b.myVote || b.votedUnits.includes("board");
    const total    = totalVotes(b);
    const met      = quorumMet(b);
    const isClosed = b.status === "closed";
    const typeClass = b.type === "election" ? "ev-type-election" : b.type === "resolution" ? "ev-type-resolution" : "ev-type-special";
    const typeLabel = b.type === "election" ? "Election" : b.type === "resolution" ? "Resolution" : "Special vote";

    return (
      <div className={`ev-ballot ${b.status === "active" ? (daysLeft(b) <= 7 ? "urgent" : "active") : "closed"}`}>
        <div className="ev-ballot-header">
          <span className={`ev-type-pill ${typeClass}`}>{typeLabel}</span>
          <div className="ev-title">{b.title}</div>
          <div className="ev-desc">{b.desc}</div>
          <div className="ev-meta-row">
            <div className="ev-meta-item">📅 Closes {b.closes}</div>
            {!isClosed && <div className="ev-meta-item" style={{ color: daysLeft(b) <= 7 ? "var(--amber)" : "var(--slate)" }}>⏱ {daysLeft(b)} days left</div>}
            <div className="ev-meta-item">{b.votedUnits.length}/{b.totalUnits} units voted</div>
          </div>
          {/* Quorum bar */}
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 11, color: met ? "var(--teal)" : "var(--slate)", marginBottom: 3 }}>
              Quorum: {Math.round(b.votedUnits.length / b.totalUnits * 100)}% of {b.quorum}% required {met ? "✓ Met" : "— not yet met"}
            </div>
            <div className="ev-quorum-bar">
              <div className="ev-quorum-fill" style={{ width: `${Math.min(100, b.votedUnits.length / b.totalUnits * 100)}%`, background: met ? "var(--teal)" : "var(--amber)" }} />
            </div>
          </div>
        </div>
        <div className="ev-ballot-body">
          {/* Options */}
          {b.options.map(opt => {
            const pct = total > 0 ? Math.round(opt.votes / total * 100) : 0;
            const isSelected = myVotes.includes(opt.id);
            const isWinner = isClosed && opt.votes === Math.max(...b.options.map(o => o.votes));
            return (
              <div key={opt.id}
                className={`ev-option ${isSelected ? "selected" : ""} ${hasVoted || isClosed ? "voted" : ""}`}
                onClick={() => !hasVoted && !isClosed && toggleOption(b.id, opt.id, b.maxChoices)}
                style={{ borderColor: isWinner && isClosed ? "rgba(42,99,214,.30)" : undefined }}>
                <div className={`ev-option-radio ${isSelected ? "selected" : ""}`}>
                  {isSelected && <div className="ev-option-radio-dot" />}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="ev-option-label" style={{ color: isWinner && isClosed ? "var(--teal)" : undefined }}>
                    {opt.label}{isWinner && isClosed ? " 🏆" : ""}
                  </div>
                  {opt.subtitle && <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 1 }}>{opt.subtitle}</div>}
                  {(hasVoted || isClosed) && (
                    <>
                      <div className="ev-result-bar"><div className="ev-result-fill" style={{ width: `${pct}%`, background: isWinner && isClosed ? "var(--teal)" : "var(--teal)" }} /></div>
                    </>
                  )}
                </div>
                {(hasVoted || isClosed) && <div className="ev-option-count">{opt.votes} vote{opt.votes !== 1 ? "s" : ""} · {pct}%</div>}
              </div>
            );
          })}
          {hasVoted ? (
            <div className="ev-voted-badge">✓ Your vote has been recorded · {b.maxChoices > 1 ? `Select up to ${b.maxChoices}` : ""}</div>
          ) : isClosed ? (
            <div style={{ fontSize: 12, color: "var(--slate)", marginTop: ".75rem", fontStyle: "italic" }}>This ballot is closed. Final results shown above.</div>
          ) : (
            <button className="ev-vote-btn" onClick={() => castVote(b)} disabled={!myVotes.length}>
              {b.maxChoices > 1 ? `Cast votes (${myVotes.length}/${b.maxChoices} selected)` : "Cast vote"}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="ph">
        <div><div className="pt">Electronic Voting</div><div className="ps">{active.length} active ballot{active.length !== 1 ? "s" : ""} · {closed.length} closed</div></div>
        <button className="btn btn-sm" onClick={() => setShowNew(true)}>+ Create ballot</button>
      </div>
      <div className="pb">
        {/* KPIs */}
        <div className="ev-stats-row">
          <div className="sc"><div className="sl">Active ballots</div><div className="sv t">{active.length}</div></div>
          <div className="sc"><div className="sl">Total voters</div><div className="sv">{owners.length}</div></div>
          <div className="sc"><div className="sl">Avg participation</div><div className="sv t">{active.length ? Math.round(active.reduce((s, b) => s + b.votedUnits.length / b.totalUnits, 0) / active.length * 100) : 0}%</div></div>
          <div className="sc"><div className="sl">Closed ballots</div><div className="sv">{closed.length}</div></div>
        </div>

        <div className="tabs">
          {[["active","Active ballots"],["closed","Closed / results"]].map(([k,l]) =>
            <div key={k} className={`tab ${tab===k?"active":""}`} onClick={() => setTab(k)}>{l}</div>
          )}
        </div>

        {tab === "active" && (
          active.length === 0
            ? <div className="op-empty"><div style={{ fontSize: 15, fontWeight: 500, color: "var(--white)", marginBottom: 4 }}>No active ballots</div><div style={{ fontSize: 13 }}>Create a new ballot to start collecting votes from your owners.</div></div>
            : <div className="ev-grid">{active.map(b => <BallotCard key={b.id} b={b} />)}</div>
        )}

        {tab === "closed" && (
          closed.length === 0
            ? <div className="op-empty"><div style={{ fontSize: 13 }}>No closed ballots yet.</div></div>
            : <div className="ev-grid">{closed.map(b => <BallotCard key={b.id} b={b} />)}</div>
        )}
      </div>

      {/* New Ballot Modal */}
      {showNew && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setShowNew(false)}>
          <div className="md md-lg">
            <div className="mt2">Create new ballot</div>
            <div className="ms2">Owners will be notified and can vote from their portal</div>
            <div className="fg"><label className="fl">Ballot title</label><input className="fi" value={newBallot.title} onChange={e => setNewBallot(p => ({...p, title:e.target.value}))} placeholder="e.g. Approve 2025 Annual Budget" /></div>
            <div className="fr">
              <div className="fg"><label className="fl">Type</label>
                <select className="fi" value={newBallot.type} onChange={e => setNewBallot(p => ({...p, type:e.target.value}))}>
                  <option value="resolution">Resolution vote (Yes/No/Abstain)</option>
                  <option value="election">Board election (multiple candidates)</option>
                  <option value="special">Special vote (custom options)</option>
                </select>
              </div>
              <div className="fg"><label className="fl">Voting closes</label><input className="fi" type="date" value={newBallot.closes} onChange={e => setNewBallot(p => ({...p, closes:e.target.value}))} /></div>
            </div>
            {newBallot.type === "election" && (
              <div className="fg">
                <label className="fl">Max choices per voter</label>
                <input className="fi" type="number" min="1" max="5" value={newBallot.maxChoices} onChange={e => setNewBallot(p => ({...p, maxChoices:Number(e.target.value)}))} style={{ maxWidth: 80 }} />
              </div>
            )}
            <div className="fg"><label className="fl">Description</label><textarea className="fi" value={newBallot.desc} onChange={e => setNewBallot(p => ({...p, desc:e.target.value}))} placeholder="Describe what owners are voting on…" rows={3} /></div>
            {newBallot.type === "election" && (
              <div className="fg">
                <label className="fl">Candidates</label>
                {candidates.map((c, i) => (
                  <div key={c.id} className="ev-candidate-row">
                    <input className="fi" style={{ flex: 2, marginBottom: 0 }} placeholder="Candidate name" value={c.label} onChange={e => setCandidates(p => p.map(x => x.id === c.id ? {...x, label:e.target.value} : x))} />
                    <input className="fi" style={{ flex: 2, marginBottom: 0 }} placeholder="Unit / bio note (optional)" value={c.subtitle} onChange={e => setCandidates(p => p.map(x => x.id === c.id ? {...x, subtitle:e.target.value} : x))} />
                    {i >= 2 && <button onClick={() => setCandidates(p => p.filter(x => x.id !== c.id))} style={{ background:"transparent", border:"none", color:"var(--slate)", cursor:"pointer", fontSize:16 }}>✕</button>}
                  </div>
                ))}
                <button className="btn-g btn-sm" onClick={() => setCandidates(p => [...p, {id:Date.now(), label:"", subtitle:""}])} style={{ marginTop: 4 }}>+ Add candidate</button>
              </div>
            )}
            <div className="ma"><button className="btn-g" onClick={() => setShowNew(false)}>Cancel</button><button className="btn" onClick={createBallot} disabled={!newBallot.title || !newBallot.closes}>Publish ballot</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Amenity Reservations ─────────────────────────────────────────────────────
function AmenityReservations({ owners, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [amenities, setAmenities] = useState([]);
  const [bookings,  setBookings]  = useState([]);
  const [active,    setActive]    = useState(1);
  const [viewTab,   setViewTab]   = useState("calendar");
  const [bookModal, setBookModal] = useState(false);
  const [bookForm,  setBookForm]  = useState({ unit:"101", date:"", start:"10:00", end:"12:00", notes:"" });
  const [calMonth,  setCalMonth]  = useState(new Date());

  const amenity = amenities.find(a => a.id === active);
  const amenityBookings = bookings.filter(b => b.amenityId === active);

  const calDays = () => {
    const yr = calMonth.getFullYear(), mo = calMonth.getMonth();
    const first = new Date(yr, mo, 1).getDay();
    const days = [];
    for (let i = 0; i < first; i++) { const d = new Date(yr, mo, -first+i+1); days.push({ date: d, other: true }); }
    const daysInMonth = new Date(yr, mo+1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) days.push({ date: new Date(yr, mo, d), other: false });
    while (days.length % 7 !== 0) { const last = days[days.length-1].date; days.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate()+1), other: true }); }
    return days;
  };

  const bookingsOnDay = (date) => {
    const ds = date.toISOString().split("T")[0];
    return amenityBookings.filter(b => b.date === ds);
  };

  const isToday = (date) => date.toISOString().split("T")[0] === "2024-04-25";

  const addBooking = () => {
    if (!bookForm.date || !bookForm.unit) return;
    const owner = owners.find(o => o.unit === bookForm.unit);
    setBookings(prev => [...prev, {
      id: Date.now(), amenityId: active, unit: bookForm.unit,
      owner: owner?.name || `Unit ${bookForm.unit}`,
      date: bookForm.date, start: bookForm.start, end: bookForm.end,
      status: amenity?.depositAmount > 0 ? "pending" : "confirmed",
      notes: bookForm.notes,
    }]);
    setBookModal(false);
    toast(`${amenity?.name} booked for Unit ${bookForm.unit} on ${bookForm.date}`);
  };

  const cancelBooking = (id) => {
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status:"cancelled" } : b));
    toast("Booking cancelled");
  };

  const approveBooking = (id) => {
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status:"confirmed" } : b));
    toast("Booking confirmed");
  };

  const monthLabel = calMonth.toLocaleDateString("en-US", { month:"long", year:"numeric" });
  const prevMonth = () => setCalMonth(p => new Date(p.getFullYear(), p.getMonth()-1, 1));
  const nextMonth = () => setCalMonth(p => new Date(p.getFullYear(), p.getMonth()+1, 1));

  return (
    <div>
      <div className="ph">
        <div><div className="pt">Amenity Reservations</div><div className="ps">{amenities.length} amenities · {bookings.filter(b => b.status==="confirmed").length} active bookings</div></div>
        <button className="btn btn-sm" onClick={() => setBookModal(true)}>+ New reservation</button>
      </div>
      <div className="am-layout">
        {/* Sidebar */}
        <div className="am-sidebar">
          <div className="am-sidebar-top">
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate)", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 6 }}>Amenities</div>
            <div style={{ fontSize: 12, color: "var(--slate)" }}>Click to view bookings & calendar</div>
          </div>
          {amenities.map(a => {
            const count = bookings.filter(b => b.amenityId === a.id && b.status !== "cancelled").length;
            return (
              <div key={a.id} className={`am-amenity ${active === a.id ? "active" : ""}`} onClick={() => setActive(a.id)}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>{a.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div className="am-amenity-name">{a.name}</div>
                    <div className="am-amenity-meta">Capacity {a.capacity} · {count} booking{count !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main content */}
        <div className="am-main">
          {/* Amenity detail header */}
          <div style={{ padding: "1rem 1.5rem", borderBottom: ".5px solid var(--border)", display: "flex", alignItems: "center", gap: "1rem", background: "var(--navy2)" }}>
            <span style={{ fontSize: 28 }}>{amenity?.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--white)" }}>{amenity?.name}</div>
              <div style={{ fontSize: 12, color: "var(--slate)" }}>Capacity {amenity?.capacity} · Max {amenity?.maxHours}hr per booking{amenity?.depositAmount > 0 ? ` · $${amenity?.depositAmount} deposit` : " · No deposit"}</div>
            </div>
            <div className="tabs" style={{ marginBottom: 0, width: "auto" }}>
              {[["calendar","Calendar"],["list","Bookings"],["rules","Rules"]].map(([k,l]) =>
                <div key={k} className={`tab ${viewTab===k?"active":""}`} style={{ padding: "6px 14px" }} onClick={() => setViewTab(k)}>{l}</div>
              )}
            </div>
          </div>

          <div style={{ padding: "1.25rem 1.5rem" }}>
            {/* Calendar view */}
            {viewTab === "calendar" && (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--white)" }}>{monthLabel}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn-g btn-sm" onClick={prevMonth}>←</button>
                    <button className="btn-g btn-sm" onClick={nextMonth}>→</button>
                  </div>
                </div>
                <div className="am-cal-header">
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="am-day-label">{d}</div>)}
                </div>
                <div className="am-cal-grid">
                  {calDays().map((d, i) => {
                    const dayBks = bookingsOnDay(d.date);
                    return (
                      <div key={i} className={`am-day ${d.other ? "other-month" : ""} ${isToday(d.date) ? "today" : ""}`}
                        onClick={() => { if (!d.other) { setBookForm(p => ({...p, date: d.date.toISOString().split("T")[0]})); setBookModal(true); } }}>
                        <div className={`am-day-num ${isToday(d.date) ? "today" : ""}`}>{d.date.getDate()}</div>
                        {dayBks.slice(0, 2).map(b => (
                          <span key={b.id} className={`am-booking-chip ${b.status==="cancelled" ? "am-booking-blocked" : b.unit==="101" ? "am-booking-mine" : "am-booking-other"}`}>
                            {b.start} {b.owner.split(" ")[0]}
                          </span>
                        ))}
                        {dayBks.length > 2 && <span style={{ fontSize: 10, color: "var(--slate)" }}>+{dayBks.length-2} more</span>}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: "1rem", fontSize: 11, color: "var(--slate)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(42,99,214,.15)" }} />Your unit</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(138,153,179,.15)" }} />Other units</div>
                </div>
              </div>
            )}

            {/* Bookings list */}
            {viewTab === "list" && (
              <div>
                {amenityBookings.length === 0 && <div className="op-empty" style={{ fontSize: 13 }}>No bookings for {amenity?.name} yet.</div>}
                {[...amenityBookings].sort((a,b) => a.date.localeCompare(b.date)).map(b => (
                  <div key={b.id} className="am-booking-list-item">
                    <div style={{ fontSize: 22, flexShrink: 0 }}>{amenity?.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)" }}>{b.owner} <span style={{ color: "var(--teal)", fontWeight: 400 }}>· Unit {b.unit}</span></div>
                      <div style={{ fontSize: 12, color: "var(--slate)" }}>{b.date} · {b.start}–{b.end}{b.notes ? ` · ${b.notes}` : ""}</div>
                    </div>
                    <span className={`am-status-badge ${b.status === "confirmed" ? "am-confirmed" : b.status === "pending" ? "am-pending" : "am-cancelled"}`}>{b.status}</span>
                    <div style={{ display: "flex", gap: 5 }}>
                      {b.status === "pending"   && <button className="btn btn-sm" onClick={() => approveBooking(b.id)}>Confirm</button>}
                      {b.status !== "cancelled" && <button className="btn-g btn-sm" style={{ color: "var(--red)" }} onClick={() => cancelBooking(b.id)}>Cancel</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Rules */}
            {viewTab === "rules" && (
              <div className="am-detail">
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)", marginBottom: ".85rem" }}>{amenity?.name} — Rules & policies</div>
                {amenity?.rules.map((r, i) => (
                  <div key={i} className="am-rule"><span className="am-rule-icon">📌</span><span>{r}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking modal */}
      {bookModal && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setBookModal(false)}>
          <div className="md">
            <div className="mt2">New reservation — {amenity?.name}</div>
            <div className="ms2">{amenity?.depositAmount > 0 ? `$${amenity?.depositAmount} deposit required · Board must confirm` : "No deposit required · Instant confirmation"}</div>
            <div className="fg"><label className="fl">Unit</label>
              <select className="fi" value={bookForm.unit} onChange={e => setBookForm(p => ({...p, unit:e.target.value}))}>
                {owners.map(o => <option key={o.id} value={o.unit}>{o.unit} — {o.name}</option>)}
              </select>
            </div>
            <div className="fg"><label className="fl">Date</label><input className="fi" type="date" value={bookForm.date} onChange={e => setBookForm(p => ({...p, date:e.target.value}))} /></div>
            <div className="fr">
              <div className="fg"><label className="fl">Start time</label>
                <select className="fi" value={bookForm.start} onChange={e => setBookForm(p => ({...p, start:e.target.value}))}>
                  {["07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="fg"><label className="fl">End time</label>
                <select className="fi" value={bookForm.end} onChange={e => setBookForm(p => ({...p, end:e.target.value}))}>
                  {["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="fg"><label className="fl">Notes (optional)</label><input className="fi" value={bookForm.notes} onChange={e => setBookForm(p => ({...p, notes:e.target.value}))} placeholder="e.g. Birthday party, book club…" /></div>
            {amenity?.depositAmount > 0 && <div style={{ background:"rgba(245,166,35,.08)", border:".5px solid rgba(245,166,35,.2)", borderRadius:"var(--r)", padding:".75rem 1rem", fontSize:12, color:"var(--amber)", marginBottom:".5rem" }}>⚠ ${amenity.depositAmount} refundable deposit required · Board will confirm within 24 hours</div>}
            <div className="ma"><button className="btn-g" onClick={() => setBookModal(false)}>Cancel</button><button className="btn" onClick={addBooking} disabled={!bookForm.date}>Book {amenity?.name}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── General Ledger ───────────────────────────────────────────────────────────


// ─── Pre-Loss Condition Documentation ────────────────────────────────────────
function PreLossDocumentation({ toast, orgName = "Your HOA" }) {
  const { orgId } = useAppCtx();
  const [lightbox, setLightbox] = useState(null);
  const [sitePhotos, setSitePhotos] = useState([]);
  useEffect(() => { if (orgId) db.sitePhotos.list(orgId, "preloss").then(({ data }) => data && setSitePhotos(data)); }, [orgId]);
  const SK = `ba_preloss_${orgName.replace(/\s+/g,"_")}`;
  const SYSTEMS = [
    { id: "roof",        label: "Roof & Gutters",          icon: "🏠", desc: "Shingles, flashing, gutters, downspouts, penetrations" },
    { id: "exterior",    label: "Building Exterior",        icon: "🧱", desc: "Siding, stucco, paint, caulking, windows, doors" },
    { id: "foundation",  label: "Foundation & Structure",   icon: "🏗️", desc: "Foundation, retaining walls, structural elements" },
    { id: "parking",     label: "Parking & Pavement",       icon: "🚗", desc: "Asphalt, concrete, striping, drainage" },
    { id: "pool",        label: "Pool & Amenities",         icon: "🏊", desc: "Pool deck, equipment, fencing, clubhouse" },
    { id: "mechanical",  label: "Mechanical Systems",       icon: "⚙️", desc: "Elevators, HVAC, boilers, irrigation" },
    { id: "common",      label: "Common Areas & Landscaping", icon: "🌳", desc: "Walkways, lighting, landscaping, fencing" },
    { id: "plumbing",    label: "Plumbing & Drainage",      icon: "🚰", desc: "Visible pipes, storm drains, sump pumps" },
  ];
  const RATINGS = ["Excellent", "Good", "Fair", "Poor", "Needs Immediate Attention"];
  const ratingColor = (r) => r === "Excellent" ? "#22c55e" : r === "Good" ? "#14b8a6" : r === "Fair" ? "#f59e0b" : r === "Poor" ? "#dc2626" : "#ef4444";

  const defaultData = () => ({
    inspections: [],
  });

  const load = () => { try { return JSON.parse(localStorage.getItem(SK)) || defaultData(); } catch { return defaultData(); } };
  const [data, setData] = useState(load);
  const [showNew, setShowNew] = useState(false);
  const [activeInspection, setActiveInspection] = useState(null);
  const [newInspection, setNewInspection] = useState({
    date: new Date().toISOString().split("T")[0],
    inspector: "",
    systems: Object.fromEntries(SYSTEMS.map(s => [s.id, { rating: "Good", notes: "", photos: [] }])),
    overallNotes: "",
    boardAttestations: [],
  });

  const save = (d) => { localStorage.setItem(SK, JSON.stringify(d)); setData(d); };

  const createInspection = () => {
    if (!newInspection.inspector) { toast("Inspector name required"); return; }
    const insp = { ...newInspection, id: Date.now(), createdAt: new Date().toISOString() };
    const updated = { ...data, inspections: [insp, ...data.inspections] };
    save(updated);
    setShowNew(false);
    setActiveInspection(insp.id);
    toast("✅ Pre-loss inspection documented and timestamped");
  };

  const printReport = (insp) => {
    const w = window.open("", "_blank");
    const overallRating = () => {
      const ratings = Object.values(insp.systems).map(s => RATINGS.indexOf(s.rating));
      const avg = ratings.reduce((a,b) => a+b, 0) / ratings.length;
      return RATINGS[Math.round(avg)];
    };
    w.document.write(`<!DOCTYPE html><html><head><title>Pre-Loss Condition Report — ${orgName}</title>
    <style>
      * { margin:0;padding:0;box-sizing:border-box; }
      body { font-family: Arial, sans-serif; color: #111; padding: 48px; font-size: 13px; }
      h1 { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
      h2 { font-size: 14px; font-weight: 700; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
      .header { border-bottom: 2px solid #111; padding-bottom: 16px; margin-bottom: 20px; }
      .sub { font-size: 11px; color: #666; margin-top: 2px; }
      .system { margin-bottom: 14px; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
      .system-header { display: flex; justify-content: space-between; background: #f9fafb; padding: 8px 12px; }
      .system-name { font-weight: 700; font-size: 13px; }
      .rating { font-weight: 700; font-size: 12px; }
      .system-body { padding: 8px 12px; font-size: 12px; color: #374151; }
      .attestation { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 16px; }
      .sig-line { border-bottom: 1px solid #999; height: 32px; margin-bottom: 4px; margin-top: 20px; }
      .sig-label { font-size: 10px; color: #666; }
      .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
      .footer { margin-top: 40px; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 12px; display: flex; justify-content: space-between; }
      .overall { background: #f0fdf4; border: 2px solid #86efac; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; }
    </style></head><body>
    <div class="header">
      <h1>${orgName} — Pre-Loss Property Condition Report</h1>
      <div class="sub">Inspection Date: ${new Date(insp.date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
      <div class="sub">Inspector: ${insp.inspector}</div>
      <div class="sub">Document Created: ${new Date(insp.createdAt).toLocaleString()}</div>
    </div>
    <div class="overall">
      <strong>Overall Property Condition: ${overallRating()}</strong><br>
      <span style="font-size:11px;color:#374151">This report documents the pre-loss condition of ${orgName} and is intended to support insurance claims by establishing the property's condition prior to any damage event.</span>
    </div>
    <h2>System-by-System Condition Assessment</h2>
    ${SYSTEMS.map(s => `
      <div class="system">
        <div class="system-header">
          <span class="system-name">${s.icon} ${s.label}</span>
          <span class="rating">${insp.systems[s.id]?.rating || "Not inspected"}</span>
        </div>
        ${insp.systems[s.id]?.notes ? `<div class="system-body">${insp.systems[s.id].notes}</div>` : ""}
      </div>
    `).join("")}
    ${insp.overallNotes ? `<h2>Inspector Notes</h2><p style="font-size:13px;color:#374151;line-height:1.6">${insp.overallNotes}</p>` : ""}
    <div class="attestation">
      <h2>Board Attestation</h2>
      <p style="font-size:12px;color:#374151;margin-bottom:16px">The undersigned board member(s) attest that this report accurately reflects the condition of ${orgName} common areas and building systems as of the inspection date above. This documentation is maintained for insurance and legal purposes.</p>
      <div class="two-col">
        <div><div class="sig-line"></div><div class="sig-label">Board Member Signature · Title · Date</div></div>
        <div><div class="sig-line"></div><div class="sig-label">Board Member Signature · Title · Date</div></div>
      </div>
    </div>
    <div class="footer">
      <span>${orgName} · Pre-Loss Condition Documentation · Confidential</span>
      <span>Generated by BoardArmor · board-armor.com</span>
    </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const overallRating = (insp) => {
    const ratings = Object.values(insp.systems).map(s => RATINGS.indexOf(s.rating));
    const avg = ratings.reduce((a,b) => a+b, 0) / ratings.length;
    return RATINGS[Math.round(avg)];
  };

  const active = data.inspections.find(i => i.id === activeInspection);

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--white)", letterSpacing: "-0.5px", marginBottom: 4 }}>📸 Pre-Loss Documentation</div>
          <div style={{ fontSize: 13, color: "var(--slate)" }}>Annual property condition reports · Timestamped · Board-attested · Insurance claim ready</div>
        </div>
        <button className="btn btn-sm" onClick={() => { setShowNew(true); setActiveInspection(null); }}>+ New inspection</button>
      </div>

      {/* Why this matters */}
      <div style={{ background: "rgba(20,184,166,.06)", border: ".5px solid rgba(20,184,166,.2)", borderRadius: "var(--r)", padding: "1rem 1.25rem", marginBottom: "1.5rem", display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ fontSize: 20, flexShrink: 0 }}>💡</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--teal)", marginBottom: 3 }}>Why pre-loss documentation matters</div>
          <div style={{ fontSize: 12, color: "var(--slate)", lineHeight: 1.6 }}>Insurance carriers frequently dispute claims by arguing damage was pre-existing. A timestamped, board-attested condition report completed before any damage event is your strongest defense. Document once a year — it could be worth hundreds of thousands of dollars.</div>
        </div>
      </div>

      {sitePhotos.length > 0 && (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1rem 1.25rem", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--white)", marginBottom: 8 }}>📸 Site photos ({sitePhotos.length}) — visible to every board member, on any device</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {sitePhotos.slice(0, 16).map(p => (
              <img key={p.id} src={p.photo_url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: ".5px solid var(--border2)", cursor: "pointer" }} onClick={() => window.open(p.photo_url, "_blank")} />
            ))}
          </div>
        </div>
      )}

      {/* New inspection form */}
      {showNew && (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "var(--white)", marginBottom: "1rem" }}>New Property Inspection</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: "1.25rem" }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Inspection date</div>
              <input className="fi" type="date" value={newInspection.date} onChange={e => setNewInspection(p => ({ ...p, date: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Inspector name *</div>
              <input className="fi" placeholder="Board member name or inspector" value={newInspection.inspector} onChange={e => setNewInspection(p => ({ ...p, inspector: e.target.value }))} />
            </div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--white)", marginBottom: ".75rem" }}>Rate each building system:</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: "1rem" }}>
            {SYSTEMS.map(s => (
              <div key={s.id} style={{ background: "var(--navy2)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".5rem" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--white)" }}>{s.icon} {s.label}</div>
                  <select style={{ fontSize: 11, padding: "3px 6px", borderRadius: 4, background: "var(--navy3)", border: ".5px solid var(--border2)", color: ratingColor(newInspection.systems[s.id]?.rating), fontWeight: 700 }}
                    value={newInspection.systems[s.id]?.rating || "Good"}
                    onChange={e => setNewInspection(p => ({ ...p, systems: { ...p.systems, [s.id]: { ...p.systems[s.id], rating: e.target.value } } }))}>
                    {RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 6 }}>{s.desc}</div>
                <textarea className="fi" rows={2} style={{ width: "100%", fontSize: 11, resize: "none" }}
                  placeholder="Condition notes, specific observations..."
                  value={newInspection.systems[s.id]?.notes || ""}
                  onChange={e => setNewInspection(p => ({ ...p, systems: { ...p.systems, [s.id]: { ...p.systems[s.id], notes: e.target.value } } }))} />
                <div style={{marginTop:6}}>
                  <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 12px",background:"var(--navy2)",border:".5px solid var(--border2)",borderRadius:"var(--r)",cursor:"pointer",fontSize:11,color:"var(--teal)",fontWeight:600,marginTop:4}}>
                    📸 Take or add photos
                    <input type="file" accept="image/*" capture="environment" multiple style={{display:"none"}}
                      onChange={async e => {
                        const files = Array.from(e.target.files || []);
                        for (const file of files) {
                          const { data: photoRow, error } = await uploadSitePhoto(orgId, "preloss", s.id, file, "", newInspection.inspector);
                          if (error) { toast("Photo upload failed — " + (error.message || "try again")); continue; }
                          setNewInspection(p=>({...p,systems:{...p.systems,[s.id]:{...p.systems[s.id],photos:[...(p.systems[s.id]?.photos||[]),{name:file.name,dataUrl:photoRow.photo_url}]}}}));
                          setSitePhotos(prev => [photoRow, ...prev]);
                        }
                        e.target.value = "";
                      }} />
                  </label>
                  {(newInspection.systems[s.id]?.photos||[]).length>0 && (
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                      {newInspection.systems[s.id].photos.map((ph,pi)=>(
                        <div key={pi} style={{position:"relative"}}>
                          <img src={ph.dataUrl} alt={ph.name} style={{width:52,height:52,objectFit:"cover",borderRadius:4,border:".5px solid var(--border2)",cursor:"pointer"}} onClick={()=>{const w=window.open(); w.focus(); w.document.write(`<img src="${ph.dataUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;background:#000;display:block;margin:0 auto;" />`);}} />
                          <button onClick={()=>setNewInspection(p=>({...p,systems:{...p.systems,[s.id]:{...p.systems[s.id],photos:p.systems[s.id].photos.filter((_,i)=>i!==pi)}}}))} style={{position:"absolute",top:-4,right:-4,background:"#ef4444",border:"none",borderRadius:"50%",width:14,height:14,fontSize:8,color:"#fff",cursor:"pointer"}}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Overall inspector notes</div>
            <textarea className="fi" rows={3} style={{ width: "100%", resize: "vertical" }}
              placeholder="General observations, items to monitor, recommended repairs, deferred maintenance..."
              value={newInspection.overallNotes} onChange={e => setNewInspection(p => ({ ...p, overallNotes: e.target.value }))} />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={createInspection}>✅ Save & timestamp inspection</button>
            <button className="btn-g btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Inspection history */}
      {data.inspections.length === 0 && !showNew ? (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "3rem", textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: "1rem" }}>📸</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: 6 }}>No inspections on record</div>
          <div style={{ fontSize: 13, color: "var(--slate)", maxWidth: 420, margin: "0 auto .75rem", lineHeight: 1.6 }}>Complete your first annual property inspection. The timestamp and board attestation become your pre-loss baseline for any future insurance claim.</div>
          <button className="btn btn-sm" onClick={() => setShowNew(true)}>+ Start first inspection</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: active ? "280px 1fr" : "1fr", gap: "1rem" }}>
          {/* Inspection list */}
          <div>
            {data.inspections.map(insp => {
              const overall = overallRating(insp);
              return (
                <div key={insp.id} onClick={() => setActiveInspection(insp.id === activeInspection ? null : insp.id)}
                  style={{ background: insp.id === activeInspection ? "rgba(20,184,166,.08)" : "var(--navy3)", border: `.5px solid ${insp.id === activeInspection ? "var(--teal)" : "var(--border2)"}`, borderRadius: "var(--r)", padding: "1rem", marginBottom: ".5rem", cursor: "pointer", transition: "all .15s" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)" }}>{fmtDate(insp.date)}</div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: ratingColor(overall), background: `${ratingColor(overall)}18`, padding: "2px 8px", borderRadius: 10 }}>{overall}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--slate)" }}>Inspector: {insp.inspector}</div>
                  <div style={{ fontSize: 11, color: "var(--slate)" }}>{SYSTEMS.length} systems assessed</div>
                  <button className="btn-g btn-sm" style={{ marginTop: ".5rem", fontSize: 11, color: "var(--teal)" }}
                    onClick={e => { e.stopPropagation(); printReport(insp); }}>🖨 Print report</button>
                </div>
              );
            })}
          </div>

          {/* Active inspection detail */}
          {active && (
            <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "var(--white)" }}>Inspection — {fmtDate(active.date)}</div>
                  <div style={{ fontSize: 12, color: "var(--slate)" }}>By {active.inspector} · Logged {new Date(active.createdAt).toLocaleString()}</div>
                </div>
                <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => printReport(active)}>🖨 Print / PDF</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {SYSTEMS.map(s => {
                  const sys = active.systems[s.id];
                  return (
                    <div key={s.id} style={{ background: "var(--navy2)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: ".85rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sys?.notes ? 4 : 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--white)" }}>{s.icon} {s.label}</div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: ratingColor(sys?.rating || "Good") }}>{sys?.rating || "Good"}</span>
                      </div>
                      {sys?.notes && <div style={{ fontSize: 11, color: "var(--slate)", lineHeight: 1.5 }}>{sys.notes}</div>}
                      {sys?.photos?.length>0 && <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>{sys.photos.map((ph,pi)=><img key={pi} src={ph.dataUrl} alt={ph.name} style={{width:44,height:44,objectFit:"cover",borderRadius:4,border:".5px solid var(--border2)",cursor:"pointer"}} onClick={()=>{const w=window.open(); w.focus(); w.document.write(`<img src="${ph.dataUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;background:#000;display:block;margin:0 auto;" />`);}} />)}</div>}
                    </div>
                  );
                })}
              </div>
              {active.overallNotes && (
                <div style={{ marginTop: "1rem", background: "var(--navy2)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: ".85rem" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate)", marginBottom: 4 }}>INSPECTOR NOTES</div>
                  <div style={{ fontSize: 12, color: "var(--white)", lineHeight: 1.6 }}>{active.overallNotes}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Claim Tracker ────────────────────────────────────────────────────────────
function ClaimTracker({ toast, orgName = "Your HOA" }) {
  const SK = `ba_claims_${orgName.replace(/\s+/g,"_")}`;
  const defaultData = { claims: [] };
  const load = () => { try { return JSON.parse(localStorage.getItem(SK)) || defaultData; } catch { return defaultData; } };
  const [data, setData] = useState(load);
  const [showNew, setShowNew] = useState(false);
  const [activeClaim, setActiveClaim] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [newClaim, setNewClaim] = useState({
    lossDate: "",
    reportedDate: new Date().toISOString().split("T")[0],
    type: "Wind/Storm",
    description: "",
    location: "",
    carrier: "",
    claimNumber: "",
    adjusterName: "",
    adjusterPhone: "",
    adjusterEmail: "",
    publicAdjusterName: "",
    publicAdjusterPhone: "",
    publicAdjusterEmail: "",
    initialOffer: "",
    currentStatus: "Filed",
    timeline: [],
    notes: "",
  });
  const [newEntry, setNewEntry] = useState({ date: new Date().toISOString().split("T")[0], note: "", amount: "" });

  const save = (d) => { localStorage.setItem(SK, JSON.stringify(d)); setData(d); };

  const STATUS_COLORS = {
    "Filed": "#14b8a6", "Under Review": "#f59e0b", "Inspection Scheduled": "#dc2626",
    "Disputed": "#ef4444", "Negotiating": "#dc2626", "Settled": "#22c55e", "Closed": "#64748b", "Reopened": "#a855f7"
  };

  const createClaim = () => {
    if (!newClaim.lossDate || !newClaim.type || !newClaim.description) { toast("Loss date, type, and description required"); return; }
    const claim = { ...newClaim, id: Date.now(), createdAt: new Date().toISOString(),
      timeline: [{ id: Date.now(), date: newClaim.reportedDate, note: "Claim filed with carrier", amount: "" }] };
    const updated = { ...data, claims: [claim, ...data.claims] };
    save(updated);
    setShowNew(false);
    setActiveClaim(claim.id);
    toast("✅ Insurance claim logged");
  };

  const addTimelineEntry = (claimId) => {
    if (!newEntry.note) { toast("Note required"); return; }
    const updated = { ...data, claims: data.claims.map(cl => cl.id === claimId
      ? { ...cl, timeline: [...(cl.timeline || []), { id: Date.now(), ...newEntry }] }
      : cl
    )};
    save(updated);
    setNewEntry({ date: new Date().toISOString().split("T")[0], note: "", amount: "" });
    toast("Timeline entry added");
  };

  const updateClaimField = (claimId, field, val) => {
    const updated = { ...data, claims: data.claims.map(cl => cl.id === claimId ? { ...cl, [field]: val } : cl) };
    save(updated);
  };

  const requestMPLC = async (claim) => {
    const MPLC_EMAIL = "info@missionae.com";
    const lines = [
      `HOA: ${orgName}`,
      `Loss type: ${claim.type}`,
      `Date of loss: ${fmtDate(claim.lossDate)}`,
      `Location: ${claim.location || "—"}`,
      `Description: ${claim.description}`,
      `Carrier: ${claim.carrier || "—"}`,
      `Claim #: ${claim.claimNumber || "—"}`,
      "",
      "Referred from the Board-Armor Claim Tracker — please reach out to the board directly.",
    ];
    try {
      await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: MPLC_EMAIL, subject: `New Loss Referral — ${orgName} (via Board-Armor)`, text: lines.join("\n"), type: "mplc-referral" }),
      });
      const updated = { ...data, claims: data.claims.map(cl => cl.id === claim.id
        ? { ...cl,
            publicAdjusterName: cl.publicAdjusterName || "Mission Property Loss Consultants",
            publicAdjusterPhone: cl.publicAdjusterPhone || "(833) 446-7728",
            publicAdjusterEmail: cl.publicAdjusterEmail || MPLC_EMAIL,
            timeline: [...(cl.timeline || []), { id: Date.now(), date: new Date().toISOString().split("T")[0], note: "Referred to Mission Property Loss Consultants (MPLC) for claim assistance", amount: "" }] }
        : cl
      )};
      save(updated);
      toast("✅ Referral sent to Mission PLC — they'll be in touch");
    } catch (e) {
      toast("Could not send referral — call MPLC directly at (833) 446-7728");
    }
  };

  const fmt = (n) => n ? `$${parseFloat(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";
  const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const active = data.claims.find(c => c.id === activeClaim);

  const printClaimReport = (claim) => {
    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Insurance Claim Report — ${orgName}</title>
    <style>
      * { margin:0;padding:0;box-sizing:border-box; }
      body { font-family: Arial, sans-serif; color: #111; padding: 48px; font-size: 13px; }
      h1 { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
      h2 { font-size: 14px; font-weight: 700; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
      .header { border-bottom: 2px solid #111; padding-bottom: 16px; margin-bottom: 20px; }
      .sub { font-size: 11px; color: #666; margin-top: 2px; }
      .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 16px; }
      .field { margin-bottom: 10px; }
      .field-label { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 2px; }
      .field-val { font-size: 13px; font-weight: 500; color: #111; }
      .timeline-entry { padding: 8px 0; border-bottom: 1px solid #eee; display: flex; gap: 16px; }
      .tl-date { font-size: 11px; color: #666; min-width: 90px; }
      .tl-note { font-size: 12px; color: #374151; flex: 1; }
      .tl-amount { font-size: 12px; font-weight: 700; color: #166534; min-width: 80px; text-align: right; }
      .footer { margin-top: 40px; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 12px; display: flex; justify-content: space-between; }
    </style></head><body>
    <div class="header">
      <h1>${orgName} — Insurance Claim Report</h1>
      <div class="sub">Claim #: ${claim.claimNumber || "Pending"} · Status: ${claim.currentStatus}</div>
      <div class="sub">Printed: ${new Date().toLocaleString()}</div>
    </div>
    <div class="two-col">
      <div>
        <h2>Loss Information</h2>
        <div class="field"><div class="field-label">Date of Loss</div><div class="field-val">${fmtDate(claim.lossDate)}</div></div>
        <div class="field"><div class="field-label">Loss Type</div><div class="field-val">${claim.type}</div></div>
        <div class="field"><div class="field-label">Location</div><div class="field-val">${claim.location || "—"}</div></div>
        <div class="field"><div class="field-label">Description</div><div class="field-val">${claim.description}</div></div>
      </div>
      <div>
        <h2>Carrier Information</h2>
        <div class="field"><div class="field-label">Insurance Carrier</div><div class="field-val">${claim.carrier || "—"}</div></div>
        <div class="field"><div class="field-label">Claim Number</div><div class="field-val">${claim.claimNumber || "—"}</div></div>
        <div class="field"><div class="field-label">Carrier Adjuster</div><div class="field-val">${claim.adjusterName || "—"}</div></div>
        <div class="field"><div class="field-label">Initial Offer</div><div class="field-val">${fmt(claim.initialOffer)}</div></div>
      </div>
    </div>
    ${claim.publicAdjusterName ? `
    <h2>Public Adjuster</h2>
    <div class="two-col">
      <div class="field"><div class="field-label">Name</div><div class="field-val">${claim.publicAdjusterName}</div></div>
      <div class="field"><div class="field-label">Contact</div><div class="field-val">${claim.publicAdjusterPhone || ""} ${claim.publicAdjusterEmail || ""}</div></div>
    </div>` : ""}
    <h2>Claim Timeline</h2>
    ${(claim.timeline || []).map(e => `
      <div class="timeline-entry">
        <span class="tl-date">${fmtDate(e.date)}</span>
        <span class="tl-note">${e.note}</span>
        <span class="tl-amount">${e.amount ? fmt(e.amount) : ""}</span>
      </div>`).join("")}
    ${claim.notes ? `<h2>Notes</h2><p style="font-size:13px;color:#374151;line-height:1.6">${claim.notes}</p>` : ""}
    <div class="footer">
      <span>${orgName} · Insurance Claim Documentation · Confidential</span>
      <span>Generated by BoardArmor · board-armor.com</span>
    </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--white)", letterSpacing: "-0.5px", marginBottom: 4 }}>📋 Claim Tracker</div>
          <div style={{ fontSize: 13, color: "var(--slate)" }}>Track active and historical insurance claims · Carrier contacts · Timeline log · Print-ready reports</div>
        </div>
        <button className="btn btn-sm" onClick={() => { setShowNew(true); setActiveClaim(null); }}>+ New claim</button>
      </div>

      {/* New claim form */}
      {showNew && (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.5rem", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "var(--white)", marginBottom: "1rem" }}>New Insurance Claim</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: "1rem" }}>
            {[
              { label: "Date of Loss *", key: "lossDate", type: "date" },
              { label: "Date Reported", key: "reportedDate", type: "date" },
              { label: "Loss Type", key: "type", type: "select", options: ["Wind/Storm","Hail","Fire","Water/Flood","Earthquake","Vandalism","Liability","Other"] },
            ].map(f => (
              <div key={f.key}>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>{f.label}</div>
                {f.type === "select"
                  ? <select className="fi" value={newClaim[f.key]} onChange={e => setNewClaim(p => ({ ...p, [f.key]: e.target.value }))}>
                      {f.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  : <input className="fi" type={f.type} value={newClaim[f.key]} onChange={e => setNewClaim(p => ({ ...p, [f.key]: e.target.value }))} />
                }
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: "1rem" }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Location / Affected area</div>
              <input className="fi" placeholder="e.g. Building B roof, Unit 204, Pool area" value={newClaim.location} onChange={e => setNewClaim(p => ({ ...p, location: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Insurance carrier</div>
              <input className="fi" placeholder="State Farm, Travelers, etc." value={newClaim.carrier} onChange={e => setNewClaim(p => ({ ...p, carrier: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Loss description *</div>
            <textarea className="fi" rows={3} style={{ width: "100%", resize: "vertical" }}
              placeholder="Describe the damage in detail..."
              value={newClaim.description} onChange={e => setNewClaim(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--white)", marginBottom: ".6rem" }}>Public adjuster (if engaged)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: "1rem" }}>
            {[
              { label: "Company/Name", key: "publicAdjusterName", placeholder: "Mission Property Loss Consultants" },
              { label: "Phone", key: "publicAdjusterPhone", placeholder: "(833) 446-7728" },
              { label: "Email", key: "publicAdjusterEmail", placeholder: "info@missionae.com" },
            ].map(f => (
              <div key={f.key}>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>{f.label}</div>
                <input className="fi" placeholder={f.placeholder} value={newClaim[f.key]} onChange={e => setNewClaim(p => ({ ...p, [f.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={createClaim}>✅ Log claim</button>
            <button className="btn-g btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Claims list + detail */}
      {data.claims.length === 0 && !showNew ? (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "3rem", textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: "1rem" }}>📋</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: 6 }}>No claims on record</div>
          <div style={{ fontSize: 13, color: "var(--slate)", maxWidth: 400, margin: "0 auto .75rem", lineHeight: 1.6 }}>When you file an insurance claim, track it here. Every interaction, offer, and timeline entry creates a documented record for your public adjuster and legal team.</div>
          <button className="btn btn-sm" onClick={() => setShowNew(true)}>+ Log first claim</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: active ? "300px 1fr" : "1fr", gap: "1rem" }}>
          {/* Claims list */}
          <div>
            {data.claims.map(cl => (
              <div key={cl.id} onClick={() => setActiveClaim(cl.id === activeClaim ? null : cl.id)}
                style={{ background: cl.id === activeClaim ? "rgba(20,184,166,.08)" : "var(--navy3)", border: `.5px solid ${cl.id === activeClaim ? "var(--teal)" : "var(--border2)"}`, borderRadius: "var(--r)", padding: "1rem", marginBottom: ".5rem", cursor: "pointer", transition: "all .15s" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)" }}>{cl.type}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: STATUS_COLORS[cl.currentStatus] || "var(--slate)", background: `${STATUS_COLORS[cl.currentStatus] || "#64748b"}18`, padding: "2px 8px", borderRadius: 10 }}>{cl.currentStatus}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 2 }}>Loss date: {fmtDate(cl.lossDate)}</div>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 2 }}>{cl.carrier || "Carrier not set"} {cl.claimNumber ? `· #${cl.claimNumber}` : ""}</div>
                {cl.initialOffer && <div style={{ fontSize: 11, color: "#dc2626" }}>Initial offer: {fmt(cl.initialOffer)}</div>}
              </div>
            ))}
          </div>

          {/* Active claim detail */}
          {active && (
            <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem", overflowY: "auto", maxHeight: 700 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "var(--white)" }}>{active.type} — {fmtDate(active.lossDate)}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <select style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: "var(--navy2)", border: ".5px solid var(--border2)", color: STATUS_COLORS[active.currentStatus], fontWeight: 700 }}
                      value={active.currentStatus} onChange={e => updateClaimField(active.id, "currentStatus", e.target.value)}>
                      {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn-g btn-sm" style={{ fontSize: 11, color: "var(--teal)" }} onClick={() => requestMPLC(active)}>📞 Request MPLC</button>
                  <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => printClaimReport(active)}>🖨 Print report</button>
                </div>
              </div>

              {/* Key details */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: "1rem" }}>
                {[
                  { label: "Carrier", key: "carrier", placeholder: "State Farm" },
                  { label: "Claim Number", key: "claimNumber", placeholder: "CLM-2026-001" },
                  { label: "Adjuster Name", key: "adjusterName", placeholder: "John Smith" },
                  { label: "Adjuster Phone", key: "adjusterPhone", placeholder: "(555) 000-0000" },
                  { label: "Initial Offer ($)", key: "initialOffer", placeholder: "15000" },
                  { label: "Public Adjuster", key: "publicAdjusterName", placeholder: "Mission PLC" },
                ].map(f => (
                  <div key={f.key}>
                    <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 3 }}>{f.label}</div>
                    <input className="fi" style={{ fontSize: 12 }} placeholder={f.placeholder} value={active[f.key] || ""}
                      onChange={e => updateClaimField(active.id, f.key, e.target.value)} />
                  </div>
                ))}
              </div>

              {/* Timeline */}
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--white)", marginBottom: ".6rem" }}>Claim Timeline</div>
              <div style={{ background: "var(--navy2)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", overflow: "hidden", marginBottom: ".75rem" }}>
                {(active.timeline || []).map((e, i) => (
                  <div key={e.id} style={{ display: "grid", gridTemplateColumns: "100px 1fr auto", gap: 10, padding: ".65rem 1rem", borderBottom: i < active.timeline.length - 1 ? ".5px solid var(--border2)" : "none", alignItems: "center" }}>
                    <div style={{ fontSize: 11, color: "var(--slate)" }}>{fmtDate(e.date)}</div>
                    <div style={{ fontSize: 12, color: "var(--white)" }}>{e.note}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#22c55e" }}>{e.amount ? fmt(e.amount) : ""}</div>
                  </div>
                ))}
              </div>

              {/* Add timeline entry */}
              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 100px auto", gap: 8, alignItems: "flex-end" }}>
                <div>
                  <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 3 }}>Date</div>
                  <input className="fi" type="date" style={{ fontSize: 11 }} value={newEntry.date} onChange={e => setNewEntry(p => ({ ...p, date: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 3 }}>Note</div>
                  <input className="fi" style={{ fontSize: 11 }} placeholder="e.g. Carrier adjuster visited, Received revised offer..." value={newEntry.note} onChange={e => setNewEntry(p => ({ ...p, note: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 3 }}>Amount ($)</div>
                  <input className="fi" style={{ fontSize: 11 }} type="number" placeholder="optional" value={newEntry.amount} onChange={e => setNewEntry(p => ({ ...p, amount: e.target.value }))} />
                </div>
                <button className="btn btn-sm" style={{ fontSize: 11, whiteSpace: "nowrap" }} onClick={() => addTimelineEntry(active.id)}>+ Add</button>
              </div>

              {/* Notes */}
              <div style={{ marginTop: "1rem" }}>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Claim notes</div>
                <textarea className="fi" rows={3} style={{ width: "100%", resize: "vertical", fontSize: 12 }}
                  placeholder="Strategy notes, attorney guidance, next steps..."
                  value={active.notes || ""} onChange={e => updateClaimField(active.id, "notes", e.target.value)} />
              </div>
            </div>
          )}
        </div>
      )}
    {lightbox && (
      <div onClick={() => setLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(255,255,255,.92)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out",padding:"1rem"}}>
        <div onClick={e => e.stopPropagation()} style={{position:"relative"}}>
          <img src={lightbox.dataUrl} alt={lightbox.name} style={{maxWidth:"90vw",maxHeight:"85vh",objectFit:"contain",borderRadius:8}} />
          <button onClick={() => setLightbox(null)} style={{position:"absolute",top:-12,right:-12,background:"#3D4B61",border:"none",borderRadius:"50%",width:28,height:28,color:"#fff",cursor:"pointer",fontSize:16}}>✕</button>
          <button onClick={() => { const a=document.createElement("a"); a.href=lightbox.dataUrl; a.download=lightbox.name; a.click(); }} style={{position:"absolute",bottom:-36,right:0,background:"var(--teal)",border:"none",borderRadius:6,padding:"4px 12px",color:"#fff",fontSize:11,cursor:"pointer",fontWeight:600}}>⬇ Download</button>
        </div>
      </div>
    )}
    </div>
  );
}

// ─── Reserve Fund Tracker ─────────────────────────────────────────────────────
function ReserveFund({ toast, orgName = "Your HOA" }) {
  const SK = `ba_reserve_${orgName.replace(/\s+/g,"_")}`;
  const defaultData = {
    currentBalance: "",
    fundingGoal: "",
    monthlyContribution: "",
    lastUpdated: "",
    notes: "",
    history: [], // { id, date, amount, type, description }
    categories: [
      { id: 1, name: "Roof Replacement", target: 0, saved: 0 },
      { id: 2, name: "Parking/Paving",   target: 0, saved: 0 },
      { id: 3, name: "Pool/Amenities",   target: 0, saved: 0 },
      { id: 4, name: "Elevator",         target: 0, saved: 0 },
    ]
  };

  const load = () => { try { return JSON.parse(localStorage.getItem(SK)) || defaultData; } catch { return defaultData; } };
  const [data, setData] = useState(load);
  const [showAdd, setShowAdd] = useState(false);
  const [entry, setEntry] = useState({ date: new Date().toISOString().split("T")[0], amount: "", type: "contribution", description: "" });
  const [editing, setEditing] = useState(false);

  const save = (d) => { localStorage.setItem(SK, JSON.stringify(d)); setData(d); };

  const balance = parseFloat(data.currentBalance) || 0;
  const goal = parseFloat(data.fundingGoal) || 0;
  const monthly = parseFloat(data.monthlyContribution) || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((balance / goal) * 100)) : 0;
  const monthsToGoal = goal > 0 && monthly > 0 && balance < goal ? Math.ceil((goal - balance) / monthly) : 0;
  const fundingStatus = pct >= 90 ? { label: "Well Funded", color: "#22c55e" } : pct >= 70 ? { label: "Adequately Funded", color: "#14b8a6" } : pct >= 50 ? { label: "Underfunded", color: "#dc2626" } : { label: "Critical — Action Required", color: "#ef4444" };

  const addEntry = () => {
    if (!entry.amount || !entry.description) { toast("Amount and description required"); return; }
    const amt = parseFloat(entry.amount);
    const newHistory = [{ id: Date.now(), ...entry, amount: amt }, ...data.history];
    const newBalance = entry.type === "contribution" || entry.type === "interest"
      ? balance + amt
      : balance - amt;
    const updated = { ...data, history: newHistory, currentBalance: newBalance.toFixed(2), lastUpdated: entry.date };
    save(updated);
    setEntry({ date: new Date().toISOString().split("T")[0], amount: "", type: "contribution", description: "" });
    setShowAdd(false);
    toast("Reserve transaction recorded");
  };

  const fmt = (n) => `$${parseFloat(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--white)", letterSpacing: "-0.5px", marginBottom: 4 }}>🏦 Reserve Fund</div>
          <div style={{ fontSize: 13, color: "var(--slate)" }}>{orgName} · Manual tracking · Updated {data.lastUpdated ? fmtDate(data.lastUpdated) : "never"}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-g btn-sm" onClick={() => setEditing(!editing)} style={{ color: "var(--teal)" }}>⚙ Settings</button>
          <button className="btn btn-sm" onClick={() => setShowAdd(true)}>+ Add transaction</button>
        </div>
      </div>

      {/* Settings panel */}
      {editing && (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: ".75rem" }}>Reserve Fund Settings</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: ".75rem" }}>
            {[
              { label: "Current Balance ($)", key: "currentBalance", placeholder: "42500.00" },
              { label: "Funding Goal ($)", key: "fundingGoal", placeholder: "150000.00" },
              { label: "Monthly Contribution ($)", key: "monthlyContribution", placeholder: "1000.00" },
            ].map(f => (
              <div key={f.key}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 4 }}>{f.label}</div>
                <input className="fi" placeholder={f.placeholder} value={data[f.key]}
                  onChange={e => setData(p => ({ ...p, [f.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom: ".75rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 4 }}>Notes (reserve study reference, CPA notes, etc.)</div>
            <textarea className="fi" rows={2} style={{ width: "100%", resize: "vertical" }} value={data.notes}
              onChange={e => setData(p => ({ ...p, notes: e.target.value }))} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={() => { save(data); setEditing(false); toast("Reserve settings saved"); }}>Save settings</button>
            <button className="btn-g btn-sm" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Add transaction modal */}
      {showAdd && (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: ".75rem" }}>Add Reserve Transaction</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 10, marginBottom: ".75rem" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 4 }}>Date</div>
              <input className="fi" type="date" value={entry.date} onChange={e => setEntry(p => ({ ...p, date: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 4 }}>Type</div>
              <select className="fi" value={entry.type} onChange={e => setEntry(p => ({ ...p, type: e.target.value }))}>
                <option value="contribution">Monthly contribution</option>
                <option value="interest">Interest earned</option>
                <option value="withdrawal">Withdrawal / expenditure</option>
                <option value="transfer">Transfer in</option>
                <option value="adjustment">Balance adjustment</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 4 }}>Amount ($)</div>
              <input className="fi" type="number" placeholder="1000.00" value={entry.amount} onChange={e => setEntry(p => ({ ...p, amount: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 4 }}>Description</div>
              <input className="fi" placeholder="e.g. April reserve contribution" value={entry.description} onChange={e => setEntry(p => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={addEntry}>Save transaction</button>
            <button className="btn-g btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Main stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: "1.25rem" }}>
        {[
          { label: "Current Balance", value: fmt(balance), color: "var(--white)", big: true },
          { label: "Funding Goal", value: fmt(goal), color: "var(--slate)" },
          { label: "Monthly Contribution", value: fmt(monthly), color: "var(--teal)" },
          { label: "Months to Goal", value: monthsToGoal > 0 ? `${monthsToGoal} months` : goal > 0 && balance >= goal ? "✅ Fully Funded" : "Set goal", color: monthsToGoal > 60 ? "#ef4444" : monthsToGoal > 24 ? "#dc2626" : "#22c55e" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1rem 1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: s.big ? 22 : 18, fontWeight: 800, color: s.color, letterSpacing: "-0.5px" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Funding progress bar */}
      {goal > 0 && (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".6rem" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)" }}>Funding Status</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: fundingStatus.color }}>{pct}% — {fundingStatus.label}</div>
          </div>
          <div style={{ height: 12, background: "var(--border2)", borderRadius: 6, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: fundingStatus.color, borderRadius: 6, transition: "width .4s" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: "var(--slate)" }}>
            <span>{fmt(balance)} saved</span>
            <span>Goal: {fmt(goal)}</span>
          </div>
          {data.notes && <div style={{ marginTop: ".75rem", fontSize: 12, color: "var(--slate)", fontStyle: "italic", borderTop: ".5px solid var(--border2)", paddingTop: ".6rem" }}>📋 {data.notes}</div>}
        </div>
      )}

      {/* Transaction history */}
      <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", overflow: "hidden" }}>
        <div style={{ padding: ".85rem 1.25rem", borderBottom: ".5px solid var(--border2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)" }}>Transaction History</div>
          <div style={{ fontSize: 12, color: "var(--slate)" }}>{data.history.length} entries</div>
        </div>
        {data.history.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "var(--slate)", fontSize: 13 }}>No transactions yet. Click "+ Add transaction" to get started.</div>
        ) : (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 3fr 1fr", padding: ".65rem 1.25rem", background: "rgba(255,255,255,.02)", borderBottom: ".5px solid var(--border2)" }}>
              {["Date", "Type", "Description", "Amount"].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</div>
              ))}
            </div>
            {data.history.map((h, i) => {
              const isOut = h.type === "withdrawal";
              return (
                <div key={h.id} style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 3fr 1fr", padding: ".75rem 1.25rem", borderBottom: i < data.history.length - 1 ? ".5px solid var(--border2)" : "none", alignItems: "center" }}>
                  <div style={{ fontSize: 12, color: "var(--slate)" }}>{fmtDate(h.date)}</div>
                  <div style={{ fontSize: 12, color: "var(--white)", textTransform: "capitalize" }}>{h.type}</div>
                  <div style={{ fontSize: 12, color: "var(--white)" }}>{h.description}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: isOut ? "#ef4444" : "#22c55e" }}>{isOut ? "-" : "+"}{fmt(h.amount)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Financial Report Generator ───────────────────────────────────────────────
function FinancialReport({ dues, owners, toast, orgName = "Your HOA" }) {
  const [month, setMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [expenses, setExpenses] = useState([
    { id: 1, category: "Landscaping",    amount: "", vendor: "" },
    { id: 2, category: "Maintenance",    amount: "", vendor: "" },
    { id: 3, category: "Utilities",      amount: "", vendor: "" },
    { id: 4, category: "Insurance",      amount: "", vendor: "" },
    { id: 5, category: "Reserve Contribution", amount: "", vendor: "" },
    { id: 6, category: "Other",          amount: "", vendor: "" },
  ]);
  const [notes, setNotes] = useState("");
  const [reserveBal, setReserveBal] = useState("");
  const [opBal, setOpBal] = useState("");

  const SK = `ba_finreport_${orgName.replace(/\s+/g,"_")}_${month}`;
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SK));
      if (saved) { setExpenses(saved.expenses || expenses); setNotes(saved.notes || ""); setReserveBal(saved.reserveBal || ""); setOpBal(saved.opBal || ""); }
    } catch {}
  }, [month]);

  const saveReport = () => {
    localStorage.setItem(SK, JSON.stringify({ expenses, notes, reserveBal, opBal }));
    toast("Report saved");
  };

  // Calculate from dues data
  const monthDues = dues.filter(d => d.due_date?.startsWith(month) || d.month === month);
  const collected = dues.filter(d => d.status === "paid" && (d.paid_date?.startsWith(month) || d.due_date?.startsWith(month))).reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const outstanding = dues.filter(d => d.status !== "paid").reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const delinquent = owners.filter(o => o.status === "delinquent").length;
  const totalExp = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const netIncome = collected - totalExp;

  const fmt = (n) => `$${parseFloat(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const monthLabel = new Date(month + "-02").toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const printReport = () => {
    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>${orgName} — Financial Report ${monthLabel}</title>
    <style>
      * { margin:0;padding:0;box-sizing:border-box; }
      body { font-family: Georgia, serif; color: #111; padding: 48px; font-size: 13px; }
      h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
      h2 { font-size: 14px; font-weight: 700; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
      .header { border-bottom: 2px solid #111; padding-bottom: 16px; margin-bottom: 20px; }
      .sub { font-size: 11px; color: #666; margin-top: 2px; }
      .row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee; font-size: 13px; }
      .row.total { font-weight: 700; border-top: 2px solid #111; border-bottom: none; margin-top: 4px; }
      .row.net { font-weight: 700; font-size: 15px; color: ${netIncome >= 0 ? "#166534" : "#991b1b"}; }
      .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
      .stat-box { background: #f9f9f9; border: 1px solid #ddd; border-radius: 6px; padding: 12px 16px; margin-bottom: 10px; }
      .stat-val { font-size: 20px; font-weight: 700; color: #111; }
      .stat-label { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 4px; }
      .notes { background: #f5f5f5; border-left: 3px solid #999; padding: 10px 14px; font-style: italic; color: #444; margin-top: 12px; }
      .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ccc; font-size: 11px; color: #999; display: flex; justify-content: space-between; }
      .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
      .green { background: #dcfce7; color: #166534; }
      .red { background: #fee2e2; color: #991b1b; }
      @media print { body { padding: 24px; } }
    </style></head><body>
    <div class="header">
      <h1>${orgName}</h1>
      <div class="sub">Monthly Financial Report — ${monthLabel}</div>
      <div class="sub">Prepared ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · ${orgName} Board of Directors</div>
    </div>

    <div class="two-col">
      <div>
        <h2>Income Summary</h2>
        <div class="row"><span>Dues collected this month</span><span>${fmt(collected)}</span></div>
        <div class="row"><span>Outstanding balance owed</span><span>${fmt(outstanding)}</span></div>
        <div class="row"><span>Delinquent homeowners</span><span>${delinquent} unit${delinquent !== 1 ? "s" : ""}</span></div>
        <div class="row total"><span>Total Income</span><span>${fmt(collected)}</span></div>
      </div>
      <div>
        <h2>Expense Summary</h2>
        ${expenses.filter(e => parseFloat(e.amount) > 0).map(e =>
          `<div class="row"><span>${e.category}${e.vendor ? ` (${e.vendor})` : ""}</span><span>${fmt(e.amount)}</span></div>`
        ).join("")}
        <div class="row total"><span>Total Expenses</span><span>${fmt(totalExp)}</span></div>
      </div>
    </div>

    <h2>Net Position</h2>
    <div class="row net">
      <span>Net Income / (Loss) for ${monthLabel}</span>
      <span class="badge ${netIncome >= 0 ? "green" : "red"}">${fmt(netIncome)}</span>
    </div>

    <h2>Balance Sheet Snapshot</h2>
    <div class="two-col">
      <div class="stat-box">
        <div class="stat-label">Operating Account Balance</div>
        <div class="stat-val">${fmt(opBal)}</div>
      </div>
      <div class="stat-box">
        <div class="stat-label">Reserve Fund Balance</div>
        <div class="stat-val">${fmt(reserveBal)}</div>
      </div>
    </div>

    ${notes ? `<div class="notes"><strong>Board Notes:</strong><br>${notes.replace(/\n/g,"<br>")}</div>` : ""}

    <div class="footer">
      <span>${orgName} · Confidential — Board Use Only</span>
      <span>Generated by BoardArmor · board-armor.com</span>
    </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--white)", letterSpacing: "-0.5px", marginBottom: 4 }}>📊 Financial Report</div>
          <div style={{ fontSize: 13, color: "var(--slate)" }}>Monthly board-ready financial summary — print or share at your next meeting</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: ".5rem .75rem", color: "var(--white)", fontSize: 13 }} />
          <button className="btn-g btn-sm" onClick={saveReport} style={{ color: "var(--teal)" }}>Save</button>
          <button className="btn btn-sm" onClick={printReport}>🖨 Print / PDF</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem", marginBottom: "1.25rem" }}>

        {/* Income */}
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: "1rem" }}>💰 Income</div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: ".5rem 0", borderBottom: ".5px solid var(--border2)" }}>
            <span style={{ fontSize: 12, color: "var(--slate)" }}>Dues collected</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#22c55e" }}>{fmt(collected)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: ".5rem 0", borderBottom: ".5px solid var(--border2)" }}>
            <span style={{ fontSize: 12, color: "var(--slate)" }}>Outstanding owed</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: outstanding > 0 ? "#dc2626" : "var(--slate)" }}>{fmt(outstanding)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: ".5rem 0" }}>
            <span style={{ fontSize: 12, color: "var(--slate)" }}>Delinquent units</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: delinquent > 0 ? "#ef4444" : "var(--slate)" }}>{delinquent}</span>
          </div>
        </div>

        {/* Expenses */}
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: ".75rem" }}>📋 Expenses</div>
          {expenses.map((e, i) => (
            <div key={e.id} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: 6, marginBottom: 6 }}>
              <div style={{ fontSize: 11, color: "var(--slate)", display: "flex", alignItems: "center" }}>{e.category}</div>
              <input className="fi" placeholder="Vendor" style={{ fontSize: 11, padding: "4px 8px" }} value={e.vendor}
                onChange={ev => setExpenses(p => p.map(x => x.id === e.id ? { ...x, vendor: ev.target.value } : x))} />
              <input className="fi" placeholder="$0.00" type="number" style={{ fontSize: 11, padding: "4px 8px" }} value={e.amount}
                onChange={ev => setExpenses(p => p.map(x => x.id === e.id ? { ...x, amount: ev.target.value } : x))} />
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: ".5rem 0", borderTop: ".5px solid var(--border2)", marginTop: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--white)" }}>Total Expenses</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#ef4444" }}>{fmt(totalExp)}</span>
          </div>
        </div>
      </div>

      {/* Net + Balances */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
        <div style={{ background: netIncome >= 0 ? "rgba(34,197,94,.08)" : "rgba(239,68,68,.08)", border: `.5px solid ${netIncome >= 0 ? "rgba(34,197,94,.3)" : "rgba(239,68,68,.3)"}`, borderRadius: "var(--r)", padding: "1rem 1.25rem", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Net Income / (Loss)</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: netIncome >= 0 ? "#22c55e" : "#ef4444", letterSpacing: "-0.5px" }}>{fmt(netIncome)}</div>
        </div>
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1rem 1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Operating Balance</div>
          <input className="fi" placeholder="Enter current balance" value={opBal} onChange={e => setOpBal(e.target.value)}
            style={{ fontSize: 16, fontWeight: 700, background: "transparent", border: "none", color: "var(--white)", padding: 0, width: "100%" }} />
        </div>
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1rem 1.25rem" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Reserve Balance</div>
          <input className="fi" placeholder="Enter reserve balance" value={reserveBal} onChange={e => setReserveBal(e.target.value)}
            style={{ fontSize: 16, fontWeight: 700, background: "transparent", border: "none", color: "var(--white)", padding: 0, width: "100%" }} />
        </div>
      </div>

      {/* Board notes */}
      <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: ".5rem" }}>Board Notes</div>
        <textarea className="fi" rows={3} style={{ width: "100%", resize: "vertical" }}
          placeholder="Add context for the board — unusual expenses, upcoming projects, delinquency notes, etc."
          value={notes} onChange={e => setNotes(e.target.value)} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="btn-g btn-sm" onClick={saveReport} style={{ color: "var(--teal)" }}>💾 Save report</button>
        <button className="btn btn-sm" onClick={printReport}>🖨 Print / Download PDF</button>
      </div>
    </div>
  );
}


// ─── MPLC Claims Partner ───────────────────────────────────────────────────────
function MPLCPartner({ toast, orgName = "Your HOA", orgEmail = "" }) {
  const MPLC = { name: "Mission Property Loss Consultants", phone: "(833) 446-7728", email: "info@missionae.com" };
  const [requestType, setRequestType] = useState(null); // null | "preloss" | "loss"
  const [form, setForm] = useState({ contactName: "", contactPhone: "", contactEmail: orgEmail || "", lossType: "Wind/Storm", lossDate: "", description: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const SCOPE = [
    { icon: "📋", title: "Pre-Loss Documentation", badge: "No cost", badgeColor: "#22c55e",
      items: ["Review and assess your HOA's insurance policies to identify coverage gaps before a loss ever happens."] },
    { icon: "🏚️", title: "Post-Loss Claim Management", badge: "Contingency · separate PA agreement per claim", badgeColor: "#dc2626",
      items: ["Immediate site inspections and damage assessments", "Detailed reports with photo and video documentation", "Scope of loss with precise repair estimates", "Claim submission with full supporting documentation", "Licensed public adjuster representation where applicable", "Direct negotiation with the insurance company", "End-to-end claim management from filing to settlement"] },
    { icon: "✅", title: "Post-Recovery Documentation", badge: "Contingency · separate PA agreement per claim", badgeColor: "#dc2626",
      items: ["Verify and document completed repairs and replacements", "Confirm policy compliance with carrier requirements", "Validate claim settlement accuracy", "Deliver a final report for future risk mitigation"] },
  ];

  const submit = async () => {
    if (!form.contactName || !form.contactEmail) { toast("Your name and email are required"); return; }
    if (requestType === "loss" && (!form.lossDate || !form.description)) { toast("Loss date and description are required"); return; }
    setSending(true);
    const subject = requestType === "loss"
      ? `New Loss Referral — ${orgName} (via Board-Armor)`
      : `Free Pre-Loss Policy Review Request — ${orgName} (via Board-Armor)`;
    const lines = [ `HOA: ${orgName}`, `Contact: ${form.contactName}`, `Phone: ${form.contactPhone || "—"}`, `Email: ${form.contactEmail}` ];
    if (requestType === "loss") {
      lines.push(`Loss type: ${form.lossType}`, `Date of loss: ${form.lossDate}`, `Description: ${form.description}`);
    } else {
      lines.push("Request: Free pre-loss insurance policy review to identify coverage gaps.");
      if (form.description) lines.push(`Notes: ${form.description}`);
    }
    lines.push("", "This request was submitted from Board-Armor's Claims Partner page.");
    try {
      await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: MPLC.email, subject, text: lines.join("\n"), type: "mplc-referral" }),
      });
      setSent(true);
      toast(`✅ Request sent to ${MPLC.name}`);
    } catch (e) {
      toast("Could not send request — please call MPLC directly");
    }
    setSending(false);
  };

  if (sent) return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ background: "rgba(34,197,94,.08)", border: ".5px solid rgba(34,197,94,.3)", borderRadius: "var(--r)", padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: "1rem" }}>✅</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: "var(--white)", marginBottom: 6 }}>Request sent to {MPLC.name}</div>
        <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1rem" }}>They'll reach out to {form.contactEmail} directly. You can also call them at {MPLC.phone}.</div>
        <button className="btn-g btn-sm" onClick={() => { setSent(false); setRequestType(null); }}>Done</button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--white)", letterSpacing: "-0.5px", marginBottom: 4 }}>🤝 Claims Partner — {MPLC.name}</div>
        <div style={{ fontSize: 13, color: "var(--slate)" }}>Licensed property loss consulting &amp; public adjusting · Referred through Board-Armor</div>
      </div>

      <div style={{ background: "rgba(20,184,166,.06)", border: ".5px solid rgba(20,184,166,.2)", borderRadius: "var(--r)", padding: "1rem 1.25rem", marginBottom: "1.5rem", display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ fontSize: 20, flexShrink: 0 }}>💡</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--teal)", marginBottom: 3 }}>Why work with a public adjuster</div>
          <div style={{ fontSize: 12, color: "var(--slate)", lineHeight: 1.6 }}>Insurance carriers have their own adjuster working for them. Mission Property Loss Consultants (MPLC) works for your HOA — documenting the loss, building the scope of repairs, and negotiating the settlement so your board doesn't have to go it alone.</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: "1.5rem" }}>
        {SCOPE.map(s => (
          <div key={s.title} style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.1rem" }}>
            <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: 4 }}>{s.title}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: s.badgeColor, background: `${s.badgeColor}18`, display: "inline-block", padding: "2px 8px", borderRadius: 10, marginBottom: 8 }}>{s.badge}</div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "var(--slate)", lineHeight: 1.7 }}>
              {s.items.map((it, i) => <li key={i}>{it}</li>)}
            </ul>
          </div>
        ))}
      </div>

      <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.25rem", marginBottom: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)" }}>{MPLC.name}</div>
          <div style={{ fontSize: 12, color: "var(--slate)" }}>{MPLC.phone} · {MPLC.email}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-g btn-sm" onClick={() => setRequestType("preloss")}>📋 Request free policy review</button>
          <button className="btn btn-sm" style={{ background: "#ef4444", border: "none" }} onClick={() => setRequestType("loss")}>🚨 Report a loss</button>
        </div>
      </div>

      {requestType && (
        <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.5rem" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "var(--white)", marginBottom: "1rem" }}>{requestType === "loss" ? "🚨 Report a loss to MPLC" : "📋 Request a free pre-loss policy review"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: "1rem" }}>
            <div><div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Your name *</div><input className="fi" value={form.contactName} onChange={e => setForm(p => ({ ...p, contactName: e.target.value }))} /></div>
            <div><div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Phone</div><input className="fi" value={form.contactPhone} onChange={e => setForm(p => ({ ...p, contactPhone: e.target.value }))} /></div>
            <div><div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Email *</div><input className="fi" value={form.contactEmail} onChange={e => setForm(p => ({ ...p, contactEmail: e.target.value }))} /></div>
          </div>
          {requestType === "loss" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: "1rem" }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Loss type</div>
                <select className="fi" value={form.lossType} onChange={e => setForm(p => ({ ...p, lossType: e.target.value }))}>
                  {["Wind/Storm","Hail","Fire","Water/Flood","Earthquake","Vandalism","Liability","Other"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div><div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Date of loss *</div><input className="fi" type="date" value={form.lossDate} onChange={e => setForm(p => ({ ...p, lossDate: e.target.value }))} /></div>
            </div>
          )}
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>{requestType === "loss" ? "Describe the damage *" : "Anything MPLC should know? (optional)"}</div>
            <textarea className="fi" rows={3} style={{ width: "100%", resize: "vertical" }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={submit} disabled={sending}>{sending ? "Sending…" : "✅ Send to MPLC"}</button>
            <button className="btn-g btn-sm" onClick={() => setRequestType(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Emergency Response ───────────────────────────────────────────────────────
function EmergencyResponse({ owners, toast, orgName = "Your HOA", orgEmail = "", isDemo = false }) {
  const { orgId } = useAppCtx();
  const pubAdjName  = isDemo ? "Mission PLC" : (localStorage.getItem("ba_pub_adj_name") || "");
  const pubAdjPhone = isDemo ? "833-425-5555" : (localStorage.getItem("ba_pub_adj_phone") || "");
  const pubAdjEmail = isDemo ? "" : (localStorage.getItem("ba_pub_adj_email") || "");
  const SK = `ba_emergency_${orgName.replace(/\s+/g,"_")}`;

  const defaultVendors = [
    { id: 1, category: "Plumbing",        name: "", phone: "", email: "", emergency24: true,  notes: "" },
    { id: 2, category: "Electrical",      name: "", phone: "", email: "", emergency24: true,  notes: "" },
    { id: 3, category: "Fire/Restoration",name: "", phone: "", email: "", emergency24: true,  notes: "" },
    { id: 4, category: "HVAC",            name: "", phone: "", email: "", emergency24: false, notes: "" },
    { id: 5, category: "Structural",      name: "", phone: "", email: "", emergency24: false, notes: "" },
    { id: 6, category: "Security",        name: "", phone: "", email: "", emergency24: true,  notes: "" },
    { id: 7, category: "Property Mgmt",   name: "", phone: "", email: "", emergency24: false, notes: "" },
    { id: 8, category: "Insurance",       name: "", phone: "", email: "", emergency24: false, notes: "" },
  ];

  const load = () => { try { return JSON.parse(localStorage.getItem(SK)) || { vendors: defaultVendors, incidents: [] }; } catch { return { vendors: defaultVendors, incidents: [] }; } };
  const [data, setData] = useState(load);
  const [tab, setTab] = useState("vendors"); // vendors | incidents | broadcast
  const [showNewIncident, setShowNewIncident] = useState(false);
  const [activating, setActivating] = useState(false);
  const [broadcastSent, setBroadcastSent] = useState(false);
  const [broadcastPrompt, setBroadcastPrompt] = useState(null); // { vendorName, incidentType }

  const [incident, setIncident] = useState({
    date: new Date().toISOString().split("T")[0],
    time: new Date().toTimeString().slice(0,5),
    type: "Water/Plumbing",
    severity: "urgent",
    location: "",
    description: "",
    vendorsCalled: [],
    status: "active",
    photos: [],
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [sitePhotos, setSitePhotos] = useState([]);
  useEffect(() => { if (orgId) db.sitePhotos.list(orgId, "emergency").then(({ data }) => data && setSitePhotos(data)); }, [orgId]);

  const [broadcast, setBroadcast] = useState({
    type: "Water Shutoff",
    message: "",
    affectedUnits: "all",
  });

  const save = (d) => { localStorage.setItem(SK, JSON.stringify(d)); setData(d); };

  const updateVendor = (id, field, val) => {
    const updated = { ...data, vendors: data.vendors.map(v => v.id === id ? { ...v, [field]: val } : v) };
    save(updated);
  };

  const createIncident = () => {
    if (!incident.location || !incident.description) { toast("Location and description required"); return; }
    const newIncident = { ...incident, id: Date.now(), createdAt: new Date().toISOString(), log: [`${new Date().toLocaleTimeString()} — Incident created and logged`] };
    const updated = { ...data, incidents: [newIncident, ...data.incidents] };
    save(updated);
    setShowNewIncident(false);
    setIncident({ date: new Date().toISOString().split("T")[0], time: new Date().toTimeString().slice(0,5), type: "Water/Plumbing", severity: "urgent", location: "", description: "", vendorsCalled: [], status: "active", photos: [] });
    toast("🚨 Emergency incident logged with timestamp");
    setTab("incidents");
  };

  const closeIncident = (id) => {
    const updated = { ...data, incidents: data.incidents.map(i => i.id === id ? { ...i, status: "resolved", resolvedAt: new Date().toISOString() } : i) };
    save(updated);
    toast("Incident marked resolved");
  };

  const sendBroadcast = async () => {
    const recipients = owners.filter(o => o.email && (o.emailOptIn || o.email_opt_in));
    if (recipients.length === 0) { toast("No homeowners with email opt-in found"); return; }
    setActivating(true);
    const templates = {
      "Water Shutoff": `URGENT: Water service to ${orgName} will be temporarily shut off.\n\n${broadcast.message}\n\nAffected units: ${broadcast.affectedUnits === "all" ? "All units" : broadcast.affectedUnits}\n\nWe apologize for the inconvenience and will restore service as quickly as possible.\n\nFor emergencies, contact the board immediately.`,
      "Power Outage": `ALERT: Power outage affecting ${orgName}.\n\n${broadcast.message}\n\nAffected units: ${broadcast.affectedUnits === "all" ? "All units" : broadcast.affectedUnits}\n\nOur electrical contractor has been contacted. Updates will follow.`,
      "Building Emergency": `EMERGENCY NOTICE — ${orgName}\n\n${broadcast.message}\n\nPlease follow all safety instructions from emergency personnel.\n\nThe board is actively managing this situation.`,
      "Storm Damage": `STORM DAMAGE NOTICE — ${orgName}\n\n${broadcast.message}\n\nOur vendors have been contacted. Documentation is being collected for insurance purposes.\n\nDo not attempt repairs without board approval.`,
      "General Notice": `NOTICE FROM ${orgName} BOARD\n\n${broadcast.message}`,
    };
    const emailBody = templates[broadcast.type] || templates["General Notice"];
    try {
      await Promise.allSettled(recipients.map(o =>
        fetch("/.netlify/functions/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: o.email,
            subject: `🚨 ${broadcast.type} — ${orgName}`,
            text: emailBody,
            type: "emergency-broadcast",
          }),
        })
      ));
      // Log broadcast to active incident if exists
      const activeIncident = data.incidents.find(i => i.status === "active");
      if (activeIncident) {
        const updated = { ...data, incidents: data.incidents.map(i => i.id === activeIncident.id
          ? { ...i, log: [...(i.log || []), `${new Date().toLocaleTimeString()} — Emergency broadcast sent to ${recipients.length} homeowners`] }
          : i
        )};
        save(updated);
      }
      setBroadcastSent(true);
      toast(`🚨 Emergency broadcast sent to ${recipients.length} homeowners`);
    } catch(e) { toast("Broadcast failed — check email settings"); }
    setActivating(false);
  };

  const severityColor = (s) => s === "critical" ? "#ef4444" : s === "urgent" ? "#dc2626" : "#14b8a6";
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  const activeIncidents = data.incidents.filter(i => i.status === "active");
  const filledVendors = data.vendors.filter(v => v.name);

  return (
    <div style={{ maxWidth: 860 }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--white)", letterSpacing: "-0.5px", marginBottom: 4 }}>🚨 Emergency Response</div>
          <div style={{ fontSize: 13, color: "var(--slate)" }}>Vendor contacts · Incident logging · Homeowner broadcast</div>
        </div>
        <button className="btn btn-sm" style={{ background: "#ef4444", border: "none" }} onClick={() => { setTab("incidents"); setShowNewIncident(true); }}>
          🚨 Declare Emergency
        </button>
      </div>

      {/* Active incident banner */}
      {activeIncidents.length > 0 && (
        <div style={{ background: "rgba(239,68,68,.1)", border: "1.5px solid rgba(239,68,68,.4)", borderRadius: "var(--r)", padding: "1rem 1.25rem", marginBottom: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#ef4444", marginBottom: 2 }}>🔴 {activeIncidents.length} Active Incident{activeIncidents.length > 1 ? "s" : ""}</div>
            <div style={{ fontSize: 12, color: "var(--slate)" }}>{activeIncidents[0].type} — {activeIncidents[0].location} — Started {fmtTime(activeIncidents[0].createdAt)}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-g btn-sm" onClick={() => setTab("broadcast")} style={{ color: "#2A63D6" }}>📢 Broadcast update</button>
            <button className="btn-g btn-sm" onClick={() => closeIncident(activeIncidents[0].id)} style={{ color: "#22c55e" }}>✓ Mark resolved</button>
          </div>
        </div>
      )}

      {/* Broadcast prompt banner — appears after vendor contact */}
      {broadcastPrompt && (
        <div style={{ background: "rgba(42,99,214,.08)", border: "1.5px solid rgba(42,99,214,.35)", borderRadius: "var(--r)", padding: "1rem 1.25rem", marginBottom: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#2A63D6", marginBottom: 3 }}>📢 Notify homeowners?</div>
            <div style={{ fontSize: 12, color: "var(--slate)" }}>
              You just contacted <strong style={{ color: "var(--white)" }}>{broadcastPrompt.vendorName}</strong> for a <strong style={{ color: "var(--white)" }}>{broadcastPrompt.incidentType}</strong> incident
              {broadcastPrompt.incidentLocation ? ` at ${broadcastPrompt.incidentLocation}` : ""}. Do you want to send a homeowner broadcast?
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 16 }}>
            <button className="btn btn-sm" style={{ background: "#2A63D6", border: "none" }}
              onClick={() => {
                // Pre-fill broadcast and jump to broadcast tab
                const typeMap = {
                  "Water/Plumbing": "Water Shutoff",
                  "Fire": "Building Emergency",
                  "Electrical": "Power Outage",
                  "Storm Damage": "Storm Damage",
                  "Structural": "Building Emergency",
                };
                const broadcastType = typeMap[broadcastPrompt.incidentType] || "General Notice";
                const suggestedMsg = `We are currently addressing a ${broadcastPrompt.incidentType} emergency${broadcastPrompt.incidentLocation ? ` at ${broadcastPrompt.incidentLocation}` : ""}. ${broadcastPrompt.vendorName} has been contacted and is responding. We will provide updates as the situation develops. Please avoid the affected area until further notice.`;
                setBroadcast({ type: broadcastType, message: suggestedMsg, affectedUnits: "all" });
                setBroadcastPrompt(null);
                setTab("broadcast");
              }}>
              📢 Yes — notify homeowners
            </button>
            <button className="btn-g btn-sm" onClick={() => setBroadcastPrompt(null)} style={{ color: "var(--slate)" }}>
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, marginBottom: "1.25rem", background: "var(--navy3)", borderRadius: "var(--r)", padding: 4 }}>
        {[
          { id: "vendors",   label: `📋 Vendor Contacts (${filledVendors.length}/${data.vendors.length})` },
          { id: "incidents", label: `🚨 Incidents (${activeIncidents.length} active)` },
          { id: "broadcast", label: "📢 Broadcast" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: ".6rem", borderRadius: "calc(var(--r) - 2px)", border: "none", background: tab === t.id ? "var(--navy2)" : "transparent", color: tab === t.id ? "var(--white)" : "var(--slate)", fontSize: 12, fontWeight: tab === t.id ? 700 : 400, cursor: "pointer", transition: "all .15s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── VENDORS TAB ── */}
      {tab === "vendors" && (
        <div>
          {/* Public Adjuster Card */}
          {pubAdjName ? (
            <div style={{ background: "rgba(20,184,166,.06)", border: "1.5px solid rgba(20,184,166,.25)", borderRadius: "var(--r)", padding: "1rem 1.25rem", marginBottom: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "var(--teal)", marginBottom: 3 }}>🤝 Public Adjuster on File</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: 2 }}>{pubAdjName}</div>
                <div style={{ fontSize: 12, color: "var(--slate)" }}>{pubAdjPhone}{pubAdjEmail ? ` · ${pubAdjEmail}` : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {pubAdjPhone && (
                  <button onClick={() => {
                    const timestamp = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                    const logEntry = `${timestamp} — Called public adjuster: ${pubAdjName} at ${pubAdjPhone}`;
                    const activeIdx = data.incidents.findIndex(i => i.status === "active");
                    if (activeIdx >= 0) {
                      const updated = { ...data, incidents: data.incidents.map((inc, i) =>
                        i === activeIdx ? { ...inc, log: [...(inc.log || []), logEntry] } : inc
                      )};
                      save(updated);
                      setBroadcastPrompt({ vendorName: pubAdjName, incidentType: data.incidents[activeIdx].type, incidentLocation: data.incidents[activeIdx].location });
                      toast(`📞 Logged: Called ${pubAdjName}`);
                    } else {
                      toast("Log an incident first to track this contact");
                    }
                    window.location.href = `tel:${pubAdjPhone.replace(/\D/g,"")}`;
                  }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 700, color: "#fff", background: "var(--teal)", border: "none", borderRadius: "var(--r)", padding: "8px 16px", cursor: "pointer" }}>
                    📞 Contact Public Adjuster
                  </button>
                )}
                {pubAdjEmail && (
                  <button onClick={() => {
                    const timestamp = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                    const logEntry = `${timestamp} — Emailed public adjuster: ${pubAdjName} at ${pubAdjEmail}`;
                    const activeIdx = data.incidents.findIndex(i => i.status === "active");
                    if (activeIdx >= 0) {
                      const updated = { ...data, incidents: data.incidents.map((inc, i) =>
                        i === activeIdx ? { ...inc, log: [...(inc.log || []), logEntry] } : inc
                      )};
                      save(updated);
                      toast(`📧 Logged: Emailed ${pubAdjName}`);
                    }
                    window.location.href = `mailto:${pubAdjEmail}?subject=Emergency — ${orgName}&body=This is an emergency contact from the ${orgName} board.`;
                  }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 700, color: "var(--teal)", background: "rgba(20,184,166,.1)", border: ".5px solid rgba(20,184,166,.3)", borderRadius: "var(--r)", padding: "8px 14px", cursor: "pointer" }}>
                    📧 Email
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: ".75rem 1.25rem", marginBottom: "1.25rem", fontSize: 12, color: "var(--slate)" }}>
              💡 Add a public adjuster in <strong style={{ color: "var(--white)", cursor: "pointer" }} onClick={() => window.location.hash = "#settings"}>Settings → HOA Profile</strong> to enable the Contact Public Adjuster button.
            </div>
          )}
          <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: "1rem" }}>Add your emergency vendors now — before you need them. These contacts are available to all board members 24/7.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {data.vendors.map(v => (
              <div key={v.id} style={{ background: "var(--navy3)", border: `.5px solid ${v.name ? "rgba(20,184,166,.2)" : "var(--border2)"}`, borderRadius: "var(--r)", padding: "1rem 1.25rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".75rem" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)", textTransform: "uppercase", letterSpacing: ".06em" }}>{v.category}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, color: v.emergency24 ? "#22c55e" : "var(--slate)" }}>{v.emergency24 ? "✅ 24/7" : "Business hours"}</span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 3 }}>Company name</div>
                    <input className="fi" placeholder="ABC Plumbing Co." value={v.name} onChange={e => updateVendor(v.id, "name", e.target.value)} style={{ fontSize: 12 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 3 }}>Emergency phone</div>
                    <input className="fi" placeholder="(503) 555-0100" value={v.phone} onChange={e => updateVendor(v.id, "phone", e.target.value)} style={{ fontSize: 12 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 3 }}>Email</div>
                    <input className="fi" placeholder="emergency@vendor.com" value={v.email} onChange={e => updateVendor(v.id, "email", e.target.value)} style={{ fontSize: 12 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--slate)", marginBottom: 3 }}>Notes</div>
                    <input className="fi" placeholder="Account #, contact name..." value={v.notes} onChange={e => updateVendor(v.id, "notes", e.target.value)} style={{ fontSize: 12 }} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: ".6rem", flexWrap: "wrap" }}>
                {v.phone && (
                  <button onClick={() => {
                    // Log the call to the active incident
                    const timestamp = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                    const logEntry = `${timestamp} — Called ${v.name} (${v.category}) at ${v.phone}`;
                    const activeIdx = data.incidents.findIndex(i => i.status === "active");
                    if (activeIdx >= 0) {
                      const activeInc = data.incidents[activeIdx];
                      const updated = { ...data, incidents: data.incidents.map((inc, i) =>
                        i === activeIdx ? { ...inc,
                          log: [...(inc.log || []), logEntry],
                          vendorsCalled: inc.vendorsCalled?.includes(v.id) ? inc.vendorsCalled : [...(inc.vendorsCalled || []), v.id]
                        } : inc
                      )};
                      save(updated);
                      // Prompt broadcast after logging
                      setBroadcastPrompt({ vendorName: v.name, incidentType: activeInc.type, incidentLocation: activeInc.location });
                      toast(`📞 Logged: Called ${v.name}`);
                    } else {
                      // No active incident — prompt to create one
                      if (window.confirm(`No active incident found.\n\nWould you like to log an emergency incident before calling ${v.name}?\n\nClick OK to go to the Incidents tab, or Cancel to call without logging.`)) {
                        setTab("incidents"); setShowNewIncident(true);
                        return;
                      }
                    }
                    // Open phone dialer
                    window.location.href = `tel:${v.phone.replace(/\D/g,"")}`;
                  }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#22c55e", background: "rgba(34,197,94,.1)", border: ".5px solid rgba(34,197,94,.25)", borderRadius: "var(--r)", padding: "5px 12px", cursor: "pointer" }}>
                    📞 Call now
                  </button>
                )}
                {v.email && (
                  <button onClick={() => {
                    const timestamp = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                    const logEntry = `${timestamp} — Emailed ${v.name} (${v.category}) at ${v.email}`;
                    const activeIdx = data.incidents.findIndex(i => i.status === "active");
                    if (activeIdx >= 0) {
                      const activeInc = data.incidents[activeIdx];
                      const updated = { ...data, incidents: data.incidents.map((inc, i) =>
                        i === activeIdx ? { ...inc, log: [...(inc.log || []), logEntry] } : inc
                      )};
                      save(updated);
                      setBroadcastPrompt({ vendorName: v.name, incidentType: activeInc.type, incidentLocation: activeInc.location });
                      toast(`📧 Logged: Emailed ${v.name}`);
                    }
                    window.location.href = `mailto:${v.email}?subject=Emergency — ${orgName}&body=This is an emergency contact from ${orgName} board.`;
                  }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#14b8a6", background: "rgba(20,184,166,.1)", border: ".5px solid rgba(20,184,166,.25)", borderRadius: "var(--r)", padding: "5px 12px", cursor: "pointer" }}>
                    📧 Email
                  </button>
                )}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: ".75rem", fontSize: 11, color: "var(--slate)", textAlign: "center" }}>Changes save automatically · Accessible to all board members</div>
        </div>
      )}

      {/* ── INCIDENTS TAB ── */}
      {tab === "incidents" && (
        <div>
          {sitePhotos.length > 0 && (
            <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--white)", marginBottom: 8 }}>📸 Site photos ({sitePhotos.length}) — visible to every board member, on any device</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {sitePhotos.slice(0, 12).map(p => (
                  <img key={p.id} src={p.photo_url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: ".5px solid var(--border2)", cursor: "pointer" }} onClick={() => window.open(p.photo_url, "_blank")} />
                ))}
              </div>
            </div>
          )}
          {showNewIncident && (
            <div style={{ background: "rgba(239,68,68,.06)", border: "1.5px solid rgba(239,68,68,.3)", borderRadius: "var(--r)", padding: "1.25rem", marginBottom: "1.25rem" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#ef4444", marginBottom: "1rem" }}>🚨 New Emergency Incident</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Date</div>
                  <input className="fi" type="date" value={incident.date} onChange={e => setIncident(p => ({ ...p, date: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Time</div>
                  <input className="fi" type="time" value={incident.time} onChange={e => setIncident(p => ({ ...p, time: e.target.value }))} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Severity</div>
                  <select className="fi" value={incident.severity} onChange={e => setIncident(p => ({ ...p, severity: e.target.value }))}>
                    <option value="critical">🔴 Critical — Life safety</option>
                    <option value="urgent">🟠 Urgent — Property damage</option>
                    <option value="moderate">🔵 Moderate — Monitor</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Incident type</div>
                  <select className="fi" value={incident.type} onChange={e => setIncident(p => ({ ...p, type: e.target.value }))}>
                    {["Water/Plumbing","Fire","Electrical","Structural","HVAC","Security","Storm Damage","Elevator","Other"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Location / Unit(s)</div>
                  <input className="fi" placeholder="e.g. Unit 304, Building B roof, Main lobby" value={incident.location} onChange={e => setIncident(p => ({ ...p, location: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Description *</div>
                <textarea className="fi" rows={3} style={{ width: "100%", resize: "vertical" }} placeholder="Describe the emergency in detail — what happened, when it was discovered, current condition..." value={incident.description} onChange={e => setIncident(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Photos</div>
                <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",background:"var(--navy2)",border:".5px solid var(--border2)",borderRadius:"var(--r)",cursor:"pointer",fontSize:12,color:"#ef4444",fontWeight:600}}>
                  {uploadingPhoto ? "Uploading…" : "📷 Take or add photo"}
                  <input type="file" accept="image/*" capture="environment" multiple disabled={uploadingPhoto} style={{display:"none"}}
                    onChange={async e => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length) return;
                      setUploadingPhoto(true);
                      for (const file of files) {
                        const { data: photoRow, error } = await uploadSitePhoto(orgId, "emergency", incident.type, file, "", "");
                        if (error) { toast("Photo upload failed — " + (error.message || "try again")); continue; }
                        setIncident(p => ({ ...p, photos: [...(p.photos || []), photoRow.photo_url] }));
                        setSitePhotos(prev => [photoRow, ...prev]);
                      }
                      setUploadingPhoto(false);
                      e.target.value = "";
                    }} />
                </label>
                {incident.photos?.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {incident.photos.map((url, pi) => (
                      <img key={pi} src={url} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, border: ".5px solid var(--border2)", cursor: "pointer" }} onClick={() => window.open(url, "_blank")} />
                    ))}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 4 }}>Vendors contacted</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {data.vendors.filter(v => v.name).map(v => (
                    <label key={v.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--white)", cursor: "pointer" }}>
                      <input type="checkbox" checked={incident.vendorsCalled.includes(v.id)}
                        onChange={e => setIncident(p => ({ ...p, vendorsCalled: e.target.checked ? [...p.vendorsCalled, v.id] : p.vendorsCalled.filter(x => x !== v.id) }))} />
                      {v.category}: {v.name}
                    </label>
                  ))}
                  {data.vendors.filter(v => v.name).length === 0 && <span style={{ fontSize: 12, color: "var(--slate)" }}>Add vendors in the Vendor Contacts tab first</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-sm" style={{ background: "#ef4444", border: "none" }} onClick={createIncident}>🚨 Log incident now</button>
                <button className="btn-g btn-sm" onClick={() => setShowNewIncident(false)}>Cancel</button>
              </div>
            </div>
          )}

          {!showNewIncident && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
              <button className="btn btn-sm" style={{ background: "#ef4444", border: "none" }} onClick={() => setShowNewIncident(true)}>+ Log new incident</button>
            </div>
          )}

          {data.incidents.length === 0 ? (
            <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "2.5rem", textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: ".75rem" }}>✅</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--white)", marginBottom: 4 }}>No incidents on record</div>
              <div style={{ fontSize: 13, color: "var(--slate)" }}>When an emergency occurs, log it here immediately. The timestamp and documentation become your insurance claim paper trail.</div>
            </div>
          ) : (
            <div>
              {data.incidents.map((inc, i) => (
                <div key={inc.id} style={{ background: "var(--navy3)", border: `.5px solid ${inc.status === "active" ? "rgba(239,68,68,.3)" : "var(--border2)"}`, borderRadius: "var(--r)", padding: "1.25rem", marginBottom: ".75rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: ".75rem" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "var(--white)" }}>{inc.type}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: severityColor(inc.severity), background: `${severityColor(inc.severity)}18`, padding: "2px 8px", borderRadius: 10, textTransform: "capitalize" }}>{inc.severity}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: inc.status === "active" ? "#ef4444" : "#22c55e", background: inc.status === "active" ? "rgba(239,68,68,.1)" : "rgba(34,197,94,.1)", padding: "2px 8px", borderRadius: 10 }}>{inc.status === "active" ? "🔴 Active" : "✅ Resolved"}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "var(--slate)" }}>📍 {inc.location} · 🕐 {fmtTime(inc.createdAt)}</div>
                    </div>
                    {inc.status === "active" && (
                      <button className="btn-g btn-sm" onClick={() => closeIncident(inc.id)} style={{ color: "#22c55e", fontSize: 11 }}>✓ Mark resolved</button>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--white)", marginBottom: ".75rem", lineHeight: 1.6 }}>{inc.description}</div>
                  {inc.vendorsCalled?.length > 0 && (
                    <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: ".5rem" }}>
                      📞 Vendors contacted: {inc.vendorsCalled.map(id => data.vendors.find(v => v.id === id)?.name || "Unknown").join(", ")}
                    </div>
                  )}
                  {inc.log?.length > 0 && (
                    <div style={{ background: "var(--navy2)", borderRadius: "calc(var(--r) - 2px)", padding: ".75rem", fontSize: 11, color: "var(--slate)" }}>
                      {inc.log.map((entry, j) => <div key={j} style={{ marginBottom: j < inc.log.length - 1 ? 3 : 0 }}>• {entry}</div>)}
                    </div>
                  )}
                  {inc.photos?.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: ".5rem" }}>
                      {inc.photos.map((url, pi) => (
                        <img key={pi} src={url} alt="" style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, border: ".5px solid var(--border2)", cursor: "pointer" }} onClick={() => window.open(url, "_blank")} />
                      ))}
                    </div>
                  )}
                  {inc.resolvedAt && <div style={{ fontSize: 11, color: "#22c55e", marginTop: ".5rem" }}>✅ Resolved: {fmtTime(inc.resolvedAt)}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── BROADCAST TAB ── */}
      {tab === "broadcast" && (
        <div>
          <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1.25rem" }}>Send an emergency notification to all opted-in homeowners instantly. Every broadcast is logged to the active incident record.</div>

          {broadcastSent ? (
            <div style={{ background: "rgba(34,197,94,.08)", border: "1.5px solid rgba(34,197,94,.3)", borderRadius: "var(--r)", padding: "2rem", textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: ".75rem" }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#22c55e", marginBottom: 4 }}>Broadcast sent successfully</div>
              <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1rem" }}>All opted-in homeowners have been notified. This broadcast has been logged to your incident record.</div>
              <button className="btn-g btn-sm" onClick={() => { setBroadcastSent(false); setBroadcast({ type: "Water Shutoff", message: "", affectedUnits: "all" }); }}>Send another</button>
            </div>
          ) : (
            <div style={{ background: "var(--navy3)", border: ".5px solid var(--border2)", borderRadius: "var(--r)", padding: "1.5rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: "1rem" }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 6 }}>Emergency type</div>
                  <select className="fi" value={broadcast.type} onChange={e => setBroadcast(p => ({ ...p, type: e.target.value }))}>
                    {["Water Shutoff","Power Outage","Building Emergency","Storm Damage","General Notice"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 6 }}>Affected units</div>
                  <input className="fi" placeholder="All units — or list specific units" value={broadcast.affectedUnits} onChange={e => setBroadcast(p => ({ ...p, affectedUnits: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--slate)", marginBottom: 6 }}>Message to homeowners *</div>
                <textarea className="fi" rows={4} style={{ width: "100%", resize: "vertical" }}
                  placeholder="Describe what happened, what is being done, and what homeowners should do..."
                  value={broadcast.message} onChange={e => setBroadcast(p => ({ ...p, message: e.target.value }))} />
              </div>
              <div style={{ background: "rgba(42,99,214,.06)", border: ".5px solid rgba(42,99,214,.15)", borderRadius: "var(--r)", padding: ".75rem 1rem", fontSize: 12, color: "var(--slate)", marginBottom: "1rem" }}>
                📬 Will be sent to <strong style={{ color: "var(--white)" }}>{owners.filter(o => o.email && (o.emailOptIn || o.email_opt_in)).length} homeowners</strong> who have email notifications enabled.
              </div>
              <button className="btn btn-sm" style={{ background: "#ef4444", border: "none", opacity: activating ? .6 : 1 }}
                onClick={sendBroadcast} disabled={activating || !broadcast.message}>
                {activating ? "Sending..." : `📢 Send emergency broadcast`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>

  );
}

function GeneralLedger({ toast , orgName = "Your HOA", orgEmail = "" }) {
  const [accounts, setAccounts] = useState([]);
  const [journal,  setJournal]  = useState([]);
  const [section,  setSection]  = useState("chart");
  const [expanded, setExpanded] = useState(null);
  const [jeModal,  setJeModal]  = useState(false);
  const [jeForm,   setJeForm]   = useState({ date: "2024-04-25", memo:"", ref:"" });
  const [jeLines,  setJeLines]  = useState([
    { id:1, account:"", debit:"", credit:"" },
    { id:2, account:"", debit:"", credit:"" },
  ]);

  const typeColor = { asset:"gl-type-asset", liability:"gl-type-liability", equity:"gl-type-equity", income:"gl-type-income", expense:"gl-type-expense" };
  const typeLabel = { asset:"Asset", liability:"Liability", equity:"Equity", income:"Income", expense:"Expense" };

  // Single pass over accounts instead of 5 separate filter+reduce calls
  const accountTotals = useMemo(() => accounts.reduce((acc, a) => {
    acc[a.type] = (acc[a.type] || 0) + a.balance;
    return acc;
  }, {}), [accounts]);
  const totalAssets      = accountTotals.asset      || 0;
  const totalLiabilities = accountTotals.liability   || 0;
  const totalEquity      = accountTotals.equity      || 0;
  const totalIncome      = accountTotals.income      || 0;
  const totalExpenses    = accountTotals.expense     || 0;
  const netIncome        = totalIncome - totalExpenses;

  // Avoid flatMap intermediate array allocation — reduce directly over entries
  const { totalDebits, totalCredits } = useMemo(() => journal.reduce(
    (acc, j) => { j.entries.forEach(e => { acc.totalDebits += e.debit; acc.totalCredits += e.credit; }); return acc; },
    { totalDebits: 0, totalCredits: 0 }
  ), [journal]);
  const balanced = Math.abs(totalDebits - totalCredits) < 0.01;

  // Account lookup map — O(1) instead of O(n) .find() on every render
  const accountMap = useMemo(() => Object.fromEntries(accounts.map(a => [a.num, a.name])), [accounts]);
  const accountName = useCallback(num => accountMap[num] || num, [accountMap]);
  const fmt  = useCallback(n => n === 0 ? "" : `$${n.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}`, []);
  const fmtB = useCallback(n => `$${n.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}`, []);

  const jeDebitTotal  = jeLines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const jeCreditTotal = jeLines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const jeBalanced    = Math.abs(jeDebitTotal - jeCreditTotal) < 0.01 && jeDebitTotal > 0;

  const postJE = () => {
    const newJE = { id: Date.now(), date: jeForm.date, ref: jeForm.ref || `JE-${String(journal.length+1).padStart(3,"0")}`, memo: jeForm.memo, entries: jeLines.filter(l => l.account && (l.debit||l.credit)).map(l => ({ account:l.account, debit:Number(l.debit)||0, credit:Number(l.credit)||0 })) };
    setJournal(p => [...p, newJE]);
    setJeLines([{id:1,account:"",debit:"",credit:""},{id:2,account:"",debit:"",credit:""}]);
    setJeForm({ date:"2024-04-25", memo:"", ref:"" });
    setJeModal(false);
    toast("Journal entry posted successfully");
  };

  const navSections = [
    { id:"chart",   label:"Chart of accounts" },
    { id:"journal", label:"Journal entries" },
    { id:"trial",   label:"Trial balance" },
    { id:"bs",      label:"Balance sheet" },
    { id:"income",  label:"Income statement" },
  ];

  const acctGroups = ["asset","liability","equity","income","expense"];

  return (
    <div>
      <div className="ph">
        <div><div className="pt">General Ledger</div><div className="ps">Double-entry accounting · {accounts.length} accounts · {journal.length} journal entries</div></div>
        <div style={{ display:"flex", gap:8 }}>
          {!balanced && <span style={{ fontSize:12, color:"var(--red)", display:"flex", alignItems:"center", gap:4 }}>⚠ Out of balance</span>}
          <button className="btn btn-sm" onClick={() => setJeModal(true)}>+ Journal entry</button>
        </div>
      </div>
      <div className="gl-layout">
        {/* Left nav */}
        <div className="gl-nav">
          <div className="gl-nav-section">Reports</div>
          {navSections.map(n => (
            <div key={n.id} className={`gl-nav-item ${section===n.id?"active":""}`} onClick={() => setSection(n.id)}>{n.label}</div>
          ))}
          <div className="gl-nav-section" style={{ marginTop:"1rem" }}>Balances</div>
          <div className="gl-nav-item" style={{ cursor:"default" }}><span>Total assets</span><span className="gl-nav-balance" style={{ color:"var(--teal)" }}>{fmtB(totalAssets)}</span></div>
          <div className="gl-nav-item" style={{ cursor:"default" }}><span>Total liabilities</span><span className="gl-nav-balance" style={{ color:"var(--red)" }}>{fmtB(totalLiabilities)}</span></div>
          <div className="gl-nav-item" style={{ cursor:"default" }}><span>Net income</span><span className="gl-nav-balance" style={{ color: netIncome>=0?"var(--teal)":"var(--red)" }}>{fmtB(netIncome)}</span></div>
        </div>

        <div className="gl-main">
          {/* Chart of accounts */}
          {section === "chart" && (
            <div>
              <div style={{ fontSize:13, color:"var(--slate)", marginBottom:"1.25rem" }}>Click any account to view its journal entry history.</div>
              {acctGroups.map(type => (
                <div key={type} style={{ marginBottom:"1.5rem" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"var(--slate)", letterSpacing:".07em", textTransform:"uppercase", marginBottom:".65rem", paddingBottom:".4rem", borderBottom:".5px solid var(--border)" }}>{typeLabel[type]} accounts</div>
                  {accounts.filter(a => a.type===type).map(a => (
                    <div key={a.num}>
                      <div className={`gl-account-card ${expanded===a.num?"expanded":""}`} onClick={() => setExpanded(expanded===a.num?null:a.num)}>
                        <div className="gl-account-header">
                          <div className="gl-account-num">{a.num}</div>
                          <div className="gl-account-name">{a.name}</div>
                          <span className={`gl-account-type ${typeColor[a.type]}`}>{typeLabel[a.type]}</span>
                          <div className={`gl-account-balance`} style={{ color: a.type==="expense"?"var(--amber)":a.type==="income"?"var(--teal)":a.type==="asset"?"var(--white)":a.type==="liability"?"var(--red)":"var(--purple)" }}>{fmtB(a.balance)}</div>
                          <span style={{ fontSize:11, color:"var(--slate)", marginLeft:4 }}>{expanded===a.num?"▲":"▼"}</span>
                        </div>
                      </div>
                      {expanded === a.num && (
                        <div style={{ background:"var(--navy3)", border:".5px solid var(--border2)", borderRadius:"0 0 var(--r) var(--r)", marginTop:-4, marginBottom:8, overflow:"hidden" }}>
                          <div className="gl-je-row gl-je-header"><span>Date</span><span>Memo</span><span>Ref</span><span style={{ textAlign:"right" }}>Debit</span><span style={{ textAlign:"right" }}>Credit</span></div>
                          {journal.flatMap(j => j.entries.filter(e => e.account===a.num).map(e => ({ ...e, date:j.date, memo:j.memo, ref:j.ref }))).map((e, i) => (
                            <div key={i} className="gl-je-row">
                              <span style={{ color:"var(--slate)", fontFamily:"monospace" }}>{e.date}</span>
                              <span style={{ color:"var(--white)" }}>{e.memo}</span>
                              <span style={{ color:"var(--slate)", fontFamily:"monospace" }}>{e.ref}</span>
                              <span className="gl-debit">{fmt(e.debit)}</span>
                              <span className="gl-credit">{fmt(e.credit)}</span>
                            </div>
                          ))}
                          {journal.flatMap(j => j.entries.filter(e => e.account===a.num)).length === 0 && <div style={{ padding:".75rem .85rem", fontSize:12, color:"var(--slate)" }}>No entries for this account.</div>}
                        </div>
                      )}
                    </div>
                  ))}
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, fontWeight:600, padding:".5rem .85rem", background:"var(--navy3)", borderRadius:"var(--r)", marginBottom:".5rem" }}>
                    <span style={{ color:"var(--slate)" }}>Total {typeLabel[type]}</span>
                    <span style={{ color: type==="expense"?"var(--amber)":type==="income"?"var(--teal)":type==="asset"?"var(--white)":type==="liability"?"var(--red)":"var(--purple)" }}>{fmtB(accounts.filter(a=>a.type===type).reduce((s,a)=>s+a.balance,0))}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Journal entries */}
          {section === "journal" && (
            <div>
              <div className="tw">
                <table>
                  <thead><tr><th>Date</th><th>Ref</th><th>Memo</th><th>Lines</th><th style={{ textAlign:"right" }}>Debits</th><th style={{ textAlign:"right" }}>Credits</th></tr></thead>
                  <tbody>{journal.map(j => {
                    const deb = j.entries.reduce((s,e)=>s+e.debit,0);
                    const cre = j.entries.reduce((s,e)=>s+e.credit,0);
                    const ok  = Math.abs(deb-cre)<0.01;
                    return (
                      <tr key={j.id} style={{ cursor:"pointer" }} onClick={() => setExpanded(expanded===`je-${j.id}`?null:`je-${j.id}`)}>
                        <td style={{ fontFamily:"monospace", fontSize:12, color:"var(--slate)" }}>{j.date}</td>
                        <td style={{ fontFamily:"monospace", fontSize:12, color:"var(--purple)" }}>{j.ref}</td>
                        <td style={{ fontWeight:500 }}>{j.memo}</td>
                        <td style={{ fontSize:12, color:"var(--slate)" }}>{j.entries.length} lines</td>
                        <td className="gl-debit">{fmtB(deb)}</td>
                        <td className="gl-credit">{fmtB(cre)}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
              <div className="gl-totals-check">
                <span style={{ color:"var(--slate)" }}>Total debits:</span><span className="gl-debit">{fmtB(totalDebits)}</span>
                <span style={{ color:"var(--slate)", marginLeft:12 }}>Total credits:</span><span className="gl-credit">{fmtB(totalCredits)}</span>
                <span style={{ marginLeft:"auto" }} className={balanced?"gl-balanced":"gl-unbalanced"}>{balanced?"✓ Books are balanced":"⚠ Out of balance"}</span>
              </div>
            </div>
          )}

          {/* Trial balance */}
          {section === "trial" && (
            <div className="tw">
              <table>
                <thead><tr><th>Acct #</th><th>Account name</th><th>Type</th><th style={{ textAlign:"right" }}>Debit</th><th style={{ textAlign:"right" }}>Credit</th></tr></thead>
                <tbody>
                  {accounts.map(a => (
                    <tr key={a.num}>
                      <td style={{ fontFamily:"monospace", fontSize:12, color:"var(--slate)" }}>{a.num}</td>
                      <td style={{ fontWeight:500 }}>{a.name}</td>
                      <td><span className={`gl-account-type ${typeColor[a.type]}`}>{typeLabel[a.type]}</span></td>
                      <td className="gl-debit">{a.normal==="debit" ? fmt(a.balance) : ""}</td>
                      <td className="gl-credit">{a.normal==="credit" ? fmt(a.balance) : ""}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop:"1.5px solid var(--border2)", fontWeight:700 }}>
                    <td colSpan={3} style={{ fontSize:13, color:"var(--white)" }}>Totals</td>
                    <td className="gl-debit">{fmtB(accounts.filter(a=>a.normal==="debit").reduce((s,a)=>s+a.balance,0))}</td>
                    <td className="gl-credit">{fmtB(accounts.filter(a=>a.normal==="credit").reduce((s,a)=>s+a.balance,0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Balance sheet */}
          {section === "bs" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
              <div>
                <div className="gl-bs-section">
                  <div className="gl-bs-title">Assets</div>
                  {accounts.filter(a=>a.type==="asset").map(a => <div key={a.num} className="gl-bs-row"><span>{a.name}</span><span style={{ color:"var(--teal)" }}>{fmtB(a.balance)}</span></div>)}
                  <div className="gl-bs-row subtotal"><span>Total assets</span><span style={{ color:"var(--teal)" }}>{fmtB(totalAssets)}</span></div>
                </div>
              </div>
              <div>
                <div className="gl-bs-section">
                  <div className="gl-bs-title">Liabilities</div>
                  {accounts.filter(a=>a.type==="liability").map(a => <div key={a.num} className="gl-bs-row"><span>{a.name}</span><span style={{ color:"var(--red)" }}>{fmtB(a.balance)}</span></div>)}
                  <div className="gl-bs-row subtotal"><span>Total liabilities</span><span style={{ color:"var(--red)" }}>{fmtB(totalLiabilities)}</span></div>
                </div>
                <div className="gl-bs-section">
                  <div className="gl-bs-title">Equity</div>
                  {accounts.filter(a=>a.type==="equity").map(a => <div key={a.num} className="gl-bs-row"><span>{a.name}</span><span style={{ color:"var(--purple)" }}>{fmtB(a.balance)}</span></div>)}
                  <div className="gl-bs-row subtotal"><span>Total equity</span><span style={{ color:"var(--purple)" }}>{fmtB(totalEquity)}</span></div>
                </div>
                <div className="gl-bs-row total-row"><span>Total liabilities + equity</span><span>{fmtB(totalLiabilities + totalEquity)}</span></div>
                <div style={{ fontSize:12, color: Math.abs(totalAssets-totalLiabilities-totalEquity)<1?"var(--teal)":"var(--red)", marginTop:".5rem" }}>
                  {Math.abs(totalAssets-totalLiabilities-totalEquity)<1 ? "✓ Balance sheet balances" : "⚠ Balance sheet out of balance"}
                </div>
              </div>
            </div>
          )}

          {/* Income statement */}
          {section === "income" && (
            <div style={{ maxWidth:600 }}>
              <div style={{ fontFamily:"var(--serif)", fontSize:20, color:"var(--white)", marginBottom:".25rem" }}>Income Statement</div>
              <div style={{ fontSize:12, color:"var(--slate)", marginBottom:"1.5rem" }}>{orgName} HOA · Year to Date</div>
              <div className="gl-bs-section">
                <div className="gl-bs-title">Income</div>
                {accounts.filter(a=>a.type==="income").map(a => <div key={a.num} className="gl-bs-row"><span>{a.name}</span><span style={{ color:"var(--teal)" }}>{fmtB(a.balance)}</span></div>)}
                <div className="gl-bs-row subtotal"><span>Total income</span><span style={{ color:"var(--teal)" }}>{fmtB(totalIncome)}</span></div>
              </div>
              <div className="gl-bs-section">
                <div className="gl-bs-title">Expenses</div>
                {accounts.filter(a=>a.type==="expense").map(a => <div key={a.num} className="gl-bs-row"><span>{a.name}</span><span style={{ color:"var(--amber)" }}>{fmtB(a.balance)}</span></div>)}
                <div className="gl-bs-row subtotal"><span>Total expenses</span><span style={{ color:"var(--amber)" }}>{fmtB(totalExpenses)}</span></div>
              </div>
              <div className="gl-bs-row total-row"><span>Net {netIncome>=0?"surplus":"deficit"}</span><span style={{ color: netIncome>=0?"var(--teal)":"var(--red)" }}>{fmtB(Math.abs(netIncome))}</span></div>
            </div>
          )}
        </div>
      </div>

      {/* Journal entry modal */}
      {jeModal && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setJeModal(false)}>
          <div className="md md-lg">
            <div className="mt2">New journal entry</div>
            <div className="ms2">Double-entry — debits must equal credits</div>
            <div className="gl-je-form">
              <div className="fg"><label className="fl">Date</label><input className="fi" type="date" value={jeForm.date} onChange={e => setJeForm(p=>({...p, date:e.target.value}))} /></div>
              <div className="fg"><label className="fl">Reference #</label><input className="fi" value={jeForm.ref} onChange={e => setJeForm(p=>({...p, ref:e.target.value}))} placeholder={`JE-${String(journal.length+1).padStart(3,"0")}`} /></div>
            </div>
            <div className="fg"><label className="fl">Memo / description</label><input className="fi" value={jeForm.memo} onChange={e => setJeForm(p=>({...p, memo:e.target.value}))} placeholder="Describe this transaction…" /></div>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--slate)", letterSpacing:".06em", textTransform:"uppercase", marginBottom:8 }}>Entry lines</div>
            <div className="gl-entry-line" style={{ marginBottom:4 }}>
              <span style={{ fontSize:11, color:"var(--slate)" }}>Account</span>
              <span style={{ fontSize:11, color:"var(--teal)", textAlign:"right" }}>Debit ($)</span>
              <span style={{ fontSize:11, color:"var(--red)", textAlign:"right" }}>Credit ($)</span>
              <span />
            </div>
            {jeLines.map((l, i) => (
              <div key={l.id} className="gl-entry-line">
                <select className="fi" style={{ marginBottom:0, fontSize:12 }} value={l.account} onChange={e => setJeLines(p=>p.map(x=>x.id===l.id?{...x,account:e.target.value}:x))}>
                  <option value="">— Select account —</option>
                  {acctGroups.map(type => (
                    <optgroup key={type} label={typeLabel[type]}>
                      {accounts.filter(a=>a.type===type).map(a => <option key={a.num} value={a.num}>{a.num} · {a.name}</option>)}
                    </optgroup>
                  ))}
                </select>
                <input className="fi" style={{ marginBottom:0, textAlign:"right", fontSize:12 }} type="number" placeholder="0.00" value={l.debit} onChange={e => setJeLines(p=>p.map(x=>x.id===l.id?{...x,debit:e.target.value,credit:e.target.value?"":(x.credit||"")}:x))} />
                <input className="fi" style={{ marginBottom:0, textAlign:"right", fontSize:12 }} type="number" placeholder="0.00" value={l.credit} onChange={e => setJeLines(p=>p.map(x=>x.id===l.id?{...x,credit:e.target.value,debit:e.target.value?"":(x.debit||"")}:x))} />
                {i>=2 ? <button onClick={()=>setJeLines(p=>p.filter(x=>x.id!==l.id))} style={{ background:"transparent",border:"none",color:"var(--slate)",cursor:"pointer",fontSize:16 }}>✕</button> : <span />}
              </div>
            ))}
            <button className="btn-g btn-sm" style={{ marginTop:8 }} onClick={()=>setJeLines(p=>[...p,{id:Date.now(),account:"",debit:"",credit:""}])}>+ Add line</button>
            <div className="gl-totals-check">
              <span style={{ color:"var(--slate)" }}>Debits:</span><span className="gl-debit">{fmtB(jeDebitTotal)}</span>
              <span style={{ color:"var(--slate)", marginLeft:12 }}>Credits:</span><span className="gl-credit">{fmtB(jeCreditTotal)}</span>
              <span style={{ marginLeft:"auto" }} className={jeBalanced?"gl-balanced":"gl-unbalanced"}>{jeBalanced?"✓ Balanced":jeDebitTotal>0?"⚠ Not balanced yet":"Enter amounts"}</span>
            </div>
            <div className="ma"><button className="btn-g" onClick={()=>setJeModal(false)}>Cancel</button><button className="btn" onClick={postJE} disabled={!jeBalanced||!jeForm.memo}>Post entry</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Insurance & Compliance Tracker ──────────────────────────────────────────
const TODAY = new Date();
const daysUntil = (dateStr) => Math.ceil((new Date(dateStr) - TODAY) / 86400000);
const urgencyLevel = (days) => days < 0 ? "overdue" : days <= 30 ? "urgent" : days <= 90 ? "warning" : "good";
const urgencyColor = (days) => days < 0 ? "var(--red)" : days <= 30 ? "var(--red)" : days <= 90 ? "var(--amber)" : "var(--teal)";
const urgencyBgClass = (days) => days < 0 ? "red" : days <= 30 ? "urgent" : days <= 90 ? "warning" : "good";
const daysBadgeClass = (days) => days < 0 ? "ic-days-red" : days <= 30 ? "ic-days-red" : days <= 90 ? "ic-days-amber" : "ic-days-green";
const daysLabel = (days) => days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "Due today!" : `${days} days`;

const INIT_POLICIES = [
  { id: 1, name: "Commercial General Liability", provider: "State Farm Insurance", policyNum: "SF-2024-HOA-4421", premium: 4800, coverage: "$2,000,000 per occurrence", renewalDate: "2024-06-01", icon: "🛡️", category: "hoa", notes: "Covers bodily injury & property damage in common areas", docs: "policy-cgl-2024.pdf" },
  { id: 2, name: "Directors & Officers (D&O)", provider: "Chubb Group", policyNum: "CB-DO-8834-HOA", premium: 1200, coverage: "$1,000,000 aggregate", renewalDate: "2024-07-15", icon: "👔", category: "hoa", notes: "Protects board members from personal liability for decisions made in their official capacity", docs: "policy-do-2024.pdf" },
  { id: 3, name: "Property — Building & Common Areas", provider: "Travelers Insurance", policyNum: "TRV-PROP-2291", premium: 6200, coverage: "$4,500,000 replacement value", renewalDate: "2024-05-20", icon: "🏢", category: "hoa", notes: "Covers common area structures, pool, clubhouse, fencing. Deductible: $5,000", docs: "policy-property-2024.pdf" },
  { id: 4, name: "Workers Compensation", provider: "SAIF Corporation", policyNum: "SAIF-WC-1847", premium: 900, coverage: "Oregon statutory limits", renewalDate: "2024-08-01", icon: "⚕️", category: "hoa", notes: "Required for any HOA employees or contractors treated as employees under OR law", docs: "policy-wc-2024.pdf" },
  { id: 5, name: "Umbrella / Excess Liability", provider: "State Farm Insurance", policyNum: "SF-UMB-6612", premium: 800, coverage: "$5,000,000 excess", renewalDate: "2024-06-01", icon: "☂️", category: "hoa", notes: "Sits above CGL and D&O policies for catastrophic claims", docs: "policy-umbrella-2024.pdf" },
  { id: 6, name: "Fidelity / Crime Bond", provider: "Travelers Insurance", policyNum: "TRV-FID-3309", premium: 450, coverage: "$250,000", renewalDate: "2025-01-15", icon: "🔐", category: "hoa", notes: "Covers employee dishonesty and theft of HOA funds — required by many state HOA statutes", docs: "policy-fidelity-2024.pdf" },
];

const INIT_VENDOR_COIS = [
  { id: 1, vendorName: "Pacific Plumbing Co.", coiExpiry: "2024-05-15", glCoverage: "$1,000,000", wcCoverage: "Yes", additionalInsured: true, verified: true, notes: "COI on file — renewal notice sent" },
  { id: 2, vendorName: "Bright Spark Electric", coiExpiry: "2024-07-30", glCoverage: "$1,000,000", wcCoverage: "Yes", additionalInsured: true, verified: true, notes: "" },
  { id: 3, vendorName: "GreenScape Landscaping", coiExpiry: "2024-04-30", glCoverage: "$500,000", wcCoverage: "Yes", additionalInsured: false, verified: true, notes: "⚠ Must be added as additional insured on renewal" },
  { id: 4, vendorName: "ProClean Janitorial", coiExpiry: "2024-06-15", glCoverage: "$500,000", wcCoverage: "No", additionalInsured: false, verified: false, notes: "COI not yet received for 2024 — follow up required" },
  { id: 5, vendorName: "Cascade Roofing", coiExpiry: "2025-02-01", glCoverage: "$2,000,000", wcCoverage: "Yes", additionalInsured: true, verified: true, notes: "" },
];

const INIT_COMPLIANCE_ITEMS = [
  { id: 1,  text: "File annual HOA registration with Oregon Secretary of State", cat: "Legal", priority: "high", dueDate: "2024-06-30", done: false, recurring: "Annual" },
  { id: 2,  text: "Distribute annual budget to all members (30 days before adoption)", cat: "Governance", priority: "high", dueDate: "2024-11-01", done: false, recurring: "Annual" },
  { id: 3,  text: "Conduct annual reserve fund study review", cat: "Financial", priority: "high", dueDate: "2024-12-31", done: false, recurring: "Annual" },
  { id: 4,  text: "Hold annual membership meeting with proper notice (15 days)", cat: "Governance", priority: "high", dueDate: "2025-01-15", done: false, recurring: "Annual" },
  { id: 5,  text: "File IRS Form 1120-H (HOA tax return)", cat: "Tax", priority: "high", dueDate: "2024-07-15", done: false, recurring: "Annual" },
  { id: 6,  text: "Commission independent CPA financial review or audit", cat: "Financial", priority: "medium", dueDate: "2024-09-30", done: false, recurring: "Annual" },
  { id: 7,  text: "Review and update emergency contact list for all board members", cat: "Governance", priority: "low", dueDate: "2024-05-01", done: true, recurring: "Annual" },
  { id: 8,  text: "Inspect fire extinguishers and safety equipment in common areas", cat: "Safety", priority: "high", dueDate: "2024-05-01", done: false, recurring: "Semi-annual" },
  { id: 9,  text: "Post pool safety rules and capacity limits before pool opening", cat: "Safety", priority: "high", dueDate: "2024-04-30", done: false, recurring: "Annual" },
  { id: 10, text: "Obtain certificates of insurance from all active vendors", cat: "Insurance", priority: "high", dueDate: "2024-05-15", done: false, recurring: "Annual" },
  { id: 11, text: "Review and renew all HOA insurance policies before expiration", cat: "Insurance", priority: "high", dueDate: "2024-05-20", done: false, recurring: "Annual" },
  { id: 12, text: "Conduct board election at annual meeting per bylaw requirements", cat: "Governance", priority: "medium", dueDate: "2025-01-15", done: false, recurring: "Annual" },
  { id: 13, text: "Update homeowner directory and verify contact information", cat: "Admin", priority: "low", dueDate: "2024-06-30", done: true, recurring: "Annual" },
  { id: 14, text: "Review and re-post parking rules and towing authorization", cat: "Legal", priority: "medium", dueDate: "2024-05-01", done: true, recurring: "Annual" },
  { id: 15, text: "Test and service pool equipment before season opening", cat: "Safety", priority: "high", dueDate: "2024-04-28", done: false, recurring: "Annual" },
];

function InsuranceCompliance({ vendors, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [policies, setPolicies]         = useState([]);
  const [vendorCOIs, setVendorCOIs]     = useState([]);
  const [checklistItems, setChecklist]  = useState([]);
  const [tab, setTab]                   = useState("overview");
  const [policyModal, setPolicyModal]   = useState(null); // null | "new" | policy obj
  const [coiModal, setCoiModal]         = useState(null);
  const [aiChecklist, setAiChecklist]   = useState("");
  const [aiLoading, setAiLoading]       = useState(false);
  const [filterCat, setFilterCat]       = useState("all");
  const [policyForm, setPolicyForm]     = useState({ name:"", provider:"", policyNum:"", premium:"", coverage:"", renewalDate:"", icon:"🛡️", category:"hoa", notes:"" });
  const [coiForm, setCoiForm]           = useState({ vendorName:"", coiExpiry:"", glCoverage:"$1,000,000", wcCoverage:"Yes", additionalInsured:true, verified:false, notes:"" });

  // ── Derived ───────────────────────────────────────────────────────────────
  const policiesWithDays     = useMemo(() => policies.map(p => ({ ...p, _days: daysUntil(p.renewalDate) })), [policies]);
  const vendorCOIsWithDays   = useMemo(() => vendorCOIs.map(c => ({ ...c, _days: daysUntil(c.coiExpiry) })), [vendorCOIs]);
  const checklistWithDays    = useMemo(() => checklistItems.map(i => ({ ...i, _days: daysUntil(i.dueDate) })), [checklistItems]);

  const urgentPolicies  = useMemo(() => policiesWithDays.filter(p => p._days <= 60).sort((a,b) => a._days - b._days), [policiesWithDays]);
  const urgentCOIs      = useMemo(() => vendorCOIsWithDays.filter(c => c._days <= 60).sort((a,b) => a._days - b._days), [vendorCOIsWithDays]);
  const overduePolicies = useMemo(() => policiesWithDays.filter(p => p._days < 0), [policiesWithDays]);
  const overdueItems    = useMemo(() => checklistWithDays.filter(i => !i.done && i._days <= 14), [checklistWithDays]);
  const totalAlerts     = urgentPolicies.length + urgentCOIs.length + overdueItems.length;
  const checkDone       = useMemo(() => checklistItems.filter(i => i.done).length, [checklistItems]);
  const checkTotal      = checklistItems.length;
  const pct             = checkTotal ? Math.round(checkDone / checkTotal * 100) : 0;
  const annualPremium   = useMemo(() => policies.reduce((s, p) => s + p.premium, 0), [policies]);

  // Empty state — must be after ALL hooks
  if (policies.length === 0 && vendorCOIs.length === 0) return (
    <div>
      <div className="ph"><div><div className="pt">Insurance &amp; Compliance</div><div className="ps">Track policies, vendor COIs, and compliance items</div></div></div>
      <div className="pb"><EmptyState icon="🛡️" title="No insurance policies yet" subtitle="Add your HOA's insurance policies and vendor certificates of insurance to track renewals and get alerts." /></div>
    </div>
  );

  const toggleCheck = (id) => setChecklist(p => p.map(i => i.id === id ? { ...i, done: !i.done } : i));

  const savePolicy = () => {
    if (!policyForm.name || !policyForm.renewalDate) return;
    if (policyForm.id) setPolicies(p => p.map(x => x.id === policyForm.id ? policyForm : x));
    else setPolicies(p => [...p, { ...policyForm, id: Date.now(), premium: Number(policyForm.premium) || 0 }]);
    setPolicyModal(null); toast("Policy saved");
  };

  const saveCOI = () => {
    if (!coiForm.vendorName || !coiForm.coiExpiry) return;
    if (coiForm.id) setVendorCOIs(p => p.map(x => x.id === coiForm.id ? coiForm : x));
    else setVendorCOIs(p => [...p, { ...coiForm, id: Date.now() }]);
    setCoiModal(null); toast("COI record saved");
  };

  // ── AI Compliance Checklist Generator ─────────────────────────────────────
  const generateAiChecklist = async () => {
    setAiLoading(true); setAiChecklist("");
    const overdueList  = policies.filter(p => daysUntil(p.renewalDate) <= 60).map(p => `${p.name} (expires ${p.renewalDate})`).join("; ");
    const coiIssues    = vendorCOIs.filter(c => !c.verified || daysUntil(c.coiExpiry) <= 30).map(c => `${c.vendorName} (expires ${c.coiExpiry})`).join("; ");
    const openItems    = checklistItems.filter(i => !i.done && daysUntil(i.dueDate) <= 60).map(i => `${i.text} (due ${i.dueDate})`).join("; ");

    const prompt = `You are a professional HOA compliance consultant. Generate a comprehensive compliance action report for ${orgName} HOA.

Current date: April 25, 2024
State: Oregon

HOA insurance policies expiring within 60 days: ${overdueList || "None"}
Vendor COIs expiring or not verified within 30 days: ${coiIssues || "None"}
Open compliance checklist items due within 60 days: ${openItems || "None"}
Annual insurance premium total: $${annualPremium.toLocaleString()}
Compliance completion rate: ${pct}% (${checkDone}/${checkTotal} items)

Write a professional compliance status report with three sections:

1. IMMEDIATE ACTIONS (next 30 days) — List specific urgent items, who should handle them, and what happens if ignored.
2. UPCOMING DEADLINES (31-90 days) — Items to calendar and prepare for now.
3. BOARD RECOMMENDATIONS — 2-3 strategic recommendations to strengthen the HOA's overall compliance posture, referencing Oregon HOA law where relevant.

Write in clear, practical business language. Be specific and actionable. No fluff.`;

    const txt = await callClaude(prompt, 1200);
    setAiChecklist(txt);
    setAiLoading(false);
  };

  // ── Coverage timeline events — sorted by date ─────────────────────────────
  const timelineEvents = [
    ...policies.map(p => ({ date: p.renewalDate, label: p.name, sub: p.provider, type: "policy", days: daysUntil(p.renewalDate) })),
    ...vendorCOIs.map(c => ({ date: c.coiExpiry, label: `${c.vendorName} COI`, sub: "Vendor certificate", type: "coi", days: daysUntil(c.coiExpiry) })),
    ...checklistItems.filter(i => !i.done && daysUntil(i.dueDate) <= 120).map(i => ({ date: i.dueDate, label: i.text, sub: i.cat, type: "task", days: daysUntil(i.dueDate) })),
  ].sort((a, b) => a.days - b.days).slice(0, 18);

  const filteredChecklist = filterCat === "all" ? checklistItems : checklistItems.filter(i => i.cat === filterCat);
  const cats = [...new Set(checklistItems.map(i => i.cat))];

  // ── Render ────────────────────────────────────────────────────────────────
  const PolicyCard = ({ p }) => {
    const days = daysUntil(p.renewalDate);
    const level = urgencyBgClass(days);
    const pctCovered = Math.max(5, Math.min(100, 100 - (days / 365) * 100));
    return (
      <div className={`ic-policy-card ${level}`} onClick={() => { setPolicyForm({ ...p }); setPolicyModal("edit"); }}>
        <div className={`ic-urgency-strip ${days < 0 ? "red" : days <= 90 ? "amber" : "green"}`} />
        <div style={{ paddingTop: 6 }}>
          <div className="ic-policy-icon">{p.icon}</div>
          <div className="ic-policy-name">{p.name}</div>
          <div className="ic-policy-provider">{p.provider}</div>
          <div className="ic-policy-meta">
            <div><div className="ic-meta-label">Policy #</div><div className="ic-meta-val" style={{ fontSize: 12 }}>{p.policyNum}</div></div>
            <div><div className="ic-meta-label">Annual premium</div><div className="ic-meta-val">${p.premium.toLocaleString()}</div></div>
            <div><div className="ic-meta-label">Coverage</div><div className="ic-meta-val" style={{ fontSize: 12 }}>{p.coverage}</div></div>
            <div><div className="ic-meta-label">Renewal</div><div className="ic-meta-val" style={{ fontSize: 12, color: urgencyColor(days) }}>{p.renewalDate}</div></div>
          </div>
          <div className={`ic-days-badge ${daysBadgeClass(days)}`}>
            {days < 0 ? "⚠" : days <= 30 ? "⏱" : "✓"} {daysLabel(days)}
          </div>
          <div className="ic-coverage-bar">
            <div className="ic-coverage-fill" style={{ width: `${pctCovered}%`, background: days < 0 ? "var(--red)" : days <= 90 ? "var(--amber)" : "var(--teal)" }} />
          </div>
          {p.notes && <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 8, lineHeight: 1.5 }}>{p.notes}</div>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">Insurance & Compliance</div>
          <div className="ps">{totalAlerts > 0 ? `${totalAlerts} item${totalAlerts > 1 ? "s" : ""} need attention` : "All policies current"} · ${annualPremium.toLocaleString()} annual premium · {pct}% compliance complete</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-g btn-sm" onClick={() => { setCoiForm({ vendorName:"", coiExpiry:"", glCoverage:"$1,000,000", wcCoverage:"Yes", additionalInsured:true, verified:false, notes:"" }); setCoiModal("new"); }}>+ Add COI</button>
          <button className="btn btn-sm" onClick={() => { setPolicyForm({ name:"", provider:"", policyNum:"", premium:"", coverage:"", renewalDate:"", icon:"🛡️", category:"hoa", notes:"" }); setPolicyModal("new"); }}>+ Add policy</button>
        </div>
      </div>

      <div className="pb">
        {/* ── Alert banners ── */}
        {overduePolicies.map(p => (
          <div key={p.id} className="ic-alert-banner red">
            <span style={{ fontSize: 18 }}>🚨</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--red)" }}>OVERDUE: {p.name}</div>
              <div style={{ fontSize: 12, color: "var(--slate)" }}>Expired {Math.abs(daysUntil(p.renewalDate))} days ago — HOA may be operating without coverage. Renew immediately.</div>
            </div>
            <button className="btn btn-sm" style={{ background: "var(--red)", color: "#fff", flexShrink: 0 }} onClick={() => { setPolicyForm({ ...p }); setPolicyModal("edit"); }}>Renew now</button>
          </div>
        ))}
        {urgentCOIs.filter(c => daysUntil(c.coiExpiry) <= 14 && daysUntil(c.coiExpiry) >= 0).map(c => (
          <div key={c.id} className="ic-alert-banner amber">
            <span style={{ fontSize: 18 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--amber)" }}>{c.vendorName} COI expires in {daysUntil(c.coiExpiry)} days</div>
              <div style={{ fontSize: 12, color: "var(--slate)" }}>Vendor's certificate of insurance must be renewed before work can continue on HOA property.</div>
            </div>
          </div>
        ))}

        {/* ── KPI row ── */}
        <div className="sr">
          <div className="sc"><div className="sl">HOA policies</div><div className="sv">{policies.length}</div><div style={{ fontSize: 11, color: urgentPolicies.length ? "var(--amber)" : "var(--green)", marginTop: 3 }}>{urgentPolicies.length ? `${urgentPolicies.length} expiring soon` : "All current"}</div></div>
          <div className="sc"><div className="sl">Annual premium</div><div className="sv t">${annualPremium.toLocaleString()}</div><div style={{ fontSize: 11, color: "var(--slate)", marginTop: 3 }}>${Math.round(annualPremium / 12).toLocaleString()}/mo</div></div>
          <div className="sc"><div className="sl">Vendor COIs</div><div className="sv">{vendorCOIs.length}</div><div style={{ fontSize: 11, color: urgentCOIs.length ? "var(--amber)" : "var(--green)", marginTop: 3 }}>{urgentCOIs.length ? `${urgentCOIs.length} need attention` : "All verified"}</div></div>
          <div className="sc"><div className="sl">Compliance</div><div className="sv" style={{ color: pct >= 80 ? "var(--green)" : pct >= 50 ? "var(--amber)" : "var(--red)" }}>{pct}%</div><div style={{ fontSize: 11, color: "var(--slate)", marginTop: 3 }}>{checkDone}/{checkTotal} items done</div></div>
        </div>

        {/* ── Tabs ── */}
        <div className="tabs">
          {[["overview","Overview"],["policies","HOA Policies"],["vendors","Vendor COIs"],["checklist","Compliance Checklist"],["timeline","Deadline Timeline"],["ai","AI Compliance Report"]].map(([k,l]) =>
            <div key={k} className={`tab ${tab===k?"active":""}`} onClick={() => setTab(k)}>{l}</div>
          )}
        </div>

        {/* ─── OVERVIEW ─── */}
        {tab === "overview" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: "1.5rem" }}>
              {/* Upcoming renewals */}
              <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.1rem 1.25rem" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate)", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: "1rem" }}>Upcoming policy renewals</div>
                {[...policies].sort((a,b) => daysUntil(a.renewalDate) - daysUntil(b.renewalDate)).slice(0, 5).map(p => {
                  const days = daysUntil(p.renewalDate);
                  return (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: ".6rem 0", borderBottom: ".5px solid var(--border)" }}>
                      <span style={{ fontSize: 16 }}>{p.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)" }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: "var(--slate)" }}>{p.renewalDate}</div>
                      </div>
                      <span className={`ic-days-badge ${daysBadgeClass(days)}`} style={{ fontSize: 11 }}>{daysLabel(days)}</span>
                    </div>
                  );
                })}
              </div>
              {/* Vendor COI status */}
              <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.1rem 1.25rem" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate)", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: "1rem" }}>Vendor COI status</div>
                {[...vendorCOIs].sort((a,b) => daysUntil(a.coiExpiry) - daysUntil(b.coiExpiry)).map(c => {
                  const days = daysUntil(c.coiExpiry);
                  const ok = days > 30 && c.verified && c.additionalInsured;
                  return (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: ".6rem 0", borderBottom: ".5px solid var(--border)" }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: ok ? "var(--green)" : days <= 0 ? "var(--red)" : "var(--amber)" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)" }}>{c.vendorName}</div>
                        <div style={{ fontSize: 11, color: "var(--slate)" }}>Expires {c.coiExpiry}{!c.additionalInsured ? " · Not additional insured" : ""}</div>
                      </div>
                      <span className={`ic-days-badge ${daysBadgeClass(days)}`} style={{ fontSize: 11 }}>{daysLabel(days)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Compliance ring + quick items */}
            <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.25rem", display: "flex", gap: "2rem", alignItems: "flex-start" }}>
              <div style={{ textAlign: "center", flexShrink: 0 }}>
                <svg width={80} height={80} viewBox="0 0 80 80">
                  <circle cx={40} cy={40} r={34} fill="none" stroke="var(--navy3)" strokeWidth={8} />
                  <circle cx={40} cy={40} r={34} fill="none" stroke={pct >= 80 ? "var(--teal)" : pct >= 50 ? "var(--amber)" : "var(--red)"} strokeWidth={8}
                    strokeDasharray={`${2 * Math.PI * 34}`} strokeDashoffset={`${2 * Math.PI * 34 * (1 - pct / 100)}`}
                    strokeLinecap="round" transform="rotate(-90 40 40)" />
                  <text x={40} y={44} textAnchor="middle" fill="white" fontSize={16} fontWeight={700}>{pct}%</text>
                </svg>
                <div style={{ fontSize: 12, color: "var(--slate)", marginTop: 6 }}>Compliance<br />score</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)", marginBottom: ".75rem" }}>Urgent compliance items</div>
                {overdueItems.length === 0 ? <div style={{ fontSize: 13, color: "var(--green)" }}>✓ No urgent items due in the next 14 days</div> : overdueItems.slice(0, 5).map(i => (
                  <div key={i.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: ".5rem 0", borderBottom: ".5px solid var(--border)" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: daysUntil(i.dueDate) <= 0 ? "var(--red)" : "var(--amber)", flexShrink: 0, marginTop: 5 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "var(--white)" }}>{i.text}</div>
                      <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 2 }}>{i.cat} · Due {i.dueDate}</div>
                    </div>
                    <span className={`ic-check-priority ${daysUntil(i.dueDate) <= 0 ? "ic-pri-high" : "ic-pri-med"}`}>{daysUntil(i.dueDate) <= 0 ? "Overdue" : "Due soon"}</span>
                  </div>
                ))}
                <button className="btn-g btn-sm" style={{ marginTop: ".85rem" }} onClick={() => setTab("checklist")}>View full checklist →</button>
              </div>
            </div>
          </div>
        )}

        {/* ─── POLICIES ─── */}
        {tab === "policies" && (
          <div>
            <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1.25rem" }}>Click any policy to edit renewal dates, coverage details, or notes. Policies within 90 days of expiration are highlighted.</div>
            <div className="ic-grid">
              {[...policies].sort((a,b) => daysUntil(a.renewalDate) - daysUntil(b.renewalDate)).map(p => <PolicyCard key={p.id} p={p} />)}
            </div>
          </div>
        )}

        {/* ─── VENDOR COIs ─── */}
        {tab === "vendors" && (
          <div>
            <div style={{ background: "var(--navy2)", border: ".5px solid rgba(245,166,35,.2)", borderRadius: "var(--r)", padding: ".85rem 1.1rem", marginBottom: "1.25rem", fontSize: 13, color: "var(--slate)", lineHeight: 1.6 }}>
              <span style={{ color: "var(--amber)", fontWeight: 600 }}>COI Requirements:</span> All vendors working on HOA property must provide a current Certificate of Insurance naming {orgName} HOA as an <strong style={{ color: "var(--white)" }}>additional insured</strong>. Minimum GL: $1,000,000. Workers' comp required for vendors with employees.
            </div>
            <div className="tw"><table>
              <thead><tr><th>Vendor</th><th>GL Coverage</th><th>Workers Comp</th><th>Addl Insured</th><th>COI Expires</th><th>Days</th><th>Verified</th><th>Actions</th></tr></thead>
              <tbody>{[...vendorCOIs].sort((a,b) => daysUntil(a.coiExpiry) - daysUntil(b.coiExpiry)).map(c => {
                const days = daysUntil(c.coiExpiry);
                const issues = [];
                if (!c.verified)         issues.push("Not verified");
                if (!c.additionalInsured) issues.push("Not addl insured");
                if (days <= 0)            issues.push("EXPIRED");
                return (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.vendorName}</div>
                      {issues.length > 0 && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 2 }}>⚠ {issues.join(" · ")}</div>}
                      {c.notes && <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 2 }}>{c.notes}</div>}
                    </td>
                    <td style={{ fontSize: 13 }}>{c.glCoverage}</td>
                    <td><span className={`bg ${c.wcCoverage === "Yes" ? "bg-g" : "bg-r"}`}>{c.wcCoverage}</span></td>
                    <td><span className={`bg ${c.additionalInsured ? "bg-g" : "bg-r"}`}>{c.additionalInsured ? "Yes" : "No"}</span></td>
                    <td style={{ fontSize: 13, color: urgencyColor(days), fontWeight: 500 }}>{c.coiExpiry}</td>
                    <td><span className={`ic-days-badge ${daysBadgeClass(days)}`} style={{ fontSize: 11 }}>{daysLabel(days)}</span></td>
                    <td><span className={`bg ${c.verified ? "bg-g" : "bg-a"}`}>{c.verified ? "Verified" : "Pending"}</span></td>
                    <td><div style={{ display: "flex", gap: 5 }}>
                      <button className="btn-g btn-sm" onClick={() => { setCoiForm({ ...c }); setCoiModal("edit"); }}>Edit</button>
                      {!c.verified && <button className="btn btn-sm" onClick={() => { setVendorCOIs(p => p.map(x => x.id === c.id ? { ...x, verified: true } : x)); toast("COI verified"); }}>Verify</button>}
                    </div></td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          </div>
        )}

        {/* ─── CHECKLIST ─── */}
        {tab === "checklist" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button className={filterCat === "all" ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setFilterCat("all")}>All ({checklistItems.length})</button>
                {cats.map(c => <button key={c} className={filterCat === c ? "btn btn-sm" : "btn-g btn-sm"} onClick={() => setFilterCat(c)}>{c}</button>)}
              </div>
              <div style={{ fontSize: 13, color: "var(--slate)" }}>{checkDone} of {checkTotal} complete</div>
            </div>
            <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", overflow: "hidden" }}>
              {[...filteredChecklist].sort((a,b) => { if (a.done !== b.done) return a.done ? 1 : -1; return daysUntil(a.dueDate) - daysUntil(b.dueDate); }).map(item => {
                const days = daysUntil(item.dueDate);
                return (
                  <div key={item.id} className="ic-checklist-item" style={{ padding: ".85rem 1.25rem" }}>
                    <div className={`ic-check-box ${item.done ? "checked" : ""}`} onClick={() => toggleCheck(item.id)}>
                      {item.done && <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="var(--navy)" strokeWidth={3}><path d="M20 6L9 17l-5-5"/></svg>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className={`ic-check-text ${item.done ? "done" : ""}`}>{item.text}</div>
                      <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                        <span className="ic-check-cat">{item.cat}</span>
                        <span style={{ fontSize: 11, color: "var(--slate)" }}>Due {item.dueDate}</span>
                        {!item.done && <span style={{ fontSize: 11, color: urgencyColor(days) }}>{daysLabel(days)}</span>}
                        <span style={{ fontSize: 10, color: "var(--slate)", background: "var(--navy3)", padding: "1px 6px", borderRadius: 10 }}>{item.recurring}</span>
                      </div>
                    </div>
                    <span className={`ic-check-priority ${item.priority === "high" ? "ic-pri-high" : item.priority === "medium" ? "ic-pri-med" : "ic-pri-low"}`}>{item.priority}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── TIMELINE ─── */}
        {tab === "timeline" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)", marginBottom: "1.25rem" }}>All deadlines — next 120 days</div>
              <div className="ic-timeline">
                {timelineEvents.map((e, i) => {
                  const dotClass = e.days < 0 ? "overdue" : e.days <= 30 ? "due-soon" : e.days <= 90 ? "upcoming" : "done";
                  const typeIcon = e.type === "policy" ? "🛡️" : e.type === "coi" ? "📋" : "✓";
                  return (
                    <div key={i} className="ic-tl-item">
                      <div className={`ic-tl-dot ${dotClass}`} />
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div className="ic-tl-title">{typeIcon} {e.label.length > 48 ? e.label.slice(0, 48) + "…" : e.label}</div>
                          <div className="ic-tl-meta">{e.sub} · {e.date}</div>
                        </div>
                        <div className={`ic-tl-date`} style={{ color: urgencyColor(e.days), flexShrink: 0, fontSize: 11, fontWeight: 700 }}>{daysLabel(e.days)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)", marginBottom: "1.25rem" }}>Coverage calendar — 2024</div>
              {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((mo, mi) => {
                const monthPolicies = policies.filter(p => new Date(p.renewalDate).getMonth() === mi && new Date(p.renewalDate).getFullYear() === 2024);
                const monthCOIs = vendorCOIs.filter(c => new Date(c.coiExpiry).getMonth() === mi && new Date(c.coiExpiry).getFullYear() === 2024);
                const isPast = mi < 3;
                return (
                  <div key={mo} style={{ display: "flex", alignItems: "center", gap: 10, padding: ".5rem 0", borderBottom: ".5px solid var(--border)" }}>
                    <div style={{ width: 36, fontSize: 12, fontWeight: 600, color: mi === 3 ? "var(--teal)" : isPast ? "var(--slate)" : "var(--white)" }}>{mo}</div>
                    <div style={{ flex: 1, display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {monthPolicies.map(p => <span key={p.id} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "rgba(42,99,214,.10)", color: "var(--teal)", border: ".5px solid rgba(42,99,214,.15)" }}>{p.icon} {p.name.split(" ")[0]}</span>)}
                      {monthCOIs.map(c => <span key={c.id} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "rgba(245,166,35,.1)", color: "var(--amber)", border: ".5px solid rgba(245,166,35,.2)" }}>📋 {c.vendorName.split(" ")[0]}</span>)}
                      {monthPolicies.length === 0 && monthCOIs.length === 0 && <span style={{ fontSize: 11, color: "var(--slate)" }}>—</span>}
                    </div>
                    {mi === 3 && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--teal)", background: "var(--teal3)", padding: "2px 7px", borderRadius: 20 }}>Today</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── AI COMPLIANCE REPORT ─── */}
        {tab === "ai" && (
          <div>
            <div style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1.25rem", marginBottom: "1rem" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--white)", marginBottom: ".35rem" }}>AI Compliance Action Report</div>
              <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1rem", lineHeight: 1.6 }}>
                AI reads your current policy renewal dates, vendor COI statuses, and open compliance checklist items — then generates a prioritized action report with immediate steps, upcoming deadlines, and strategic board recommendations.
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 13, marginBottom: "1.25rem" }}>
                <span><span style={{ color: "var(--red)", fontWeight: 600 }}>{urgentPolicies.length}</span> policies expiring within 60 days</span>
                <span><span style={{ color: "var(--amber)", fontWeight: 600 }}>{urgentCOIs.length}</span> vendor COIs need attention</span>
                <span><span style={{ color: "var(--white)", fontWeight: 600 }}>{checkTotal - checkDone}</span> open compliance items</span>
              </div>
              <button className="btn" onClick={generateAiChecklist} disabled={aiLoading} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {aiLoading ? <><span className="ai-spin" />Generating report…</> : "✦ Generate compliance report"}
              </button>
            </div>

            {aiLoading && (
              <div className="ic-ai-checklist">
                <div className="ic-ai-checklist-header"><span className="ai-spin" style={{ width: 14, height: 14 }} />Analyzing policies and generating action report…</div>
                {[85, 70, 60, 80, 55].map((w, i) => <div key={i} style={{ height: 13, width: `${w}%`, background: "rgba(255,255,255,.06)", borderRadius: 5, marginBottom: 10 }} />)}
              </div>
            )}

            {aiChecklist && !aiLoading && (
              <div className="ic-ai-checklist">
                <div className="ic-ai-checklist-header"><Ic n="zap" s={12} />AI Compliance Action Report — {orgName} HOA</div>
                <div style={{ fontSize: 13, color: "var(--ice)", lineHeight: 1.85, whiteSpace: "pre-wrap" }}>{aiChecklist}</div>
                <div style={{ display: "flex", gap: 8, marginTop: "1.25rem", paddingTop: "1rem", borderTop: ".5px solid var(--border)" }}>
                  <button className="btn-g btn-sm" onClick={generateAiChecklist}>Regenerate</button>
                  <button className="btn btn-sm" onClick={() => { navigator.clipboard?.writeText(aiChecklist); toast("Report copied"); }}>Copy report</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Policy modal ── */}
      {policyModal && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setPolicyModal(null)}>
          <div className="md md-lg">
            <div className="mt2">{policyModal === "new" ? "Add insurance policy" : "Edit policy"}</div>
            <div className="ms2">{orgName}</div>
            <div className="ic-modal-grid">
              <div className="fg"><label className="fl">Policy name</label><input className="fi" value={policyForm.name} onChange={e => setPolicyForm(p=>({...p,name:e.target.value}))} placeholder="e.g. General Liability" /></div>
              <div className="fg"><label className="fl">Provider</label><input className="fi" value={policyForm.provider} onChange={e => setPolicyForm(p=>({...p,provider:e.target.value}))} /></div>
              <div className="fg"><label className="fl">Policy number</label><input className="fi" value={policyForm.policyNum} onChange={e => setPolicyForm(p=>({...p,policyNum:e.target.value}))} /></div>
              <div className="fg"><label className="fl">Annual premium ($)</label><input className="fi" type="number" value={policyForm.premium} onChange={e => setPolicyForm(p=>({...p,premium:e.target.value}))} /></div>
              <div className="fg"><label className="fl">Coverage amount</label><input className="fi" value={policyForm.coverage} onChange={e => setPolicyForm(p=>({...p,coverage:e.target.value}))} /></div>
              <div className="fg"><label className="fl">Renewal date</label><input className="fi" type="date" value={policyForm.renewalDate} onChange={e => setPolicyForm(p=>({...p,renewalDate:e.target.value}))} /></div>
            </div>
            <div className="fg"><label className="fl">Notes</label><textarea className="fi" value={policyForm.notes} onChange={e => setPolicyForm(p=>({...p,notes:e.target.value}))} rows={2} /></div>
            <div className="ma"><button className="btn-g" onClick={() => setPolicyModal(null)}>Cancel</button><button className="btn" onClick={savePolicy} disabled={!policyForm.name || !policyForm.renewalDate}>Save policy</button></div>
          </div>
        </div>
      )}

      {/* ── COI modal ── */}
      {coiModal && (
        <div className="mo" onClick={e => e.target === e.currentTarget && setCoiModal(null)}>
          <div className="md">
            <div className="mt2">{coiModal === "new" ? "Add vendor COI" : "Edit COI record"}</div>
            <div className="ms2">Certificate of insurance tracking</div>
            <div className="ic-modal-grid">
              <div className="fg"><label className="fl">Vendor name</label><input className="fi" value={coiForm.vendorName} onChange={e => setCoiForm(p=>({...p,vendorName:e.target.value}))} /></div>
              <div className="fg"><label className="fl">COI expiry date</label><input className="fi" type="date" value={coiForm.coiExpiry} onChange={e => setCoiForm(p=>({...p,coiExpiry:e.target.value}))} /></div>
              <div className="fg"><label className="fl">GL coverage</label><input className="fi" value={coiForm.glCoverage} onChange={e => setCoiForm(p=>({...p,glCoverage:e.target.value}))} /></div>
              <div className="fg"><label className="fl">Workers comp</label>
                <select className="fi" value={coiForm.wcCoverage} onChange={e => setCoiForm(p=>({...p,wcCoverage:e.target.value}))}>
                  <option value="Yes">Yes</option><option value="No">No</option><option value="Exempt">Exempt (sole proprietor)</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 20, marginBottom: "1rem" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--slate)", cursor: "pointer" }}>
                <input type="checkbox" checked={coiForm.additionalInsured} onChange={e => setCoiForm(p=>({...p,additionalInsured:e.target.checked}))} style={{ accentColor:"var(--teal)", width:15, height:15 }} />HOA named as additional insured
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--slate)", cursor: "pointer" }}>
                <input type="checkbox" checked={coiForm.verified} onChange={e => setCoiForm(p=>({...p,verified:e.target.checked}))} style={{ accentColor:"var(--teal)", width:15, height:15 }} />COI verified and on file
              </label>
            </div>
            <div className="fg"><label className="fl">Notes</label><input className="fi" value={coiForm.notes} onChange={e => setCoiForm(p=>({...p,notes:e.target.value}))} placeholder="Optional notes…" /></div>
            <div className="ma"><button className="btn-g" onClick={() => setCoiModal(null)}>Cancel</button><button className="btn" onClick={saveCOI} disabled={!coiForm.vendorName || !coiForm.coiExpiry}>Save COI</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Document Vault & AI Search ───────────────────────────────────────────────

// Simulated document content for demo — in production these come from actual file uploads
const DEMO_DOCS = [
  {
    id: 1, name: "CC&Rs — Maplewood Commons", type: "ccr", uploaded: "2024-01-15", pages: 34, size: "1.2 MB",
    icon: "📜",
    content: `DECLARATION OF COVENANTS, CONDITIONS AND RESTRICTIONS
MAPLEWOOD COMMONS HOMEOWNERS ASSOCIATION

ARTICLE I — DEFINITIONS
Section 1.1 "Association" means the Maplewood Commons Homeowners Association.
Section 1.2 "Common Area" means all real property owned by the Association for the common use and enjoyment of the Owners.
Section 1.3 "Owner" means the record owner of any Lot in Maplewood Commons.
Section 1.4 "Lot" means any numbered lot shown upon the recorded subdivision map.

ARTICLE II — USE RESTRICTIONS
Section 2.1 Residential Use. All Lots shall be used for single-family residential purposes only. No trade, business, or commercial activity shall be conducted on any Lot without prior written approval of the Board.
Section 2.2 Leasing. Owners may lease their Lot subject to the following conditions: (a) all leases must be in writing; (b) minimum lease term is 12 months; (c) short-term rentals of fewer than 30 consecutive days are strictly prohibited, including but not limited to Airbnb, VRBO, or similar platforms; (d) the Owner must provide the Association with a copy of the lease within 10 days of execution.
Section 2.3 Pets. Owners may keep a maximum of two (2) domestic pets per unit. Pets must be leashed in all common areas. Dog breeds classified as "dangerous" under Oregon law are prohibited. No livestock or exotic animals are permitted.
Section 2.4 Noise. No Owner or occupant shall create or permit noise that unreasonably disturbs neighbors. Quiet hours are from 10:00 PM to 7:00 AM, seven days per week.
Section 2.5 Parking. Each unit is assigned one (1) covered parking space. Visitor parking spaces may not be used by residents for periods exceeding 72 hours. Inoperable vehicles may not be stored in any parking area.
Section 2.6 Exterior Modifications. No Owner shall make any exterior modification, addition, or alteration to their unit or the common area without prior written approval of the Architectural Review Committee (ARC). This includes but is not limited to fences, decks, paint colors, satellite dishes, and signage.

ARTICLE III — ASSESSMENTS
Section 3.1 Regular Assessments. Each Owner shall pay monthly assessments as established by the Board, currently set at $300 per month per unit.
Section 3.2 Late Fees. Assessments not received within 15 days of the due date shall be subject to a late fee of $50. Assessments more than 60 days past due shall accrue interest at 12% per annum.
Section 3.3 Liens. The Association shall have the right to place a lien against any Lot for unpaid assessments, late fees, and interest. A lien may be recorded after assessments are 30 days past due.
Section 3.4 Special Assessments. The Board may levy special assessments for major capital improvements or unexpected expenses, not to exceed $1,500 per unit per year without a vote of the membership.

ARTICLE IV — ARCHITECTURAL CONTROL
Section 4.1 ARC Approval. All exterior modifications require submission of an ARC Request Form at least 30 days before the proposed start date. The ARC shall approve or deny within 30 days of receiving a complete application.
Section 4.2 Approved Colors. Exterior paint colors must be selected from the Association's approved color palette, which is available from the management office.
Section 4.3 Fencing. Fences may not exceed 6 feet in height. All fences must be of wood or composite material in natural earth tones. Chain-link and metal fencing are not permitted.
Section 4.4 Landscaping Standards. All private patio and landscaping areas visible from common areas must be maintained in neat and orderly condition. Dead plants must be removed within 30 days.

ARTICLE V — ENFORCEMENT
Section 5.1 Violation Notices. The Association shall provide written notice of any violation to the Owner. The Owner shall have 14 days to cure the violation.
Section 5.2 Fines. If a violation is not cured within 14 days, the Board may levy fines of $50 per day for the first 30 days, and $100 per day thereafter.
Section 5.3 Hearings. Any Owner wishing to contest a violation or fine shall submit a written request for a hearing within 10 days of receiving notice. The Board shall schedule a hearing within 30 days.
Section 5.4 Legal Action. The Association reserves the right to pursue legal action, including injunctive relief, to enforce these CC&Rs.`
  },
  {
    id: 2, name: "Bylaws — Maplewood Commons HOA", type: "bylaw", uploaded: "2024-01-15", pages: 18, size: "640 KB",
    icon: "📋",
    content: `BYLAWS OF MAPLEWOOD COMMONS HOMEOWNERS ASSOCIATION

ARTICLE I — MEETINGS OF MEMBERS
Section 1.1 Annual Meeting. The annual meeting of the members shall be held each year in January. The Board shall provide at least 15 days written notice to all members.
Section 1.2 Special Meetings. Special meetings may be called by the President, a majority of the Board, or by written petition signed by at least 25% of the voting membership.
Section 1.3 Quorum. A quorum for the transaction of business at any meeting of members shall consist of members holding 30% of the voting power.
Section 1.4 Voting. Each unit shall have one vote. Cumulative voting is not permitted. Votes may be cast in person or by proxy. Proxies must be in writing and submitted before the meeting.

ARTICLE II — BOARD OF DIRECTORS
Section 2.1 Number. The Board shall consist of five (5) directors.
Section 2.2 Election. Directors shall be elected by the members at the annual meeting. Each director serves a two-year term. Directors may serve a maximum of three consecutive terms.
Section 2.3 Vacancies. Vacancies on the Board may be filled by appointment of the remaining directors for the remainder of the term.
Section 2.4 Removal. Any director may be removed by a vote of a majority of the total voting power at a special meeting called for that purpose.
Section 2.5 Compensation. Directors shall serve without compensation. Reimbursement for actual expenses is permitted with Board approval.

ARTICLE III — OFFICERS
Section 3.1 Officers. The officers of the Association shall be: President, Vice President, Secretary, and Treasurer.
Section 3.2 Election of Officers. Officers shall be elected by the Board at the first meeting following the annual meeting.
Section 3.3 President. The President shall preside at all meetings, execute contracts on behalf of the Association, and perform such other duties as the Board may direct.
Section 3.4 Treasurer. The Treasurer shall maintain the Association's financial records, prepare monthly financial reports, and present the annual budget to the membership.

ARTICLE IV — MEETINGS OF THE BOARD
Section 4.1 Regular Meetings. The Board shall meet at least once per month. Notice of regular meetings shall be provided to all directors at least 5 days in advance.
Section 4.2 Open Meetings. All Board meetings shall be open to members, except that the Board may convene an executive session to discuss: (a) pending litigation; (b) personnel matters; (c) contract negotiations; (d) delinquent assessments.
Section 4.3 Quorum. A majority of the directors shall constitute a quorum. A majority vote of directors present at a duly constituted meeting shall be required for any action.

ARTICLE V — FINANCES
Section 5.1 Fiscal Year. The fiscal year of the Association shall be January 1 through December 31.
Section 5.2 Budget. The Board shall prepare an annual budget and distribute it to all members at least 30 days before adoption.
Section 5.3 Reserve Fund. The Association shall maintain a reserve fund for major repairs and replacements. Reserve contributions shall be based on a reserve study conducted at least every three years.
Section 5.4 Audit. The financial records of the Association shall be reviewed or audited annually by an independent CPA.`
  },
  {
    id: 3, name: "Pool & Amenity Rules 2024", type: "rule", uploaded: "2024-03-01", pages: 4, size: "210 KB",
    icon: "🏊",
    content: `MAPLEWOOD COMMONS HOA — POOL AND AMENITY RULES (Effective May 1, 2024)

POOL HOURS AND ACCESS
1. Pool hours: 7:00 AM to 10:00 PM daily, May 1 through September 30.
2. Pool is closed Tuesdays from 7:00 AM to 12:00 PM for maintenance.
3. Access is restricted to residents and their guests only. Residents are responsible for their guests at all times.
4. Maximum 4 guests per unit at any time.
5. The pool gate must remain closed at all times. Do not prop the gate open.

SAFETY RULES
6. No lifeguard on duty. Swim at your own risk. Children under age 14 must be accompanied by an adult resident at all times.
7. No diving. No running on pool deck. No glass containers in or around the pool area.
8. No flotation devices other than US Coast Guard approved life vests.
9. Persons with open sores, infections, or communicable diseases may not use the pool.
10. Shower before entering the pool.

BEHAVIOR
11. Quiet hours apply to the pool area: no excessive noise after 9:00 PM.
12. Music must be played through personal headphones or at conversational volume only.
13. No smoking or vaping anywhere in the pool area.
14. The Association reserves the right to revoke pool privileges for violations.

CLUBHOUSE RESERVATIONS
15. Residents may reserve the clubhouse for private events up to 4 hours. Maximum capacity: 30 persons.
16. Reservations must be made at least 7 days in advance via the board portal. A refundable deposit of $75 is required.
17. The resident is responsible for cleanup. Deposit will be forfeited if the space is not returned to its original condition.
18. Alcohol is permitted for residents of legal age. Residents are responsible for their guests' behavior.
19. No amplified music after 9:00 PM.`
  },
  {
    id: 4, name: "Pet Policy — Updated 2023", type: "rule", uploaded: "2023-11-12", pages: 2, size: "95 KB",
    icon: "🐾",
    content: `MAPLEWOOD COMMONS HOA — PET POLICY (Updated November 2023)

PERMITTED PETS
1. Residents may keep a maximum of two (2) domestic pets per unit. Domestic pets include dogs, cats, caged birds, and fish aquariums up to 20 gallons.
2. The following dog breeds are prohibited due to insurance restrictions: Pit Bull Terrier, Rottweiler, Doberman Pinscher, Chow Chow, Akita, Wolf Hybrid. This list may be updated by the Board with 30 days notice.
3. Exotic animals, reptiles, livestock, and farm animals are strictly prohibited.

REGISTRATION
4. All dogs and cats must be registered with the HOA within 30 days of move-in or acquisition. Submit the Pet Registration Form along with proof of current vaccinations to the management office.
5. Annual vaccination updates are required. Failure to maintain registrations may result in fines.

COMMON AREA RULES
6. All dogs must be on a leash no longer than 6 feet when in any common area. Retractable leashes are permitted when fully retracted to 6 feet or less.
7. Pet owners must immediately clean up after their pets in all common areas. Pet waste bags are provided at the designated stations near the east and west entrances.
8. Pets are not permitted in the pool area, clubhouse, or gym.

SERVICE AND EMOTIONAL SUPPORT ANIMALS
9. Service animals as defined under the Americans with Disabilities Act (ADA) are permitted without restriction. Owners with service animals should notify the board.
10. Reasonable accommodation requests for emotional support animals (ESAs) will be considered on a case-by-case basis. Requests must be accompanied by documentation from a licensed healthcare provider. The Association will respond to ESA requests within 14 days.

VIOLATIONS
11. First violation: written warning.
12. Second violation: $75 fine.
13. Third and subsequent violations: $150 fine per incident.`
  },
  {
    id: 5, name: "Parking Rules & Towing Policy", type: "rule", uploaded: "2024-02-20", pages: 3, size: "148 KB",
    icon: "🅿️",
    content: `MAPLEWOOD COMMONS HOA — PARKING RULES AND TOWING POLICY

ASSIGNED PARKING
1. Each unit is assigned one (1) covered parking space designated by unit number. Residents may not use parking spaces assigned to other units.
2. Assigned spaces must be used for operable, registered, and insured personal vehicles only. Commercial vehicles over 3/4 ton, RVs, boats, trailers, and motorcycles with sidecars may not be stored in assigned spaces.

VISITOR PARKING
3. Visitor spaces (marked in blue) are for guests only and are limited to a maximum of 72 consecutive hours.
4. Residents may not use visitor spaces for their own vehicles for any reason. Repeated violations will result in fines and/or towing.
5. Overnight use of visitor spaces by residents is prohibited.

TOWING POLICY
6. Unauthorized vehicles are subject to towing at the vehicle owner's expense. A vehicle is unauthorized if it: (a) is parked in another resident's assigned space; (b) has been in a visitor space for more than 72 hours; (c) is inoperable or unregistered; (d) blocks a fire lane, dumpster access, or ADA space.
7. Towing will be conducted by Portland Towing Services (503-555-0911). The Association has authorized 24/7 towing of unauthorized vehicles.
8. Prior notice to the vehicle owner is not required for vehicles in fire lanes or blocking emergency access. For other violations, a warning notice will be placed on the vehicle and towing may occur 24 hours after notice placement.

ELECTRIC VEHICLE CHARGING
9. Residents may request permission to install a Level 2 EV charger in their assigned parking space at their own expense, subject to ARC approval and a licensed electrical inspection.
10. EV chargers must not extend into adjacent spaces or common areas.`
  }
];

const DOC_TYPE_COLORS = {
  ccr:   { bg: "rgba(42,99,214,.12)",  color: "var(--teal)",   label: "CC&Rs"  },
  bylaw: { bg: "rgba(167,139,250,.12)", color: "var(--purple)", label: "Bylaws" },
  rule:  { bg: "rgba(245,166,35,.12)",  color: "var(--amber)",  label: "Rules"  },
  other: { bg: "rgba(138,153,179,.12)", color: "var(--slate)",  label: "Other"  },
};

const SUGGESTED_QUESTIONS = [
  "Can owners do short-term Airbnb rentals?",
  "What are the quiet hours?",
  "How many pets can a resident have?",
  "What breeds of dogs are prohibited?",
  "How long can a visitor use a parking space?",
  "What is the process to paint my unit exterior?",
  "Can the HOA put a lien on my property?",
  "What are the fines for violations?",
  "How do I reserve the clubhouse?",
  "Can I install an EV charger?",
  "How much notice before a board meeting?",
  "What is the maximum fence height?",
];

function DocumentVault({ toast , orgName = "Your HOA", orgEmail = "" }) {
  const [lightbox, setLightbox] = useState(null);
  const [docs, setDocs] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [viewMode, setViewMode] = useState("search"); // "search" | "read" | "upload"
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const filteredDocs = filterType === "all" ? docs : docs.filter(d => d.type === filterType);

  // ── Core AI search across selected or all docs ────────────────────────────
  const searchDocs = async (q) => {
    if (!q.trim() || searching) return;
    setSearching(true);

    const targetDocs = activeDoc ? [activeDoc] : docs;
    const docsContext = targetDocs.map(d =>
      `--- DOCUMENT: ${d.name} (${DOC_TYPE_COLORS[d.type]?.label || "Other"}) ---\n${d.content}`
    ).join("\n\n");

    const prompt = `You are an expert HOA document analyst for ${orgName} HOA. You have been given the following HOA documents to analyze:

${docsContext}

A board member or homeowner is asking this question:
"${q}"

Provide a thorough, helpful answer by:
1. Directly answering the question in 1-2 clear sentences first
2. Then quoting the EXACT relevant sections from the documents (use quotation marks and cite the document name and section number)
3. If multiple documents are relevant, cite each one
4. If the question cannot be answered from the documents, say so clearly and suggest the board consult legal counsel
5. End with any practical notes or caveats the board should be aware of

Format your response naturally — no markdown headers or bullet points. Write in flowing paragraphs. Use quotation marks for direct quotes from the documents.`;

    try {
      const answer = await callClaude(prompt, 1200);
      const result = {
        id: Date.now(),
        question: q,
        answer,
        docs: targetDocs.map(d => d.name),
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };
      setAnswers(prev => [result, ...prev]);
    } catch {
      toast("Search error — please try again");
    }
    setSearching(false);
    setQuery("");
  };

  // ── File upload handler (reads text-extractable files) ────────────────────
  const getFileIcon = (name) => {
    const ext = (name.split(".").pop() || "").toLowerCase();
    if (["jpg","jpeg","png","heic","gif","webp"].includes(ext)) return "🖼️";
    if (ext === "pdf") return "📕";
    if (["doc","docx"].includes(ext)) return "📝";
    if (["xls","xlsx"].includes(ext)) return "📊";
    if (["csv","txt"].includes(ext)) return "📋";
    if (["eml","msg"].includes(ext)) return "📧";
    return "📄";
  };
  const isImg = (n) => ["jpg","jpeg","png","gif","webp","heic"].includes((n.split(".").pop()||"").toLowerCase());
  const isTxt = (n) => ["txt","md","csv"].includes((n.split(".").pop()||"").toLowerCase());

  const handleFiles = (files) => {
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const newDoc = {
          id: Date.now() + Math.random(),
          name: file.name.replace(/\.[^.]+$/, ""),
          type: "other",
          uploaded: new Date().toISOString().split("T")[0],
          pages: Math.max(1, Math.floor(content.length / 2500)),
          size: `${(file.size / 1024).toFixed(0)} KB`,
          icon: "📄",
          content: content.slice(0, 15000), // Cap for demo
        };
        setDocs(prev => [...prev, newDoc]);
        toast(`"${newDoc.name}" uploaded and ready to search`);
      };
      const base = {
        id: Date.now() + Math.random(),
        name: file.name.replace(/\.[^.]+$/, ""),
        fileName: file.name,
        type: "other",
        uploaded: new Date().toISOString().split("T")[0],
        size: file.size > 1048576 ? `${(file.size/1048576).toFixed(1)} MB` : `${(file.size/1024).toFixed(0)} KB`,
        icon: getFileIcon(file.name),
      };
      if (isImg(file.name)) {
        reader.onload = (e) => { setDocs(prev => [...prev, { ...base, dataUrl: e.target.result, content: `[Image: ${file.name}]`, pages: 1 }]); toast(`📸 "${base.name}" uploaded`); };
        reader.readAsDataURL(file);
      } else if (isTxt(file.name)) {
        reader.onload = (e) => { const t = e.target.result; setDocs(prev => [...prev, { ...base, content: t.slice(0,15000), pages: Math.max(1,Math.floor(t.length/2500)) }]); toast(`"${base.name}" uploaded and ready to search`); };
        reader.readAsText(file);
      } else {
        reader.onload = (e) => { setDocs(prev => [...prev, { ...base, dataUrl: e.target.result, content: `[${file.name}] uploaded ${new Date().toLocaleDateString()}. Click Download to open.`, pages: "—" }]); toast(`"${base.name}" uploaded`); };
        reader.readAsDataURL(file);
      }
    });
    setViewMode("search");
  };

  const deleteDoc = (id) => {
    setDocs(prev => prev.filter(d => d.id !== id));
    if (activeDoc?.id === id) setActiveDoc(null);
    toast("Document removed");
  };

  const TypeTag = ({ type }) => {
    const cfg = DOC_TYPE_COLORS[type] || DOC_TYPE_COLORS.other;
    return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
  };

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">Document Vault</div>
          <div className="ps">{docs.length} documents · AI-powered search across all HOA governing documents</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-g btn-sm" onClick={() => setViewMode("upload")}>+ Upload document</button>
          {activeDoc && <button className="btn-g btn-sm" style={{ color: "var(--teal)" }} onClick={() => setActiveDoc(null)}>Search all docs</button>}
        </div>
      </div>

      <div className="dv-layout">
        {/* ── Left sidebar — doc list ── */}
        <div className="dv-sidebar">
          <div className="dv-sidebar-top">
            {/* Filter by type */}
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {[["all", "All"], ["ccr", "CC&Rs"], ["bylaw", "Bylaws"], ["rule", "Rules"], ["other", "Other"]].map(([k, l]) => (
                <button key={k} className={filterType === k ? "btn btn-sm" : "btn-g btn-sm"} style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => setFilterType(k)}>{l}</button>
              ))}
            </div>
          </div>

          <div className="dv-doc-list">
            <div className="dv-section-label">Documents ({filteredDocs.length})</div>
            {filteredDocs.map(doc => (
              <div key={doc.id} className={`dv-doc-item ${activeDoc?.id === doc.id ? "active" : ""}`}
                onClick={() => { setActiveDoc(doc); setViewMode("search"); }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div className="dv-doc-icon" style={{ background:(DOC_TYPE_COLORS[doc.type]||DOC_TYPE_COLORS.other).bg, overflow:"hidden", padding:doc.dataUrl&&doc.icon==="🖼️"?0:undefined }}>
                    {doc.dataUrl&&doc.icon==="🖼️" ? <img src={doc.dataUrl} alt={doc.name} style={{width:40,height:40,objectFit:"cover",borderRadius:6}} /> : doc.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dv-doc-name">{doc.name}</div>
                    <div className="dv-doc-meta">{doc.pages} pages · {doc.size}</div>
                    <div style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center", justifyContent: "space-between" }}>
                      <TypeTag type={doc.type} />
                      <div style={{ display: "flex", gap: 4 }}>
                        {doc.dataUrl && <button onClick={(e) => { e.stopPropagation(); const a=document.createElement("a"); a.href=doc.dataUrl; a.download=doc.fileName||doc.name; a.click(); }} style={{ fontSize:10, background:"transparent", border:".5px solid var(--border2)", borderRadius:6, padding:"2px 6px", color:"var(--teal)", cursor:"pointer" }}>⬇ Download</button>}
                        <button onClick={(e) => { e.stopPropagation(); setActiveDoc(doc); setViewMode("read"); }} style={{ fontSize:10, background:"transparent", border:".5px solid var(--border2)", borderRadius:6, padding:"2px 6px", color:"var(--slate)", cursor:"pointer" }}>{doc.icon === "🖼️" ? "View" : "Read"}</button>
                        <button onClick={(e) => { e.stopPropagation(); deleteDoc(doc.id); }} style={{ fontSize:10, background:"transparent", border:"none", color:"var(--slate)", cursor:"pointer", padding:"2px 4px" }}>✕</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredDocs.length === 0 && (
              <div style={{ padding: "1.5rem .9rem", fontSize: 12, color: "var(--slate)", textAlign: "center" }}>No documents of this type.</div>
            )}

            {/* Upload prompt */}
            <div className="dv-section-label" style={{ marginTop: "1rem" }}>Add documents</div>
            <div style={{ padding: "0 .9rem" }}>
              <div onClick={() => setViewMode("upload")} style={{ border: "1px dashed var(--border2)", borderRadius: "var(--r)", padding: ".75rem", textAlign: "center", cursor: "pointer", fontSize: 12, color: "var(--slate)", transition: "border-color .15s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(42,99,214,.30)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,.13)"}>
                + Upload documents or photos
              </div>
            </div>
          </div>
        </div>

        {/* ── Right main panel ── */}
        <div className="dv-main">

          {/* Search bar — always visible */}
          {viewMode !== "upload" && (
            <div className="dv-search-bar">
              <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: 6 }}>
                {activeDoc
                  ? <span>Searching in: <span style={{ color: "var(--teal)", fontWeight: 600 }}>{activeDoc.name}</span></span>
                  : <span>Searching across <span style={{ color: "var(--white)", fontWeight: 600 }}>all {docs.length} documents</span></span>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  ref={inputRef}
                  className="dv-search-input"
                  placeholder="Ask anything about your HOA documents… e.g. 'Can I Airbnb my unit?' or 'What are the fines for violations?'"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && searchDocs(query)}
                />
                <button className="btn" onClick={() => searchDocs(query)} disabled={!query.trim() || searching} style={{ flexShrink: 0, minWidth: 80, display: "flex", alignItems: "center", gap: 6 }}>
                  {searching ? <><span className="ai-spin" style={{ width: 12, height: 12, borderWidth: 1.5 }} />Searching</> : "Search →"}
                </button>
              </div>
            </div>
          )}

          <div className="dv-content">

            {/* ── Upload view ── */}
            {viewMode === "upload" && (
              <div>
                <div style={{ fontSize: 15, fontWeight: 500, color: "var(--white)", marginBottom: ".5rem" }}>Upload documents</div>
                <div style={{ fontSize: 13, color: "var(--slate)", marginBottom: "1.5rem" }}>Upload CC&Rs, bylaws, rules, meeting minutes, or any HOA documents. AI will read and search across all uploaded files instantly.</div>

                <div
                  className={`dv-upload-zone ${dragging ? "dragging" : ""}`}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
                  onClick={() => fileRef.current?.click()}
                >
                  <div style={{ fontSize: 36, marginBottom: ".75rem" }}>📁</div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--white)", marginBottom: ".4rem" }}>Drop files here or click to browse</div>
                  <div style={{ fontSize: 13, color: "var(--slate)" }}>Supports PDF, DOCX, XLSX, TXT, JPG, PNG, HEIC, EML & more · Multiple files at once</div>
                  <div style={{ fontSize: 12, color: "var(--slate)", marginTop: ".5rem" }}>Files are processed and made searchable immediately</div>
                  <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.doc,.txt,.md,.xlsx,.xls,.jpg,.jpeg,.png,.heic,.eml,.msg" style={{ display: "none" }} onChange={e => handleFiles(e.target.files)} />
                </div>

                <div style={{ marginTop: "1.5rem", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                  {[
                    { icon: "📜", label: "CC&Rs", hint: "Declaration of covenants, conditions & restrictions" },
                    { icon: "📋", label: "Bylaws", hint: "Board governance and meeting procedures" },
                    { icon: "📄", label: "Rules & Regs", hint: "Pool rules, pet policy, parking, etc." },
                    { icon: "📊", label: "Budget docs", hint: "Annual budgets and reserve studies" },
                    { icon: "🗒️", label: "Meeting minutes", hint: "Board meeting records and resolutions" },
                    { icon: "📑", label: "Contracts", hint: "Vendor agreements and insurance policies" },
                  ].map(f => (
                    <div key={f.label} style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r)", padding: ".85rem 1rem" }}>
                      <div style={{ fontSize: 20, marginBottom: 5 }}>{f.icon}</div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)", marginBottom: 3 }}>{f.label}</div>
                      <div style={{ fontSize: 11, color: "var(--slate)" }}>{f.hint}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Read view ── */}
            {viewMode === "read" && activeDoc && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
                  <div style={{ fontSize: 20 }}>{activeDoc.icon}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "var(--white)" }}>{activeDoc.name}</div>
                    <div style={{ fontSize: 12, color: "var(--slate)" }}>{activeDoc.pages} pages · Uploaded {activeDoc.uploaded}</div>
                  </div>
                  <div style={{ marginLeft: "auto" }}><TypeTag type={activeDoc.type} /></div>
                </div>
                {activeDoc.dataUrl && activeDoc.icon==="🖼️"
                  ? <div style={{textAlign:"center",background:"var(--navy2)",borderRadius:"var(--r)",padding:"1rem",cursor:"zoom-in"}} onClick={()=>{const w=window.open(); w.focus(); w.document.write(`<img src="${activeDoc.dataUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;background:#000;display:block;margin:0 auto;" />`);}}><img src={activeDoc.dataUrl} alt={activeDoc.name} style={{maxWidth:"100%",maxHeight:400,objectFit:"contain",borderRadius:6,cursor:"zoom-in"}} /><div style={{fontSize:11,color:"var(--slate)",marginTop:6}}>Click to expand</div></div>
                  : <div className="dv-read-pane">{activeDoc.content}</div>}
                {activeDoc.dataUrl && <button className="btn btn-sm" style={{marginTop:8,fontSize:11}} onClick={() => { const a=document.createElement("a"); a.href=activeDoc.dataUrl; a.download=activeDoc.fileName||activeDoc.name; a.click(); }}>⬇ Download file</button>}
                <button className="btn-g btn-sm" style={{ marginTop: 10 }} onClick={() => setViewMode("search")}>← Back to search</button>
              </div>
            )}

            {/* ── Search view ── */}
            {viewMode === "search" && (
              <div>
                {/* Suggested questions */}
                {answers.length === 0 && !searching && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)", marginBottom: ".75rem" }}>
                      {activeDoc ? `Ask anything about "${activeDoc.name}"` : "Ask anything across all documents"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--slate)", marginBottom: "1rem" }}>
                      AI reads the actual document text and quotes the exact relevant sections back to you. Try one of these:
                    </div>
                    <div className="dv-chip-row">
                      {SUGGESTED_QUESTIONS.map(q => (
                        <div key={q} className="dv-chip" onClick={() => { setQuery(q); setTimeout(() => searchDocs(q), 50); }}>{q}</div>
                      ))}
                    </div>

                    {/* Document overview cards */}
                    <div style={{ marginTop: "1.5rem" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate)", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: ".75rem" }}>Your document vault</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
                        {docs.map(d => (
                          <div key={d.id} onClick={() => { setActiveDoc(d); inputRef.current?.focus(); }}
                            style={{ background: "var(--navy2)", border: ".5px solid var(--border)", borderRadius: "var(--r2)", padding: "1rem 1.1rem", cursor: "pointer", transition: "border-color .15s" }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = (DOC_TYPE_COLORS[d.type] || DOC_TYPE_COLORS.other).color}
                            onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,.07)"}>
                            <div style={{ fontSize: 22, marginBottom: 8 }}>{d.icon}</div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--white)", marginBottom: 3, lineHeight: 1.3 }}>{d.name}</div>
                            <div style={{ fontSize: 11, color: "var(--slate)", marginBottom: 8 }}>{d.pages} pages · {d.size}</div>
                            <TypeTag type={d.type} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Searching spinner */}
                {searching && (
                  <div className="dv-answer-box">
                    <div className="dv-answer-header">
                      <span className="ai-spin" style={{ width: 13, height: 13, borderWidth: 1.5 }} />
                      <span className="dv-answer-label">Reading documents and finding relevant sections…</span>
                    </div>
                    <div style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: 10 }}>
                      {[90, 70, 55, 80].map((w, i) => (
                        <div key={i} style={{ height: 14, width: `${w}%`, background: "rgba(255,255,255,.06)", borderRadius: 6 }} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Answers */}
                {answers.map((a, idx) => (
                  <div key={a.id} style={{ marginBottom: "1.5rem" }}>
                    {/* Question bubble */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                      <div style={{ background: "rgba(42,99,214,.10)", border: ".5px solid rgba(42,99,214,.15)", borderRadius: "14px 4px 14px 14px", padding: ".7rem 1rem", maxWidth: "70%", fontSize: 14, color: "var(--white)", fontWeight: 500, lineHeight: 1.5 }}>
                        {a.question}
                      </div>
                    </div>

                    {/* Answer box */}
                    <div className="dv-answer-box">
                      <div className="dv-answer-header">
                        <Ic n="zap" s={13} />
                        <span className="dv-answer-label">AI Document Search</span>
                        <span style={{ fontSize: 11, color: "var(--slate)", marginLeft: "auto" }}>{a.time}</span>
                      </div>
                      <div className="dv-answer-body">
                        {/* Highlight quoted text in the answer */}
                        {a.answer.split(/("(?:[^"\\]|\\.)*")/g).map((part, i) =>
                          part.startsWith('"') && part.endsWith('"') && part.length > 20
                            ? <span key={i} style={{ display: "block", margin: ".75rem 0", background: "var(--navy3)", borderLeft: "3px solid var(--teal)", padding: ".75rem 1rem", borderRadius: "0 var(--r) var(--r) 0", fontStyle: "italic", color: "var(--ice)", lineHeight: 1.7, fontSize: 13 }}>{part}</span>
                            : <span key={i}>{part}</span>
                        )}
                      </div>
                      <div style={{ padding: ".75rem 1.25rem", borderTop: ".5px solid var(--border)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: "var(--slate)" }}>Sources:</span>
                        {a.docs.map(d => (
                          <span key={d} className="dv-source-chip">{d}</span>
                        ))}
                        <button className="btn-g btn-sm" style={{ marginLeft: "auto", fontSize: 11 }}
                          onClick={() => { navigator.clipboard?.writeText(`Q: ${a.question}\n\nA: ${a.answer}`); toast("Copied to clipboard"); }}>
                          Copy answer
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Search history summary */}
                {answers.length > 0 && (
                  <div style={{ borderTop: ".5px solid var(--border)", paddingTop: "1rem", marginTop: ".5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".75rem" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate)", letterSpacing: ".07em", textTransform: "uppercase" }}>Search history — this session</div>
                      <button className="btn-g btn-sm" style={{ fontSize: 11 }} onClick={() => setAnswers([])}>Clear</button>
                    </div>
                    {answers.slice(1).map(a => (
                      <div key={a.id} className="dv-history-item" onClick={() => { const el = document.getElementById(`answer-${a.id}`); el?.scrollIntoView({ behavior: "smooth" }); }}>
                        <div className="dv-history-q">{a.question}</div>
                        <div className="dv-history-meta">{a.time} · {a.docs.length} doc{a.docs.length > 1 ? "s" : ""} searched</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    {lightbox && (
      <div onClick={() => setLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(255,255,255,.92)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out",padding:"1rem"}}>
        <div onClick={e => e.stopPropagation()} style={{position:"relative"}}>
          <img src={lightbox.dataUrl} alt={lightbox.name} style={{maxWidth:"90vw",maxHeight:"85vh",objectFit:"contain",borderRadius:8}} />
          <button onClick={() => setLightbox(null)} style={{position:"absolute",top:-12,right:-12,background:"#3D4B61",border:"none",borderRadius:"50%",width:28,height:28,color:"#fff",cursor:"pointer",fontSize:16}}>✕</button>
          <button onClick={() => { const a=document.createElement("a"); a.href=lightbox.dataUrl; a.download=lightbox.name; a.click(); }} style={{position:"absolute",bottom:-36,right:0,background:"var(--teal)",border:"none",borderRadius:6,padding:"4px 12px",color:"#fff",fontSize:11,cursor:"pointer",fontWeight:600}}>⬇ Download</button>
        </div>
      </div>
    )}
    </div>
  );
}

// ─── AI Compliance Auditor ────────────────────────────────────────────────────
const STATE_NOTICE_RULES = [
  { state:"Oregon (ORS 94.650)",    annual:15, special:10, board:5,  quorum:30, notes:"Notice must be mailed or personally delivered. Email allowed if owner consents in writing." },
  { state:"California (Civil Code §4920)", annual:10, special:4, board:4, quorum:25, notes:"Annual meeting notice must include agenda. Electronic notice requires prior consent." },
  { state:"Florida (§720.306)",     annual:14, special:14, board:14, quorum:30, notes:"Notice must be mailed and posted conspicuously on the property." },
  { state:"Texas (§209.0051)",      annual:10, special:10, board:3,  quorum:25, notes:"Notice may be by mail, hand delivery, or electronic transmission if consented to." },
  { state:"Nevada (NRS 116.31083)", annual:15, special:15, board:3,  quorum:25, notes:"Notice must be posted in a conspicuous place on the common elements." },
  { state:"Arizona (§33-1804)",     annual:10, special:10, board:2,  quorum:25, notes:"Meeting may be conducted electronically if authorized by the declaration." },
];

const INIT_MEETINGS = [
  { id:1, type:"annual",   title:"2024 Annual Meeting",        date:"2025-01-15", noticeDate:"2024-12-28", method:"email", state:"Oregon (ORS 94.650)",    status:"scheduled", agendaPosted:true,  quorumMet:null  },
  { id:2, type:"board",    title:"April Board Meeting",        date:"2024-04-30", noticeDate:"2024-04-24", method:"email", state:"Oregon (ORS 94.650)",    status:"scheduled", agendaPosted:true,  quorumMet:null  },
  { id:3, type:"special",  title:"Special Assessment Vote",    date:"2024-05-15", noticeDate:"2024-05-10", method:"mail",  state:"Oregon (ORS 94.650)",    status:"scheduled", agendaPosted:false, quorumMet:null  },
  { id:4, type:"board",    title:"March Board Meeting",        date:"2024-03-26", noticeDate:"2024-03-20", method:"email", state:"Oregon (ORS 94.650)",    status:"completed", agendaPosted:true,  quorumMet:true  },
  { id:5, type:"annual",   title:"2023 Annual Meeting",        date:"2024-01-18", noticeDate:"2024-01-02", method:"mail",  state:"Oregon (ORS 94.650)",    status:"completed", agendaPosted:true,  quorumMet:true  },
];

function ComplianceAuditor({ toast , orgName = "Your HOA", orgEmail = "" }) {
  const [meetings,  setMeetings]  = useState([]);
  const [selState,  setSelState]  = useState("Oregon (ORS 94.650)");
  const [tab,       setTab]       = useState("audits");
  const [newForm,   setNewForm]   = useState({ type:"board", title:"", date:"", noticeDate:"", method:"email" });
  const [aiReport,  setAiReport]  = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showNew,   setShowNew]   = useState(false);

  const TODAY = new Date();
  const rule  = STATE_NOTICE_RULES.find(r => r.state === selState) || STATE_NOTICE_RULES[0];

  const auditMeeting = (m) => {
    const reqDays = m.type === "annual" ? rule.annual : m.type === "special" ? rule.special : rule.board;
    const meetingDate = new Date(m.date);
    const noticeDate  = new Date(m.noticeDate);
    const daysGiven   = Math.round((meetingDate - noticeDate) / 86400000);
    const daysShort   = reqDays - daysGiven;
    const isUpcoming  = meetingDate > TODAY;
    const daysUntil   = Math.round((meetingDate - TODAY) / 86400000);
    const noticeDeadline = new Date(meetingDate - reqDays * 86400000);
    const noticeDeadlineStr = noticeDeadline.toISOString().split("T")[0];
    const noticeAlreadySent = noticeDate <= TODAY;
    const noticeLate  = noticeDate > noticeDeadline;

    let status = "ok";
    let issues = [];

    if (noticeLate && daysShort > 0) {
      status = "fail";
      issues.push(`Notice sent ${daysShort} day${daysShort>1?"s":""} late — ${daysGiven} days given, ${reqDays} required under ${selState}`);
    } else if (!noticeAlreadySent && isUpcoming) {
      const daysUntilDeadline = Math.round((noticeDeadline - TODAY) / 86400000);
      if (daysUntilDeadline <= 3) {
        status = "warn";
        issues.push(`Notice deadline is in ${daysUntilDeadline} day${daysUntilDeadline!==1?"s":""} (by ${noticeDeadlineStr}) — send immediately`);
      } else if (daysUntilDeadline <= 7) {
        status = "warn";
        issues.push(`Notice must be sent by ${noticeDeadlineStr} (${daysUntilDeadline} days away) to meet ${reqDays}-day requirement`);
      }
    }
    if (!m.agendaPosted && (m.type === "annual" || m.type === "special")) {
      if (status === "ok") status = "warn";
      issues.push("Agenda not yet posted — required for annual and special meetings");
    }
    if (m.method === "email" && selState.includes("Florida")) {
      if (status === "ok") status = "warn";
      issues.push("Florida §720.306 requires physical posting — email-only notice may be insufficient");
    }

    return { status, issues, daysGiven, reqDays, daysShort, noticeLate, noticeDeadlineStr, daysUntil, isUpcoming };
  };

  const generateReport = async () => {
    setAiLoading(true); setAiReport("");
    try {
      const auditSummary = meetings.map(m => {
        const a = auditMeeting(m);
        return `${m.title} (${m.date}): ${a.status.toUpperCase()} — notice gave ${a.daysGiven} days (${a.reqDays} required). ${a.issues.join(". ")||"No issues."}`;
      }).join("\n");
      const txt = await callClaude(`You are an HOA compliance expert. Generate a brief compliance audit report for ${orgName} HOA.
State: ${selState}
Meetings audited:\n${auditSummary}
Write 3 paragraphs: (1) Overall compliance status, (2) Specific issues and legal risk, (3) Recommended actions. Professional, concise, actionable.`, 800);
      setAiReport(txt);
    } catch {
      toast("Report generation failed — try again");
    } finally {
      setAiLoading(false);
    }
  };

  const addMeeting = () => {
    if (!newForm.title || !newForm.date || !newForm.noticeDate) return;
    setMeetings(p => [...p, { ...newForm, id:Date.now(), state:selState, status:"scheduled", agendaPosted:false, quorumMet:null }]);
    setNewForm({ type:"board", title:"", date:"", noticeDate:"", method:"email" });
    setShowNew(false); toast("Meeting added and audited");
  };

  // Pre-compute audit results once — auditMeeting() was called 3x per meeting per render

  const auditResults = useMemo(
    () => meetings.map(m => ({ ...m, _audit: auditMeeting(m) })),
    [meetings, selState] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const statusIcon = { ok:"✅", warn:"⚠️", fail:"🚫" };
  const statusLabel = { ok:"Compliant", warn:"Review needed", fail:"Violation" };
  const statusClass = { ok:"ok", warn:"warn", fail:"fail" };

  const violations = useMemo(() => auditResults.filter(m => m._audit.status === "fail").length, [auditResults]);
  const warnings   = useMemo(() => auditResults.filter(m => m._audit.status === "warn").length, [auditResults]);
  const compliant  = useMemo(() => auditResults.filter(m => m._audit.status === "ok").length, [auditResults]);

  // Empty state — after all hooks
  if (meetings.length === 0) return (
    <div>
      <div className="ph"><div><div className="pt">AI Compliance Auditor</div><div className="ps">Meeting notice compliance tracking</div></div></div>
      <div className="pb"><EmptyState icon="⚖️" title="No meetings tracked yet" subtitle="Add your board meetings to audit notice compliance and track legal requirements by state." /></div>
    </div>
  );

  return (
    <div>
      <div className="ph">
        <div><div className="pt">AI Compliance Auditor</div><div className="ps">Meeting notice windows · Agenda requirements · State law monitoring</div></div>
        <div style={{ display:"flex",gap:8 }}>
          <select className="fi" style={{ fontSize:12,padding:"6px 10px" }} value={selState} onChange={e=>setSelState(e.target.value)}>
            {STATE_NOTICE_RULES.map(r=><option key={r.state} value={r.state}>{r.state}</option>)}
          </select>
          <button className="btn btn-sm" onClick={()=>setShowNew(true)}>+ Add meeting</button>
        </div>
      </div>
      <div className="pb">
        {/* Alert strips */}
        {violations > 0 && <div className="ca-alert-strip red"><span style={{fontSize:18}}>🚨</span><div><strong style={{color:"var(--red)"}}>Notice violation detected</strong><div style={{fontSize:12,color:"var(--slate)",marginTop:2}}>{violations} meeting{violations>1?"s":""} sent notice outside the legally required window — board may face challenge to meeting validity</div></div></div>}
        {warnings  > 0 && <div className="ca-alert-strip amber"><span style={{fontSize:18}}>⚠️</span><div><strong style={{color:"var(--amber)"}}>Action required soon</strong><div style={{fontSize:12,color:"var(--slate)",marginTop:2}}>{warnings} upcoming meeting{warnings>1?"s":""} need{warnings===1?"s":""} attention to stay compliant</div></div></div>}
        {violations === 0 && warnings === 0 && <div className="ca-alert-strip green"><span style={{fontSize:18}}>✅</span><div><strong style={{color:"var(--green)"}}>All meetings compliant</strong><div style={{fontSize:12,color:"var(--slate)",marginTop:2}}>All {compliant} meetings meet notice requirements under {selState}</div></div></div>}

        <div className="sr" style={{gridTemplateColumns:"repeat(4,1fr)",marginTop:"1rem"}}>
          <div className="sc"><div className="sl">Total meetings</div><div className="sv">{meetings.length}</div></div>
          <div className="sc"><div className="sl">Compliant</div><div className="sv t">{compliant}</div></div>
          <div className="sc"><div className="sl">Need action</div><div className="sv a">{warnings}</div></div>
          <div className="sc"><div className="sl">Violations</div><div className="sv r">{violations}</div></div>
        </div>

        <div className="tabs">
          {[["audits","Meeting audits"],["rules","State law rules"],["report","AI audit report"]].map(([k,l])=>
            <div key={k} className={`tab ${tab===k?"active":""}`} onClick={()=>setTab(k)}>{l}</div>
          )}
        </div>

        {tab === "audits" && (
          <div className="ca-layout">
            {meetings.map(m => {
              const a = auditMeeting(m);
              const pct = Math.min(100, Math.round(a.daysGiven / a.reqDays * 100));
              return (
                <div key={m.id} className={`ca-audit-card ${statusClass[a.status]}`}>
                  <div className="ca-audit-header">
                    <span className="ca-audit-icon">{m.type==="annual"?"📅":m.type==="special"?"⚡":"📋"}</span>
                    <div style={{flex:1}}>
                      <div className="ca-audit-title">{m.title}</div>
                      <div style={{fontSize:11,color:"var(--slate)",marginTop:1,textTransform:"capitalize"}}>{m.type} meeting · {m.method}</div>
                    </div>
                    <span className={`ca-status-badge ca-${statusClass[a.status]}`}>{statusIcon[a.status]} {statusLabel[a.status]}</span>
                  </div>
                  <div className="ca-audit-body">
                    <div className="ca-fact"><strong>Meeting date:</strong> {m.date}{a.isUpcoming ? ` · ${a.daysUntil} days away` : " · Completed"}</div>
                    <div className="ca-fact"><strong>Notice sent:</strong> {m.noticeDate} ({a.daysGiven} days before meeting)</div>
                    <div className="ca-fact"><strong>Required:</strong> {a.reqDays} days under {selState}</div>
                    <div style={{fontSize:11,color:"var(--slate)",margin:".35rem 0 .2rem"}}>Notice adequacy</div>
                    <div className="ca-deadline-bar">
                      <div className="ca-deadline-fill" style={{width:`${pct}%`,background:a.status==="ok"?"var(--teal)":a.status==="warn"?"var(--amber)":"var(--red)"}} />
                    </div>
                    <div style={{fontSize:11,color:"var(--slate)",marginBottom:".5rem"}}>{pct}% of required notice given</div>

                    {a.issues.map((issue,i) => (
                      <div key={i} style={{fontSize:12,color:a.status==="fail"?"var(--red)":"var(--amber)",background:a.status==="fail"?"var(--red2)":"var(--amber2)",borderRadius:"var(--r)",padding:"5px 9px",marginBottom:4,lineHeight:1.5}}>
                        {a.status==="fail"?"⚠ ":"💡 "}{issue}
                      </div>
                    ))}

                    <div className="ca-timeline" style={{marginTop:".85rem"}}>
                      {[
                        {label:`Notice deadline`, date:a.noticeDeadlineStr, done:new Date(a.noticeDeadlineStr)<=TODAY, active:new Date(a.noticeDeadlineStr)>TODAY},
                        {label:`Notice sent (${m.noticeDate})`, date:m.noticeDate, done:new Date(m.noticeDate)<=TODAY, missed:a.noticeLate},
                        {label:`Meeting (${m.date})`, date:m.date, done:!a.isUpcoming, active:a.isUpcoming},
                      ].map((t,i)=>(
                        <div key={i} className="ca-tl-item">
                          <div className={`ca-tl-dot ${t.missed?"missed":t.done?"done":t.active?"due":""}`} />
                          <div className="ca-tl-text" style={{color:t.missed?"var(--red)":t.active?"var(--amber)":t.done?"var(--white)":"var(--slate)"}}>{t.label}</div>
                        </div>
                      ))}
                    </div>

                    {!m.agendaPosted && (m.type==="annual"||m.type==="special") && (
                      <button className="ca-action-btn primary" onClick={()=>{setMeetings(p=>p.map(x=>x.id===m.id?{...x,agendaPosted:true}:x));toast("Agenda marked as posted");}}>Post agenda →</button>
                    )}
                    {a.issues.length===0 && <div style={{fontSize:12,color:"var(--teal)",marginTop:".5rem"}}>✓ Meets all notice requirements</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "rules" && (
          <div>
            <div style={{fontSize:13,color:"var(--slate)",marginBottom:"1.25rem",lineHeight:1.7}}>Notice requirements vary significantly by state. Board-Armor monitors your meetings against the rules for your selected state. Always verify with a licensed HOA attorney for your specific situation.</div>
            {STATE_NOTICE_RULES.map((r,i)=>(
              <div key={i} style={{background:"var(--navy2)",border:`.5px solid ${selState===r.state?"rgba(42,99,214,.25)":"var(--border)"}`,borderRadius:"var(--r2)",padding:"1rem 1.25rem",marginBottom:10,cursor:"pointer",transition:"border-color .15s"}} onClick={()=>setSelState(r.state)}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:".75rem"}}>
                  <div style={{fontSize:14,fontWeight:600,color:selState===r.state?"var(--teal)":"var(--white)"}}>{r.state}</div>
                  {selState===r.state && <span className="bg bg-t" style={{fontSize:11}}>Active</span>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:".75rem"}}>
                  {[["Annual meeting",r.annual],["Special meeting",r.special],["Board meeting",r.board]].map(([l,d])=>(
                    <div key={l} style={{background:"var(--navy3)",borderRadius:"var(--r)",padding:".6rem .85rem"}}>
                      <div style={{fontSize:10,color:"var(--slate)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:3}}>{l}</div>
                      <div style={{fontSize:18,fontWeight:700,color:"var(--white)"}}>{d}<span style={{fontSize:12,fontWeight:400,color:"var(--slate)"}}> days</span></div>
                    </div>
                  ))}
                </div>
                <div className="ca-rule-row" style={{padding:".4rem 0",border:"none"}}>
                  <span className="ca-rule-icon">ℹ️</span>
                  <span className="ca-rule-text">{r.notes}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "report" && (
          <div>
            <div style={{background:"var(--navy2)",border:".5px solid var(--border)",borderRadius:"var(--r2)",padding:"1.25rem",marginBottom:"1rem"}}>
              <div style={{fontSize:14,fontWeight:600,color:"var(--white)",marginBottom:".35rem"}}>AI Compliance Audit Report</div>
              <div style={{fontSize:13,color:"var(--slate)",marginBottom:"1rem",lineHeight:1.6}}>AI reviews all {meetings.length} meetings against {selState} requirements and generates a prioritized action report suitable for board records.</div>
              <button className="btn" onClick={generateReport} disabled={aiLoading} style={{display:"flex",alignItems:"center",gap:6}}>
                {aiLoading?<><span className="ai-spin"/>Generating…</>:"✦ Generate audit report"}
              </button>
            </div>
            {aiReport && (
              <div className="ca-ai-report">
                <div style={{fontSize:11,fontWeight:700,color:"var(--purple)",letterSpacing:".07em",textTransform:"uppercase",marginBottom:".75rem",display:"flex",alignItems:"center",gap:5}}><Ic n="zap" s={11}/>AI Compliance Audit — {new Date().toLocaleDateString()}</div>
                <div style={{fontSize:13,color:"var(--ice)",lineHeight:1.8,whiteSpace:"pre-wrap"}}>{aiReport}</div>
                <div style={{display:"flex",gap:8,marginTop:"1rem"}}>
                  <button className="btn-g btn-sm" onClick={generateReport}>Regenerate</button>
                  <button className="btn btn-sm" onClick={()=>{navigator.clipboard?.writeText(aiReport);toast("Report copied");}}>Copy report</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showNew && (
        <div className="mo" onClick={e=>e.target===e.currentTarget&&setShowNew(false)}>
          <div className="md">
            <div className="mt2">Add meeting</div>
            <div className="ms2">Board-Armor will audit notice compliance automatically</div>
            <div className="fr">
              <div className="fg"><label className="fl">Meeting type</label>
                <select className="fi" value={newForm.type} onChange={e=>setNewForm(p=>({...p,type:e.target.value}))}>
                  <option value="board">Board meeting</option>
                  <option value="annual">Annual meeting</option>
                  <option value="special">Special meeting</option>
                </select>
              </div>
              <div className="fg"><label className="fl">Notice method</label>
                <select className="fi" value={newForm.method} onChange={e=>setNewForm(p=>({...p,method:e.target.value}))}>
                  <option value="email">Email</option>
                  <option value="mail">Physical mail</option>
                  <option value="both">Email + mail</option>
                  <option value="posted">Posted on property</option>
                </select>
              </div>
            </div>
            <div className="fg"><label className="fl">Meeting title</label><input className="fi" value={newForm.title} onChange={e=>setNewForm(p=>({...p,title:e.target.value}))} placeholder="e.g. May Board Meeting" /></div>
            <div className="fr">
              <div className="fg"><label className="fl">Meeting date</label><input className="fi" type="date" value={newForm.date} onChange={e=>setNewForm(p=>({...p,date:e.target.value}))} /></div>
              <div className="fg"><label className="fl">Notice sent / planned</label><input className="fi" type="date" value={newForm.noticeDate} onChange={e=>setNewForm(p=>({...p,noticeDate:e.target.value}))} /></div>
            </div>
            <div className="ma"><button className="btn-g" onClick={()=>setShowNew(false)}>Cancel</button><button className="btn" onClick={addMeeting} disabled={!newForm.title||!newForm.date||!newForm.noticeDate}>Add & audit</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── USPS One-Click Mailer (Lob Integration) ──────────────────────────────────
// NOTE: In production, replace LOB_PROXY_URL calls with your Netlify serverless
// function that holds your Lob API key server-side.
const LOB_PROXY_URL = "/api/lob"; // → netlify/functions/lob.js

const MAIL_TYPES = [
  { id:"violation",   label:"Violation notice",       icon:"⚠️",  desc:"Official violation letter with 14-day cure notice" },
  { id:"dues",        label:"Dues reminder",           icon:"💰",  desc:"Monthly dues statement or delinquency notice" },
  { id:"ballot",      label:"Annual ballot packet",    icon:"🗳️",  desc:"Election ballot, proxy form, and candidate bios" },
  { id:"lien",        label:"Lien warning",            icon:"⚖️",  desc:"Formal pre-lien demand letter" },
  { id:"meeting",     label:"Meeting notice",          icon:"📋",  desc:"Annual or special meeting notice with agenda" },
  { id:"welcome",     label:"Welcome letter",          icon:"🏠",  desc:"New owner welcome packet with community rules" },
  { id:"custom",      label:"Custom letter",           icon:"✉️",  desc:"Write your own letter content" },
];

const INIT_MAIL_LOG = [
  { id:1, type:"violation", to:"Damien Torres",   unit:"202", date:"2024-03-18", status:"delivered", trackingNum:"2410500265182451812678", cost:1.20 },
  { id:2, type:"dues",      to:"Robert Walsh",    unit:"102", date:"2024-04-01", status:"in_transit", trackingNum:"9400100897846352306803", cost:1.20 },
  { id:3, type:"ballot",    to:"All Owners",      unit:"All", date:"2024-03-01", status:"delivered", trackingNum:"Bulk mailing",           cost:7.20 },
];

function USPSMailer({ owners, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [mailType,     setMailType]     = useState("violation");
  const [selectedOwners, setSelOwners]  = useState([]);
  const [sendToAll,    setSendToAll]    = useState(false);
  const [customContent,setContent]      = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [sending,      setSending]      = useState(false);
  const [mailLog,      setMailLog]      = useState([]);
  const [tab,          setTab]          = useState("compose");
  const [showPreview,  setShowPreview]  = useState(true);
  const [sendSuccess,  setSendSuccess]  = useState(false);

  const mt      = MAIL_TYPES.find(t=>t.id===mailType)||MAIL_TYPES[0];
  const recipients = sendToAll ? owners : owners.filter(o=>selectedOwners.includes(o.id));
  const perLetterCost = 1.20;
  const totalCost = (recipients.length * perLetterCost).toFixed(2);

  const toggleOwner = (id) => setSelOwners(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);

  const generateContent = async () => {
    setAiGenerating(true);
    const sample = recipients[0] || owners?.[0] || null;
    const prompt = `Write a professional, legally sound HOA ${mt.label.toLowerCase()} letter for ${orgName} HOA. 
Owner: ${sample?.name||"[Owner Name]"}, Unit ${sample?.unit||"[Unit]"}.
${mailType==="violation"?"Violation: Trash bins left out for 72+ hours past pickup.":""}
${mailType==="dues"?"Amount owed: $300. Due date: May 1, 2024.":""}
${mailType==="meeting"?"Annual meeting date: January 15, 2025. Location: Clubhouse.":""}
Keep under 200 words. Professional letterhead format. Include proper salutation, body, and closing.`;
    const txt = await callClaude(prompt, 600);
    setContent(txt);
    setAiGenerating(false);
  };

  const sendMail = () => {
    if (!recipients.length) { toast("Select at least one recipient"); return; }
    setSending(true);
    setTimeout(() => {
      const newEntries = recipients.map(o=>({
        id:Date.now()+Math.random(), type:mailType,
        to:o.name, unit:o.unit,
        date:new Date().toISOString().split("T")[0],
        status:"in_transit",
        trackingNum:`94001${Math.floor(Math.random()*1e13).toString().padStart(13,"0")}`,
        cost:perLetterCost,
      }));
      setMailLog(p=>[...newEntries,...p]);
      setSending(false); setSendSuccess(true);
      toast(`${recipients.length} letter${recipients.length>1?"s":""} queued for USPS First Class mail`);
      setTimeout(()=>{ setSendSuccess(false); setSelOwners([]); setSendToAll(false); setContent(""); },4000);
    }, 2200);
  };

  const sample = recipients[0]||owners?.[0]||null;
  const letterDate = new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"});

  return (
    <div>
      <div className="ph">
        <div><div className="pt">One-Click USPS Mailer</div><div className="ps">Send legally-tracked physical mail to owners in one click · Powered by Lob API</div></div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:11,background:"rgba(0,102,204,.15)",color:"#60a5fa",border:".5px solid rgba(0,102,204,.3)",borderRadius:20,padding:"3px 10px",fontWeight:600}}>📬 USPS First Class</span>
        </div>
      </div>
      <div className="lob-grid">
        {/* Form panel */}
        <div className="lob-form-panel">
          <div className="tabs" style={{marginBottom:"1rem"}}>
            {[["compose","Compose"],["tracking","Tracking"],["history","History"]].map(([k,l])=>
              <div key={k} className={`tab ${tab===k?"active":""}`} onClick={()=>setTab(k)}>{l}</div>
            )}
          </div>

          {tab === "compose" && (
            <div>
              <div className="lob-section-label">Letter type</div>
              <div className="lob-type-grid">
                {MAIL_TYPES.map(t=>(
                  <button key={t.id} className={`lob-type-btn ${mailType===t.id?"active":""}`} onClick={()=>{setMailType(t.id);setContent("");}}>
                    <span className="lob-type-icon">{t.icon}</span>
                    <span style={{fontWeight:600,display:"block"}}>{t.label}</span>
                  </button>
                ))}
              </div>
              <div style={{fontSize:12,color:"var(--slate)",marginBottom:"1rem"}}>{mt.desc}</div>

              <div className="lob-section-label">Recipients</div>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"var(--slate)",cursor:"pointer",marginBottom:".75rem"}}>
                <input type="checkbox" checked={sendToAll} onChange={e=>{setSendToAll(e.target.checked);setSelOwners([]);}} style={{accentColor:"var(--teal)",width:15,height:15}} />
                <span>Send to all {owners.length} homeowners</span>
              </label>
              {!sendToAll && owners.map(o=>(
                <div key={o.id} className="lob-recipient-row">
                  <input type="checkbox" className="lob-recipient-cb" checked={selectedOwners.includes(o.id)} onChange={()=>toggleOwner(o.id)} />
                  <div style={{flex:1}}>
                    <div className="lob-recipient-name">{o.name}</div>
                    <div className="lob-recipient-addr">Unit {o.unit} · {o.email}</div>
                  </div>
                </div>
              ))}

              <div className="lob-section-label" style={{marginTop:".75rem"}}>Letter content</div>
              <div style={{display:"flex",gap:8,marginBottom:".65rem"}}>
                <button className="btn-g btn-sm" style={{flex:1,color:"var(--purple)"}} onClick={generateContent} disabled={aiGenerating}>
                  {aiGenerating?<><span className="ai-spin" style={{width:11,height:11}}/> Writing…</>:"✦ AI write letter"}
                </button>
                <button className="btn-g btn-sm" onClick={()=>setContent("")} disabled={!customContent}>Clear</button>
              </div>
              <textarea className="fi" value={customContent} onChange={e=>setContent(e.target.value)} placeholder="Write or AI-generate your letter content here…" rows={8} style={{fontSize:12,lineHeight:1.7}} />

              <div className="lob-section-label" style={{marginTop:".75rem"}}>Estimated cost</div>
              <div style={{background:"var(--navy3)",borderRadius:"var(--r)",padding:"1rem",marginBottom:".75rem"}}>
                <div className="lob-cost-row"><span>Recipients</span><span style={{fontWeight:600}}>{recipients.length}</span></div>
                <div className="lob-cost-row"><span>Per letter (First Class + printing)</span><span style={{fontWeight:600}}>${perLetterCost.toFixed(2)}</span></div>
                <div className="lob-cost-row" style={{fontWeight:700,color:"var(--teal)"}}><span>Total</span><span>${totalCost}</span></div>
              </div>

              {sendSuccess && <div className="op-success" style={{marginBottom:".75rem"}}>✓ {recipients.length} letter{recipients.length>1?"s":""} queued for USPS printing and mailing. Tracking numbers will appear in the Tracking tab.</div>}

              <button className="lob-send-btn" onClick={sendMail} disabled={!recipients.length||sending||!customContent}>
                {sending?<><span className="ai-spin" style={{width:14,height:14,borderTopColor:"#fff",borderColor:"rgba(255,255,255,.3)"}}/>Sending to Lob…</>:<>📬 Send {recipients.length||0} letter{recipients.length!==1?"s":""} via USPS</>}
              </button>
              <div style={{fontSize:11,color:"var(--slate)",marginTop:".5rem",textAlign:"center"}}>Letters printed and mailed by next business day · USPS tracking included</div>
              <div style={{fontSize:11,color:"var(--slate)",marginTop:".35rem",textAlign:"center"}}>
                <strong style={{color:"var(--white)"}}>Production:</strong> Calls <code style={{color:"var(--teal)",fontSize:10}}>POST /api/lob/letters</code> with your server-side Lob API key
              </div>
            </div>
          )}

          {tab === "tracking" && (
            <div>
              <div style={{fontSize:13,color:"var(--slate)",marginBottom:"1rem"}}>Real-time USPS tracking for all sent letters. Lob provides automatic status updates.</div>
              {mailLog.filter(l=>l.status!=="delivered").map(l=>(
                <div key={l.id} className="lob-tracking-row">
                  <span className="lob-track-icon">{MAIL_TYPES.find(t=>t.id===l.type)?.icon||"✉️"}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500,color:"var(--white)"}}>{l.to} · Unit {l.unit}</div>
                    <div style={{fontSize:11,color:"var(--slate)",fontFamily:"monospace"}}>{l.trackingNum}</div>
                    <div style={{fontSize:11,color:"var(--slate)"}}>Sent {l.date}</div>
                  </div>
                  <span className={`lob-track-status ${l.status==="delivered"?"lob-delivered":l.status==="in_transit"?"lob-transit":"lob-mailed"}`}>
                    {l.status==="delivered"?"✓ Delivered":l.status==="in_transit"?"In transit":"Mailed"}
                  </span>
                </div>
              ))}
              {mailLog.filter(l=>l.status!=="delivered").length===0 && <div className="op-empty">No letters currently in transit.</div>}
            </div>
          )}

          {tab === "history" && (
            <div>
              <div className="tw">
                <table>
                  <thead><tr><th>Type</th><th>Recipient</th><th>Sent</th><th style={{textAlign:"right"}}>Cost</th><th>Status</th></tr></thead>
                  <tbody>{mailLog.map(l=>(
                    <tr key={l.id}>
                      <td style={{fontSize:13}}>{MAIL_TYPES.find(t=>t.id===l.type)?.icon} {MAIL_TYPES.find(t=>t.id===l.type)?.label||l.type}</td>
                      <td style={{fontWeight:500}}>{l.to} <span style={{color:"var(--teal)",fontSize:11}}>· {l.unit}</span></td>
                      <td style={{fontSize:12,color:"var(--slate)"}}>{l.date}</td>
                      <td style={{textAlign:"right",fontFamily:"monospace",fontSize:12}}>${l.cost.toFixed(2)}</td>
                      <td><span className={`lob-track-status ${l.status==="delivered"?"lob-delivered":l.status==="in_transit"?"lob-transit":"lob-mailed"}`}>{l.status==="delivered"?"✓ Delivered":l.status==="in_transit"?"In transit":"Mailed"}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div style={{fontSize:12,color:"var(--slate)",marginTop:".5rem",textAlign:"right"}}>Total spent: <strong style={{color:"var(--white)"}}>${mailLog.reduce((s,l)=>s+l.cost,0).toFixed(2)}</strong></div>
            </div>
          )}
        </div>

        {/* Letter preview panel */}
        <div className="lob-preview-panel">
          <div style={{fontSize:12,fontWeight:600,color:"#555",letterSpacing:".06em",textTransform:"uppercase",marginBottom:"1.25rem"}}>Letter preview</div>
          <div className="letter-preview">
            <div className="lp-stamp">USPS<br/>FIRST<br/>CLASS</div>
            <div className="lp-logo">{orgName}</div>
            <div className="lp-addr">{orgEmail}</div>
            <div className="lp-date">{letterDate}</div>
            <div className="lp-to">
              {sample?.name||"[Owner Name]"}<br/>
              Unit {sample?.unit||"[Unit]"}<br/>
              Unit {sample?.unit||"[Unit]"}<br/>
              Portland, OR 97201
            </div>
            <div className="lp-subject">RE: {mt.label} — Unit {sample?.unit||"[Unit]"}</div>
            <div className="lp-body">
              {customContent || <span style={{color:"#aaa",fontStyle:"italic"}}>Click "AI write letter" or type your letter content in the form to preview it here.</span>}
            </div>
            {customContent && (
              <div className="lp-sig">
                Sincerely,<br/><br/>
                The {orgName} HOA Board<br/>
                {orgEmail}
              </div>
            )}
            <div className="lp-barcode" />
          </div>
          <div style={{fontSize:11,color:"#888",marginTop:"1rem",textAlign:"center"}}>This is a print-accurate preview of what will be mailed</div>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile-First Resident Portal ─────────────────────────────────────────────
function MobilePortal({ owners, dues, violations, maintenance, setMaintenance, announcements, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [portalUser, setPortalUser]   = useState(owners[0] || null);
  const [screen,     setScreen]       = useState("home");
  const [paySuccess, setPaySuccess]   = useState(false);
  const [reqForm,    setReqForm]      = useState({ category:"Plumbing", description:"", urgency:"medium" });
  const [reqSent,    setReqSent]      = useState(false);
  const [activeTab,  setActiveTab]    = useState("home");
  const [simulating, setSimulating]   = useState(false);

  const payT1 = useRef(null);
  const payT2 = useRef(null);
  const reqT  = useRef(null);
  useEffect(() => () => { clearTimeout(payT1.current); clearTimeout(payT2.current); clearTimeout(reqT.current); }, []);

  // Guard: show empty state if no homeowners added yet
  if (!owners || owners.length === 0 || !portalUser) {
    return (
      <div>
        <div className="ph"><div><div className="pt">Mobile Resident Portal</div><div className="ps">Homeowner-facing mobile app</div></div></div>
        <div className="pb">
          <div style={{ textAlign:"center", padding:"3rem 1rem", color:"var(--slate)" }}>
            <div style={{ fontSize:40, marginBottom:16 }}>📱</div>
            <div style={{ fontSize:16, fontWeight:600, color:"var(--ink)", marginBottom:8 }}>No homeowners added yet</div>
            <div style={{ fontSize:14 }}>Add homeowners in the Homeowners Directory first, then come back to preview the portal.</div>
          </div>
        </div>
      </div>
    );
  }

  const owner      = portalUser;
  const myDues     = dues.filter(d=>d.unit===owner.unit);
  const myViol     = violations.filter(v=>v.unit===owner.unit&&v.status!=="resolved");
  const myRequests = maintenance.filter(m=>m.unit===owner.unit);
  const totalOwed  = owner.dues_owed||owner.duesOwed||0;

  const handleApplePay = () => {
    const amount = totalOwed; // capture now — don't close over state
    setSimulating(true);
    payT1.current = setTimeout(()=>{
      setSimulating(false); setPaySuccess(true);
      toast("Apple Pay payment of $"+amount+" processed");
      payT2.current = setTimeout(()=>{ setPaySuccess(false); setScreen("home"); }, 2500);
    }, 1800);
  };

  const submitRequest = () => {
    if (!reqForm.description) return;
    setMaintenance(prev=>[...prev,{ id:Date.now(), unit:owner.unit, owner:owner.name, category:reqForm.category, title:reqForm.description.slice(0,40), description:reqForm.description, submitted:new Date().toISOString().split("T")[0], status:"open", priority:reqForm.urgency, boardNote:"" }]);
    setReqSent(true); reqT.current = setTimeout(()=>{ setReqSent(false); setReqForm({category:"Plumbing",description:"",urgency:"medium"}); setScreen("home"); }, 2000);
  };

  const Phone = ({children}) => (
    <div className="mp-device">
      <div className="mp-screen">{children}</div>
    </div>
  );

  const HomeScreen = ()=>(
    <div>
      <div className="mp-header">
        <div className="mp-header-logo" style={{ display: "flex", alignItems: "center", gap: 8 }}><BoardArmorLogo size={22} />Board-<span>Armor</span></div>
        <div className="mp-header-name">Hey, {owner.name.split(" ")[0]}! 👋</div>
        <div className="mp-header-unit">Unit {owner?.unit} · {orgName}</div>
        <div className="mp-header-avatar">{owner.name.split(" ").map(w=>w[0]).join("").slice(0,2)}</div>
      </div>
      <div className="mp-cards-row">
        <div className="mp-mini-card" style={{background:totalOwed>0?"linear-gradient(135deg,#1f0a00,#3d1500)":undefined}}>
          <div className="mp-mini-label">Balance</div>
          <div className="mp-mini-val" style={{color:totalOwed>0?"#f59e0b":"#dc2626"}}>{totalOwed>0?`$${totalOwed}`:"Current"}</div>
          <div className="mp-mini-sub">{totalOwed>0?"Due now":"No balance"}</div>
        </div>
        <div className="mp-mini-card">
          <div className="mp-mini-label">Violations</div>
          <div className="mp-mini-val" style={{color:myViol.length?"#dc2626":"#dc2626"}}>{myViol.length}</div>
          <div className="mp-mini-sub">{myViol.length?"Open":"Clear"}</div>
        </div>
        <div className="mp-mini-card">
          <div className="mp-mini-label">Requests</div>
          <div className="mp-mini-val">{myRequests.length}</div>
          <div className="mp-mini-sub">Submitted</div>
        </div>
        <div className="mp-mini-card">
          <div className="mp-mini-label">News</div>
          <div className="mp-mini-val">{announcements.length}</div>
          <div className="mp-mini-sub">Updates</div>
        </div>
      </div>

      {totalOwed>0 && (
        <div style={{margin:"0 16px 16px",background:"linear-gradient(135deg,#1f0a00,#3d2200)",borderRadius:16,padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"#f59e0b"}}>Payment overdue</div>
            <div style={{fontSize:22,fontWeight:800,color:"#fff"}}>${totalOwed}</div>
          </div>
          <button onClick={()=>setScreen("pay")} style={{background:"#f59e0b",color:"#ffffff",border:"none",borderRadius:12,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"var(--sans)"}}>Pay now →</button>
        </div>
      )}

      <div className="mp-section-title">Quick actions</div>
      <div className="mp-action-grid">
        {[
          { icon:"💰", label:"Pay dues", sub:"Apple Pay · Card", color:"#e8f5ee", action:"pay" },
          { icon:"🔧", label:"Report issue", sub:"Maintenance request", color:"#e8eef5", action:"request" },
          { icon:"📋", label:"My violations", sub:myViol.length?" open":"None open", color:"#fef3e2", action:"violations" },
          { icon:"📣", label:"Announcements", sub:`${announcements.length} from board`, color:"#f0ebfe", action:"announcements" },
        ].map(a=>(
          <button key={a.label} className="mp-action-btn" onClick={()=>setScreen(a.action)}>
            <div className="mp-action-icon" style={{background:a.color}}>{a.icon}</div>
            <div className="mp-action-label">{a.label}</div>
            <div className="mp-action-sub">{a.sub}</div>
          </button>
        ))}
      </div>

      <div className="mp-section-title">Latest news</div>
      {announcements.slice(0,2).map(a=>(
        <div key={a.id} className="mp-list-item">
          <div className="mp-list-icon" style={{background:"#f0ebfe"}}>📣</div>
          <div>
            <div className="mp-list-title">{a.title}</div>
            <div className="mp-list-sub">{a.body.slice(0,60)}…</div>
            <div style={{fontSize:11,color:"#bbb",marginTop:3}}>{a.date}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const PayScreen = ()=>(
    <div>
      <div className="mp-header" style={{paddingBottom:30}}>
        <button onClick={()=>setScreen("home")} style={{background:"rgba(255,255,255,.10)",border:"none",color:"#fff",borderRadius:20,padding:"5px 12px",fontSize:12,cursor:"pointer",marginBottom:12,fontFamily:"var(--sans)"}}>← Back</button>
        <div style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,.7)"}}>Pay dues</div>
      </div>
      {paySuccess ? (
        <div className="mp-success-screen">
          <div className="mp-success-icon">✅</div>
          <div style={{fontSize:20,fontWeight:800,color:"#ffffff",marginBottom:6}}>Payment successful!</div>
          <div style={{fontSize:14,color:"#64748b"}}>Your account has been updated.</div>
        </div>
      ) : (
        <div>
          <div className="mp-pay-sheet">
            <div style={{fontSize:13,color:"#64748b",textAlign:"center"}}>Amount due</div>
            <div className="mp-pay-amount">${totalOwed||300}</div>
            <div className="mp-pay-due">HOA dues · Due 1</div>

            <button className="mp-apple-pay-btn" onClick={handleApplePay} disabled={simulating}>
              {simulating?<><span className="ai-spin" style={{width:14,height:14,borderTopColor:"#fff",borderColor:"rgba(255,255,255,.3)"}}/>Processing…</>:<><span style={{fontSize:20}}></span> Apple Pay</>}
            </button>
            <button className="mp-google-pay-btn">
              <span style={{fontSize:18,fontWeight:900,color:"#4285F4"}}>G</span> Pay
            </button>
            <div className="mp-divider"><div className="mp-divider-line"/><div className="mp-divider-text">or pay with card</div><div className="mp-divider-line"/></div>
            <button className="mp-card-pay-btn">💳 Pay with card</button>
            <div style={{fontSize:11,color:"#bbb",textAlign:"center",marginTop:12}}>🔒 Secured by Stripe · 256-bit SSL</div>
          </div>
        </div>
      )}
    </div>
  );

  const RequestScreen = ()=>(
    <div>
      <div className="mp-header" style={{paddingBottom:24}}>
        <button onClick={()=>setScreen("home")} style={{background:"rgba(255,255,255,.10)",border:"none",color:"#fff",borderRadius:20,padding:"5px 12px",fontSize:12,cursor:"pointer",marginBottom:12,fontFamily:"var(--sans)"}}>← Back</button>
        <div style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,.7)"}}>Report an issue</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,.35)"}}>Unit {owner?.unit} · {orgName}</div>
      </div>
      {reqSent ? (
        <div className="mp-success-screen">
          <div className="mp-success-icon">✅</div>
          <div style={{fontSize:18,fontWeight:800,color:"#ffffff",marginBottom:6}}>Request submitted!</div>
          <div style={{fontSize:13,color:"#64748b"}}>The board will respond within 3–5 business days.</div>
        </div>
      ) : (
        <div className="mp-request-form" style={{paddingTop:16}}>
          <label style={{fontSize:12,fontWeight:700,color:"#64748b",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:".05em"}}>Category</label>
          <select className="mp-request-input" value={reqForm.category} onChange={e=>setReqForm(p=>({...p,category:e.target.value}))}>
            {["Plumbing","Electrical","HVAC","Landscaping","Common Area","Structural","Pest Control","Other"].map(c=><option key={c}>{c}</option>)}
          </select>
          <label style={{fontSize:12,fontWeight:700,color:"#64748b",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:".05em"}}>Urgency</label>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
            {[["low","Low 🟢"],["medium","Medium 🟡"],["high","Urgent 🔴"]].map(([k,l])=>(
              <button key={k} onClick={()=>setReqForm(p=>({...p,urgency:k}))} style={{padding:"10px 8px",borderRadius:10,border:`1.5px solid ${reqForm.urgency===k?"#dc2626":"#e8eaed"}`,background:reqForm.urgency===k?"#e8f5ee":"#fff",color:reqForm.urgency===k?"#ffffff":"#64748b",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"var(--sans)",transition:"all .15s"}}>{l}</button>
            ))}
          </div>
          <label style={{fontSize:12,fontWeight:700,color:"#64748b",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:".05em"}}>Description</label>
          <textarea className="mp-request-input" placeholder="Describe the issue in detail — where it is, when it started, how urgent…" value={reqForm.description} onChange={e=>setReqForm(p=>({...p,description:e.target.value}))} />
          <button className="mp-submit-btn" onClick={submitRequest} disabled={!reqForm.description} style={{opacity:!reqForm.description?.4:1}}>Submit request →</button>
        </div>
      )}
    </div>
  );

  const ViolationsScreen = ()=>(
    <div>
      <div className="mp-header" style={{paddingBottom:24}}>
        <button onClick={()=>setScreen("home")} style={{background:"rgba(255,255,255,.10)",border:"none",color:"#fff",borderRadius:20,padding:"5px 12px",fontSize:12,cursor:"pointer",marginBottom:12,fontFamily:"var(--sans)"}}>← Back</button>
        <div style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,.7)"}}>My violations</div>
      </div>
      {myViol.length===0
        ? <div style={{padding:"40px 20px",textAlign:"center"}}><div style={{fontSize:36,marginBottom:10}}>✅</div><div style={{fontSize:16,fontWeight:700,color:"#ffffff"}}>All clear!</div><div style={{fontSize:13,color:"#64748b",marginTop:4}}>No open violations on your unit.</div></div>
        : myViol.map(v=>(
          <div key={v.id} className="mp-list-item">
            <div className="mp-list-icon" style={{background:"#fde8e8"}}>⚠️</div>
            <div>
              <div className="mp-list-title">{v.type} violation</div>
              <div className="mp-list-sub">{v.description}</div>
              <div style={{marginTop:5}}><span className="mp-badge mp-badge-red">{v.status}</span></div>
            </div>
          </div>
        ))
      }
    </div>
  );

  const AnnouncementsScreen = ()=>(
    <div>
      <div className="mp-header" style={{paddingBottom:24}}>
        <button onClick={()=>setScreen("home")} style={{background:"rgba(255,255,255,.10)",border:"none",color:"#fff",borderRadius:20,padding:"5px 12px",fontSize:12,cursor:"pointer",marginBottom:12,fontFamily:"var(--sans)"}}>← Back</button>
        <div style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,.7)"}}>Announcements</div>
      </div>
      {announcements.map(a=>(
        <div key={a.id} style={{padding:"16px",borderBottom:"1px solid #f0f2f5"}}>
          <div style={{fontSize:14,fontWeight:700,color:"#ffffff",marginBottom:6}}>{a.title}</div>
          <div style={{fontSize:13,color:"#555",lineHeight:1.6}}>{a.body}</div>
          <div style={{fontSize:11,color:"#bbb",marginTop:8}}>{a.date}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div className="ph">
        <div><div className="pt">Mobile Resident Portal</div><div className="ps">Apple Pay · Maintenance requests · Violations · Announcements · Installable PWA</div></div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{fontSize:11,background:"rgba(255,255,255,.3)",color:"#fff",border:".5px solid rgba(255,255,255,.12)",borderRadius:20,padding:"3px 10px",fontWeight:600}}> Mobile-first</span>
        </div>
      </div>
      <div className="mp-outer-wrap">
        {/* Controls */}
        <div className="mp-controls">
          <div style={{fontSize:13,fontWeight:600,color:"var(--white)",marginBottom:".75rem"}}>Preview as homeowner</div>
          <div style={{fontSize:12,color:"var(--slate)",marginBottom:"1rem"}}>Select a unit to preview their mobile experience</div>
          {owners.map(o=>(
            <div key={o.id} onClick={()=>{setPortalUser(o);setScreen("home");}} style={{display:"flex",alignItems:"center",gap:10,padding:".65rem .85rem",background:portalUser?.id===o.id?"rgba(42,99,214,.07)":"var(--navy2)",border:`.5px solid ${portalUser?.id===o.id?"rgba(42,99,214,.25)":"var(--border)"}`,borderRadius:"var(--r)",marginBottom:6,cursor:"pointer",transition:"all .15s"}}>
              <div style={{width:28,height:28,borderRadius:"50%",background:"var(--teal3)",color:"var(--teal)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{o.name.split(" ").map(w=>w[0]).join("").slice(0,2)}</div>
              <div style={{flex:1}}><div style={{fontSize:13,fontWeight:500,color:"var(--white)"}}>{o.name}</div><div style={{fontSize:11,color:"var(--slate)"}}>Unit {o.unit}{o.duesOwed>0?" · $"+o.duesOwed+" owed":""}</div></div>
              {portalUser?.id===o.id&&<span style={{color:"var(--teal)",fontSize:11}}>Viewing</span>}
            </div>
          ))}

          <div style={{marginTop:"1.5rem",borderTop:".5px solid var(--border)",paddingTop:"1.25rem"}}>
            <div style={{fontSize:12,fontWeight:600,color:"var(--white)",marginBottom:".75rem"}}>PWA install instructions</div>
            {[
              {icon:"📱",label:"iOS Safari",step:"Tap Share → Add to Home Screen"},
              {icon:"🤖",label:"Android Chrome",step:"Tap ⋮ menu → Add to Home Screen"},
              {icon:"💻",label:"Desktop Chrome",step:"Click ⊕ in address bar → Install"},
            ].map((i,idx)=>(
              <div key={idx} style={{display:"flex",gap:8,padding:".5rem 0",borderBottom:".5px solid var(--border)",fontSize:12}}>
                <span style={{fontSize:16}}>{i.icon}</span>
                <div><div style={{fontWeight:500,color:"var(--white)"}}>{i.label}</div><div style={{color:"var(--slate)"}}>{i.step}</div></div>
              </div>
            ))}
            <div style={{fontSize:11,color:"var(--slate)",marginTop:".85rem",lineHeight:1.6}}>
              The PWA manifest and service worker files are in the project's <code style={{color:"var(--teal)",fontSize:10}}>public/</code> folder. Deploy to Netlify and the app installs like a native app on any device.
            </div>
          </div>
        </div>

        {/* Phone preview */}
        <div className="mp-preview-wrap">
          <Phone>
            {screen==="home"       && <HomeScreen/>}
            {screen==="pay"        && <PayScreen/>}
            {screen==="request"    && <RequestScreen/>}
            {screen==="violations" && <ViolationsScreen/>}
            {screen==="announcements" && <AnnouncementsScreen/>}

            {/* Tab bar */}
            <div className="mp-tab-bar">
              {[
                {id:"home",label:"Home",icon:"🏠"},
                {id:"pay",label:"Pay",icon:"💳"},
                {id:"request",label:"Report",icon:"🔧"},
                {id:"announcements",label:"News",icon:"📣"},
              ].map(t=>(
                <div key={t.id} className={`mp-tab ${screen===t.id?"active":""}`} onClick={()=>setScreen(t.id)}>
                  <span className="mp-tab-icon">{t.icon}</span>
                  {t.label}
                </div>
              ))}
            </div>
          </Phone>
        </div>
      </div>
    </div>
  );
}

// ─── Legal Pre-Send Scanner ───────────────────────────────────────────────────
const SCANNER_SYSTEM = `You are a specialized HOA legal compliance reviewer. Your job is to scan outgoing HOA communications — violation notices, dues letters, announcements, emails, and any other written correspondence — BEFORE they are sent to homeowners.

Review the message for these specific legal risk categories:
1. FAIR HOUSING ACT violations — language that could be discriminatory based on race, color, national origin, religion, sex, familial status, or disability
2. ADA / DISABILITY issues — improper denial of reasonable accommodations, inaccessible language, violations of disability rights
3. DUE PROCESS problems — fines or penalties threatened without proper hearing notice, insufficient cure periods, failure to follow CC&R enforcement procedures
4. DEFAMATION / HARASSMENT risk — accusatory, threatening, or personally offensive language that could expose the HOA to liability
5. IMPROPER FINE THREATS — fine amounts or escalation timelines that violate CC&Rs or state law
6. SELECTIVE ENFORCEMENT — language suggesting inconsistent enforcement that could evidence discrimination
7. PRIVACY VIOLATIONS — sharing homeowner personal information improperly
8. LEGAL OVERREACH — threatening actions (liens, foreclosure, legal action) without proper authority or notice periods
9. TONE / PROFESSIONALISM — unnecessarily hostile, condescending, or inflammatory language that could damage community relations

Return a JSON object ONLY (no markdown, no backticks) with this exact structure:
{
  "verdict": "CLEAR" | "CAUTION" | "BLOCKED",
  "riskScore": 0-100,
  "summary": "1-2 sentence plain-English verdict",
  "issues": [
    {
      "severity": "high" | "medium" | "low",
      "category": "category name",
      "title": "Issue title (5-8 words)",
      "description": "What the problem is and why it's legally risky (2-3 sentences)",
      "fix": "Specific suggested fix or rewording (1-2 sentences)"
    }
  ],
  "rewrite": "If verdict is CAUTION or BLOCKED, provide a complete rewrite of the message that fixes all issues while preserving the intent. If CLEAR, return empty string.",
  "approvedToSend": true | false
}

Rules:
- CLEAR (riskScore 0-25): Message is legally sound. approvedToSend = true.
- CAUTION (riskScore 26-65): Issues present but message can be sent with modifications. approvedToSend = false until revised.
- BLOCKED (riskScore 66-100): Serious legal risk. Do not send without legal review. approvedToSend = false.
- Be thorough but proportionate. Don't flag minor tone issues as BLOCKED.
- Focus on real legal risk, not stylistic preferences.`;

const SCAN_TEMPLATES = [
  {
    type: "Violation notice",
    label: "Violation notice",
    color: "var(--red)",
    sample: `Dear Mr. Johnson,

You have been violating our community rules AGAIN by leaving your garbage cans out for days. This is completely unacceptable and frankly disgraceful behavior that reflects poorly on our entire community.

You are hereby fined $500 effective immediately. If you don't fix this within 24 hours we will tow your car and take you to court. We have been watching your property and this is the third time.

Do better.

The Board`
  },
  {
    type: "Dues delinquency",
    label: "Dues notice",
    color: "var(--amber)",
    sample: `Hello Maria,

Your HOA dues of $300 are now 45 days past due. Per our collections policy, a $50 late fee has been added to your account, bringing your total balance to $350.

Please submit payment by May 15, 2024 to avoid further late fees. If payment is not received within 30 days of this notice, the Association reserves the right to record an assessment lien against your property as authorized by our CC&Rs Section 3.3.

Please contact our board if you would like to discuss a payment arrangement.

Sincerely,
The HOA Board`
  },
  {
    type: "Pet violation",
    label: "Pet violation",
    color: "var(--amber)",
    sample: `Dear Resident,

We have received multiple complaints about your dog. Large dangerous breeds are not allowed in this community per our rules. Your dog must be removed from the property within 7 days.

The Board`
  },
  {
    type: "Accommodation denial",
    label: "Accommodation request",
    color: "var(--red)",
    sample: `Dear Resident,

Thank you for your accommodation request. After reviewing your request, the Board has decided to deny this accommodation as we have a strict no-pets policy that applies to everyone equally.

If you choose to keep the animal, you will be fined $200/day.

The Board`
  },
  {
    type: "Community announcement",
    label: "Announcement",
    color: "var(--teal)",
    sample: `Dear Residents,

We are pleased to announce that the community pool will open for the season on May 1st. Pool hours will be 7:00 AM to 10:00 PM daily through September 30th.

Please review the pool rules posted at the gate before your first visit. All guests must be accompanied by a resident, and a maximum of 4 guests per unit are permitted at any time.

We look forward to a wonderful season together!

Warm regards,
The HOA Board`
  },
];

// Shared intercept modal — can be triggered from Email Center, Violations, etc.
export function LegalScannerModal({ message, messageType, onApprove, onRevise, onClose , orgName = "Your HOA", orgEmail = "" }) {
  const [scanning,  setScanning]  = useState(true);
  const [result,    setResult]    = useState(null);
  const [useRewrite, setUseRewrite] = useState(false);
  const [editText,  setEditText]  = useState(message);

  useState(() => {
    (async () => {
      try {
        const prompt = `Message type: ${messageType}\n\nMessage to review:\n\n${message}`;
        const txt = await callClaude(`${SCANNER_SYSTEM}\n\nReview this HOA communication:\n${prompt}`, 1400);
        const clean = txt.replace(/```json|```/g, "").trim();
        const parsed = JSON.parse(clean);
        setResult(parsed);
      } catch {
        setResult({ verdict:"ERROR", riskScore:null, summary:"Scan could not be completed. Do not send this message until it has been successfully scanned.", issues:[{ severity:"high", title:"Scan failed", description:"The legal scan could not complete due to a network issue or service interruption.", fix:"Try scanning again. If the issue persists, have an HOA attorney review before sending." }], rewrite:"", approvedToSend:false });
      }
      setScanning(false);
    })();
  }, []);

  const verdictConfig = {
    CLEAR:   { icon:"✅", label:"Cleared to send",              class:"clear", bg:"rgba(58,191,126,.1)",  border:"rgba(58,191,126,.25)" },
    CAUTION: { icon:"⚠️", label:"Review before sending",        class:"warn",  bg:"rgba(245,166,35,.1)",  border:"rgba(245,166,35,.25)" },
    BLOCKED: { icon:"🚫", label:"Do not send — legal risk",     class:"block", bg:"rgba(224,82,82,.1)",   border:"rgba(224,82,82,.25)"  },
    ERROR:   { icon:"⚡", label:"Scan failed — do not send",    class:"block", bg:"rgba(100,116,139,.1)", border:"rgba(100,116,139,.3)" },
  };
  const vc = result ? verdictConfig[result.verdict] : null;

  return (
    <div className="ls-intercept-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="ls-intercept-modal">
        <div className="ls-intercept-header">
          <span style={{ fontSize:20 }}>⚖️</span>
          <div>
            <div style={{ fontSize:14,fontWeight:600,color:"var(--white)" }}>Legal Pre-Send Scanner</div>
            <div style={{ fontSize:11,color:"var(--slate)" }}>AI reviewing message for legal compliance…</div>
          </div>
          <button onClick={onClose} style={{ marginLeft:"auto",background:"transparent",border:"none",color:"var(--slate)",cursor:"pointer",fontSize:18 }}>✕</button>
        </div>

        {scanning ? (
          <div className="ls-scanning-anim">
            <div className="ls-scan-ring" />
            <div style={{ fontSize:14,fontWeight:500,color:"var(--white)" }}>Scanning for legal issues…</div>
            <div style={{ fontSize:12,color:"var(--slate)" }}>Checking Fair Housing, due process, tone, and HOA law compliance</div>
            <div className="ls-scan-lines">
              {[80,60,90,50,70].map((w,i) => <div key={i} className="ls-scan-line" style={{ width:`${w}%`, background:"var(--border2)", animationDelay:`${i*0.2}s` }} />)}
            </div>
          </div>
        ) : result ? (
          <div style={{ padding:"1.25rem 1.5rem" }}>
            {/* Verdict banner */}
            <div style={{ background:vc.bg, border:`.5px solid ${vc.border}`, borderRadius:"var(--r)", padding:"1rem 1.25rem", marginBottom:"1.25rem", display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:26,flexShrink:0 }}>{vc.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:16,fontWeight:700,color:result.verdict==="CLEAR"?"var(--green)":result.verdict==="CAUTION"?"var(--amber)":"var(--red)", marginBottom:3 }}>{vc.label}</div>
                <div style={{ fontSize:13,color:"var(--slate)",lineHeight:1.5 }}>{result.summary}</div>
              </div>
              {/* Risk score ring */}
              <div style={{ textAlign:"center",flexShrink:0 }}>
                <svg width={56} height={56} viewBox="0 0 56 56">
                  <circle cx={28} cy={28} r={22} fill="none" stroke="var(--border2)" strokeWidth={5} />
                  <circle cx={28} cy={28} r={22} fill="none"
                    stroke={result.riskScore<=25?"var(--green)":result.riskScore<=65?"var(--amber)":"var(--red)"}
                    strokeWidth={5} strokeDasharray={`${2*Math.PI*22}`}
                    strokeDashoffset={`${2*Math.PI*22*(1-result.riskScore/100)}`}
                    strokeLinecap="round" transform="rotate(-90 28 28)" />
                  <text x={28} y={32} textAnchor="middle" fill="white" fontSize={13} fontWeight={700}>{result.riskScore}</text>
                </svg>
                <div style={{ fontSize:10,color:"var(--slate)",marginTop:2 }}>Risk score</div>
              </div>
            </div>

            {/* Issues */}
            {result.issues?.length > 0 && (
              <div style={{ marginBottom:"1.25rem" }}>
                <div style={{ fontSize:12,fontWeight:700,color:"var(--slate)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:".75rem" }}>Issues found ({result.issues.length})</div>
                {result.issues.map((issue, i) => (
                  <div key={i} className={`ls-issue ${issue.severity}`}>
                    <div className="ls-issue-title">
                      <span style={{ fontSize:14 }}>{issue.severity==="high"?"🔴":issue.severity==="medium"?"🟡":"🔵"}</span>
                      <span style={{ color:issue.severity==="high"?"var(--red)":issue.severity==="medium"?"var(--amber)":"var(--teal)", fontSize:11,fontWeight:700,letterSpacing:".05em",textTransform:"uppercase" }}>{issue.severity}</span>
                      <span>{issue.title}</span>
                    </div>
                    <div style={{ fontSize:11,fontWeight:600,color:"var(--slate)",marginBottom:4,textTransform:"uppercase",letterSpacing:".05em" }}>{issue.category}</div>
                    <div className="ls-issue-body">{issue.description}</div>
                    {issue.fix && <div className="ls-issue-fix">💡 Fix: {issue.fix}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* AI rewrite */}
            {result.rewrite && (
              <div style={{ marginBottom:"1.25rem" }}>
                <div style={{ fontSize:12,fontWeight:700,color:"var(--teal)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:".5rem",display:"flex",alignItems:"center",gap:6 }}>
                  <Ic n="zap" s={12} /> AI-corrected version
                </div>
                <div style={{ fontSize:12,color:"var(--slate)",marginBottom:".5rem" }}>This rewrite preserves the intent of your message while removing legal risk:</div>
                {useRewrite
                  ? <textarea className="fi" style={{ width:"100%",minHeight:160,fontSize:13,lineHeight:1.7,resize:"vertical",padding:"10px 12px" }} value={editText} onChange={e=>setEditText(e.target.value)} />
                  : <div className="ls-rewrite">{result.rewrite}</div>
                }
                <div style={{ display:"flex",gap:8,marginTop:8 }}>
                  <button className="btn-g btn-sm" onClick={() => { setUseRewrite(true); setEditText(result.rewrite); }}>Use this rewrite</button>
                  {useRewrite && <button className="btn-g btn-sm" onClick={() => setUseRewrite(false)}>View original</button>}
                </div>
              </div>
            )}

            {/* Action row */}
            <div className="ls-send-row">
              {result.approvedToSend ? (
                <>
                  <button className="btn" onClick={() => onApprove(useRewrite ? editText : message)} style={{ display:"flex",alignItems:"center",gap:6 }}>
                    ✅ Send message →
                  </button>
                  <button className="btn-g" onClick={onClose}>Cancel</button>
                  <span style={{ fontSize:11,color:"var(--green)",marginLeft:4 }}>Cleared by AI legal scanner</span>
                </>
              ) : result.rewrite ? (
                <>
                  <button className="btn" onClick={() => onRevise(useRewrite ? editText : result.rewrite)} style={{ background:"var(--amber)",color:"var(--navy)",display:"flex",alignItems:"center",gap:6 }}>
                    ✦ Send revised version
                  </button>
                  <button className="btn-g" onClick={onClose}>Edit manually</button>
                  <button className="btn-g" onClick={onClose} style={{ color:"var(--red)" }}>Cancel send</button>
                </>
              ) : (
                <>
                  <button className="btn-g" onClick={onClose} style={{ color:"var(--red)",borderColor:"rgba(224,82,82,.3)" }}>
                    🚫 Do not send — seek legal review
                  </button>
                  <button className="btn-g" onClick={onClose}>Edit message</button>
                </>
              )}
            </div>

            <div style={{ fontSize:11,color:"var(--slate)",marginTop:".75rem",lineHeight:1.6 }}>
              ⚠ This scan provides general guidance only and is not legal advice. For high-stakes communications, always consult a licensed HOA attorney.
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LegalScanner({ owners, toast , orgName = "Your HOA", orgEmail = "" }) {
  const [msgType,   setMsgType]   = useState("Violation notice");
  const [message,   setMessage]   = useState("");
  const [recipient, setRecipient] = useState("");
  const [unit,      setUnit]      = useState("");
  const [scanning,  setScanning]  = useState(false);
  const [result,    setResult]    = useState(null);
  const [useRewrite,setUseRewrite]= useState(false);
  const [editText,  setEditText]  = useState("");
  const [history,   setHistory]   = useState([]);
  const [activeTab, setActiveTab] = useState("compose");

  const runScan = async () => {
    if (!message.trim()) return;
    setScanning(true); setResult(null); setUseRewrite(false);
    try {
      const context = `Message type: ${msgType}${recipient ? `\nRecipient: ${recipient}` : ""}${unit ? ` (Unit ${unit})` : ""}\n\nMessage text:\n${message}`;
      const txt = await callClaude(`${SCANNER_SYSTEM}\n\nReview this HOA communication:\n${context}`, 1400);
      const clean = txt.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      if (!parsed.verdict || parsed.riskScore == null) throw new Error("Incomplete scan result");
      setResult(parsed);
      setHistory(prev => [{ id:Date.now(), type:msgType, snippet:message.slice(0,80), verdict:parsed.verdict, riskScore:parsed.riskScore, issues:parsed.issues?.length||0, time:new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}) }, ...prev.slice(0,19)]);
    } catch {
      // NEVER return a false CLEAR — show an explicit scan failure so the user
      // does not send a message believing it was legally reviewed when it was not.
      setResult({ verdict:"ERROR", riskScore:null, summary:"Scan could not be completed. Do not send this message until it has been successfully scanned.", issues:[{ severity:"high", title:"Scan failed", description:"The legal scan could not complete due to a network issue or service interruption.", fix:"Try scanning again. If the issue persists, have an HOA attorney review before sending." }], rewrite:"", approvedToSend:false });
    } finally {
      setScanning(false);
    }
  };

  const loadTemplate = (tpl) => { setMessage(tpl.sample); setMsgType(tpl.type); setResult(null); };
  const verdictConfig = {
    CLEAR:   { icon:"✅", label:"Cleared to send",              bg:"rgba(58,191,126,.08)",  border:"rgba(58,191,126,.25)", color:"var(--green)" },
    CAUTION: { icon:"⚠️", label:"Review before sending",        bg:"rgba(245,166,35,.08)",  border:"rgba(245,166,35,.25)", color:"var(--amber)" },
    BLOCKED: { icon:"🚫", label:"Do not send — legal risk",     bg:"rgba(224,82,82,.1)",    border:"rgba(224,82,82,.3)",   color:"var(--red)"   },
    ERROR:   { icon:"⚡", label:"Scan failed — do not send",    bg:"rgba(100,116,139,.08)", border:"rgba(100,116,139,.3)", color:"var(--slate)" },
  };

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">Legal Pre-Send Scanner</div>
          <div className="ps">Every outgoing message scanned for Fair Housing, due process, ADA, and HOA law compliance before it reaches a homeowner</div>
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
          <span style={{ fontSize:11,background:"rgba(245,166,35,.12)",color:"var(--amber)",border:".5px solid rgba(245,166,35,.25)",borderRadius:20,padding:"3px 10px",fontWeight:600 }}>
            ⚖️ AI Legal Review
          </span>
        </div>
      </div>

      <div className="pb">
        <div className="tabs">
          {[["compose","Scan a message"],["history","Scan history"],["risks","What we check"]].map(([k,l]) =>
            <div key={k} className={`tab ${activeTab===k?"active":""}`} onClick={() => setActiveTab(k)}>{l}</div>
          )}
        </div>

        {/* ── Compose tab ── */}
        {activeTab === "compose" && (
          <div className="ls-wrap">
            {/* Template bar */}
            <div style={{ marginBottom:"1.25rem" }}>
              <div style={{ fontSize:12,color:"var(--slate)",marginBottom:".6rem" }}>Quick-load a sample to see how the scanner works:</div>
              <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                {SCAN_TEMPLATES.map(t => (
                  <button key={t.type} className="ls-type-btn" onClick={() => loadTemplate(t)}
                    style={{ borderColor: msgType===t.type?"var(--teal)":undefined, color:msgType===t.type?"var(--teal)":undefined, background:msgType===t.type?"rgba(42,99,214,.08)":undefined }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ls-compose">
              <div className="ls-compose-title">Compose or paste message</div>
              <div className="ls-compose-sub">Write or paste any outgoing communication — violation letters, dues notices, emails, announcements, or any other homeowner correspondence.</div>

              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:"1rem" }}>
                <div>
                  <label className="fl">Message type</label>
                  <select className="fi" value={msgType} onChange={e => setMsgType(e.target.value)}>
                    {["Violation notice","Dues notice","Delinquency demand","Lien warning","Pet violation","Noise complaint","Parking notice","ARC denial","Accommodation response","Community announcement","Board meeting notice","Legal threat","General email"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="fl">Recipient (optional)</label>
                  <input className="fi" value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="e.g. Mr. Torres" />
                </div>
                <div>
                  <label className="fl">Unit (optional)</label>
                  <select className="fi" value={unit} onChange={e => setUnit(e.target.value)}>
                    <option value="">— Select unit —</option>
                    {owners.map(o => <option key={o.id} value={o.unit}>{o.unit} — {o.name}</option>)}
                  </select>
                </div>
              </div>

              <textarea
                className="ls-textarea"
                placeholder="Paste or type your outgoing message here…"
                value={message}
                onChange={e => { setMessage(e.target.value); setResult(null); }}
              />

              <div className="ls-actions">
                <button className="ls-scan-btn" onClick={runScan} disabled={!message.trim() || scanning}>
                  {scanning ? <><span className="ai-spin" style={{ width:14,height:14,borderColor:"rgba(15,31,61,.3)",borderTopColor:"var(--navy)" }} />Scanning…</> : <><Ic n="scan" s={15} /> Scan for legal issues</>}
                </button>
                {message && <button className="btn-g btn-sm" onClick={() => { setMessage(""); setResult(null); }}>Clear</button>}
                <span style={{ fontSize:12,color:"var(--slate)",marginLeft:4 }}>{message.length} characters</span>
              </div>
            </div>

            {/* Results */}
            {scanning && (
              <div style={{ background:"var(--navy2)",border:".5px solid var(--border)",borderRadius:"var(--r2)",padding:"2rem",textAlign:"center" }}>
                <div className="ls-scan-ring" style={{ margin:"0 auto 1rem" }} />
                <div style={{ fontSize:14,fontWeight:500,color:"var(--white)",marginBottom:".4rem" }}>Scanning for legal issues…</div>
                <div style={{ fontSize:12,color:"var(--slate)" }}>Checking Fair Housing Act, ADA compliance, due process, defamation risk, and HOA law…</div>
              </div>
            )}

            {result && !scanning && (() => {
              const vc = verdictConfig[result.verdict];
              return (
                <div>
                  {/* Verdict banner */}
                  <div style={{ background:vc.bg,border:`.5px solid ${vc.border}`,borderRadius:"var(--r2)",padding:"1.1rem 1.5rem",marginBottom:"1.25rem",display:"flex",alignItems:"center",gap:14 }}>
                    <span style={{ fontSize:28,flexShrink:0 }}>{vc.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:17,fontWeight:700,color:vc.color,marginBottom:4 }}>{vc.label}</div>
                      <div style={{ fontSize:13,color:"var(--slate)",lineHeight:1.5 }}>{result.summary}</div>
                    </div>
                    <div style={{ textAlign:"center",flexShrink:0 }}>
                      <svg width={64} height={64} viewBox="0 0 64 64">
                        <circle cx={32} cy={32} r={26} fill="none" stroke="var(--border2)" strokeWidth={6} />
                        <circle cx={32} cy={32} r={26} fill="none" stroke={result.riskScore<=25?"var(--green)":result.riskScore<=65?"var(--amber)":"var(--red)"} strokeWidth={6}
                          strokeDasharray={`${2*Math.PI*26}`} strokeDashoffset={`${2*Math.PI*26*(1-result.riskScore/100)}`}
                          strokeLinecap="round" transform="rotate(-90 32 32)" />
                        <text x={32} y={37} textAnchor="middle" fill="white" fontSize={16} fontWeight={700}>{result.riskScore}</text>
                      </svg>
                      <div style={{ fontSize:11,color:"var(--slate)",marginTop:2 }}>Risk score /100</div>
                    </div>
                  </div>

                  {/* Issues */}
                  {result.issues?.length > 0 ? (
                    <div style={{ marginBottom:"1.25rem" }}>
                      <div style={{ fontSize:12,fontWeight:700,color:"var(--slate)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:".75rem" }}>{result.issues.length} issue{result.issues.length!==1?"s":""} found</div>
                      {result.issues.map((issue,i) => (
                        <div key={i} className={`ls-issue ${issue.severity}`}>
                          <div className="ls-issue-title">
                            <span>{issue.severity==="high"?"🔴":issue.severity==="medium"?"🟡":"🔵"}</span>
                            <span style={{ color:issue.severity==="high"?"var(--red)":issue.severity==="medium"?"var(--amber)":"var(--teal)",fontSize:10,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase" }}>{issue.severity} risk</span>
                            <span style={{ fontSize:13,fontWeight:600 }}>{issue.title}</span>
                          </div>
                          <div style={{ fontSize:11,color:"var(--purple)",fontWeight:600,marginBottom:4,textTransform:"uppercase",letterSpacing:".04em" }}>{issue.category}</div>
                          <div className="ls-issue-body">{issue.description}</div>
                          {issue.fix && <div className="ls-issue-fix">💡 Suggested fix: {issue.fix}</div>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ background:"rgba(58,191,126,.06)",border:".5px solid rgba(58,191,126,.2)",borderRadius:"var(--r)",padding:".85rem 1rem",marginBottom:"1.25rem",fontSize:13,color:"var(--green)",display:"flex",alignItems:"center",gap:8 }}>
                      ✓ No legal issues detected. Message follows HOA best practices.
                    </div>
                  )}

                  {/* AI rewrite */}
                  {result.rewrite && (
                    <div style={{ marginBottom:"1.25rem" }}>
                      <div style={{ fontSize:12,fontWeight:700,color:"var(--teal)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:".5rem",display:"flex",alignItems:"center",gap:6 }}>
                        <Ic n="zap" s={12} /> AI-corrected version
                      </div>
                      <div style={{ fontSize:12,color:"var(--slate)",marginBottom:".75rem" }}>This rewrite preserves your intent while removing the legal issues flagged above:</div>
                      {useRewrite
                        ? <textarea className="ls-textarea" value={editText} onChange={e=>setEditText(e.target.value)} style={{ minHeight:180 }} />
                        : <div className="ls-rewrite">{result.rewrite}</div>
                      }
                      <div style={{ display:"flex",gap:8,marginTop:".75rem" }}>
                        <button className="btn btn-sm" onClick={() => { setUseRewrite(true); setEditText(result.rewrite); }}>
                          {useRewrite ? "Editing rewrite…" : "Use this rewrite ↓"}
                        </button>
                        {useRewrite && <button className="btn-g btn-sm" onClick={() => setResult(null)}>Re-scan revised version</button>}
                      </div>
                    </div>
                  )}

                  {/* Send decision */}
                  <div className="ls-send-row">
                    {result.approvedToSend ? (
                      <>
                        <button className="btn" onClick={() => { toast("Message approved and queued for send"); setMessage(""); setResult(null); }}>✅ Approved — send message</button>
                        <span style={{ fontSize:12,color:"var(--green)" }}>Cleared by AI legal scanner</span>
                      </>
                    ) : result.rewrite ? (
                      <>
                        <button className="btn" style={{ background:"var(--amber)",color:"var(--navy)" }} onClick={() => { toast("Revised message sent"); setMessage(useRewrite?editText:result.rewrite); setResult(null); }}>
                          ✦ Send revised version
                        </button>
                        <button className="btn-g" onClick={() => { setMessage(useRewrite?editText:result.rewrite); setResult(null); }}>Edit before sending</button>
                        <span style={{ fontSize:12,color:"var(--amber)" }}>Original flagged — send revised version only</span>
                      </>
                    ) : (
                      <>
                        <button className="btn-g" style={{ color:"var(--red)",borderColor:"rgba(224,82,82,.3)" }}>🚫 Do not send</button>
                        <span style={{ fontSize:12,color:"var(--red)" }}>Serious legal risk — consult HOA attorney before proceeding</span>
                      </>
                    )}
                  </div>

                  <div style={{ fontSize:11,color:"var(--slate)",marginTop:".75rem",lineHeight:1.6,padding:".65rem .85rem",background:"var(--navy3)",borderRadius:"var(--r)" }}>
                    ⚠ AI legal scanning provides general guidance only and does not constitute legal advice. For high-stakes communications or complex legal situations, consult a licensed HOA attorney.
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── History tab ── */}
        {activeTab === "history" && (
          <div>
            {history.length === 0 ? (
              <div style={{ textAlign:"center",padding:"3rem",color:"var(--slate)",fontSize:13 }}>
                <div style={{ fontSize:36,marginBottom:".75rem" }}>⚖️</div>
                No scans yet. Go to "Scan a message" to review your first communication.
              </div>
            ) : (
              <>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:"1.5rem" }}>
                  <div className="sc"><div className="sl">Total scanned</div><div className="sv">{history.length}</div></div>
                  <div className="sc"><div className="sl">Cleared</div><div className="sv t">{history.filter(h=>h.verdict==="CLEAR").length}</div></div>
                  <div className="sc"><div className="sl">Issues found</div><div className="sv a">{history.filter(h=>h.verdict!=="CLEAR").length}</div></div>
                </div>
                {history.map(h => (
                  <div key={h.id} className="ls-history-item" onClick={() => { setMessage(h.snippet); setActiveTab("compose"); }}>
                    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4 }}>
                      <span className={`ls-risk-pill ${h.verdict==="CLEAR"?"lsr-clear":h.verdict==="CAUTION"?"lsr-warn":"lsr-block"}`}>
                        {h.verdict==="CLEAR"?"✅ Clear":h.verdict==="CAUTION"?"⚠ Caution":"🚫 Blocked"}
                      </span>
                      <span style={{ fontSize:11,color:"var(--slate)" }}>{h.time}</span>
                    </div>
                    <div className="ls-history-snippet">{h.snippet}{h.snippet.length>=80?"…":""}</div>
                    <div className="ls-history-meta">
                      <span>{h.type}</span>
                      <span>Risk score: <strong style={{ color:h.riskScore<=25?"var(--green)":h.riskScore<=65?"var(--amber)":"var(--red)" }}>{h.riskScore}</strong></span>
                      {h.issues > 0 && <span style={{ color:"var(--amber)" }}>{h.issues} issue{h.issues!==1?"s":""} flagged</span>}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── What we check tab ── */}
        {activeTab === "risks" && (
          <div style={{ maxWidth:720 }}>
            <div style={{ fontSize:14,color:"var(--slate)",marginBottom:"1.5rem",lineHeight:1.7 }}>
              Every outgoing HOA communication is reviewed against these legal risk categories before it reaches a homeowner. The scanner flags issues, explains the legal risk, and suggests a corrected version.
            </div>
            {[
              { icon:"🏠", color:"var(--red)", title:"Fair Housing Act violations", desc:"Scans for language that could be construed as discriminatory based on race, color, national origin, religion, sex, familial status, or disability — even unintentionally. A single poorly worded sentence can trigger a HUD complaint." },
              { icon:"♿", color:"var(--purple)", title:"ADA & disability accommodation", desc:"Flags improper denials of reasonable accommodation requests, failure to engage in the interactive process, and language that violates the rights of homeowners with disabilities under the Fair Housing Act and ADA." },
              { icon:"⚖️", color:"var(--amber)", title:"Due process violations", desc:"Checks that violation notices include proper cure periods (typically 14 days under most CC&Rs), hearing rights, and appeal procedures. Fines issued without proper notice can be challenged and reversed." },
              { icon:"🗣️", color:"var(--red)", title:"Defamation & harassment risk", desc:"Identifies accusatory, threatening, or inflammatory language that could expose the board to personal liability. HOA board members have been personally sued for harassing letters sent to homeowners." },
              { icon:"💰", color:"var(--amber)", title:"Improper fine threats", desc:"Verifies that fine amounts and escalation timelines align with your CC&Rs and state law. Threatening fines not authorized by governing documents is an enforceable claim against the HOA." },
              { icon:"🔍", color:"var(--teal)", title:"Selective enforcement risk", desc:"Detects language suggesting inconsistent enforcement across homeowners, which is one of the most common Fair Housing complaint triggers and can void the HOA's ability to enforce the rule at all." },
              { icon:"🔒", color:"var(--slate)", title:"Privacy & data exposure", desc:"Catches messages that share homeowner personal information (financial details, personal circumstances, medical information) with other residents or unauthorized parties." },
              { icon:"📋", color:"var(--purple)", title:"Legal overreach", desc:"Confirms the HOA has proper authority for threatened actions (liens, towing, legal action) and that required notice periods have been met. Threatening foreclosure prematurely can itself be a legal violation." },
              { icon:"💬", color:"var(--teal)", title:"Tone & professionalism", desc:"Flags unnecessarily hostile, condescending, or inflammatory language that, while not always illegal, damages community relations and makes disputes more likely to escalate to litigation." },
            ].map((r,i) => (
              <div key={i} style={{ display:"flex",gap:14,padding:"1rem",background:"var(--navy2)",border:`.5px solid var(--border)`,borderLeft:`3px solid ${r.color}`,borderRadius:`0 var(--r) var(--r) 0`,marginBottom:8 }}>
                <span style={{ fontSize:22,flexShrink:0 }}>{r.icon}</span>
                <div>
                  <div style={{ fontSize:14,fontWeight:600,color:"var(--white)",marginBottom:4 }}>{r.title}</div>
                  <div style={{ fontSize:13,color:"var(--slate)",lineHeight:1.6 }}>{r.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI Support Chat Widget ───────────────────────────────────────────────────
const SUPPORT_SYSTEM = `You are Aria, the AI support assistant for Board-Armor — an AI-powered HOA management platform for self-managed homeowners associations. You are helpful, warm, and concise.

Board-Armor features you can help with:
- Homeowner Directory: adding/editing owners, units, email opt-in, dues balances
- Dues Ledger: tracking payments, marking paid, delinquency sequences
- Violations: logging violations, AI letter writer, sending notices
- Email Center: announcements, automated sequences, delinquency emails
- Document Vault: uploading CC&Rs/bylaws, asking questions in plain English
- Budget & Finance: income/expense tracking, budget vs actual, AI treasurer report
- General Ledger: double-entry accounting, journal entries, bank feeds
- Insurance & Compliance: policy renewals, vendor COIs, compliance checklist
- Compliance Auditor: meeting notice window enforcement, state statutes
- Electronic Voting: creating ballots, quorum tracking, recording votes
- Amenity Reservations: booking pool/clubhouse, deposit management
- USPS Mailer: sending physical letters via Lob API
- Mobile Portal: homeowner self-service, Apple Pay, maintenance requests
- Legal Scanner: pre-send review for Fair Housing, due process, defamation risk
- AI Legal Advisor: HOA law guidance for OR, CA, FL, TX, NV, AZ
- Settings: HOA profile, billing, board members, custom domain
- Pricing: $59/month flat for Pro plan (any HOA size), 30-day free trial

Rules:
- Keep answers under 4 sentences unless a step-by-step is needed
- For step-by-step instructions, use numbered format
- If unsure, say so and offer to escalate to a human
- Never make up features that don't exist
- Always be encouraging — boards are volunteers doing hard work
- If the user seems frustrated, acknowledge it before helping
- Flag billing issues, data loss concerns, or legal questions for human escalation`;

const QUICK_QUESTIONS = [
  "How do I add a homeowner?",
  "How does the AI letter writer work?",
  "How do I upload CC&Rs?",
  "Can I import from a spreadsheet?",
  "How does billing work?",
  "What states does the legal advisor cover?",
];

function SupportChat() {
  const [open, setOpen]         = useState(false);
  const [input, setInput]       = useState("");
  const [thinking, setThinking] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1, role: "bot",
      text: "Hi! I'm Aria, your Board-Armor support assistant. 👋\n\nI can help you with any feature, billing question, or how-to. What can I help you with today?",
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    }
  ]);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const sendMessage = async (text) => {
    const q = text || input.trim();
    if (!q) return;
    setInput("");
    setShowEscalate(false);

    const userMsg = {
      id: Date.now(), role: "user", text: q,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
    setMessages(prev => [...prev, userMsg]);
    setThinking(true);

    // Build conversation history for context
    const history = messages.slice(-6).map(m => ({
      role: m.role === "bot" ? "assistant" : "user",
      content: m.text,
    }));
    history.push({ role: "user", content: q });

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 500,
          system: SUPPORT_SYSTEM,
          messages: history,
        }),
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "I'm having trouble connecting. Please try again in a moment.";

      // Detect escalation triggers
      const escalateTriggers = ["billing issue", "charge", "refund", "cancel", "data loss", "delete", "lost my data", "broken", "not working", "bug", "error", "can't log in", "locked out"];
      const shouldEscalate = escalateTriggers.some(t => q.toLowerCase().includes(t));

      setThinking(false);
      setMessages(prev => [...prev, {
        id: Date.now() + 1, role: "bot", text: reply,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      }]);

      if (shouldEscalate) setShowEscalate(true);

    } catch {
      setThinking(false);
      setMessages(prev => [...prev, {
        id: Date.now() + 1, role: "bot",
        text: "I'm having trouble connecting right now. You can email us directly at support@board-armor.app and we'll respond within a few hours.",
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      }]);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // Show chips only if last message is from bot
  const showChips = messages[messages.length - 1]?.role === "bot" && !thinking && messages.length < 3;

  return (
    <>
      {/* FAB button */}
      <button
        className={`support-fab ${open ? "open" : ""}`}
        onClick={() => setOpen(o => !o)}
        title="Get support"
        aria-label={open ? "Close support" : "Open support chat"}
      >
        {open ? "✕" : "💬"}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="support-panel">
          {/* Header */}
          <div className="support-header">
            <div className="support-header-avatar">🛡️</div>
            <div>
              <div className="support-header-name">Board-Armor Support</div>
              <div className="support-header-sub">Aria · AI assistant · usually instant</div>
            </div>
            <div className="support-online" title="Online" />
          </div>

          {/* Messages */}
          <div className="support-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`support-msg ${msg.role}`}>
                <div className={`support-msg-avatar ${msg.role}`}>
                  {msg.role === "bot" ? "🛡️" : "👤"}
                </div>
                <div>
                  <div className={`support-bubble ${msg.role}`}
                    style={{ whiteSpace: "pre-wrap" }}>
                    {msg.text}
                  </div>
                  <div className="support-time">{msg.time}</div>
                </div>
              </div>
            ))}

            {/* Escalation card */}
            {showEscalate && (
              <div className="support-escalate">
                <strong>Want to talk to a real person?</strong>
                This sounds like something our team should handle directly. We typically respond within a few hours during business hours.
                <br/>
                <button className="support-escalate-link" onClick={() => window.open("mailto:support@board-armor.app?subject=Support Request", "_blank")}>
                  📧 Email support@board-armor.app →
                </button>
              </div>
            )}

            {/* Thinking indicator */}
            {thinking && (
              <div className="support-msg bot">
                <div className="support-msg-avatar">🛡️</div>
                <div className="support-bubble thinking">
                  <div className="thinking-dot" />
                  <div className="thinking-dot" />
                  <div className="thinking-dot" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick chips — shown early in conversation */}
          {showChips && (
            <div className="support-chips">
              {QUICK_QUESTIONS.map(q => (
                <button key={q} className="support-chip" onClick={() => sendMessage(q)}>{q}</button>
              ))}
            </div>
          )}

          {/* Input row */}
          <div className="support-input-row">
            <textarea
              ref={inputRef}
              className="support-input"
              placeholder="Ask anything about Board-Armor…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={1}
            />
            <button
              className="support-send"
              onClick={() => sendMessage()}
              disabled={!input.trim() || thinking}
              title="Send"
            >
              ➤
            </button>
          </div>

          <div className="support-footer-bar">
            Powered by Board-Armor AI · <span style={{ color: "#2A63D6", fontWeight: 600 }}>support@board-armor.app</span>
          </div>
        </div>
      )}
    </>
  );
}

// ─── App root ─────────────────────────────────────────────────────────────────
export default function App() {
  // ── Auth + org state ───────────────────────────────────────────────────────
  const [user,    setUser]    = useState(null);   // { id, name, role, initials, org, orgId }
  const [loading, setLoading] = useState(true);   // checking existing session on mount

  // ── Real data from Supabase (replaces all INIT_* constants) ───────────────
  const [owners,       setOwners]       = useState([]);
  const [violations,   setViolations]   = useState([]);
  const [dues,         setDues]         = useState([]);
  const [emailLog,     setEmailLog]     = useState([]);
  const [maintenance,  setMaintenance]  = useState([]);
  const [announcements,setAnnouncements]= useState([]);
  const [workOrders,   setWorkOrders]   = useState([]);
  const [vendors,      setVendors]      = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [ballots,      setBallots]      = useState([]);
  const [bookings,     setBookings]     = useState([]);
  const [dataLoading,  setDataLoading]  = useState(false);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [page,         setPage]         = useState("dashboard");
  const [toastMsg,     setToastMsg]     = useState("");
  const [portalMode,   setPortalMode]   = useState(false);
  const [checkout,     setCheckout]     = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState("pro");
  const [paywalled,    setPaywalled]    = useState(false);
  const [navOpen,      setNavOpen]      = useState(false);
  const mainRef = useRef(null);

  // Update org context whenever user changes so components get real org name
  useEffect(() => { if (user?.org) setOrgContext(user.org); }, [user]);

  // Paywall: if logged in and no active subscription, force checkout
  useEffect(() => {
    if (!user || user.isDemo) return;
    const ADMIN_EMAILS = ["garygatewood@gmail.com", "cg5@gmail.com"];
    if (ADMIN_EMAILS.includes(user?.email || user?.org?.email)) { setPaywalled(false); return; }
    const status = user?.org?.plan_status;
    const isActive = status === "active" || status === "trialing";
    setPaywalled(!isActive);
  }, [user]);

  // Auto-navigate brand new accounts (no homeowners yet) into Getting Started once, without blocking the dashboard
  const ADMIN_ORGS = ["a1b2c3d4-1111-1111-1111-000000000001"];
  const ADMIN_EMAILS = ["garygatewood@gmail.com", "cg5@gmail.com"];
  useEffect(() => {
    const isAdmin = ADMIN_EMAILS.includes(user?.email) || ADMIN_ORGS.includes(user?.orgId);
    if (user && !user.isDemo && !isAdmin && owners.length === 0 && !loading && !paywalled) {
      // Only trigger once per session using sessionStorage
      const key = `ba_onboarded_${user.orgId}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        setPage("getting-started");
      }
    }
  }, [user, owners, loading, paywalled]);

  const toast = useCallback(msg => setToastMsg(msg), []);

  // ── On mount: check for demo mode or existing session ────────────────────
  useEffect(() => {
    // Check for ?demo=true URL parameter — load demo data instantly
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("demo") === "true") {
        LIVE_DEMO_MODE = true;
        setUser({
          id: "demo-user",
          name: "Sarah Kim",
          role: "Board President",
          initials: "SK",
          org: { id: "demo-org", name: "Maplewood Commons HOA", slug: "maplewood-commons", plan: "pro", referral_code: "DEMO1234" },
          orgId: "demo-org",
          isDemo: true,
        });
        // Load all INIT data for demo
        setOwners(INIT_OWNERS);
        setViolations(INIT_VIOLATIONS);
        setDues(INIT_DUES);
        setEmailLog(INIT_LOG);
        setMaintenance(INIT_MAINTENANCE);
        setAnnouncements(INIT_ANNOUNCEMENTS);
        setWorkOrders(INIT_WORKORDERS);
        setVendors(INIT_VENDORS);
        setTransactions(INIT_TRANSACTIONS);
        setBallots(INIT_BALLOTS);
        setBookings(INIT_BOOKINGS);
        setLoading(false);
        return;
      }
    } catch(e) {}

  // ── On mount: check for existing session ─────────────────────────────────

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // Use server-side function to look up member + org
        try {
          const res = await fetch('/.netlify/functions/get-member', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: session.user.id }),
          });
          if (res.ok) {
            const { member, org } = await res.json();
            setUser({
              id: session.user.id,
              name: member.name,
              role: member.role,
              initials: member.initials || member.name.split(" ").map(n => n[0]).join("").toUpperCase(),
              org,
              orgId: member.org_id,
            });
          }
        } catch { /* session restore failed silently — user will need to sign in */ }
      }
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") { setUser(null); setOwners([]); setViolations([]); setDues([]); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load data when user logs in ───────────────────────────────────────────
  useEffect(() => {
    if (!user?.orgId) return;

    // Load from Supabase
    const oid = user.orgId;
    setDataLoading(true);
    Promise.all([
      db.homeowners.list(oid), db.violations.list(oid), db.dues.list(oid),
      db.emailLog.list(oid), db.maintenance.list(oid), db.announcements.list(oid),
      db.workOrders.list(oid), db.vendors.list(oid), db.transactions.list(oid),
      db.ballots.list(oid), db.reservations.list(oid),
    ]).then(([h, v, d, el, m, an, wo, vn, tx, bl, bk]) => {
      // Normalize snake_case from Supabase to camelCase expected by components
      const norm = arr => (arr || []).map(o => ({
        ...o,
        // homeowners
        duesOwed:    o.dues_owed    ?? o.duesOwed   ?? 0,
        duesPaid:    o.dues_paid    ?? o.duesPaid   ?? 0,
        emailOptIn:  o.email_opt_in ?? o.emailOptIn ?? true,
        moveIn:      o.move_in      ?? o.moveIn,
        // violations & dues — normalize owner name
        owner:       o.owner_name   ?? o.owner      ?? "",
        owner_name:  o.owner_name   ?? o.owner      ?? "",
        // violations
        emailSent:   o.email_sent   ?? o.emailSent  ?? false,
        date:        o.date_observed ?? o.date,
        // dues
        amount:      o.amount       ?? 0,
        due_date:    o.due_date     ?? o.due,
        paid_date:   o.paid_date    ?? o.paid,
        // maintenance
        boardNote:   o.board_note   ?? o.boardNote,
        // work orders
        vendorId:    o.vendor_id    ?? o.vendorId,
      }));
      if (h.data)  setOwners(norm(h.data));
      if (v.data)  setViolations(norm(v.data));
      if (d.data)  setDues(norm(d.data));
      if (el.data) setEmailLog(norm(el.data));
      if (m.data)  setMaintenance(norm(m.data));
      if (an.data) setAnnouncements(an.data);
      if (wo.data) setWorkOrders(norm(wo.data));
      if (vn.data) setVendors(vn.data);
      if (tx.data) setTransactions(tx.data);
      if (bl.data) setBallots(bl.data);
      if (bk.data) setBookings(bk.data);
    }).finally(() => setDataLoading(false));
  }, [user?.orgId]);

  // ── Org name as reactive state — drives all JSX, triggers re-renders ────────
  const [orgName, setOrgName_]   = useState("Your HOA");
  const [orgEmail, setOrgEmail_] = useState("");
  useEffect(() => {
    if (user?.org?.name) {
      setOrgName_(user.org.name);
      setOrgEmail_(user.org.email || "");
      setOrgContext(user.org); // keep global in sync for AI prompts
    }
  }, [user?.org?.name]);

  // ── Memoized badge counts ─────────────────────────────────────────────────
  const openV           = useMemo(() => violations.filter(v => v.status !== "resolved").length, [violations]);
  const unsentV         = useMemo(() => violations.filter(v => v.status !== "resolved" && !v.email_sent).length, [violations]);
  const openMaintenance = useMemo(() => maintenance.filter(m => m.status === "open").length, [maintenance]);
  const unassignedWOs   = useMemo(() => workOrders.filter(w => w.status === "open" && !w.vendor_id).length, [workOrders]);
  const activeVotes     = useMemo(() => ballots.filter(b => b.status === "active").length, [ballots]);
  const complianceAlerts = 0; // loaded from DB in InsuranceCompliance component

  // ── Memoized nav ──────────────────────────────────────────────────────────
  const nav = useMemo(() => [
    { id: "dashboard",    label: "Dashboard",              icon: "home",    badge: null },
    { id: "directory",    label: "Homeowners",             icon: "users",   badge: null },
    { id: "dues",         label: "Dues Ledger",            icon: "dollar",  badge: null },
    { id: "violations",   label: "Violations",             icon: "alert",   badge: openV || null },
    { id: "email",        label: "Email Center",           icon: "mail",    badge: unsentV || null },
    { id: "maintenance",  label: "Maintenance",            icon: "wrench",  badge: openMaintenance || null },
    { id: "vendors",      label: "Vendors & Work Orders",  icon: "wrench",  badge: unassignedWOs || null },
    { id: "budget",       label: "Budget & Finance",       icon: "chart",   badge: null },
    { id: "vault",        label: "Document Vault",         icon: "vault",   badge: null },
    { id: "compliance",   label: "Insurance & Compliance", icon: "shield",  badge: complianceAlerts || null },
    { id: "auditor",      label: "Compliance Auditor",     icon: "audit",   badge: null },
    { id: "uspsmail",     label: "USPS One-Click Mailer",  icon: "mail2",   badge: null },
    { id: "mobileportal", label: "Mobile Resident Portal", icon: "phone",   badge: null },
    { id: "voting",       label: "Electronic Voting",      icon: "vote",    badge: activeVotes || null },
    { id: "amenities",    label: "Amenity Reservations",   icon: "amenity", badge: null },
    { id: "ledger",       label: "General Ledger",         icon: "ledger",  badge: null },
    { id: "reserve",      label: "Reserve Fund",           icon: "shield",  badge: null },
    { id: "finreport",    label: "Financial Report",       icon: "chart",   badge: null },
    { id: "emergency",    label: "Emergency Response",     icon: "alert",   badge: null },
    { id: "preloss",      label: "Pre-Loss Documentation",  icon: "camera",  badge: null },
    { id: "claimtracker", label: "Claim Tracker",           icon: "file",    badge: null },
    { id: "mplc",          label: "Claims Partner (MPLC)",  icon: "phone",   badge: null },
    { id: "bankfeed",     label: "Bank Feed",              icon: "bank",    badge: null },
    { id: "ai",      label: "AI Intelligence",        icon: "brain",   badge: null, badgePurple: true },
    { id: "legal",   label: "AI Legal Advisor",       icon: "scale",   badge: null, badgeAmber: true },
    { id: "scanner", label: "Legal Pre-Send Scanner", icon: "scan",    badge: null },
    { id: "flyer",   label: "Flyer Generator",        icon: "image",   badge: null },
    { id: "settings",label: "Settings",               icon: "settings",badge: null },
  ], [openV, unsentV, openMaintenance, unassignedWOs, activeVotes]);

  // ── Sign out ──────────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  // ── Loading screen (checking session) ────────────────────────────────────
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "system-ui", color: "#64748b", flexDirection: "column", gap: 12 }}>
      <div style={{ width: 32, height: 32, border: "3px solid #f3f4f6", borderTopColor: "#2A63D6", borderRadius: "50%", animation: "spin .7s linear infinite" }} />
      <div style={{ fontSize: 14 }}>Loading Board-Armor…</div>
    </div>
  );

  if (!user) return (<><style>{CSS}</style><Login onLogin={setUser} /></>);

  if (paywalled) return (
    <><style>{CSS}</style>
      <PaywallScreen
        orgName={orgName}
        orgId={user?.orgId}
        onSuccess={() => setPaywalled(false)}
      />
    </>
  );

  if (checkout) return (
    <><style>{CSS}</style>
      <StripeCheckout
        initialPlan={checkoutPlan}
        orgId={user?.orgId}
        onBack={() => setCheckout(false)}
        onSuccess={({ plan }) => { setCheckout(false); toast(`🎉 ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan activated!`); }}
      />
    </>
  );

  if (portalMode) return (
    <><style>{CSS}</style>
      <HomeownerPortal
        owners={owners} dues={dues} violations={violations}
        maintenance={maintenance} setMaintenance={setMaintenance}
        announcements={announcements}
        orgName={orgName} orgEmail={orgEmail}
        onClose={() => setPortalMode(false)}
      />
    </>
  );

  // ── Context value — shared across all page components ────────────────────
  const ctx = {
    user, orgId: user.orgId, org: user.org, orgName, orgEmail,
    owners, setOwners, violations, setViolations,
    dues, setDues, emailLog, setEmailLog,
    maintenance, setMaintenance, announcements,
    workOrders, setWorkOrders, vendors, setVendors,
    transactions, setTransactions, ballots, setBallots,
    bookings, setBookings, toast, setPage, dataLoading,
  };

  return (
    <AppCtx.Provider value={ctx}>
      <style>{CSS}</style>
      {/* Demo mode banner — outside app grid so it spans full width */}
      {user?.isDemo && (
        <div style={{
          background:"linear-gradient(90deg,#2A63D6,#1B3C78)",
          color:"#fff",
          textAlign:"center",
          padding:"10px 16px",
          fontSize:13,
          fontWeight:600,
          display:"flex",
          alignItems:"center",
          justifyContent:"center",
          gap:16,
          flexWrap:"wrap",
          position:"sticky",
          top:0,
          zIndex:9999,
        }}>
          <span>🎯 You're exploring Board-Armor in demo mode — this is a live preview with sample data</span>
          <button
            onClick={() => window.location.href = "https://app.board-armor.com"}
            style={{background:"#fff",color:"#2A63D6",border:"none",borderRadius:6,padding:"5px 14px",fontWeight:700,cursor:"pointer",fontSize:13}}
          >Start free trial →</button>
        </div>
      )}
      {/* Data loading overlay */}
      {dataLoading && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 3, background: "var(--teal)", zIndex: 9999, animation: "none" }} />
      )}
      <button onClick={() => setNavOpen(true)} style={{display:"none",position:"fixed",top:"12px",left:"12px",zIndex:1001,background:"var(--sb-bg)",border:".5px solid var(--sb-border)",borderRadius:"8px",padding:"8px 11px",cursor:"pointer",fontSize:"20px",color:"#fff",lineHeight:1}} className="mob-ham" aria-label="Open menu">☰</button>
      <div onClick={() => setNavOpen(false)} style={{display:navOpen?"block":"none",position:"fixed",inset:0,background:"rgba(255,255,255,.55)",zIndex:999,backdropFilter:"blur(1px)"}} />
      <div className="app">
        <aside className="sb" style={navOpen ? {left:0,boxShadow:"4px 0 20px rgba(255,255,255,.5)"} : {}}>
          <button onClick={() => setNavOpen(false)} style={{display:"none",position:"absolute",top:"12px",right:"12px",background:"rgba(255,255,255,.08)",border:".5px solid var(--border2)",borderRadius:"6px",padding:"5px 10px",cursor:"pointer",fontSize:"13px",color:"#fff",fontWeight:700}} className="mob-close">✕ Close</button>
          <div className="sb-logo" style={{ display: "flex", alignItems: "center", gap: 8 }}><BoardArmorLogo size={38} />Board-<span>Armor</span><span className="sb-ver">v3</span></div>
          <div className={`ni ${page === "getting-started" ? "active" : ""}`} style={{ color: "var(--purple)", fontWeight: 700, background: "rgba(167,139,250,.1)", border: ".5px solid rgba(167,139,250,.25)", borderRadius: "var(--r)", margin: "0 .75rem .85rem", cursor: "pointer" }} onClick={() => { setPage("getting-started"); setNavOpen(false); }}>
            <Ic n="zap" s={14} />Getting Started
          </div>
          <div className="sb-sec">Navigation</div>
          {[
            { group: "community", label: "Community", ids: ["dashboard","directory","dues","violations","email","maintenance"] },
            { group: "operations", label: "Operations", ids: ["vendors","amenities","voting","uspsmail","mobileportal"] },
            { group: "financial", label: "Financial", ids: ["budget","ledger","reserve","finreport","bankfeed"] },
            { group: "risk", label: "Risk & Insurance", ids: ["emergency","preloss","claimtracker","mplc","compliance"] },
            { group: "ai", label: "AI Tools", ids: ["vault","ai","legal","scanner","auditor","flyer"] },
            { group: "account", label: "Account", ids: ["settings"] },
          ].map(({ group, label, ids }) => {
            const items = nav.filter(n => ids.includes(n.id));
            if (!items.length) return null;
            return (
              <div key={group} className={`sb-group ${group}`}>
                <div className="sb-group-label">{label}</div>
                {items.map(n => (
                  <div key={n.id} className={`ni ${page === n.id ? "active" : ""}`} onClick={() => { setPage(n.id); setNavOpen(false); mainRef.current?.scrollTo({top:0,behavior:"instant"}); }}>
                    <Ic n={n.icon} s={15} />{n.label}
                    {n.badge > 0 && <span className={`ni-badge${n.badgePurple ? " purple" : n.badgeAmber ? " amber" : ""}`}>{n.badge}</span>}
                  </div>
                ))}
              </div>
            );
          })}
          <div className="sb-sec">Community</div>
          <div className="ni" style={{ cursor: "default", fontSize: 12 }}>
            <Ic n="settings" s={14} />{user.org?.name || "Your HOA"}
          </div>
          <div className="ni" style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => setPortalMode(true)}>
            <Ic n="portal" s={15} />Homeowner Portal
            <span style={{ marginLeft: "auto", fontSize: 9, background: "var(--teal)", color: "var(--navy)", borderRadius: 10, padding: "1px 6px", fontWeight: 700 }}>NEW</span>
          </div>
          <div className="sb-ft">
            <div style={{ background: "rgba(42,99,214,.07)", border: ".5px solid rgba(42,99,214,.18)", borderRadius: "var(--r)", padding: ".6rem .85rem", marginBottom: ".85rem", cursor: "pointer" }}
              onClick={() => { setCheckoutPlan("pro"); setCheckout(true); }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--teal)", marginBottom: 2 }}>Starter · $69/mo · up to 75 units</div>
              <div style={{ fontSize: 11, color: "var(--sb-text-dim)" }}>All features included → upgrade as you grow</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="ua">{user.initials}</div>
              <div><div className="un">{user.name}</div><div className="ur">{user.role}</div></div>
            </div>
            <button className="lo" onClick={signOut}>Sign out</button>
          </div>
        </aside>
        <main className="main" ref={mainRef}>
          {page === "dashboard"    && <Dashboard owners={owners} violations={violations} dues={dues} emailLog={emailLog} setPage={setPage} orgName={orgName} orgEmail={orgEmail} />}
          {page === "getting-started" && <GettingStarted owners={owners} setOwners={setOwners} toast={toast} orgName={orgName} orgEmail={orgEmail} setPage={setPage} />}
          {page === "directory"    && <Directory owners={owners} setOwners={setOwners} dues={dues} setDues={setDues} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "dues"         && <DuesLedger dues={dues} setDues={setDues} owners={owners} setOwners={setOwners} setEmailLog={setEmailLog} toast={toast} orgName={orgName} orgEmail={orgEmail} stripeLink={localStorage.getItem("ba_stripe_link") || ""} />}
          {page === "violations"   && <Violations violations={violations} setViolations={setViolations} owners={owners} setEmailLog={setEmailLog} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "email"        && <EmailCenter owners={owners} emailLog={emailLog} setEmailLog={setEmailLog} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "maintenance"  && <MaintenanceBoard maintenance={maintenance} setMaintenance={setMaintenance} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "vendors"      && <VendorTracker workOrders={workOrders} setWorkOrders={setWorkOrders} vendors={vendors} setVendors={setVendors} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "budget"       && <BudgetFinance owners={owners} dues={dues} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "vault"        && <DocumentVault toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "compliance"   && <InsuranceCompliance vendors={vendors} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "auditor"      && <ComplianceAuditor toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "uspsmail"     && <USPSMailer owners={owners} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "mobileportal" && <MobilePortal owners={owners} dues={dues} violations={violations} maintenance={maintenance} setMaintenance={setMaintenance} announcements={announcements} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "voting"       && <ElectronicVoting owners={owners} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "amenities"    && <AmenityReservations owners={owners} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "reserve"      && <ReserveFund toast={toast} orgName={orgName} />}
          {page === "finreport"    && <FinancialReport dues={dues} owners={owners} toast={toast} orgName={orgName} />}
          {page === "emergency"    && <EmergencyResponse owners={owners} toast={toast} orgName={orgName} orgEmail={orgEmail} isDemo={!!user?.isDemo} />}
          {page === "preloss"      && <PreLossDocumentation toast={toast} orgName={orgName} />}
          {page === "claimtracker" && <ClaimTracker toast={toast} orgName={orgName} />}
          {page === "mplc"         && <MPLCPartner toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "ledger"       && (
            <div>
              <div className="ph"><div><div className="pt">General Ledger</div><div className="ps">Full double-entry accounting and financial reporting</div></div></div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "5rem 2rem", textAlign: "center" }}>
                <div style={{ fontSize: 48, marginBottom: "1rem" }}>📊</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#1B3C78", marginBottom: ".5rem" }}>General Ledger — Coming Soon</div>
                <div style={{ fontSize: 14, color: "var(--slate)", maxWidth: 420, lineHeight: 1.7, marginBottom: "2rem" }}>
                  Full double-entry accounting, chart of accounts, journal entries, and financial statements are coming in a future update. Your HOA's complete financial picture in one place.
                </div>
                <div style={{ background: "rgba(42,99,214,.07)", border: ".5px solid rgba(42,99,214,.2)", borderRadius: "var(--r2)", padding: "1rem 1.5rem", fontSize: 13, color: "var(--slate)", maxWidth: 380 }}>
                  <strong style={{ color: "var(--fg)" }}>In the meantime:</strong> Use the Dues Ledger to track collections and the Budget section under Financials for expense planning.
                </div>
              </div>
            </div>
          )}
          {page === "bankfeed"     && (
            <div>
              <div className="ph"><div><div className="pt">Bank Feed</div><div className="ps">Connect your HOA bank account to automatically import transactions</div></div></div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "5rem 2rem", textAlign: "center" }}>
                <div style={{ fontSize: 48, marginBottom: "1rem" }}>🏦</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#1B3C78", marginBottom: ".5rem" }}>Bank Feed — Coming Soon</div>
                <div style={{ fontSize: 14, color: "var(--slate)", maxWidth: 420, lineHeight: 1.7, marginBottom: "2rem" }}>
                  Direct bank connection via Plaid is coming in a future update. You'll be able to automatically import transactions, reconcile dues payments, and track HOA expenses in real time.
                </div>
                <div style={{ background: "rgba(42,99,214,.07)", border: ".5px solid rgba(42,99,214,.2)", borderRadius: "var(--r2)", padding: "1rem 1.5rem", fontSize: 13, color: "var(--slate)", maxWidth: 380 }}>
                  <strong style={{ color: "var(--fg)" }}>In the meantime:</strong> Use the General Ledger to manually track income and expenses, and the Dues Ledger to record payments.
                </div>
              </div>
            </div>
          )}
          {page === "ai"           && <AIHub owners={owners} violations={violations} dues={dues} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "legal"        && <LegalAdvisor orgName={orgName} orgEmail={orgEmail} />}
          {page === "scanner"      && <LegalScanner owners={owners} toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "flyer"        && <FlyerGenerator toast={toast} orgName={orgName} orgEmail={orgEmail} />}
          {page === "settings"     && <Settings user={user} owners={owners} toast={toast} onUpgrade={() => { setCheckoutPlan("pro"); setCheckout(true); }} orgName={orgName} orgEmail={orgEmail} />}
        </main>
      </div>
      {toastMsg && <Toast msg={toastMsg} onDone={() => setToastMsg("")} />}
      <SupportChat />
    </AppCtx.Provider>
  );
}
